// Increment this when adding/modifying questions so auto-merge picks up changes
export const CONTENT_VERSION = 4;

/**
 * CCNP ENCOR 350-401 Exam Pressure Simulator — Question Pool
 *
 * Covers exam blueprint v1.1 domains with approximate weighting:
 *   Domain 1.0 Architecture      (15%) ~9 questions
 *   Domain 2.0 Virtualization    (10%) ~6 questions
 *   Domain 3.0 Infrastructure    (30%) ~18 questions
 *   Domain 4.0 Network Assurance (10%) ~6 questions
 *   Domain 5.0 Security          (20%) ~12 questions
 *   Domain 6.0 Automation        (15%) ~9 questions
 *
 * Question type mix: ~70% single_choice, ~20% multi_choice, ~10% drag_drop
 * Expert trap warnings: 8 questions flagged
 */

export function getQuestionPool() {
  return [

    // =========================================================
    // DOMAIN 1.0 — ARCHITECTURE (~9 questions)
    // =========================================================

    {
      id: "arch-001",
      type: "single_choice",
      domain: "1.0",
      subdomain: "SD-Access",
      difficulty: 2,
      prompt: "Which component in SD-Access is responsible for maintaining the host database (EID-to-RLOC mappings)?",
      options: [
        { id: "A", text: "Edge Node", is_correct: false },
        { id: "B", text: "Control Plane Node", is_correct: true },
        { id: "C", text: "Border Node", is_correct: false },
        { id: "D", text: "Fabric Wireless Controller", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The Control Plane Node runs LISP Map Server and Map Resolver (MS/MR) functions. It stores and resolves EID-to-RLOC mappings so that fabric edge nodes can forward traffic to the correct destination RLOC (underlay IP).",
      cisco_reference: "ENCOR OCG Chapter 16 — SD-Access Fabric",
      expert_trap_warning: null,
      tags: ["sd-access", "control-plane", "lisp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "arch-002",
      type: "single_choice",
      domain: "1.0",
      subdomain: "SD-Access",
      difficulty: 2,
      prompt: "Which SD-Access fabric node role connects the fabric to external networks (e.g., internet, data center)?",
      options: [
        { id: "A", text: "Edge Node", is_correct: false },
        { id: "B", text: "Control Plane Node", is_correct: false },
        { id: "C", text: "Border Node", is_correct: true },
        { id: "D", text: "Intermediate Node", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Border Nodes are the ingress/egress points between the SD-Access fabric and external Layer 3 networks. They perform the LISP encap/decap for traffic leaving or entering the fabric overlay.",
      cisco_reference: "ENCOR OCG Chapter 16",
      expert_trap_warning: null,
      tags: ["sd-access", "border-node"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "arch-003",
      type: "single_choice",
      domain: "1.0",
      subdomain: "SD-WAN",
      difficulty: 2,
      prompt: "In Cisco SD-WAN, which plane is responsible for distributing routing and policy information to vEdge/cEdge routers?",
      options: [
        { id: "A", text: "vManage (management plane)", is_correct: false },
        { id: "B", text: "vSmart (control plane)", is_correct: true },
        { id: "C", text: "vBond (orchestration plane)", is_correct: false },
        { id: "D", text: "vAnalytics (data plane)", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "vSmart controllers run OMP (Overlay Management Protocol) and distribute routing updates, policies, and key exchange information to all WAN Edge routers. vBond handles initial authentication/onboarding, and vManage is the GUI/configuration plane.",
      cisco_reference: "ENCOR OCG Chapter 17 — SD-WAN",
      expert_trap_warning: null,
      tags: ["sd-wan", "vsmart", "omp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "arch-004",
      type: "multi_choice",
      domain: "1.0",
      subdomain: "SD-WAN",
      difficulty: 3,
      prompt: "Which TWO statements about OMP (Overlay Management Protocol) in Cisco SD-WAN are correct? (Choose two)",
      options: [
        { id: "A", text: "OMP runs between vEdge routers and vSmart controllers using TCP port 23456", is_correct: true },
        { id: "B", text: "OMP uses DTLS or TLS for securing control-plane sessions", is_correct: true },
        { id: "C", text: "OMP routes are redistributed directly into BGP without any policy", is_correct: false },
        { id: "D", text: "OMP uses UDP port 4789 for VXLAN encapsulation", is_correct: false }
      ],
      correct_answer: ["A", "B"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "OMP sessions are established between WAN Edge devices and vSmart controllers over TLS or DTLS-secured connections. OMP carries route updates (vRoutes), TLOCs, and policies. UDP 4789 is used for VXLAN data plane, not OMP.",
      cisco_reference: "ENCOR OCG Chapter 17",
      expert_trap_warning: null,
      tags: ["sd-wan", "omp", "security"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "arch-005",
      type: "single_choice",
      domain: "1.0",
      subdomain: "High Availability",
      difficulty: 2,
      prompt: "Which Cisco IOS-XE high-availability feature allows a standby supervisor to take over without dropping any CEF-switched traffic?",
      options: [
        { id: "A", text: "NSF (Non-Stop Forwarding) alone", is_correct: false },
        { id: "B", text: "SSO (Stateful Switchover) alone", is_correct: false },
        { id: "C", text: "NSF with SSO", is_correct: true },
        { id: "D", text: "GR (Graceful Restart) without SSO", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "NSF (Non-Stop Forwarding) keeps the data plane forwarding during a supervisor switchover by using the pre-populated FIB/CEF tables. SSO synchronises the control-plane state between active and standby supervisors. Together (NSF+SSO) they provide hitless failover for CEF-forwarded traffic.",
      cisco_reference: "ENCOR OCG Chapter 6 — High Availability",
      expert_trap_warning: "Experienced engineers sometimes confuse NSF alone (data-plane only) with SSO alone (control-plane sync only). The exam expects NSF+SSO as the complete solution for hitless switchover.",
      tags: ["high-availability", "nsf", "sso"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "arch-007",
      type: "drag_drop",
      domain: "1.0",
      subdomain: "SD-Access",
      difficulty: 2,
      prompt: "Match each SD-Access fabric node role to its primary function.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "Edge Node" },
        { id: "item2", text: "Border Node" },
        { id: "item3", text: "Control Plane Node" },
        { id: "item4", text: "Intermediate Node" }
      ],
      drop_targets: [
        { id: "target1", label: "Connects end-hosts to the fabric and performs LISP encapsulation", accepts: "item1" },
        { id: "target2", label: "Provides LISP Map Server/Resolver — maintains EID-to-RLOC database", accepts: "item3" },
        { id: "target3", label: "Connects the fabric to external Layer 3 networks", accepts: "item2" },
        { id: "target4", label: "Provides underlay transit — carries VXLAN-encapsulated traffic only", accepts: "item4" }
      ],
      explanation: "Edge Nodes connect endpoints and perform LISP EID registration. Control Plane Nodes run LISP MS/MR. Border Nodes are the fabric gateway to external networks. Intermediate Nodes only carry encapsulated traffic and have no fabric intelligence.",
      cisco_reference: "ENCOR OCG Chapter 16",
      expert_trap_warning: null,
      tags: ["sd-access", "fabric-roles"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "arch-009",
      type: "single_choice",
      domain: "1.0",
      subdomain: "SD-WAN",
      difficulty: 3,
      prompt: "Which SD-WAN component is the FIRST device a new WAN Edge router contacts during the onboarding (ZTP) process?",
      options: [
        { id: "A", text: "vManage", is_correct: false },
        { id: "B", text: "vSmart", is_correct: false },
        { id: "C", text: "vBond", is_correct: true },
        { id: "D", text: "vAnalytics", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "vBond is the SD-WAN orchestrator. It is the first controller contacted during ZTP/onboarding. vBond authenticates the WAN Edge device and then informs it of the vSmart and vManage controller addresses.",
      cisco_reference: "ENCOR OCG Chapter 17",
      expert_trap_warning: null,
      tags: ["sd-wan", "vbond", "ztp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "arch-010",
      type: "single_choice",
      domain: "1.0",
      subdomain: "High Availability",
      difficulty: 2,
      prompt: "Which Cisco IOS feature enables a router to continue forwarding traffic while its OSPF/BGP routing protocol process restarts after a supervisor switchover?",
      options: [
        { id: "A", text: "BFD (Bidirectional Forwarding Detection)", is_correct: false },
        { id: "B", text: "Graceful Restart (GR) / NSF", is_correct: true },
        { id: "C", text: "ECMP with IP SLA tracking", is_correct: false },
        { id: "D", text: "HSRP preemption", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "NSF (Non-Stop Forwarding) / Graceful Restart allows a router undergoing a control-plane restart to notify its neighbours to continue forwarding traffic along pre-established paths while the restarting router rebuilds its routing tables. GR-capable helper routers maintain adjacency during the restart period.",
      cisco_reference: "ENCOR OCG Chapter 6",
      expert_trap_warning: null,
      tags: ["high-availability", "graceful-restart", "nsf"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // DOMAIN 2.0 — VIRTUALIZATION (~6 questions)
    // =========================================================

    {
      id: "virt-001",
      type: "single_choice",
      domain: "2.0",
      subdomain: "VRF",
      difficulty: 2,
      prompt: "An engineer wants to isolate routing tables for two customers on the same router without separate physical hardware. Which technology is most appropriate?",
      options: [
        { id: "A", text: "VLANs", is_correct: false },
        { id: "B", text: "VRF-Lite", is_correct: true },
        { id: "C", text: "VXLAN", is_correct: false },
        { id: "D", text: "MPLS TE", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "VRF-Lite creates multiple independent routing table instances on a single router. Each VRF has its own routing table, interface assignments, and forwarding information. This provides Layer 3 traffic isolation without requiring MPLS.",
      cisco_reference: "ENCOR OCG Chapter 8 — VRF",
      expert_trap_warning: null,
      tags: ["vrf", "virtualization", "routing"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "virt-002",
      type: "single_choice",
      domain: "2.0",
      subdomain: "DMVPN",
      difficulty: 2,
      prompt: "In DMVPN Phase 3, which feature allows spoke-to-spoke tunnels to be established without traffic traversing the hub?",
      options: [
        { id: "A", text: "NHRP redirect and shortcut switching", is_correct: true },
        { id: "B", text: "NHRP registration with hub forwarding", is_correct: false },
        { id: "C", text: "GRE multipoint with static NHRP mappings", is_correct: false },
        { id: "D", text: "IPsec profile with IKEv2 spoke-to-spoke authentication", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DMVPN Phase 3 uses NHRP Traffic Indication (redirect) messages from the hub and NHRP shortcut switching on spokes. When the hub receives traffic destined for another spoke, it sends an NHRP redirect back to the originating spoke, enabling direct spoke-to-spoke tunnels with a summarised routing design at the hub.",
      cisco_reference: "ENCOR OCG Chapter 9 — DMVPN",
      expert_trap_warning: "Phase 2 also supports spoke-to-spoke tunnels, but requires a full routing table at the hub (no summarisation). Phase 3 is the Cisco-preferred answer when summarisation or default routing at the hub is mentioned.",
      tags: ["dmvpn", "nhrp", "phase3"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "virt-003",
      type: "single_choice",
      domain: "2.0",
      subdomain: "LISP",
      difficulty: 3,
      prompt: "In LISP terminology, what is the function of the ITR (Ingress Tunnel Router)?",
      options: [
        { id: "A", text: "Receives LISP-encapsulated packets and decapsulates them for the destination EID", is_correct: false },
        { id: "B", text: "Accepts packets from locally attached EID space and encapsulates them toward the destination RLOC", is_correct: true },
        { id: "C", text: "Maintains the mapping database for all EID prefixes", is_correct: false },
        { id: "D", text: "Handles LISP map-register messages from ETRs", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The ITR receives packets from locally attached EID space and performs a map lookup to find the RLOC for the destination EID. It then encapsulates the packet with the RLOC outer header and forwards it through the underlay. The ETR (Egress Tunnel Router) decapsulates packets arriving at the destination site.",
      cisco_reference: "ENCOR OCG Chapter 16 — LISP",
      expert_trap_warning: null,
      tags: ["lisp", "itr", "etr"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "virt-004",
      type: "multi_choice",
      domain: "2.0",
      subdomain: "GRE / Tunneling",
      difficulty: 2,
      prompt: "Which TWO statements about Generic Routing Encapsulation (GRE) tunnels are correct? (Choose two)",
      options: [
        { id: "A", text: "GRE adds a minimum of 24 bytes of overhead (4-byte GRE header + 20-byte outer IP header)", is_correct: true },
        { id: "B", text: "GRE provides native encryption for tunnel traffic", is_correct: false },
        { id: "C", text: "GRE tunnels support multicast traffic, enabling dynamic routing protocols over the tunnel", is_correct: true },
        { id: "D", text: "GRE uses UDP port 1194 for encapsulation", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "GRE encapsulates packets with a 4-byte GRE header plus a 20-byte outer IP header (24 bytes minimum). GRE supports multicast, making it suitable for running OSPF, EIGRP, etc. over the tunnel. GRE provides NO encryption — IPsec must be added separately. UDP 1194 is OpenVPN.",
      cisco_reference: "ENCOR OCG Chapter 9",
      expert_trap_warning: null,
      tags: ["gre", "tunneling", "overhead"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "virt-005",
      type: "single_choice",
      domain: "2.0",
      subdomain: "VXLAN",
      difficulty: 2,
      prompt: "Which UDP destination port does VXLAN use to encapsulate Layer 2 frames over an IP/UDP underlay?",
      options: [
        { id: "A", text: "UDP 4789", is_correct: true },
        { id: "B", text: "UDP 8472", is_correct: false },
        { id: "C", text: "UDP 1194", is_correct: false },
        { id: "D", text: "TCP 179", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "RFC 7348 defines VXLAN and specifies UDP destination port 4789 (IANA-assigned). Some Linux kernel implementations historically used 8472, but the IANA-registered and Cisco SD-Access standard port is 4789.",
      cisco_reference: "ENCOR OCG Chapter 16; RFC 7348",
      expert_trap_warning: null,
      tags: ["vxlan", "encapsulation", "udp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "virt-006",
      type: "single_choice",
      domain: "2.0",
      subdomain: "DMVPN",
      difficulty: 2,
      prompt: "Which protocol does DMVPN use to dynamically resolve the physical (NBMA) address of a spoke router from its tunnel IP address?",
      options: [
        { id: "A", text: "ARP", is_correct: false },
        { id: "B", text: "NHRP (Next Hop Resolution Protocol)", is_correct: true },
        { id: "C", text: "LISP Map Request", is_correct: false },
        { id: "D", text: "EIGRP neighbour discovery", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "NHRP (RFC 2332) maps tunnel (overlay) IP addresses to NBMA (physical/underlay) addresses in DMVPN. Spoke routers register their NBMA address with the hub (NHS), and spokes query NHRP to resolve other spokes' tunnel-to-NBMA address mappings for direct dynamic tunnels.",
      cisco_reference: "ENCOR OCG Chapter 9",
      expert_trap_warning: null,
      tags: ["dmvpn", "nhrp", "nbma"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "virt-007",
      type: "single_choice",
      domain: "2.0",
      subdomain: "DMVPN",
      difficulty: 3,
      prompt: "Which DMVPN phase is required to support a hub-and-spoke design where the hub uses a summarised default route AND spokes can still build direct spoke-to-spoke tunnels?",
      options: [
        { id: "A", text: "Phase 1 — spoke-to-hub-to-spoke only, no spoke-to-spoke", is_correct: false },
        { id: "B", text: "Phase 2 — requires full routing table at hub, no summarisation", is_correct: false },
        { id: "C", text: "Phase 3 — NHRP shortcut switching with hub summarisation", is_correct: true },
        { id: "D", text: "Both Phase 2 and Phase 3 support this equally", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DMVPN Phase 3 is the only phase that supports BOTH hub route summarisation (or a default route) AND spoke-to-spoke dynamic tunnel capability. Phase 2 allows spoke-to-spoke but requires specific spoke prefixes in the routing table at the hub — summarisation breaks Phase 2 spoke-to-spoke functionality.",
      cisco_reference: "ENCOR OCG Chapter 9",
      expert_trap_warning: null,
      tags: ["dmvpn", "phase3", "summarisation"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // DOMAIN 3.0 — INFRASTRUCTURE (~18 questions)
    // =========================================================

    {
      id: "infra-001",
      type: "single_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 2,
      prompt: "An OSPF router has learned the same prefix from both an intra-area (O) route and an inter-area (O IA) route. Which route will be installed in the routing table?",
      options: [
        { id: "A", text: "The inter-area route (O IA), because it has more topology information", is_correct: false },
        { id: "B", text: "The intra-area route (O), because intra-area routes have higher preference than inter-area routes", is_correct: true },
        { id: "C", text: "The route with the lower metric will be preferred regardless of route type", is_correct: false },
        { id: "D", text: "Both routes are installed via equal-cost load balancing", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "OSPF route preference order: O (intra-area) > O IA (inter-area) > O E1/N1 > O E2/N2. Intra-area routes are always preferred over inter-area routes regardless of metric, because OSPF evaluates route type before comparing metrics.",
      cisco_reference: "ENCOR OCG Chapter 10 — OSPF",
      expert_trap_warning: null,
      tags: ["ospf", "route-preference", "intra-area"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-002",
      type: "drag_drop",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 2,
      prompt: "Match each OSPF LSA type to its correct description.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "Type 1 — Router LSA" },
        { id: "item2", text: "Type 2 — Network LSA" },
        { id: "item3", text: "Type 3 — Summary LSA" },
        { id: "item4", text: "Type 5 — AS External LSA" }
      ],
      drop_targets: [
        { id: "target1", label: "Generated by every router; describes links and costs within an area", accepts: "item1" },
        { id: "target2", label: "Generated by the DR; describes routers connected to a multi-access segment", accepts: "item2" },
        { id: "target3", label: "Generated by an ABR; advertises inter-area prefixes", accepts: "item3" },
        { id: "target4", label: "Generated by an ASBR; advertises external routes into OSPF", accepts: "item4" }
      ],
      explanation: "Type 1 (Router) and Type 2 (Network) LSAs are area-local. Type 3 (Summary) is generated by ABRs to carry routes between areas. Type 5 (AS External) is generated by ASBRs and flooded throughout the OSPF domain (except stub/totally stub areas).",
      cisco_reference: "ENCOR OCG Chapter 10",
      expert_trap_warning: null,
      tags: ["ospf", "lsa-types"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-003",
      type: "single_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 3,
      prompt: "A router is the ABR for a totally stub area. Which LSA types does it suppress from entering the totally stub area?",
      options: [
        { id: "A", text: "Type 3 and Type 5 only", is_correct: false },
        { id: "B", text: "Type 5 and Type 7 only", is_correct: false },
        { id: "C", text: "Type 3, Type 4, and Type 5", is_correct: true },
        { id: "D", text: "Type 2, Type 3, and Type 5", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "A totally stub area blocks Type 3 (inter-area summary), Type 4 (ASBR summary), and Type 5 (external) LSAs. The ABR injects a single Type 3 default route (0.0.0.0/0) instead. Regular stub areas block Type 4 and Type 5 but still allow Type 3 inter-area LSAs.",
      cisco_reference: "ENCOR OCG Chapter 10",
      expert_trap_warning: "Many engineers know stub areas block Type 5, but forget that totally stub areas also block Type 3 summary LSAs. The exam tests this distinction carefully.",
      tags: ["ospf", "stub-area", "lsa"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-004",
      type: "single_choice",
      domain: "3.0",
      subdomain: "EIGRP",
      difficulty: 2,
      prompt: "In EIGRP, what is the Feasibility Condition (FC) used to determine a Feasible Successor?",
      options: [
        { id: "A", text: "The neighbour's Reported Distance must be less than the local router's current Feasible Distance to the destination", is_correct: true },
        { id: "B", text: "The neighbour must be reachable via the same interface as the successor", is_correct: false },
        { id: "C", text: "The feasible distance of the backup route must equal the feasible distance of the successor", is_correct: false },
        { id: "D", text: "The neighbour's advertised distance must be less than the successor's advertised distance", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The Feasibility Condition: a neighbour's Reported Distance (RD) must be strictly less than the local router's current Feasible Distance (FD). If satisfied, the route via that neighbour qualifies as a Feasible Successor and can be used for instant failover without running the DUAL algorithm.",
      cisco_reference: "ENCOR OCG Chapter 11 — EIGRP",
      expert_trap_warning: null,
      tags: ["eigrp", "feasibility-condition", "dual"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-005",
      type: "single_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 3,
      prompt: "A BGP router receives the same prefix from two eBGP peers. Peer A advertises it with LOCAL_PREF 200 and AS_PATH 65001 65002. Peer B advertises it with LOCAL_PREF 100 and AS_PATH 65003. Which path will BGP prefer?",
      options: [
        { id: "A", text: "Peer B — shorter AS_PATH length (1 hop vs 2 hops)", is_correct: false },
        { id: "B", text: "Peer A — higher LOCAL_PREF value", is_correct: true },
        { id: "C", text: "Peer A — longer AS_PATH is preferred by Cisco IOS", is_correct: false },
        { id: "D", text: "Peer B — lower LOCAL_PREF is preferred", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "BGP best-path selection: WEIGHT is evaluated first (local only), then LOCAL_PREF. LOCAL_PREF has higher priority than AS_PATH length. Peer A's LOCAL_PREF of 200 beats Peer B's 100, so Peer A's path is selected regardless of AS_PATH.",
      cisco_reference: "ENCOR OCG Chapter 12 — BGP",
      expert_trap_warning: null,
      tags: ["bgp", "best-path", "local-pref"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-006",
      type: "single_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 3,
      prompt: "Which BGP attribute is used to influence INBOUND traffic from external autonomous systems (how external routers send traffic INTO your AS)?",
      options: [
        { id: "A", text: "LOCAL_PREF", is_correct: false },
        { id: "B", text: "WEIGHT", is_correct: false },
        { id: "C", text: "MED (Multi-Exit Discriminator)", is_correct: true },
        { id: "D", text: "ORIGIN", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "MED (also called METRIC) is advertised to external BGP peers to suggest a preferred entry point into your AS. A lower MED is preferred. LOCAL_PREF and WEIGHT are used to influence outbound path selection within or from the local AS — they are not propagated to eBGP peers.",
      cisco_reference: "ENCOR OCG Chapter 12",
      expert_trap_warning: "A common real-world mistake is trying to set LOCAL_PREF to influence inbound traffic. LOCAL_PREF is NOT propagated to eBGP peers. MED is the correct attribute for signalling preferred ingress to neighbouring ASes.",
      tags: ["bgp", "med", "inbound-traffic"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-007",
      type: "multi_choice",
      domain: "3.0",
      subdomain: "STP",
      difficulty: 2,
      prompt: "Which TWO Spanning Tree Protocol features does Cisco recommend to protect access-layer ports from receiving unexpected BPDUs? (Choose two)",
      options: [
        { id: "A", text: "PortFast", is_correct: false },
        { id: "B", text: "BPDU Guard", is_correct: true },
        { id: "C", text: "Root Guard", is_correct: false },
        { id: "D", text: "BPDU Filter", is_correct: true }
      ],
      correct_answer: ["B", "D"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "BPDU Guard places a PortFast-enabled port into err-disabled state if a BPDU is received, protecting the STP topology from rogue switches. BPDU Filter suppresses sending and processing of BPDUs on a port. Root Guard prevents a port from becoming a root port, protecting the existing root bridge — it is not specifically for access ports.",
      cisco_reference: "ENCOR OCG Chapter 7 — STP",
      expert_trap_warning: "BPDU Filter configured globally only suppresses BPDUs on PortFast-enabled ports but will still react to received BPDUs by disabling PortFast. Per-interface BPDU Filter completely suppresses both directions and can cause bridging loops — the exam generally recommends BPDU Guard as the safer choice for access ports.",
      tags: ["stp", "bpdu-guard", "bpdu-filter", "portfast"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-008",
      type: "single_choice",
      domain: "3.0",
      subdomain: "STP",
      difficulty: 2,
      prompt: "A switch port is connected to a neighbouring switch that should never become the STP root bridge. Which STP feature should be configured on that port?",
      options: [
        { id: "A", text: "BPDU Guard", is_correct: false },
        { id: "B", text: "Loop Guard", is_correct: false },
        { id: "C", text: "Root Guard", is_correct: true },
        { id: "D", text: "PortFast", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Root Guard is configured on ports where superior BPDUs should never be received. If a superior BPDU arrives, Root Guard places the port into root-inconsistent (blocking) state, preventing that switch from becoming root. BPDU Guard is for access ports with PortFast and err-disables the port on any BPDU.",
      cisco_reference: "ENCOR OCG Chapter 7",
      expert_trap_warning: null,
      tags: ["stp", "root-guard"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-009",
      type: "single_choice",
      domain: "3.0",
      subdomain: "FHRP",
      difficulty: 2,
      prompt: "In HSRP, which router is responsible for forwarding packets sent to the virtual MAC address?",
      options: [
        { id: "A", text: "The router with the highest priority (always the active router)", is_correct: false },
        { id: "B", text: "The active router", is_correct: true },
        { id: "C", text: "Both the active and standby routers share forwarding equally", is_correct: false },
        { id: "D", text: "The router with the highest real IP address on that subnet", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In HSRP, only the active router forwards traffic destined to the virtual IP/MAC address. The standby router monitors the active router via hello messages and takes over if the active fails. GLBP differs in that multiple gateways actively forward traffic using different virtual MACs.",
      cisco_reference: "ENCOR OCG Chapter 6 — FHRP",
      expert_trap_warning: null,
      tags: ["hsrp", "fhrp", "active-router"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-010",
      type: "single_choice",
      domain: "3.0",
      subdomain: "FHRP",
      difficulty: 2,
      prompt: "Which FHRP protocol is Cisco proprietary and supports load balancing across multiple gateways using a single virtual IP address?",
      options: [
        { id: "A", text: "HSRP (Hot Standby Router Protocol)", is_correct: false },
        { id: "B", text: "VRRP (Virtual Router Redundancy Protocol)", is_correct: false },
        { id: "C", text: "GLBP (Gateway Load Balancing Protocol)", is_correct: true },
        { id: "D", text: "ECMP (Equal-Cost Multi-Path)", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "GLBP is Cisco proprietary and allows multiple routers to actively forward traffic for a single virtual IP address by assigning different virtual MAC addresses to each Active Virtual Forwarder (AVF). An Active Virtual Gateway (AVG) manages MAC assignments.",
      cisco_reference: "ENCOR OCG Chapter 6",
      expert_trap_warning: null,
      tags: ["glbp", "fhrp", "load-balancing"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-012",
      type: "single_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 3,
      prompt: "An OSPF neighborship is stuck in the EXSTART state between two routers. What is the MOST likely cause?",
      options: [
        { id: "A", text: "Mismatched OSPF area IDs", is_correct: false },
        { id: "B", text: "MTU mismatch between the two routers", is_correct: true },
        { id: "C", text: "Mismatched hello/dead timers", is_correct: false },
        { id: "D", text: "Duplicate OSPF router IDs in the area", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "EXSTART/EXCHANGE stalling is most commonly caused by an MTU mismatch. OSPF uses the MTU in Database Description (DBD) packets to ensure both sides can exchange complete LSDBs. Mismatched hello/dead timers prevent neighbours from reaching INIT/2WAY. Mismatched area IDs also block adjacency but at an earlier stage.",
      cisco_reference: "ENCOR OCG Chapter 10",
      expert_trap_warning: null,
      tags: ["ospf", "troubleshooting", "mtu", "exstart"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-013",
      type: "single_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 2,
      prompt: "By default, how does an iBGP router handle routes received from an iBGP peer?",
      options: [
        { id: "A", text: "It re-advertises them to all other iBGP and eBGP peers", is_correct: false },
        { id: "B", text: "It does NOT re-advertise them to other iBGP peers (iBGP split horizon)", is_correct: true },
        { id: "C", text: "It re-advertises them only to peers in the same cluster", is_correct: false },
        { id: "D", text: "It re-advertises them only if the route was locally originated", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "BGP iBGP split horizon: routes learned from an iBGP peer are NOT forwarded to other iBGP peers. This prevents routing loops within the AS but requires either a full mesh of iBGP sessions, Route Reflectors, or Confederations to distribute routes to all iBGP peers.",
      cisco_reference: "ENCOR OCG Chapter 12",
      expert_trap_warning: null,
      tags: ["bgp", "ibgp", "split-horizon", "route-reflector"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-014",
      type: "multi_choice",
      domain: "3.0",
      subdomain: "EIGRP",
      difficulty: 2,
      prompt: "Which TWO statements about EIGRP Named Mode (multi-AF mode) are correct compared to classic EIGRP? (Choose two)",
      options: [
        { id: "A", text: "Named mode supports IPv4 and IPv6 address families within the same EIGRP process", is_correct: true },
        { id: "B", text: "Named mode uses a different hello interval by default (30 seconds instead of 5)", is_correct: false },
        { id: "C", text: "Named mode allows per-interface configuration within the address-family context", is_correct: true },
        { id: "D", text: "Named mode replaces the composite K-value metric formula with OSPF cost", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "EIGRP Named Mode (IOS 15.0(1)M+) consolidates IPv4 and IPv6 into a single named process with address-family sub-modes. It allows per-interface configuration (bandwidth-percent, hello timers, authentication) within the interface section of the address-family. K-values and hello timers remain the same as classic mode.",
      cisco_reference: "ENCOR OCG Chapter 11",
      expert_trap_warning: null,
      tags: ["eigrp", "named-mode", "multi-af"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-015",
      type: "single_choice",
      domain: "3.0",
      subdomain: "QoS",
      difficulty: 2,
      prompt: "Which QoS mechanism is used at the network edge to mark traffic into DSCP values based on a traffic policy?",
      options: [
        { id: "A", text: "Policing", is_correct: false },
        { id: "B", text: "Shaping", is_correct: false },
        { id: "C", text: "Classification and Marking", is_correct: true },
        { id: "D", text: "Congestion avoidance (WRED)", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Classification and Marking at the trust boundary sets DSCP/CoS values that determine how traffic is treated (queued, policed, shaped, dropped) throughout the network. Policing and shaping enforce bandwidth contracts. WRED is a congestion-avoidance mechanism applied at congestion points.",
      cisco_reference: "ENCOR OCG Chapter 13 — QoS",
      expert_trap_warning: null,
      tags: ["qos", "dscp", "marking"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-016",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Multicast",
      difficulty: 2,
      prompt: "In PIM Sparse Mode (PIM-SM), what is the role of the Rendezvous Point (RP)?",
      options: [
        { id: "A", text: "It forwards multicast traffic on all interfaces using dense-mode flooding", is_correct: false },
        { id: "B", text: "It acts as the meeting point for sources and receivers to establish the initial shared tree", is_correct: true },
        { id: "C", text: "It replaces IGMP and handles group membership tracking", is_correct: false },
        { id: "D", text: "It provides MSDP peering for inter-domain multicast only", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In PIM-SM, the RP is a central meeting point. Sources register with the RP and receivers join the shared tree rooted at the RP (RPT). Once traffic flows, routers may optionally switch to a Shortest Path Tree (SPT) directly from the source. The RP is essential for bootstrapping multicast group delivery.",
      cisco_reference: "ENCOR OCG Chapter 14 — Multicast",
      expert_trap_warning: null,
      tags: ["multicast", "pim-sm", "rp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-017",
      type: "single_choice",
      domain: "3.0",
      subdomain: "STP",
      difficulty: 3,
      prompt: "Rapid PVST+ (802.1w) introduces new port roles compared to classic 802.1D. Which port role exists in 802.1w but has NO equivalent in 802.1D?",
      options: [
        { id: "A", text: "Root port", is_correct: false },
        { id: "B", text: "Designated port", is_correct: false },
        { id: "C", text: "Alternate port", is_correct: true },
        { id: "D", text: "Non-designated (blocking) port", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "802.1w RSTP defines Alternate and Backup port roles. The Alternate port provides an alternate path to the root bridge (discarding state, receives superior BPDUs from another bridge). The Backup port is a redundant port on the same segment. Both roles enable rapid failover without TCN flooding. 802.1D only has root, designated, and non-designated (blocking) roles.",
      cisco_reference: "ENCOR OCG Chapter 7",
      expert_trap_warning: null,
      tags: ["stp", "rstp", "alternate-port"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-018",
      type: "drag_drop",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 3,
      prompt: "Place these BGP best-path selection attributes in the correct evaluation order from HIGHEST to LOWEST priority.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "LOCAL_PREF (higher is preferred)" },
        { id: "item2", text: "WEIGHT (Cisco proprietary, higher is preferred)" },
        { id: "item3", text: "AS_PATH length (shorter is preferred)" },
        { id: "item4", text: "MED (lower is preferred)" }
      ],
      drop_targets: [
        { id: "target1", label: "Evaluated 1st (highest priority)", accepts: "item2" },
        { id: "target2", label: "Evaluated 2nd", accepts: "item1" },
        { id: "target3", label: "Evaluated 3rd", accepts: "item3" },
        { id: "target4", label: "Evaluated 4th", accepts: "item4" }
      ],
      explanation: "BGP best-path order (Cisco IOS): 1. WEIGHT (highest) → 2. LOCAL_PREF (highest) → 3. Locally originated → 4. AS_PATH (shortest) → 5. ORIGIN code → 6. MED (lowest) → 7. eBGP over iBGP → 8. Lowest IGP metric to next-hop → 9. Oldest eBGP path → 10. Lowest router-ID.",
      cisco_reference: "ENCOR OCG Chapter 12",
      expert_trap_warning: "Experienced BGP engineers often forget WEIGHT is evaluated before LOCAL_PREF because WEIGHT is Cisco-only and unused in multi-vendor environments. The ENCOR exam always tests WEIGHT as the first tiebreaker.",
      tags: ["bgp", "best-path", "attributes"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-019",
      type: "single_choice",
      domain: "3.0",
      subdomain: "EIGRP",
      difficulty: 2,
      prompt: "What does EIGRP use as its default composite metric with default K-values (K1=1, K3=1, all others=0)?",
      options: [
        { id: "A", text: "Bandwidth + Delay", is_correct: true },
        { id: "B", text: "Bandwidth + Load", is_correct: false },
        { id: "C", text: "Bandwidth + Reliability + Delay", is_correct: false },
        { id: "D", text: "MTU + Delay", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "EIGRP default K-values: K1=1 (bandwidth), K2=0 (load), K3=1 (delay), K4=0 (reliability), K5=0 (MTU). With these defaults, the metric simplifies to 256 × (10^7 / min_bandwidth_kbps + cumulative_delay/10). Only bandwidth and delay contribute by default.",
      cisco_reference: "ENCOR OCG Chapter 11",
      expert_trap_warning: null,
      tags: ["eigrp", "metric", "k-values"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-020",
      type: "single_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 3,
      prompt: "A BGP Route Reflector (RR) receives a route from a non-client iBGP peer. To which peers will the RR reflect this route?",
      options: [
        { id: "A", text: "To all iBGP peers, including other non-client peers", is_correct: false },
        { id: "B", text: "To both RR clients and other non-client iBGP peers", is_correct: false },
        { id: "C", text: "To its RR clients only", is_correct: true },
        { id: "D", text: "It will not reflect the route at all — only eBGP-learned routes are reflected", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "BGP RR reflection rules: Routes from eBGP peers → reflected to all iBGP peers. Routes from RR clients → reflected to all iBGP peers (clients and non-clients). Routes from non-client iBGP peers → reflected to RR clients ONLY, NOT to other non-clients (preserving iBGP split horizon between non-clients).",
      cisco_reference: "ENCOR OCG Chapter 12",
      expert_trap_warning: "Many engineers assume a Route Reflector reflects all routes to all peers. Routes from non-client iBGP peers are ONLY reflected to clients. This is a key exam distinction.",
      tags: ["bgp", "route-reflector", "ibgp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-021",
      type: "single_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 2,
      prompt: "On a broadcast multi-access (Ethernet) network, which OSPF statement about DR/BDR elections is TRUE?",
      options: [
        { id: "A", text: "The router with the highest OSPF priority wins; a priority of 0 makes a router ineligible for DR or BDR", is_correct: true },
        { id: "B", text: "OSPF DR elections are preemptive — a new higher-priority router immediately becomes DR", is_correct: false },
        { id: "C", text: "The router with the lowest router-ID wins the DR election when priorities are equal", is_correct: false },
        { id: "D", text: "The DR sends updates to 224.0.0.6 and BDR listens on 224.0.0.5", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "OSPF DR election: highest interface priority wins (default 1); tie broken by highest router-ID. Priority 0 = ineligible. Elections are non-preemptive. Non-DROTHERs send to 224.0.0.6 (DR and BDR); the DR sends to 224.0.0.5 (all OSPF routers).",
      cisco_reference: "ENCOR OCG Chapter 10",
      expert_trap_warning: null,
      tags: ["ospf", "dr-bdr", "election"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-023",
      type: "multi_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 3,
      prompt: "Which TWO conditions must be met for two OSPF routers to form a full adjacency? (Choose two)",
      options: [
        { id: "A", text: "The OSPF hello and dead intervals must match on the shared link", is_correct: true },
        { id: "B", text: "Both routers must use the same OSPF process ID", is_correct: false },
        { id: "C", text: "Both routers must be in the same OSPF area for their shared link", is_correct: true },
        { id: "D", text: "Both routers must have the same router-ID", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "OSPF adjacency requirements include: matching hello/dead timers, same area ID on the shared link, same subnet/mask, compatible authentication, and matching stub flags. The OSPF process ID is locally significant and does NOT need to match. Router IDs must be UNIQUE — identical router IDs prevent adjacency.",
      cisco_reference: "ENCOR OCG Chapter 10",
      expert_trap_warning: null,
      tags: ["ospf", "adjacency", "requirements"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-024",
      type: "single_choice",
      domain: "3.0",
      subdomain: "QoS",
      difficulty: 2,
      prompt: "Which DSCP Per-Hop Behaviour (PHB) is recommended for voice bearer (RTP) traffic to provide the lowest latency and jitter?",
      options: [
        { id: "A", text: "AF41 (DSCP 34)", is_correct: false },
        { id: "B", text: "CS3 (DSCP 24)", is_correct: false },
        { id: "C", text: "EF (Expedited Forwarding, DSCP 46)", is_correct: true },
        { id: "D", text: "CS6 (DSCP 48)", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "EF (Expedited Forwarding, DSCP 46 / binary 101110) is specified in RFC 3246 for latency-sensitive, low-jitter traffic such as VoIP RTP. EF traffic is placed in the priority queue. AF41 is used for interactive video. CS6 is for routing protocol traffic. CS3/AF31 are used for call signalling.",
      cisco_reference: "ENCOR OCG Chapter 13",
      expert_trap_warning: null,
      tags: ["qos", "dscp", "ef", "voip"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "infra-025",
      type: "single_choice",
      domain: "3.0",
      subdomain: "FHRP",
      difficulty: 3,
      prompt: "Which feature is available in HSRPv2 but NOT in HSRPv1?",
      options: [
        { id: "A", text: "Active/Standby router roles", is_correct: false },
        { id: "B", text: "Support for group numbers 0–4095 (4096 groups)", is_correct: true },
        { id: "C", text: "Virtual MAC address format 0000.0c07.acXX", is_correct: false },
        { id: "D", text: "Preemption with configurable delay", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "HSRPv2 supports group numbers 0–4095 (4096 groups vs HSRPv1's 0–255). HSRPv2 uses virtual MAC format 0000.0c9f.fXXX (XXX = hex group number) and multicast 224.0.0.102 (vs 224.0.0.2 for v1). Active/standby roles and preemption exist in both versions.",
      cisco_reference: "ENCOR OCG Chapter 6",
      expert_trap_warning: null,
      tags: ["hsrp", "hsrpv2", "fhrp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // DOMAIN 4.0 — NETWORK ASSURANCE (~6 questions)
    // =========================================================

    {
      id: "assur-001",
      type: "single_choice",
      domain: "4.0",
      subdomain: "SNMP",
      difficulty: 1,
      prompt: "Which SNMP version provides authentication and encryption using a User-Based Security Model (USM)?",
      options: [
        { id: "A", text: "SNMPv1", is_correct: false },
        { id: "B", text: "SNMPv2c", is_correct: false },
        { id: "C", text: "SNMPv3", is_correct: true },
        { id: "D", text: "SNMPv2 with community strings", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "SNMPv3 provides authentication (HMAC-MD5 or HMAC-SHA) and encryption (DES, 3DES, AES) via its User-Based Security Model (USM). SNMPv1 and SNMPv2c use cleartext community strings. SNMPv3 security levels: noAuthNoPriv, authNoPriv, authPriv.",
      cisco_reference: "ENCOR OCG Chapter 18 — Network Assurance",
      expert_trap_warning: null,
      tags: ["snmp", "snmpv3", "security"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "assur-002",
      type: "single_choice",
      domain: "4.0",
      subdomain: "NetFlow",
      difficulty: 2,
      prompt: "Which of the following correctly describes the relationship between NetFlow v9 and IPFIX?",
      options: [
        { id: "A", text: "IPFIX uses a fixed 7-tuple flow key whereas NetFlow v9 supports variable templates", is_correct: false },
        { id: "B", text: "IPFIX is an IETF standardisation of the NetFlow v9 template-based approach, adding variable-length fields", is_correct: true },
        { id: "C", text: "NetFlow v9 and IPFIX are identical standards from different organisations with no differences", is_correct: false },
        { id: "D", text: "IPFIX only supports IPv4 and does not export IPv6 statistics", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "IPFIX (RFC 7011) is the IETF standardisation of Cisco NetFlow v9. It uses a template-based approach and adds support for variable-length fields and enterprise-specific information elements. NetFlow v9 templates directly influenced the IPFIX design.",
      cisco_reference: "ENCOR OCG Chapter 18",
      expert_trap_warning: null,
      tags: ["netflow", "ipfix", "telemetry"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "assur-003",
      type: "multi_choice",
      domain: "4.0",
      subdomain: "Syslog",
      difficulty: 1,
      prompt: "Which TWO syslog severity levels indicate conditions requiring immediate action? (Choose two)",
      options: [
        { id: "A", text: "Level 0 — Emergency (system unusable)", is_correct: true },
        { id: "B", text: "Level 1 — Alert (immediate action needed)", is_correct: true },
        { id: "C", text: "Level 3 — Error", is_correct: false },
        { id: "D", text: "Level 6 — Informational", is_correct: false }
      ],
      correct_answer: ["A", "B"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Syslog severity levels: 0=Emergency (system unusable), 1=Alert (immediate action needed), 2=Critical, 3=Error, 4=Warning, 5=Notice, 6=Informational, 7=Debug. Levels 0 and 1 explicitly signal system-affecting conditions requiring urgent response.",
      cisco_reference: "ENCOR OCG Chapter 18",
      expert_trap_warning: null,
      tags: ["syslog", "severity-levels"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "assur-004",
      type: "single_choice",
      domain: "4.0",
      subdomain: "Streaming Telemetry",
      difficulty: 2,
      prompt: "What is the primary advantage of model-driven streaming telemetry (MDT) over SNMP polling for network monitoring?",
      options: [
        { id: "A", text: "MDT uses the same MIB objects as SNMP for compatibility with existing tools", is_correct: false },
        { id: "B", text: "MDT pushes data continuously at configured intervals, reducing latency and eliminating polling overhead", is_correct: true },
        { id: "C", text: "MDT encrypts data using MD5 while SNMP transmits in cleartext", is_correct: false },
        { id: "D", text: "MDT is exclusively available on Cisco Catalyst 9000 series switches", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "MDT uses a push model: the device streams data to the collector at configured intervals or on-change, eliminating the request-response cycle of SNMP polling. This provides lower latency, higher-frequency data, and reduced management plane load. MDT uses YANG models with gRPC/gNMI or NETCONF transport.",
      cisco_reference: "ENCOR OCG Chapter 19 — Automation",
      expert_trap_warning: null,
      tags: ["telemetry", "mdt", "snmp", "monitoring"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "assur-005",
      type: "single_choice",
      domain: "4.0",
      subdomain: "IP SLA",
      difficulty: 2,
      prompt: "An engineer uses Cisco IP SLA to measure jitter and packet loss for a VoIP application. Which probe type is most appropriate?",
      options: [
        { id: "A", text: "ICMP Echo", is_correct: false },
        { id: "B", text: "UDP Jitter (codec simulation)", is_correct: true },
        { id: "C", text: "TCP Connect", is_correct: false },
        { id: "D", text: "DNS", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "IP SLA UDP Jitter operations generate a stream of UDP packets and measure one-way delay, jitter, and packet loss — the critical metrics for VoIP quality. ICMP Echo measures only RTT without jitter. The codec simulation option generates packets mimicking a specific codec's packet rate and size.",
      cisco_reference: "ENCOR OCG Chapter 18",
      expert_trap_warning: null,
      tags: ["ip-sla", "voip", "jitter"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "assur-006",
      type: "single_choice",
      domain: "4.0",
      subdomain: "SPAN / RSPAN",
      difficulty: 2,
      prompt: "Which statement correctly describes the difference between SPAN and RSPAN?",
      options: [
        { id: "A", text: "SPAN can mirror traffic across multiple switches; RSPAN is limited to a single switch", is_correct: false },
        { id: "B", text: "RSPAN uses a dedicated VLAN to carry mirrored traffic across multiple switches to a remote destination port", is_correct: true },
        { id: "C", text: "RSPAN requires a GRE tunnel while SPAN uses a dedicated VLAN", is_correct: false },
        { id: "D", text: "SPAN and RSPAN are functionally identical but differ only in configuration syntax", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "SPAN mirrors traffic locally on a single switch to a local destination port. RSPAN extends this across multiple switches using a dedicated RSPAN VLAN that carries mirrored traffic. ERSPAN (Encapsulated RSPAN) encapsulates mirrored frames in GRE to cross Layer 3 boundaries.",
      cisco_reference: "ENCOR OCG Chapter 18",
      expert_trap_warning: null,
      tags: ["span", "rspan", "monitoring"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // DOMAIN 5.0 — SECURITY (~12 questions)
    // =========================================================

    {
      id: "sec-001",
      type: "single_choice",
      domain: "5.0",
      subdomain: "AAA",
      difficulty: 2,
      prompt: "What is the key difference between RADIUS and TACACS+ regarding how they handle AAA transactions?",
      options: [
        { id: "A", text: "RADIUS separates Authentication, Authorization, and Accounting into individual transactions; TACACS+ combines them", is_correct: false },
        { id: "B", text: "TACACS+ separates Authentication, Authorization, and Accounting; RADIUS combines Authentication and Authorization in a single exchange", is_correct: true },
        { id: "C", text: "RADIUS uses TCP port 49; TACACS+ uses UDP ports 1812 and 1813", is_correct: false },
        { id: "D", text: "TACACS+ encrypts only the password; RADIUS encrypts the entire payload", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "TACACS+ (TCP 49) separates Authentication, Authorization, and Accounting into independent transactions and encrypts the entire packet body. RADIUS (UDP 1812/1813) combines Authentication and Authorization in a single exchange and encrypts only the password field. TACACS+ is preferred for device administration; RADIUS for network access (802.1X).",
      cisco_reference: "ENCOR OCG Chapter 20 — Security",
      expert_trap_warning: null,
      tags: ["aaa", "tacacs+", "radius"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sec-002",
      type: "single_choice",
      domain: "5.0",
      subdomain: "802.1X",
      difficulty: 2,
      prompt: "In 802.1X port-based authentication, what are the three key roles defined by the standard?",
      options: [
        { id: "A", text: "Client, Switch, LDAP server", is_correct: false },
        { id: "B", text: "Supplicant, Authenticator, Authentication Server", is_correct: true },
        { id: "C", text: "Client, NAS, TACACS+ server", is_correct: false },
        { id: "D", text: "Requestor, NAD, Policy server", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "802.1X defines: Supplicant (end device with 802.1X client), Authenticator (switch or WLC enforcing access control), and Authentication Server (RADIUS server such as Cisco ISE validating credentials). EAP frames are exchanged between supplicant and authenticator; RADIUS carries EAP to the authentication server.",
      cisco_reference: "ENCOR OCG Chapter 20",
      expert_trap_warning: null,
      tags: ["802.1x", "aaa", "eap", "ise"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sec-003",
      type: "single_choice",
      domain: "5.0",
      subdomain: "ACL",
      difficulty: 2,
      prompt: "Where should an extended ACL be placed for the most efficient packet filtering?",
      options: [
        { id: "A", text: "As close to the destination as possible", is_correct: false },
        { id: "B", text: "As close to the source as possible", is_correct: true },
        { id: "C", text: "On the distribution layer switches only", is_correct: false },
        { id: "D", text: "On the core router to inspect all transit traffic", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Extended ACLs should be placed as close to the source as possible to discard unwanted traffic early, minimising unnecessary network utilisation. Standard ACLs (source IP only) should be placed close to the destination to avoid inadvertently blocking traffic to other destinations.",
      cisco_reference: "ENCOR OCG Chapter 21 — ACLs",
      expert_trap_warning: null,
      tags: ["acl", "extended-acl", "placement"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sec-004",
      type: "multi_choice",
      domain: "5.0",
      subdomain: "ACL",
      difficulty: 2,
      prompt: "Which TWO statements about IPv4 standard numbered ACLs are correct? (Choose two)",
      options: [
        { id: "A", text: "Standard ACLs match on source IP address only", is_correct: true },
        { id: "B", text: "Standard ACLs use numbers in the range 100–199 and 2000–2699", is_correct: false },
        { id: "C", text: "All ACLs end with an implicit deny all statement", is_correct: true },
        { id: "D", text: "Standard ACLs can match on destination port numbers", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Standard ACLs (1–99 and 1300–1999) match only on source IP address. All ACLs end with an implicit 'deny any' statement that is not displayed in the running config. Extended ACLs (100–199, 2000–2699) match on source/destination IP, protocol, and ports.",
      cisco_reference: "ENCOR OCG Chapter 21",
      expert_trap_warning: null,
      tags: ["acl", "standard-acl"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sec-006",
      type: "single_choice",
      domain: "5.0",
      subdomain: "CoPP",
      difficulty: 3,
      prompt: "What is the purpose of Control Plane Policing (CoPP) on Cisco IOS devices?",
      options: [
        { id: "A", text: "To encrypt all traffic destined to the router's control plane", is_correct: false },
        { id: "B", text: "To rate-limit or drop traffic directed to the router CPU, protecting it from DoS attacks and excessive punted traffic", is_correct: true },
        { id: "C", text: "To provide DSCP marking for traffic transiting the router", is_correct: false },
        { id: "D", text: "To filter routing protocol updates based on route-map policies", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "CoPP applies MQC (Modular QoS CLI) policies to the control plane to rate-limit or drop traffic directed at the router's CPU (routing protocol packets, ICMP, SNMP, SSH). This protects the CPU from being overwhelmed by DoS attacks or excessive punted traffic, maintaining routing protocol stability.",
      cisco_reference: "ENCOR OCG Chapter 20",
      expert_trap_warning: null,
      tags: ["copp", "security", "control-plane"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sec-007",
      type: "single_choice",
      domain: "5.0",
      subdomain: "AAA",
      difficulty: 3,
      prompt: "A router is configured with `aaa authentication login default group radius local`. What happens if all RADIUS servers are unreachable due to timeout?",
      options: [
        { id: "A", text: "Authentication fails immediately and access is denied", is_correct: false },
        { id: "B", text: "The router falls back to the local username/password database", is_correct: true },
        { id: "C", text: "The router prompts for the enable password instead", is_correct: false },
        { id: "D", text: "The router bypasses authentication and grants full access", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The method list tries RADIUS first. If RADIUS is unreachable (server timeout), IOS falls back to the local username/password database. This differs critically from an authentication FAILURE — if RADIUS explicitly rejects the credentials, there is NO fallback to local.",
      cisco_reference: "ENCOR OCG Chapter 20",
      expert_trap_warning: "A critical exam trap: fallback to local only occurs on RADIUS UNREACHABILITY (timeout), NOT on authentication failure (explicit REJECT). Many engineers assume any RADIUS problem triggers fallback, but a RADIUS REJECT stops the process — no local fallback occurs.",
      tags: ["aaa", "radius", "fallback", "local"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sec-008",
      type: "single_choice",
      domain: "5.0",
      subdomain: "DHCP Security",
      difficulty: 2,
      prompt: "Which Layer 2 security feature prevents rogue DHCP servers from responding to client requests on an access switch?",
      options: [
        { id: "A", text: "Dynamic ARP Inspection (DAI)", is_correct: false },
        { id: "B", text: "DHCP Snooping", is_correct: true },
        { id: "C", text: "IP Source Guard", is_correct: false },
        { id: "D", text: "Port Security", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DHCP Snooping classifies switch ports as trusted (uplinks to legitimate DHCP servers) or untrusted (access ports). DHCP OFFER and ACK messages received on untrusted ports are dropped, preventing rogue servers. It also builds a binding table used by DAI and IP Source Guard.",
      cisco_reference: "ENCOR OCG Chapter 21",
      expert_trap_warning: null,
      tags: ["dhcp-snooping", "layer2-security"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sec-009",
      type: "single_choice",
      domain: "5.0",
      subdomain: "Zone-Based Firewall",
      difficulty: 2,
      prompt: "In Cisco IOS Zone-Based Policy Firewall (ZBF), what is the default behaviour for traffic between two interfaces that are NOT assigned to any zone?",
      options: [
        { id: "A", text: "Traffic passes freely (no implicit drop) — classic IOS behaviour is maintained", is_correct: true },
        { id: "B", text: "Traffic is dropped by default — all unzoned interfaces are blocked", is_correct: false },
        { id: "C", text: "Traffic is inspected as if both interfaces are in the same zone", is_correct: false },
        { id: "D", text: "Traffic is forwarded only if a zone-pair explicitly permits it", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In ZBF, interfaces NOT assigned to a zone retain classic IOS firewall behaviour — traffic flows freely between them. However, once an interface IS assigned to a zone, ALL inter-zone traffic is denied unless explicitly permitted by a zone-pair policy. Traffic within the same zone is always permitted.",
      cisco_reference: "ENCOR OCG Chapter 20",
      expert_trap_warning: "Stateful firewall engineers assume all unmatched traffic is dropped. In IOS ZBF, the deny-all behaviour ONLY applies to interfaces assigned to zones. Unzoned interfaces pass traffic freely — a key trap when migrating from classic CBAC to ZBF.",
      tags: ["zone-based-firewall", "zbf", "security"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sec-011",
      type: "single_choice",
      domain: "5.0",
      subdomain: "VPN",
      difficulty: 2,
      prompt: "Which IKEv2 feature allows a VPN client to change its IP address (e.g., roaming from Wi-Fi to LTE) without re-negotiating the IKEv2 SA?",
      options: [
        { id: "A", text: "IKEv2 Dead Peer Detection (DPD)", is_correct: false },
        { id: "B", text: "IKEv2 Session Resumption (RFC 5723)", is_correct: false },
        { id: "C", text: "IKEv2 MOBIKE (RFC 4555)", is_correct: true },
        { id: "D", text: "IKEv2 Redirect (RFC 5685)", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "MOBIKE (RFC 4555) allows the VPN client's IP address or network interface to change while maintaining an existing IKEv2/IPsec SA — critical for mobile devices roaming between networks. Session Resumption (RFC 5723) uses a ticket mechanism for fast re-authentication after a connection drop.",
      cisco_reference: "ENCOR OCG Chapter 20",
      expert_trap_warning: null,
      tags: ["ikev2", "vpn", "mobike"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sec-012",
      type: "single_choice",
      domain: "5.0",
      subdomain: "ACL",
      difficulty: 3,
      prompt: "A stateless ACL is applied inbound on Gi0/0:\n  permit tcp 10.0.0.0 0.0.0.255 any eq 80\n  deny ip any any log\n\nA host at 10.0.0.5 sends a TCP SYN to 192.168.1.1 port 80. The server replies with a SYN-ACK. Will the SYN-ACK be permitted inbound on Gi0/0?",
      options: [
        { id: "A", text: "Yes — the ACL automatically permits TCP return traffic for established sessions", is_correct: false },
        { id: "B", text: "No — the ACL is stateless; the SYN-ACK from 192.168.1.1 does not match the permit statement and is dropped", is_correct: true },
        { id: "C", text: "Yes — the permit tcp statement implicitly allows return traffic", is_correct: false },
        { id: "D", text: "No — unless the 'established' keyword is added and applied in the inbound direction", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Extended ACLs are stateless. The permit statement matches source 10.0.0.0/24 + destination port 80. The SYN-ACK comes FROM 192.168.1.1 TO 10.0.0.5 — it does NOT match the permit (wrong source). Without an explicit 'permit tcp any 10.0.0.0 0.0.0.255 established' or a stateful mechanism (ZBF/CBAC), return traffic is dropped by the implicit deny.",
      cisco_reference: "ENCOR OCG Chapter 21",
      expert_trap_warning: "Engineers accustomed to stateful firewalls expect return traffic to be automatically permitted. IOS ACLs are stateless — return traffic must be explicitly permitted using the 'established' keyword or a stateful firewall mechanism must be used.",
      tags: ["acl", "stateless", "return-traffic", "established"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sec-013",
      type: "single_choice",
      domain: "5.0",
      subdomain: "uRPF",
      difficulty: 3,
      prompt: "uRPF strict mode is configured on a customer-facing interface. A packet arrives with a source IP that exists in the routing table but the best return path is via a DIFFERENT interface. What happens?",
      options: [
        { id: "A", text: "The packet is forwarded normally because the source IP is reachable", is_correct: false },
        { id: "B", text: "The packet is dropped — strict mode requires the return path to exit the same interface the packet arrived on", is_correct: true },
        { id: "C", text: "The packet is forwarded but an SNMP trap is generated", is_correct: false },
        { id: "D", text: "The packet is forwarded because loose mode takes precedence when the source is routable", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "uRPF strict mode (RFC 3704) checks that the FIB's best reverse path for the source IP exits the same interface the packet arrived on. If it doesn't, the packet is dropped as a potential source-spoofed packet. Loose mode only verifies the source exists in the routing table — appropriate for asymmetrically routed networks.",
      cisco_reference: "ENCOR OCG Chapter 21",
      expert_trap_warning: "Engineers with asymmetric routing experience often disable uRPF strict mode to avoid dropping legitimate traffic. The exam tests whether strict vs loose mode is appropriate: loose mode is the answer for asymmetric paths; strict mode is for symmetric (single-homed) interfaces.",
      tags: ["urpf", "antispoofing", "security"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // DOMAIN 6.0 — AUTOMATION (~9 questions)
    // =========================================================

    {
      id: "auto-001",
      type: "single_choice",
      domain: "6.0",
      subdomain: "REST APIs",
      difficulty: 1,
      prompt: "Which HTTP method is used to retrieve data from a REST API without modifying the resource?",
      options: [
        { id: "A", text: "POST", is_correct: false },
        { id: "B", text: "PUT", is_correct: false },
        { id: "C", text: "GET", is_correct: true },
        { id: "D", text: "PATCH", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "REST HTTP methods: GET (read/retrieve — idempotent, no side effects), POST (create new resource), PUT (replace entire resource), PATCH (partial update), DELETE (remove resource). GET is the correct choice for read-only data retrieval.",
      cisco_reference: "ENCOR OCG Chapter 19 — Automation",
      expert_trap_warning: null,
      tags: ["rest", "api", "http-methods"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "auto-002",
      type: "single_choice",
      domain: "6.0",
      subdomain: "NETCONF",
      difficulty: 2,
      prompt: "Which data encoding and transport does NETCONF use for its messages?",
      options: [
        { id: "A", text: "JSON over HTTP/1.1", is_correct: false },
        { id: "B", text: "XML over SSH (TCP 830 by default)", is_correct: true },
        { id: "C", text: "YANG models over gRPC", is_correct: false },
        { id: "D", text: "XML over UDP port 161", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "NETCONF (RFC 6241) uses XML for message encoding and SSH as its transport (default TCP port 830). RESTCONF uses HTTP/HTTPS with JSON or XML. gNMI uses gRPC for streaming telemetry. YANG is the data modelling language, not a transport.",
      cisco_reference: "ENCOR OCG Chapter 19",
      expert_trap_warning: null,
      tags: ["netconf", "xml", "ssh", "yang"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "auto-003",
      type: "single_choice",
      domain: "6.0",
      subdomain: "YANG",
      difficulty: 2,
      prompt: "Which statement correctly describes the relationship between YANG and NETCONF/RESTCONF?",
      options: [
        { id: "A", text: "YANG is a transport protocol that carries NETCONF messages between devices", is_correct: false },
        { id: "B", text: "YANG defines the data model (structure and constraints); NETCONF/RESTCONF are protocols that operate on YANG-modelled data", is_correct: true },
        { id: "C", text: "YANG is only used with RESTCONF; NETCONF uses SMIv2 MIBs exclusively", is_correct: false },
        { id: "D", text: "YANG replaces XML and JSON as the on-wire encoding format", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "YANG (RFC 7950) is a data modelling language that defines the structure, syntax, and constraints of configuration and operational data. NETCONF and RESTCONF are management protocols that use YANG models to validate and structure the data they exchange. YANG is to NETCONF/RESTCONF as a schema is to a database.",
      cisco_reference: "ENCOR OCG Chapter 19",
      expert_trap_warning: null,
      tags: ["yang", "netconf", "restconf", "data-model"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "auto-004",
      type: "multi_choice",
      domain: "6.0",
      subdomain: "Ansible",
      difficulty: 2,
      prompt: "Which TWO statements about Ansible for network automation are correct? (Choose two)",
      options: [
        { id: "A", text: "Ansible is agentless — it communicates with devices via SSH or APIs without a client agent on the device", is_correct: true },
        { id: "B", text: "Ansible requires a running daemon (ansibled) on each managed network device", is_correct: false },
        { id: "C", text: "Ansible playbooks are written in YAML and define the desired state or tasks for managed devices", is_correct: true },
        { id: "D", text: "Ansible uses Ruby as its primary language for playbooks and modules", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Ansible is agentless — it connects using SSH (for Linux/network CLI) or APIs (for modern network OS) with no agent required. Playbooks are YAML files describing tasks. Ansible itself is written in Python. Puppet/Chef use agents; Puppet uses its own DSL and Chef uses Ruby.",
      cisco_reference: "ENCOR OCG Chapter 19",
      expert_trap_warning: null,
      tags: ["ansible", "automation", "yaml"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "auto-005",
      type: "single_choice",
      domain: "6.0",
      subdomain: "DNA Center",
      difficulty: 2,
      prompt: "In Cisco DNA Center, what is the purpose of the Intent API (Northbound API)?",
      options: [
        { id: "A", text: "It is used by DNA Center to communicate southbound to network devices via NETCONF and CLI", is_correct: false },
        { id: "B", text: "It exposes DNA Center capabilities to external applications via REST, enabling programmable access to IBN functions", is_correct: true },
        { id: "C", text: "It provides direct CLI-style access to network devices through a DNA Center proxy", is_correct: false },
        { id: "D", text: "It is used exclusively for exporting telemetry data to third-party SIEMs", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DNA Center's Northbound (Intent) API exposes DNA Center functions to external applications and automation platforms via REST. This allows third-party tools to trigger workflows, retrieve network inventory, deploy policies, and consume assurance data. Southbound APIs (NETCONF, RESTCONF, CLI via SSH) are used by DNA Center to communicate with network devices.",
      cisco_reference: "ENCOR OCG Chapter 19",
      expert_trap_warning: null,
      tags: ["dna-center", "intent-api", "northbound"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "auto-006",
      type: "single_choice",
      domain: "6.0",
      subdomain: "REST APIs",
      difficulty: 2,
      prompt: "A developer receives HTTP status code 401 when calling a Cisco DNA Center REST API. What does this indicate?",
      options: [
        { id: "A", text: "The requested resource was not found on the server", is_correct: false },
        { id: "B", text: "The request is missing valid authentication credentials or the token has expired", is_correct: true },
        { id: "C", text: "The client is authenticated but not authorised to access the resource (Forbidden)", is_correct: false },
        { id: "D", text: "The server encountered an internal error processing the request", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "HTTP 401 Unauthorized means the request lacks valid authentication credentials (no token or expired token). HTTP 403 Forbidden = authenticated but not authorised. 404 = Not Found. 500 = Internal Server Error. For DNA Center, 401 typically means the X-Auth-Token header is missing or expired.",
      cisco_reference: "ENCOR OCG Chapter 19",
      expert_trap_warning: null,
      tags: ["rest", "http-status", "authentication"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "auto-007",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Configuration Management",
      difficulty: 2,
      prompt: "Which configuration management tool uses a 'pull' model where managed nodes periodically check in with a central server to retrieve and apply their desired state?",
      options: [
        { id: "A", text: "Ansible", is_correct: false },
        { id: "B", text: "Puppet", is_correct: true },
        { id: "C", text: "Terraform", is_correct: false },
        { id: "D", text: "Python with Netmiko", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Puppet uses a pull model: the Puppet agent on each node contacts the Puppet master to retrieve its catalog and apply differences. Ansible uses a push model — the control node pushes to managed nodes. Chef also uses pull. Ansible's agentless push model is most common for network device automation.",
      cisco_reference: "ENCOR OCG Chapter 19",
      expert_trap_warning: null,
      tags: ["puppet", "ansible", "push-pull", "configuration-management"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "auto-008",
      type: "drag_drop",
      domain: "6.0",
      subdomain: "NETCONF / RESTCONF",
      difficulty: 2,
      prompt: "Match each network management protocol to its correct characteristic.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "XML encoding over SSH transport (TCP 830)" },
        { id: "item2", text: "JSON or XML over HTTPS; maps to HTTP verbs (GET/POST/PUT/DELETE/PATCH)" },
        { id: "item3", text: "Protocol Buffers over gRPC; optimised for high-frequency streaming telemetry" },
        { id: "item4", text: "Community strings; agent-based polling; UDP 161/162" }
      ],
      drop_targets: [
        { id: "target1", label: "NETCONF", accepts: "item1" },
        { id: "target2", label: "RESTCONF", accepts: "item2" },
        { id: "target3", label: "gNMI / gRPC", accepts: "item3" },
        { id: "target4", label: "SNMPv2c", accepts: "item4" }
      ],
      explanation: "NETCONF: XML/SSH (port 830). RESTCONF: HTTP verbs + JSON or XML over HTTPS. gNMI uses Protocol Buffers + gRPC for efficient high-frequency telemetry. SNMPv2c is the legacy agent-based protocol using community strings over UDP.",
      cisco_reference: "ENCOR OCG Chapter 19",
      expert_trap_warning: null,
      tags: ["netconf", "restconf", "gnmi", "snmp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "auto-009",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Python / Scripting",
      difficulty: 2,
      prompt: "A network engineer uses Python with the `requests` library to call the Cisco DNA Center API. After obtaining an authentication token via POST /dna/system/api/v1/auth/token, which HTTP header must be included in all subsequent API calls?",
      options: [
        { id: "A", text: "Authorization: Basic <base64 credentials>", is_correct: false },
        { id: "B", text: "X-Auth-Token: <token value>", is_correct: true },
        { id: "C", text: "Content-Type: application/token", is_correct: false },
        { id: "D", text: "API-Key: <token value>", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Cisco DNA Center API: the initial token request uses HTTP Basic Auth (Authorization: Basic header). The returned token must then be passed as 'X-Auth-Token: <token>' in all subsequent API requests. Using Basic Auth on every call is incorrect after the token is obtained.",
      cisco_reference: "ENCOR OCG Chapter 19; Cisco DNA Center API Reference",
      expert_trap_warning: null,
      tags: ["dna-center", "api", "python", "authentication"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "auto-010",
      type: "drag_drop",
      domain: "6.0",
      subdomain: "Automation Tools",
      difficulty: 2,
      prompt: "Match each automation/orchestration tool to its primary characteristic.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "Ansible" },
        { id: "item2", text: "Puppet" },
        { id: "item3", text: "Terraform" },
        { id: "item4", text: "Chef" }
      ],
      drop_targets: [
        { id: "target1", label: "Agentless push model; YAML playbooks; Python-based; no server daemon required", accepts: "item1" },
        { id: "target2", label: "Agent-based pull model; declarative Puppet DSL; Puppet master server", accepts: "item2" },
        { id: "target3", label: "Infrastructure-as-code; HCL language; provider-based; immutable infrastructure", accepts: "item3" },
        { id: "target4", label: "Agent-based pull model; Ruby-based recipes and cookbooks; Chef server", accepts: "item4" }
      ],
      explanation: "Ansible: agentless push using SSH/APIs, YAML playbooks. Puppet: agent-based pull model, declarative Puppet DSL. Terraform: IaC tool using HashiCorp Configuration Language (HCL) for infrastructure provisioning via providers. Chef: agent-based pull model using Ruby cookbooks on a Chef server.",
      cisco_reference: "ENCOR OCG Chapter 19",
      expert_trap_warning: null,
      tags: ["ansible", "puppet", "terraform", "chef", "automation"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // SCENARIO / TESTLET QUESTIONS
    // =========================================================

    // ---------------------------------------------------------
    // SCENARIO 1 — OSPF Multi-Area (Domain 3.0)
    // ---------------------------------------------------------
    {
      id: "exam-sc-001",
      type: "scenario",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 3,
      prompt: "Refer to the network diagram and scenario below.",
      diagram:
        "    [R1]───Area 0───[R2]───Area 1───[R3]\n" +
        "     |                  |\n" +
        "   Area 2             [R4]\n" +
        "     |\n" +
        "    [R5]",
      scenario_context:
        "A company runs multi-area OSPF. R2 is the ABR connecting Area 0, Area 1, and Area 2. " +
        "R5 is an internal router in Area 2 only. R3 and R4 are internal routers in Area 1. " +
        "R1 is in Area 0 and Area 2. The network administrator configures Area 1 as a Totally " +
        "Stubby Area (TSA). A default route is needed in Area 2 but no external routes should " +
        "be imported into it. Area 2 is subsequently configured as an NSSA.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      sub_questions: [
        {
          id: "exam-sc-001-q1",
          type: "single_choice",
          prompt:
            "R5 (Area 2 internal router) needs to reach networks in Area 0. " +
            "Which LSA type will R5 receive describing those Area 0 networks, assuming Area 2 is a normal area?",
          options: [
            { id: "A", text: "Type 1 — Router LSA", is_correct: false },
            { id: "B", text: "Type 2 — Network LSA", is_correct: false },
            { id: "C", text: "Type 3 — Summary LSA", is_correct: true },
            { id: "D", text: "Type 5 — AS External LSA", is_correct: false }
          ],
          correct_answer: "C",
          explanation:
            "ABR R2 summarises inter-area prefixes as Type 3 Summary LSAs and floods them into adjacent areas. " +
            "R5, an Area 2 internal router, will only ever see Type 1/2 LSAs from within Area 2 plus " +
            "Type 3 LSAs generated by its ABR (R2) for networks in other areas."
        },
        {
          id: "exam-sc-001-q2",
          type: "single_choice",
          prompt:
            "Area 1 is configured as a Totally Stubby Area. Which statement correctly describes the impact on R3?",
          options: [
            {
              id: "A",
              text: "R3 receives Type 3, Type 4, and Type 5 LSAs filtered at the ABR.",
              is_correct: false
            },
            {
              id: "B",
              text: "R3 receives only a single Type 3 default-route LSA from ABR R2; all other inter-area and external LSAs are blocked.",
              is_correct: true
            },
            {
              id: "C",
              text: "R3 receives Type 7 LSAs instead of Type 5 LSAs for external routes.",
              is_correct: false
            },
            {
              id: "D",
              text: "R3 loses all OSPF adjacency with R2 because TSA requires special hello parameters.",
              is_correct: false
            }
          ],
          correct_answer: "B",
          explanation:
            "A Totally Stubby Area blocks Type 3 inter-area Summary LSAs, Type 4 ASBR Summary LSAs, and " +
            "Type 5 External LSAs. The only exception is a single Type 3 LSA carrying 0.0.0.0/0 injected " +
            "by the ABR, giving routers inside the TSA a default path to reach all external destinations."
        },
        {
          id: "exam-sc-001-q3",
          type: "single_choice",
          prompt:
            "Area 2 is reconfigured as an NSSA. A redistributed external route from R1 must reach R3 in Area 1. " +
            "Which sequence of LSA types correctly describes how that route travels from R1 to R3?",
          options: [
            {
              id: "A",
              text: "Type 5 in Area 2 → Type 5 forwarded into Area 0 → Type 5 into Area 1",
              is_correct: false
            },
            {
              id: "B",
              text: "Type 7 in Area 2 → converted to Type 5 by ABR R2 → Type 5 into Area 0 and Area 1",
              is_correct: true
            },
            {
              id: "C",
              text: "Type 7 in Area 2 → Type 3 by ABR R2 → Type 3 into Area 1",
              is_correct: false
            },
            {
              id: "D",
              text: "Type 5 in Area 2 → Type 7 by ABR R2 → Type 5 in Area 1",
              is_correct: false
            }
          ],
          correct_answer: "B",
          explanation:
            "In an NSSA, external routes are redistributed as Type 7 LSAs that flood only within the NSSA. " +
            "The ABR (R2) translates Type 7 into Type 5 LSAs at the NSSA boundary so the external route " +
            "can propagate into the backbone (Area 0) and then into Area 1 as a standard Type 5 LSA. " +
            "Type 7 LSAs are never forwarded outside the NSSA."
        }
      ],
      explanation:
        "This scenario tests understanding of multi-area OSPF LSA types, stub area variants (Stub, TSA, NSSA), " +
        "and how ABRs control LSA flooding between areas. Key facts: Type 3 carries inter-area summary routes; " +
        "TSA injects only a default Type 3; NSSA translates Type 7 → Type 5 at the ABR.",
      cisco_reference: "ENCOR OCG Chapters 8–9 — OSPF Multi-Area and Route Summarisation",
      expert_trap_warning:
        "Candidates often confuse Stub vs Totally Stubby vs NSSA. Remember: only NSSA supports redistribution " +
        "inside the area (via Type 7). A plain Stub area blocks ALL external AND inter-area LSAs except the default.",
      tags: ["ospf", "multi-area", "lsa", "nssa", "stub", "abr", "scenario"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // ---------------------------------------------------------
    // SCENARIO 2 — BGP Path Selection (Domain 3.0)
    // ---------------------------------------------------------
    {
      id: "exam-sc-002",
      type: "scenario",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 4,
      prompt: "Refer to the network diagram and scenario below.",
      diagram:
        "  AS 65100                     AS 65200\n" +
        "  ┌────────────────────┐       ┌──────────────────┐\n" +
        "  │  R1 ─── R2 (RR)  │◄eBGP►│ R4               │\n" +
        "  │  │       │         │       └──────────────────┘\n" +
        "  │  └──── R3         │◄eBGP►┌──────────────────┐\n" +
        "  └────────────────────┘       │ R5  (AS 65300)   │\n" +
        "                               └──────────────────┘",
      scenario_context:
        "AS 65100 contains three routers: R1, R2 (configured as iBGP Route Reflector), and R3. " +
        "R2 has eBGP peerings to R4 in AS 65200 and R5 in AS 65300. " +
        "Both R4 and R5 advertise prefix 10.50.0.0/16 to R2. " +
        "R2 applies the following inbound route-maps: from R4 — sets LOCAL_PREF 200; " +
        "from R5 — sets LOCAL_PREF 150 and prepends AS 65300 once (making AS-path: 65300 65300). " +
        "R1 also has a neighbour-specific weight of 500 configured for routes learned via R2 that " +
        "originated from AS 65200. R3 has no special policy.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      sub_questions: [
        {
          id: "exam-sc-002-q1",
          type: "single_choice",
          prompt:
            "R2 receives 10.50.0.0/16 from both R4 (AS 65200) and R5 (AS 65300). " +
            "Which path will R2 select as its best path and advertise to iBGP peers?",
          options: [
            {
              id: "A",
              text: "Path via R4 — higher LOCAL_PREF (200 > 150) wins after Weight is tied.",
              is_correct: true
            },
            {
              id: "B",
              text: "Path via R5 — shorter AS-path length wins.",
              is_correct: false
            },
            {
              id: "C",
              text: "Path via R4 — lower router-ID breaks the tie.",
              is_correct: false
            },
            {
              id: "D",
              text: "Path via R5 — MED is compared first and R5 has a lower MED.",
              is_correct: false
            }
          ],
          correct_answer: "A",
          explanation:
            "On R2 itself, Weight is 0 for both paths (the weight=500 is configured on R1, not R2). " +
            "BGP best-path then compares LOCAL_PREF: 200 (R4) > 150 (R5). Higher LOCAL_PREF wins, " +
            "so R2 selects the path through R4 and reflects it to R1 and R3. " +
            "AS-path length is only evaluated after LOCAL_PREF when LOCAL_PREF values differ — it is not reached here."
        },
        {
          id: "exam-sc-002-q2",
          type: "single_choice",
          prompt:
            "R1 receives the best-path reflection from R2 for 10.50.0.0/16 (via R4/AS 65200). " +
            "R1 also has a weight of 500 applied to all prefixes received from R2 that originate in AS 65200. " +
            "What is the next-hop R1 will use to forward traffic to 10.50.0.0/16?",
          options: [
            {
              id: "A",
              text: "R2's loopback — iBGP next-hop is always the iBGP peer address.",
              is_correct: false
            },
            {
              id: "B",
              text: "R4's eBGP interface address — the next-hop is unchanged by default for iBGP.",
              is_correct: true
            },
            {
              id: "C",
              text: "R1's own address — weight causes R1 to prefer a locally-generated default.",
              is_correct: false
            },
            {
              id: "D",
              text: "R5's address — R1 prefers the lower-LOCAL_PREF path to avoid R4.",
              is_correct: false
            }
          ],
          correct_answer: "B",
          explanation:
            "By default, iBGP does NOT change the NEXT_HOP attribute. R2 learned 10.50.0.0/16 from R4 " +
            "via eBGP; the next-hop is R4's eBGP-facing IP. R2 reflects this unchanged to R1. " +
            "R1 must have a route to that next-hop (typically via IGP). " +
            "The 'next-hop-self' command on R2 would change this behavior, but it is not mentioned here."
        },
        {
          id: "exam-sc-002-q3",
          type: "single_choice",
          prompt:
            "The network team wants AS 65100 to prefer the path via R5 (AS 65300) for 10.50.0.0/16 " +
            "without modifying LOCAL_PREF. Which BGP attribute, applied inbound on R2 from R5, " +
            "would accomplish this with the least administrative overhead?",
          options: [
            {
              id: "A",
              text: "Increase the MED received from R5 to a higher value.",
              is_correct: false
            },
            {
              id: "B",
              text: "Set WEIGHT on R2 for routes from R5 to a value greater than 0.",
              is_correct: true
            },
            {
              id: "C",
              text: "Set COMMUNITY no-export on routes from R5.",
              is_correct: false
            },
            {
              id: "D",
              text: "Remove the AS-path prepend from the R5 route-map; shorter AS-path will then win.",
              is_correct: false
            }
          ],
          correct_answer: "B",
          explanation:
            "WEIGHT is the highest-priority BGP attribute in Cisco's best-path algorithm and is locally " +
            "significant to the router (not advertised). Setting weight > 0 for routes from R5 on R2 " +
            "causes R2 to prefer that path before even considering LOCAL_PREF, making it the most direct " +
            "and least-invasive solution. Removing the AS-path prepend would also work but the AS-path " +
            "step comes after LOCAL_PREF — LOCAL_PREF 200 vs 150 would still favour R4."
        }
      ],
      explanation:
        "BGP best-path selection order (Cisco): Weight → LOCAL_PREF → Locally originated → AS-path length → " +
        "Origin → MED → eBGP over iBGP → IGP metric to next-hop → oldest eBGP path → lowest router-ID. " +
        "Understanding where each attribute sits in this sequence is essential for ENCOR.",
      cisco_reference: "ENCOR OCG Chapter 13 — Advanced BGP",
      expert_trap_warning:
        "Weight is Cisco-proprietary, locally significant, and NOT advertised to peers. " +
        "LOCAL_PREF IS advertised within the AS. Confusing these two is the most common BGP mistake on the exam.",
      tags: ["bgp", "path-selection", "weight", "local-pref", "as-path", "route-reflector", "scenario"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // ---------------------------------------------------------
    // SCENARIO 3 — SD-Access Fabric (Domain 1.0)
    // ---------------------------------------------------------
    {
      id: "exam-sc-003",
      type: "scenario",
      domain: "1.0",
      subdomain: "SD-Access",
      difficulty: 3,
      prompt: "Refer to the network diagram and scenario below.",
      diagram:
        "  ┌─────────────────────────────────────────────────────┐\n" +
        "  │                  SD-Access Fabric                    │\n" +
        "  │                                                       │\n" +
        "  │   [Edge Node A]──underlay──[Edge Node B]             │\n" +
        "  │         │                        │                   │\n" +
        "  │    [Host H1]               [Host H2]                 │\n" +
        "  │         │                        │                   │\n" +
        "  │   [Control Plane Node]   [Border Node]──[Fusion Rtr] │\n" +
        "  └─────────────────────────────────────────────────────┘",
      scenario_context:
        "A campus SD-Access fabric is deployed using Cisco DNA Center. The fabric has two Edge Nodes " +
        "(A and B), one Control Plane Node running LISP MS/MR, and one Border Node connecting the fabric " +
        "to an external routed network via a Fusion Router. " +
        "Host H1 is connected to Edge Node A and Host H2 is connected to Edge Node B. " +
        "Both hosts are in the same Virtual Network (VN) but different IP subnets. " +
        "VXLAN is used for the data-plane overlay. The underlay uses IS-IS for reachability between " +
        "fabric nodes.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      sub_questions: [
        {
          id: "exam-sc-003-q1",
          type: "single_choice",
          prompt:
            "H1 sends its first packet to H2. Both are in the same VN. " +
            "Which sequence of events correctly describes how Edge Node A resolves H2's location?",
          options: [
            {
              id: "A",
              text: "Edge Node A broadcasts an ARP request across the VXLAN fabric to all edge nodes.",
              is_correct: false
            },
            {
              id: "B",
              text: "Edge Node A sends a LISP Map-Request to the Control Plane Node; the CP Node replies with a Map-Reply containing Edge Node B's RLOC.",
              is_correct: true
            },
            {
              id: "C",
              text: "Edge Node A sends a BGP EVPN Type-2 route query to the Border Node.",
              is_correct: false
            },
            {
              id: "D",
              text: "Edge Node A consults its local FIB; the route to H2 was installed by IS-IS.",
              is_correct: false
            }
          ],
          correct_answer: "B",
          explanation:
            "SD-Access uses LISP as the control plane. When Edge Node A has no LISP cache entry for H2's EID " +
            "(IP address), it sends a LISP Map-Request to the Control Plane Node (LISP Map Resolver/Map Server). " +
            "The CP Node looks up its EID database (populated when H2 registered via Edge Node B) and " +
            "returns a Map-Reply with Edge Node B's RLOC. Edge Node A then encapsulates the traffic in " +
            "VXLAN and sends it to Edge Node B's RLOC in the underlay."
        },
        {
          id: "exam-sc-003-q2",
          type: "single_choice",
          prompt:
            "H1 needs to communicate with a server in the traditional campus network outside the SD-Access fabric. " +
            "Which fabric node handles this inter-domain forwarding, and what function does it perform?",
          options: [
            {
              id: "A",
              text: "Control Plane Node — it de-encapsulates VXLAN and routes to the external network via LISP.",
              is_correct: false
            },
            {
              id: "B",
              text: "Edge Node A — it performs Policy-Based Routing to send traffic directly to the Fusion Router.",
              is_correct: false
            },
            {
              id: "C",
              text: "Border Node — it de-encapsulates VXLAN overlay traffic and forwards it to the Fusion Router using standard IP routing.",
              is_correct: true
            },
            {
              id: "D",
              text: "Any Edge Node — edge nodes maintain a default route pointing directly to the external WAN.",
              is_correct: false
            }
          ],
          correct_answer: "C",
          explanation:
            "The Border Node is the gateway between the SD-Access fabric overlay and external networks. " +
            "It decapsulates the VXLAN encapsulation applied by the originating Edge Node and forwards " +
            "the native IP packet to the Fusion Router (or directly to external infrastructure). " +
            "The Fusion Router then routes to the traditional campus or WAN. " +
            "Border Nodes can be 'Default' (handling unknown EIDs) or 'External' (handling specific prefixes)."
        }
      ],
      explanation:
        "SD-Access fabric roles: Edge Node (connects endpoints, VXLAN xTR), Control Plane Node (LISP MS/MR, " +
        "EID-to-RLOC database), Border Node (fabric-to-external gateway, VXLAN de-encapsulation). " +
        "The underlay (IS-IS) provides reachability between RLOCs; VXLAN carries the overlay; LISP resolves locations.",
      cisco_reference: "ENCOR OCG Chapter 16 — SD-Access Architecture",
      expert_trap_warning:
        "Do not confuse the Control Plane Node (LISP) with the Border Node (external gateway). " +
        "The CP Node handles lookup/resolution; the Border Node handles actual data-plane exit from the fabric.",
      tags: ["sd-access", "lisp", "vxlan", "fabric", "border-node", "control-plane", "scenario"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // ---------------------------------------------------------
    // SCENARIO 4 — DMVPN Phase 3 with IPsec (Domain 2.0)
    // ---------------------------------------------------------
    {
      id: "exam-sc-004",
      type: "scenario",
      domain: "2.0",
      subdomain: "DMVPN",
      difficulty: 4,
      prompt: "Refer to the network diagram and scenario below.",
      diagram:
        "              [Hub R0]  (NBMA: 100.0.0.1)\n" +
        "             /         \\\n" +
        "    [Spoke R1]         [Spoke R2]\n" +
        "  (NBMA: 100.0.0.2)  (NBMA: 100.0.0.3)\n" +
        "  (Tunnel: 10.0.0.1)  (Tunnel: 10.0.0.2)\n" +
        "  (LAN: 192.168.1.0/24) (LAN: 192.168.2.0/24)",
      scenario_context:
        "A DMVPN Phase 3 hub-and-spoke network is deployed. R0 is the hub with a static public IP. " +
        "R1 and R2 are spokes with dynamic public IPs registered to the hub via NHRP. " +
        "IPsec is applied to the mGRE tunnel using a crypto profile. " +
        "EIGRP runs over the DMVPN tunnel interface. " +
        "The hub is configured with 'ip nhrp redirect' and spokes with 'ip nhrp shortcut'. " +
        "R1 needs to send traffic to R2's LAN (192.168.2.0/24).",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      sub_questions: [
        {
          id: "exam-sc-004-q1",
          type: "single_choice",
          prompt:
            "R1 sends the first packet destined for 192.168.2.1 (R2's LAN). " +
            "Describe the initial NHRP resolution process for a spoke-to-spoke tunnel in DMVPN Phase 3.",
          options: [
            {
              id: "A",
              text: "R1 queries the hub's NHS; the hub sends an NHRP Redirect to R1 and an NHRP Resolution Request to R2 on R1's behalf.",
              is_correct: true
            },
            {
              id: "B",
              text: "R1 sends an NHRP Registration directly to R2 to establish the tunnel.",
              is_correct: false
            },
            {
              id: "C",
              text: "R1 broadcasts an NHRP query across the mGRE interface; R2 responds directly.",
              is_correct: false
            },
            {
              id: "D",
              text: "R1 uses EIGRP to discover R2's NBMA address from the routing table.",
              is_correct: false
            }
          ],
          correct_answer: "A",
          explanation:
            "In DMVPN Phase 3, the first packet from R1 to 192.168.2.x travels hub-routed via R0. " +
            "R0 detects the packet is destined for a spoke it knows about (R2) and sends an NHRP Redirect " +
            "back to R1, telling it that a shorter path exists. R1 then sends an NHRP Resolution Request " +
            "to the hub (NHS) asking for R2's NBMA address. The hub proxies this and R2 replies with " +
            "its NBMA IP. R1 caches the mapping and subsequent packets go directly spoke-to-spoke."
        },
        {
          id: "exam-sc-004-q2",
          type: "single_choice",
          prompt:
            "IPsec is configured on the DMVPN tunnel. After NHRP resolution, R1 attempts to build " +
            "a direct IPsec tunnel to R2. What is required for this dynamic spoke-to-spoke IPsec " +
            "session to succeed?",
          options: [
            {
              id: "A",
              text: "A static crypto map entry on R1 must pre-define R2's public IP address.",
              is_correct: false
            },
            {
              id: "B",
              text: "IKEv1/IKEv2 must be initiated using R2's NBMA IP obtained via NHRP; a dynamic crypto map or IPsec profile on the tunnel interface handles this automatically.",
              is_correct: true
            },
            {
              id: "C",
              text: "DMVPN Phase 3 does not support spoke-to-spoke IPsec; all encrypted traffic must traverse the hub.",
              is_correct: false
            },
            {
              id: "D",
              text: "R1 must have a GRE tunnel sub-interface dedicated to each spoke for IPsec to work.",
              is_correct: false
            }
          ],
          correct_answer: "B",
          explanation:
            "DMVPN Phase 3 with IPsec uses an IPsec profile (tunnel protection) on the mGRE interface rather " +
            "than static crypto maps. When NHRP provides R2's NBMA IP, R1 dynamically initiates IKE to that " +
            "address. The IPsec profile allows dynamic peer creation, which is essential since spoke NBMA " +
            "addresses are not known in advance. Static crypto maps cannot handle dynamic spoke IPs."
        },
        {
          id: "exam-sc-004-q3",
          type: "single_choice",
          prompt:
            "EIGRP is running over the DMVPN hub-and-spoke topology. Which EIGRP configuration " +
            "is required on the hub to allow spoke-to-spoke routing after NHRP shortcut resolution?",
          options: [
            {
              id: "A",
              text: "The hub must enable EIGRP split-horizon on the tunnel interface so spokes learn each other's routes.",
              is_correct: false
            },
            {
              id: "B",
              text: "Split-horizon must be DISABLED on the hub's tunnel interface so EIGRP re-advertises spoke routes back out the same interface to other spokes.",
              is_correct: true
            },
            {
              id: "C",
              text: "EIGRP stub must be configured on the hub to prevent routing loops between spokes.",
              is_correct: false
            },
            {
              id: "D",
              text: "EIGRP passive-interface must be removed from the hub tunnel; no other change is needed.",
              is_correct: false
            }
          ],
          correct_answer: "B",
          explanation:
            "By default, EIGRP split-horizon is enabled, which prevents the hub from advertising a route " +
            "learned from Spoke R1 back out the same tunnel interface to Spoke R2. In DMVPN, all spokes " +
            "share the same mGRE interface on the hub, so split-horizon must be disabled " +
            "('no ip split-horizon eigrp <AS>') to allow the hub to reflect spoke routes to all other spokes. " +
            "Without this, spokes cannot route to each other even after NHRP shortcut resolution."
        }
      ],
      explanation:
        "DMVPN Phase 3 key concepts: hub sends NHRP Redirect to trigger spoke-to-spoke resolution; " +
        "spokes use 'ip nhrp shortcut' to install CEF shortcuts; dynamic IPsec profiles allow on-demand " +
        "spoke-to-spoke encryption; EIGRP split-horizon must be disabled on the hub tunnel interface.",
      cisco_reference: "ENCOR OCG Chapter 14 — DMVPN",
      expert_trap_warning:
        "Phase 3 uses NHRP Redirect (hub-initiated) + shortcut, not the Phase 2 spoke-initiated Resolution Request. " +
        "Forgetting to disable split-horizon on the hub is the most common DMVPN lab failure point.",
      tags: ["dmvpn", "phase3", "nhrp", "ipsec", "eigrp", "split-horizon", "scenario"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // ---------------------------------------------------------
    // SCENARIO 6 — IP SLA with PBR Failover (Domain 4.0)
    // ---------------------------------------------------------
    {
      id: "exam-sc-006",
      type: "scenario",
      domain: "4.0",
      subdomain: "IP SLA",
      difficulty: 3,
      prompt: "Refer to the network diagram and scenario below.",
      diagram:
        "                    [ISP-A]  ← Primary (10.1.1.2)\n" +
        "                   /\n" +
        "  [LAN]──[R1]──e0/0\n" +
        "                   \\\n" +
        "                    [ISP-B]  ← Backup  (10.2.2.2)\n" +
        "                         e0/1",
      scenario_context:
        "R1 has two ISP uplinks: e0/0 to ISP-A (primary, 10.1.1.2) and e0/1 to ISP-B (backup, 10.2.2.2). " +
        "Policy-Based Routing (PBR) is applied inbound on the LAN interface to steer traffic. " +
        "IP SLA 1 is configured to send ICMP echo probes to 8.8.8.8 via ISP-A every 5 seconds with a " +
        "threshold of 3 consecutive failures. A track object (track 1) monitors IP SLA 1. " +
        "The PBR route-map uses 'set ip next-hop verify-availability 10.1.1.2 1 track 1' as the primary " +
        "action and 'set ip next-hop 10.2.2.2' as the fallback.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      sub_questions: [
        {
          id: "exam-sc-006-q1",
          type: "single_choice",
          prompt:
            "ISP-A's link fails and three consecutive ICMP probes to 8.8.8.8 do not receive replies. " +
            "What is the sequence of events that causes PBR to redirect traffic to ISP-B?",
          options: [
            {
              id: "A",
              text: "IP SLA 1 transitions to 'down'; track 1 state changes to 'down'; PBR skips the 'verify-availability' next-hop and uses the fallback next-hop 10.2.2.2.",
              is_correct: true
            },
            {
              id: "B",
              text: "The static route to 10.1.1.2 is removed from the RIB; the PBR route-map re-evaluates and installs a new static route to 10.2.2.2.",
              is_correct: false
            },
            {
              id: "C",
              text: "IP SLA 1 transitions to 'down'; R1 automatically enables HSRP to fail over to ISP-B.",
              is_correct: false
            },
            {
              id: "D",
              text: "Track 1 sends a Syslog alert; the administrator must manually update the PBR route-map to use 10.2.2.2.",
              is_correct: false
            }
          ],
          correct_answer: "A",
          explanation:
            "IP SLA monitors the path to 8.8.8.8 via ISP-A. After the configured failure threshold is met, " +
            "the SLA object transitions to 'down'. The track object (track 1) tied to IP SLA 1 also goes " +
            "'down'. The PBR route-map statement 'set ip next-hop verify-availability 10.1.1.2 1 track 1' " +
            "checks the track state before using that next-hop. With track 1 down, PBR skips 10.1.1.2 and " +
            "falls through to the unconditional 'set ip next-hop 10.2.2.2', redirecting traffic to ISP-B. " +
            "This is fully automated — no manual intervention is required."
        },
        {
          id: "exam-sc-006-q2",
          type: "single_choice",
          prompt:
            "The administrator wants IP SLA to verify end-to-end reachability rather than just interface " +
            "liveness. Which IP SLA probe type is MOST appropriate, and what is a key operational consideration?",
          options: [
            {
              id: "A",
              text: "ICMP Echo — probes the target IP using ICMP; the target does not need special configuration but ICMP must be permitted through the path.",
              is_correct: true
            },
            {
              id: "B",
              text: "UDP Jitter — best for verifying reachability; requires no responder at the destination.",
              is_correct: false
            },
            {
              id: "C",
              text: "TCP Connect — the only probe type that verifies layer-3 reachability end-to-end.",
              is_correct: false
            },
            {
              id: "D",
              text: "ICMP Path Echo — traces each hop and is the standard probe for failover tracking.",
              is_correct: false
            }
          ],
          correct_answer: "A",
          explanation:
            "ICMP Echo is the most common IP SLA probe for reachability tracking in dual-ISP scenarios. " +
            "The target (e.g., 8.8.8.8 or an ISP-provided DNS/gateway) simply needs to respond to pings — " +
            "no Cisco IP SLA Responder software is required on the far end. " +
            "UDP Jitter and other performance-measurement probes require a Cisco IP SLA Responder at the " +
            "destination, making them unsuitable for monitoring public internet reachability. " +
            "The key consideration is that ICMP must not be blocked by an ACL in the path."
        }
      ],
      explanation:
        "IP SLA + Object Tracking + PBR is a classic dual-ISP failover solution. Key config elements: " +
        "'ip sla <n>' with probe type and schedule; 'track <n> ip sla <n> reachability'; PBR route-map with " +
        "'set ip next-hop verify-availability <ip> <seq> track <n>'. The verify-availability keyword is " +
        "critical — without it, PBR uses the next-hop regardless of track state.",
      cisco_reference: "ENCOR OCG Chapter 18 — Network Assurance (IP SLA, Tracking, PBR)",
      expert_trap_warning:
        "'set ip next-hop' vs 'set ip next-hop verify-availability': without verify-availability, " +
        "the track object has NO effect on PBR — traffic blackholes if the next-hop is unreachable. " +
        "This is one of the most frequently tested and most commonly missed PBR details.",
      tags: ["ip-sla", "pbr", "object-tracking", "dual-isp", "failover", "icmp-echo", "scenario"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // EXPANDED QUESTION SET — DOMAIN 1.0 ARCHITECTURE
    // =========================================================

    {
      id: "exam-exp-001",
      type: "single_choice",
      domain: "1.0",
      subdomain: "SD-Access",
      difficulty: 3,
      prompt: "In Cisco SD-Access, which encapsulation protocol carries the overlay traffic between fabric Edge Nodes across the underlay network?",
      options: [
        { id: "A", text: "GRE (Generic Routing Encapsulation)", is_correct: false },
        { id: "B", text: "VXLAN (Virtual Extensible LAN)", is_correct: true },
        { id: "C", text: "MPLS with LDP labels", is_correct: false },
        { id: "D", text: "IPsec transport mode", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "SD-Access uses VXLAN (RFC 7348, UDP 4789) as the data-plane overlay encapsulation. VXLAN carries the original Layer 2 frame inside a UDP packet, allowing endpoints in different physical locations to appear on the same Layer 2 segment. LISP is the control plane; VXLAN is the data plane. GRE and MPLS are not used in the SD-Access fabric overlay.",
      cisco_reference: "ENCOR OCG Chapter 16 — SD-Access Fabric Architecture",
      expert_trap_warning: "Candidates sometimes answer LISP here because LISP is prominent in SD-Access discussions. LISP handles control-plane EID-to-RLOC mapping; VXLAN is the data-plane encapsulation. They serve different functions.",
      tags: ["sd-access", "vxlan", "overlay", "encapsulation"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-002",
      type: "single_choice",
      domain: "1.0",
      subdomain: "SD-Access",
      difficulty: 3,
      prompt: "Which TrustSec component in Cisco SD-Access is responsible for enforcing access policy between groups based on Scalable Group Tags (SGTs)?",
      options: [
        { id: "A", text: "LISP Map Server", is_correct: false },
        { id: "B", text: "SGACL (Scalable Group ACL) on the egress node", is_correct: true },
        { id: "C", text: "The 802.1X supplicant on the endpoint", is_correct: false },
        { id: "D", text: "CAPWAP control channel between AP and WLC", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In TrustSec / SD-Access, endpoints are assigned Scalable Group Tags (SGTs) at ingress. Policy enforcement occurs at the egress fabric node via SGACLs (Scalable Group ACLs), which define what traffic is permitted or denied between source and destination SGT pairs. The LISP Map Server handles location resolution, not policy enforcement.",
      cisco_reference: "ENCOR OCG Chapter 16 — TrustSec and SGT",
      expert_trap_warning: null,
      tags: ["trustsec", "sgt", "sgacl", "sd-access"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-003",
      type: "single_choice",
      domain: "1.0",
      subdomain: "SD-WAN",
      difficulty: 3,
      prompt: "In Cisco SD-WAN, a vEdge router has two transport connections: MPLS and broadband internet. Which OMP attribute is used to identify each transport link and its associated colour/characteristics?",
      options: [
        { id: "A", text: "TLOC (Transport Location Identifier)", is_correct: true },
        { id: "B", text: "vRoute (OMP route advertisement)", is_correct: false },
        { id: "C", text: "RLOC (Routing Locator)", is_correct: false },
        { id: "D", text: "Service-side prefix", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "A TLOC (Transport Location Identifier) is a three-tuple: {system IP, colour, encapsulation} that uniquely identifies each transport tunnel endpoint on a WAN Edge router. Colours (mpls, biz-internet, public-internet, etc.) classify transport types. OMP advertises TLOCs to vSmart, enabling policy-based path selection per transport colour.",
      cisco_reference: "ENCOR OCG Chapter 17 — SD-WAN OMP",
      expert_trap_warning: null,
      tags: ["sd-wan", "omp", "tloc", "transport"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-004",
      type: "multi_choice",
      domain: "1.0",
      subdomain: "SD-WAN",
      difficulty: 3,
      prompt: "Which TWO control connections does a new SD-WAN WAN Edge router establish with vSmart after ZTP onboarding via vBond? (Choose two)",
      options: [
        { id: "A", text: "OMP session to vSmart for routing and policy distribution", is_correct: true },
        { id: "B", text: "DTLS/TLS control connection to vSmart for secure management", is_correct: true },
        { id: "C", text: "SNMP trap connection to vManage for real-time alerting", is_correct: false },
        { id: "D", text: "BFD session to vBond for keepalive monitoring", is_correct: false }
      ],
      correct_answer: ["A", "B"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "After vBond onboarding, WAN Edge routers establish DTLS or TLS connections to vSmart controllers and then bring up OMP sessions over those secure tunnels. OMP is the control-plane protocol that carries routes, policies, and keys. BFD is used for data-plane tunnel liveliness between WAN Edge routers — not toward vBond. SNMP is not part of the core SD-WAN control-plane.",
      cisco_reference: "ENCOR OCG Chapter 17 — SD-WAN Architecture",
      expert_trap_warning: null,
      tags: ["sd-wan", "vsmart", "omp", "dtls", "control-connection"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-006",
      type: "single_choice",
      domain: "1.0",
      subdomain: "QoS",
      difficulty: 2,
      prompt: "Where is the QoS trust boundary typically established in a campus network to prevent end users from setting their own DSCP markings?",
      options: [
        { id: "A", text: "At the core layer switches where bandwidth is plentiful", is_correct: false },
        { id: "B", text: "At the access layer switch port connected to the endpoint", is_correct: true },
        { id: "C", text: "At the WAN edge router where traffic leaves the enterprise", is_correct: false },
        { id: "D", text: "At the distribution layer for easier policy management", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The QoS trust boundary is the point where incoming DSCP/CoS markings are either trusted or overwritten. In a campus, this is placed at the access layer switch port connected to the endpoint. Markings from untrusted sources (PCs) are overwritten by the switch's ingress policy. IP phones can be trusted based on CoS from the phone.",
      cisco_reference: "ENCOR OCG Chapter 13 — QoS Trust Boundaries",
      expert_trap_warning: null,
      tags: ["qos", "trust-boundary", "dscp", "access-layer"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-007",
      type: "single_choice",
      domain: "1.0",
      subdomain: "QoS",
      difficulty: 3,
      prompt: "Which queuing mechanism provides a strict-priority queue for low-latency traffic (like VoIP) while preventing queue starvation of other traffic classes?",
      options: [
        { id: "A", text: "WFQ (Weighted Fair Queuing)", is_correct: false },
        { id: "B", text: "PQ (Priority Queuing) with FIFO fallback", is_correct: false },
        { id: "C", text: "LLQ (Low Latency Queuing) — CBWFQ with a strict priority queue", is_correct: true },
        { id: "D", text: "WRED (Weighted Random Early Detection)", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "LLQ (Low Latency Queuing) combines CBWFQ (Class-Based Weighted Fair Queuing) with a strict priority queue (PQ). The PQ is always serviced first, providing guaranteed low latency for VoIP. The bandwidth allocation for the PQ is policed to prevent it from starving other CBWFQ classes. Pure PQ without policing can starve lower-priority queues indefinitely.",
      cisco_reference: "ENCOR OCG Chapter 13 — LLQ",
      expert_trap_warning: null,
      tags: ["qos", "llq", "cbwfq", "priority-queue", "voip"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-008",
      type: "single_choice",
      domain: "1.0",
      subdomain: "High Availability",
      difficulty: 3,
      prompt: "What is the primary difference between Cisco StackWise Virtual and a traditional VSS (Virtual Switching System) on Catalyst platforms?",
      options: [
        { id: "A", text: "StackWise Virtual uses a dedicated StackWise cable; VSS uses a Virtual Switch Link (VSL) over standard ports", is_correct: true },
        { id: "B", text: "VSS supports more member switches than StackWise Virtual", is_correct: false },
        { id: "C", text: "StackWise Virtual only operates on fixed-configuration switches; VSS is for chassis switches", is_correct: false },
        { id: "D", text: "Both use the same underlying technology with different marketing names", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "StackWise Virtual (Catalyst 9000) uses a dedicated physical StackWise cable (or SVL over 10/40/100G interfaces) to link two switches into a single logical switch. VSS (Catalyst 6500/4500) uses a Virtual Switch Link (VSL) — standard Ethernet links configured as a VSL EtherChannel. Both create a single management and control plane from two physical chassis, but the interconnect technology differs.",
      cisco_reference: "ENCOR OCG Chapter 6 — StackWise Virtual",
      expert_trap_warning: null,
      tags: ["stackwise-virtual", "vss", "high-availability", "chassis"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-009",
      type: "single_choice",
      domain: "1.0",
      subdomain: "DNA Center",
      difficulty: 2,
      prompt: "Which Cisco DNA Center feature continuously monitors the network for issues and uses AI/ML to proactively identify problems before users are impacted?",
      options: [
        { id: "A", text: "Network Discovery", is_correct: false },
        { id: "B", text: "DNA Center Assurance", is_correct: true },
        { id: "C", text: "Template Programmer", is_correct: false },
        { id: "D", text: "Software Image Management (SWIM)", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DNA Center Assurance uses streaming telemetry, SNMP, and syslog data combined with AI/ML to provide real-time network health scores, root cause analysis, and guided remediation. Network Discovery finds devices. Template Programmer deploys configurations. SWIM manages software images. Assurance is the monitoring/analytics component.",
      cisco_reference: "ENCOR OCG Chapter 19 — DNA Center Assurance",
      expert_trap_warning: null,
      tags: ["dna-center", "assurance", "ai-ml", "monitoring"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-010",
      type: "multi_choice",
      domain: "1.0",
      subdomain: "QoS",
      difficulty: 3,
      prompt: "Which TWO DSCP values are standardised for voice and video signalling traffic respectively in the Cisco QoS baseline model? (Choose two)",
      options: [
        { id: "A", text: "DSCP CS3 (24) for call signalling", is_correct: true },
        { id: "B", text: "DSCP AF41 (34) for interactive video (video conferencing)", is_correct: true },
        { id: "C", text: "DSCP EF (46) for call signalling", is_correct: false },
        { id: "D", text: "DSCP CS6 (48) for video conferencing streams", is_correct: false }
      ],
      correct_answer: ["A", "B"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Cisco QoS baseline: EF (DSCP 46) = voice bearer (RTP). CS3 (DSCP 24) = call signalling (SCCP, SIP). AF41 (DSCP 34) = interactive video (videoconferencing). CS6 (DSCP 48) = routing protocol control traffic. AF31 (DSCP 26) = mission-critical data. CS7 (DSCP 56) = reserved network control.",
      cisco_reference: "ENCOR OCG Chapter 13 — Cisco QoS Baseline",
      expert_trap_warning: "EF is for voice BEARER (the audio RTP stream), NOT signalling. CS3 is for signalling. Confusing these two is a very common exam error.",
      tags: ["qos", "dscp", "cs3", "af41", "voice", "video"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-011",
      type: "single_choice",
      domain: "1.0",
      subdomain: "SD-Access",
      difficulty: 3,
      prompt: "An SD-Access fabric uses IS-IS as the underlay routing protocol. What is the primary reason IS-IS is preferred over OSPF for the SD-Access underlay?",
      options: [
        { id: "A", text: "IS-IS supports more ECMP paths than OSPF in Cisco implementations", is_correct: false },
        { id: "B", text: "IS-IS is protocol-agnostic at Layer 3 and natively supports IPv4 and IPv6 in a single process without separate instances", is_correct: true },
        { id: "C", text: "IS-IS provides faster convergence than OSPF by using SPF timers that OSPF cannot match", is_correct: false },
        { id: "D", text: "IS-IS is required for LISP to function; OSPF is incompatible with LISP", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "IS-IS runs at Layer 2 (directly over data link) rather than inside IP, making it independent of Layer 3 addressing. It natively supports both IPv4 and IPv6 in a single process. Cisco's DNA Center automation also deploys IS-IS for the SD-Access underlay by default. While OSPF can also be used, IS-IS is the Cisco-recommended default for SD-Access fabric underlays.",
      cisco_reference: "ENCOR OCG Chapter 16 — SD-Access Underlay",
      expert_trap_warning: null,
      tags: ["sd-access", "is-is", "underlay", "ospf"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-012",
      type: "drag_drop",
      domain: "1.0",
      subdomain: "SD-WAN",
      difficulty: 3,
      prompt: "Match each Cisco SD-WAN component to its primary plane and function.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "vManage" },
        { id: "item2", text: "vSmart" },
        { id: "item3", text: "vBond" },
        { id: "item4", text: "WAN Edge (vEdge/cEdge)" }
      ],
      drop_targets: [
        { id: "target1", label: "Management plane — GUI, REST API, configuration, monitoring", accepts: "item1" },
        { id: "target2", label: "Control plane — OMP routing, policy distribution, key exchange", accepts: "item2" },
        { id: "target3", label: "Orchestration plane — ZTP, NAT traversal, initial device onboarding", accepts: "item3" },
        { id: "target4", label: "Data plane — IPsec BFD tunnels, forwarding, QoS enforcement", accepts: "item4" }
      ],
      explanation: "SD-WAN planes: vManage (management/GUI/API), vSmart (control/OMP), vBond (orchestration/ZTP/NAT), WAN Edge (data plane forwarding with IPsec tunnels). The separation of these planes enables centralised policy control with distributed data-plane forwarding.",
      cisco_reference: "ENCOR OCG Chapter 17 — SD-WAN Components",
      expert_trap_warning: null,
      tags: ["sd-wan", "vmanage", "vsmart", "vbond", "planes"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-013",
      type: "single_choice",
      domain: "1.0",
      subdomain: "High Availability",
      difficulty: 2,
      prompt: "When Cisco StackWise Virtual is deployed and the SVL (StackWise Virtual Link) fails, what mechanism prevents a dual-active condition where both switches forward traffic independently?",
      options: [
        { id: "A", text: "HSRP priority adjustment on both switches", is_correct: false },
        { id: "B", text: "Dual-Active Detection (DAD) using a dedicated DAD link or through fast-hello on uplinks", is_correct: true },
        { id: "C", text: "BFD sessions across all routed interfaces", is_correct: false },
        { id: "D", text: "The secondary switch automatically shuts down all interfaces upon SVL loss", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "StackWise Virtual uses Dual-Active Detection (DAD) to prevent both switches from operating as independent active units if the SVL fails. DAD can use a dedicated DAD link (a direct physical port between the two switches), fast-hello detection on MEC uplinks, or EPAGP. When a dual-active condition is detected, the recovery switch shuts down all non-SVL interfaces except management.",
      cisco_reference: "ENCOR OCG Chapter 6 — StackWise Virtual DAD",
      expert_trap_warning: null,
      tags: ["stackwise-virtual", "dual-active", "dad", "high-availability"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-017",
      type: "single_choice",
      domain: "1.0",
      subdomain: "QoS",
      difficulty: 3,
      prompt: "Traffic shaping and traffic policing both enforce a bandwidth rate limit. Which statement correctly describes a key difference between them?",
      options: [
        { id: "A", text: "Policing can burst above the rate temporarily; shaping always drops excess traffic immediately", is_correct: false },
        { id: "B", text: "Shaping buffers (delays) excess traffic to smooth it to the configured rate; policing drops or re-marks excess traffic without buffering", is_correct: true },
        { id: "C", text: "Shaping is applied only on ingress; policing is applied only on egress", is_correct: false },
        { id: "D", text: "Policing requires a token bucket; shaping uses a leaky bucket algorithm only", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Traffic shaping places excess traffic into a buffer (queue) and transmits it at the shaped rate, introducing delay but minimising drops — ideal for traffic conforming to a service provider's CIR. Traffic policing immediately drops or re-marks traffic that exceeds the rate — it does not buffer. Policing is applied on both ingress and egress; shaping is typically egress only. Both use token bucket mechanisms.",
      cisco_reference: "ENCOR OCG Chapter 13 — Policing vs Shaping",
      expert_trap_warning: null,
      tags: ["qos", "policing", "shaping", "token-bucket"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-018",
      type: "single_choice",
      domain: "1.0",
      subdomain: "High Availability",
      difficulty: 2,
      prompt: "In Cisco IOS-XE, what is the function of the In-Service Software Upgrade (ISSU) feature?",
      options: [
        { id: "A", text: "It allows non-disruptive software upgrades on single-supervisor routers via a warm reboot", is_correct: false },
        { id: "B", text: "It allows a software image to be upgraded on a dual-supervisor chassis without dropping forwarding traffic, leveraging SSO", is_correct: true },
        { id: "C", text: "It upgrades only the line card software without affecting the supervisor", is_correct: false },
        { id: "D", text: "It performs automated rollback if a configuration error is detected post-upgrade", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "ISSU allows a software upgrade on dual-supervisor platforms (e.g., Catalyst 6500/9400 with two supervisors) without dropping forwarding traffic. The standby supervisor loads the new image first, SSO synchronises state, a switchover promotes the standby to active, and the original active supervisor then upgrades. NSF+SSO is a prerequisite for ISSU.",
      cisco_reference: "ENCOR OCG Chapter 6 — ISSU",
      expert_trap_warning: null,
      tags: ["issu", "high-availability", "sso", "nsf", "upgrade"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // DOMAIN 2.0 — VIRTUALIZATION (expanded)
    // =========================================================

    {
      id: "exam-exp-019",
      type: "single_choice",
      domain: "2.0",
      subdomain: "VRF",
      difficulty: 3,
      prompt: "In MPLS L3VPN, what is the purpose of the Route Distinguisher (RD) attached to VPN routes?",
      options: [
        { id: "A", text: "It identifies which VRF a route should be imported into on the receiving PE router", is_correct: false },
        { id: "B", text: "It makes overlapping IPv4 prefixes from different VRFs globally unique in the MP-BGP table (VPNv4 address family)", is_correct: true },
        { id: "C", text: "It provides encryption for VPN routes carried across the MPLS backbone", is_correct: false },
        { id: "D", text: "It replaces the AS number in eBGP sessions between CE and PE routers", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The Route Distinguisher (RD) is an 8-byte value prepended to a VPN IPv4 prefix, creating a globally unique VPNv4 prefix in the MP-BGP table. Two customers can both use 10.0.0.0/8; the RD makes their prefixes distinct (e.g., 1:1:10.0.0.0/8 and 2:2:10.0.0.0/8). The Route Target (RT) extended community controls VRF import/export policy.",
      cisco_reference: "ENCOR OCG Chapter 8 — MPLS L3VPN",
      expert_trap_warning: "RD and RT are frequently confused. RD makes prefixes unique; RT controls import/export policy. The RD does NOT control which VRF receives a route — that is the RT's job.",
      tags: ["vrf", "mpls-l3vpn", "rd", "route-distinguisher", "mp-bgp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-020",
      type: "single_choice",
      domain: "2.0",
      subdomain: "VRF",
      difficulty: 3,
      prompt: "In MPLS L3VPN, what mechanism controls which VPN routes from one VRF are imported into another VRF on the same or a different PE router?",
      options: [
        { id: "A", text: "Route Distinguisher (RD)", is_correct: false },
        { id: "B", text: "Route Target (RT) extended community — export RT matched against import RT", is_correct: true },
        { id: "C", text: "MP-BGP community attribute with a well-known community value", is_correct: false },
        { id: "D", text: "VRF route-map applied on the CE-PE eBGP session", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Route Targets (RTs) are BGP extended communities attached to VPNv4 prefixes. A VRF's export RT is attached when routes are exported to MP-BGP. A remote VRF's import RT list is compared against incoming route RTs — if they match, the route is imported into that VRF. This enables hub-and-spoke and extranet VPN designs.",
      cisco_reference: "ENCOR OCG Chapter 8 — VRF Route Target",
      expert_trap_warning: null,
      tags: ["vrf", "route-target", "mpls-l3vpn", "mp-bgp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-021",
      type: "single_choice",
      domain: "2.0",
      subdomain: "VXLAN",
      difficulty: 3,
      prompt: "In VXLAN BGP EVPN, what is the primary role of the BGP EVPN control plane compared to flood-and-learn VXLAN?",
      options: [
        { id: "A", text: "BGP EVPN uses multicast for BUM (broadcast, unknown unicast, multicast) traffic, reducing head-end replication", is_correct: false },
        { id: "B", text: "BGP EVPN distributes MAC and IP reachability information between VTEPs, eliminating the need for data-plane flooding to learn end-host locations", is_correct: true },
        { id: "C", text: "BGP EVPN encrypts VXLAN tunnels between VTEPs using IPsec", is_correct: false },
        { id: "D", text: "BGP EVPN replaces the VXLAN UDP encapsulation with MPLS labels", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In flood-and-learn VXLAN, VTEPs learn remote MAC-to-VTEP mappings by flooding BUM traffic and learning from data-plane traffic — similar to traditional Ethernet learning. BGP EVPN (RFC 7432) distributes MAC/IP bindings via BGP Type-2 routes, so VTEPs know remote endpoint locations via the control plane without flooding. This scales better and supports distributed anycast gateways.",
      cisco_reference: "ENCOR OCG Chapter 16 — VXLAN BGP EVPN",
      expert_trap_warning: null,
      tags: ["vxlan", "bgp-evpn", "vtep", "flood-and-learn"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-022",
      type: "single_choice",
      domain: "2.0",
      subdomain: "VXLAN",
      difficulty: 2,
      prompt: "In VXLAN, what is the VTEP and what is its function?",
      options: [
        { id: "A", text: "VXLAN Tunnel Endpoint — the device that encapsulates and decapsulates VXLAN frames at the fabric edge", is_correct: true },
        { id: "B", text: "Virtual Trunk Encapsulation Protocol — the protocol that tags frames with VNI values", is_correct: false },
        { id: "C", text: "VLAN Translation and Encapsulation Point — maps VLANs to VXLANs at the core", is_correct: false },
        { id: "D", text: "Virtual Topology End Point — the BGP EVPN route-reflector for VXLAN fabrics", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "A VTEP (VXLAN Tunnel Endpoint) is the logical entity (on a switch, router, or hypervisor) that originates and terminates VXLAN tunnels. The ingress VTEP encapsulates the original Layer 2 frame with a VXLAN header (including the 24-bit VNI), outer UDP, and outer IP header. The egress VTEP decapsulates and delivers the original frame to the destination segment. VNI (VXLAN Network Identifier) identifies the logical segment.",
      cisco_reference: "ENCOR OCG Chapter 16 — VXLAN Architecture",
      expert_trap_warning: null,
      tags: ["vxlan", "vtep", "vni", "encapsulation"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-023",
      type: "multi_choice",
      domain: "2.0",
      subdomain: "LISP",
      difficulty: 3,
      prompt: "Which TWO LISP components are combined in the Cisco SD-Access Control Plane Node? (Choose two)",
      options: [
        { id: "A", text: "Map Server (MS)", is_correct: true },
        { id: "B", text: "Map Resolver (MR)", is_correct: true },
        { id: "C", text: "Ingress Tunnel Router (ITR)", is_correct: false },
        { id: "D", text: "Proxy Egress Tunnel Router (PETR)", is_correct: false }
      ],
      correct_answer: ["A", "B"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "The SD-Access Control Plane Node runs both the LISP Map Server (MS) and Map Resolver (MR) functions. The Map Server receives Map-Register messages from ETRs (Edge Nodes) to build the EID-to-RLOC database. The Map Resolver processes Map-Request queries from ITRs (Edge Nodes) and returns Map-Reply with the RLOC. In SD-Access, Edge Nodes serve as both ITR and ETR (xTR).",
      cisco_reference: "ENCOR OCG Chapter 16 — LISP MS/MR",
      expert_trap_warning: null,
      tags: ["lisp", "map-server", "map-resolver", "sd-access", "control-plane-node"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-024",
      type: "single_choice",
      domain: "2.0",
      subdomain: "DMVPN",
      difficulty: 2,
      prompt: "In DMVPN, what is the role of mGRE (Multipoint GRE) compared to standard point-to-point GRE?",
      options: [
        { id: "A", text: "mGRE adds IPsec encryption to GRE tunnels automatically", is_correct: false },
        { id: "B", text: "mGRE allows a single tunnel interface to support multiple dynamic peer endpoints, enabling hub-and-spoke and spoke-to-spoke connectivity", is_correct: true },
        { id: "C", text: "mGRE uses TCP instead of IP protocol 47 for transport", is_correct: false },
        { id: "D", text: "mGRE reduces GRE overhead from 24 bytes to 8 bytes by eliminating the outer IP header", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "mGRE (IP protocol 47, same as GRE) extends standard GRE by allowing a single tunnel interface to dynamically accept connections from multiple endpoints without pre-configuring individual point-to-point GRE tunnels for each peer. Combined with NHRP for address resolution, mGRE is the foundation for DMVPN's scalable hub-and-spoke and spoke-to-spoke architecture.",
      cisco_reference: "ENCOR OCG Chapter 9 — DMVPN mGRE",
      expert_trap_warning: null,
      tags: ["dmvpn", "mgre", "gre", "tunnel"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-025",
      type: "single_choice",
      domain: "2.0",
      subdomain: "Multicast",
      difficulty: 3,
      prompt: "Which PIM mode requires all multicast traffic to flow through the Rendezvous Point (RP) and never switches to a source-based Shortest Path Tree (SPT)?",
      options: [
        { id: "A", text: "PIM Sparse Mode (PIM-SM)", is_correct: false },
        { id: "B", text: "PIM Bidirectional Mode (BIDIR-PIM)", is_correct: true },
        { id: "C", text: "PIM Dense Mode (PIM-DM)", is_correct: false },
        { id: "D", text: "PIM Source-Specific Multicast (PIM-SSM)", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "BIDIR-PIM always uses a shared tree (RPT) rooted at the RP — it never switches to the SPT. This makes it efficient for many-to-many multicast groups where multiple sources send to the same group. PIM-SM uses shared trees initially but can switch to SPT (per default threshold). PIM-DM uses dense flooding. PIM-SSM uses source-specific trees with no RP.",
      cisco_reference: "ENCOR OCG Chapter 14 — PIM Modes",
      expert_trap_warning: null,
      tags: ["multicast", "bidir-pim", "rp", "spt"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-026",
      type: "single_choice",
      domain: "2.0",
      subdomain: "Multicast",
      difficulty: 2,
      prompt: "Which protocol do hosts use to join and leave multicast groups on an IPv4 network, and which version supports source-specific group membership (SSM)?",
      options: [
        { id: "A", text: "IGMP — IGMPv3 supports source-specific (S,G) membership", is_correct: true },
        { id: "B", text: "IGMP — IGMPv2 supports source-specific (S,G) membership", is_correct: false },
        { id: "C", text: "MLD — MLDv1 supports source-specific membership for IPv4", is_correct: false },
        { id: "D", text: "PIM — PIM-SM handles group membership directly without IGMP", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "IGMP (Internet Group Management Protocol) is used by hosts to signal multicast group membership to their directly connected router. IGMPv1 and IGMPv2 support (*,G) any-source multicast. IGMPv3 adds support for source filtering — hosts can join (S,G) specific source-group pairs for SSM (Source-Specific Multicast, 232.0.0.0/8). MLD is the IPv6 equivalent of IGMP.",
      cisco_reference: "ENCOR OCG Chapter 14 — IGMP",
      expert_trap_warning: null,
      tags: ["igmp", "multicast", "ssm", "igmpv3"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-027",
      type: "single_choice",
      domain: "2.0",
      subdomain: "VRF",
      difficulty: 2,
      prompt: "An engineer configures VRF-Lite on a router with two customers. Which command correctly assigns interface GigabitEthernet0/1 to VRF 'CUSTOMER_A'?",
      options: [
        { id: "A", text: "ip vrf forwarding CUSTOMER_A (applied under the interface)", is_correct: true },
        { id: "B", text: "vrf definition CUSTOMER_A (applied under the interface)", is_correct: false },
        { id: "C", text: "ip vrf select CUSTOMER_A (applied globally)", is_correct: false },
        { id: "D", text: "vrf member CUSTOMER_A (applied under the interface)", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "To assign an interface to a VRF in IOS-XE, use 'ip vrf forwarding <vrf-name>' under the interface configuration. This removes any existing IP address from the interface (it must be re-configured). The 'vrf definition' command is used to create the VRF globally. 'vrf member' is NX-OS syntax.",
      cisco_reference: "ENCOR OCG Chapter 8 — VRF Configuration",
      expert_trap_warning: "Applying 'ip vrf forwarding' to an interface removes the configured IP address. This is a common trap on production networks and on the exam — always re-apply the IP address after assigning the VRF.",
      tags: ["vrf", "vrf-lite", "configuration", "ios-xe"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-028",
      type: "multi_choice",
      domain: "2.0",
      subdomain: "GRE / Tunneling",
      difficulty: 3,
      prompt: "Which TWO statements about IPv6-over-IPv4 tunnelling using 6in4 manual tunnels are correct? (Choose two)",
      options: [
        { id: "A", text: "6in4 tunnels use IP protocol number 41 to encapsulate IPv6 inside IPv4", is_correct: true },
        { id: "B", text: "6in4 tunnels require explicit configuration of both tunnel source and tunnel destination endpoints", is_correct: true },
        { id: "C", text: "6in4 tunnels use GRE protocol 47 as the encapsulation", is_correct: false },
        { id: "D", text: "6in4 supports dynamic peer discovery using NHRP", is_correct: false }
      ],
      correct_answer: ["A", "B"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "6in4 (RFC 4213) encapsulates IPv6 packets inside IPv4 using IP protocol 41 (not GRE protocol 47). These are manually configured point-to-point tunnels requiring explicit tunnel source and destination configuration. ISATAP and 6to4 are automatic tunnelling mechanisms; NHRP is used with GRE-based DMVPN, not 6in4.",
      cisco_reference: "ENCOR OCG Chapter 9 — IPv6 Tunnelling",
      expert_trap_warning: null,
      tags: ["ipv6", "tunneling", "6in4", "protocol-41"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-029",
      type: "single_choice",
      domain: "2.0",
      subdomain: "DMVPN",
      difficulty: 2,
      prompt: "In DMVPN Phase 1, how does traffic flow between two spoke routers that need to communicate?",
      options: [
        { id: "A", text: "Spokes build direct dynamic tunnels to each other using NHRP", is_correct: false },
        { id: "B", text: "All spoke-to-spoke traffic must travel through the hub router (hub-and-spoke forwarding)", is_correct: true },
        { id: "C", text: "Spokes use OSPF to find direct paths and bypass the hub", is_correct: false },
        { id: "D", text: "Phase 1 uses static GRE tunnels; spoke-to-spoke traffic is layer-switched", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DMVPN Phase 1 is a pure hub-and-spoke design. The hub uses mGRE; spokes use point-to-point GRE to the hub. All traffic between spokes must travel through the hub — there are no direct spoke-to-spoke tunnels. Phase 2 enables spoke-to-spoke dynamic tunnels. Phase 3 enables spoke-to-spoke with hub route summarisation.",
      cisco_reference: "ENCOR OCG Chapter 9 — DMVPN Phases",
      expert_trap_warning: null,
      tags: ["dmvpn", "phase1", "hub-and-spoke"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-030",
      type: "single_choice",
      domain: "2.0",
      subdomain: "Multicast",
      difficulty: 3,
      prompt: "An OSPF network uses Auto-RP for multicast RP discovery. Which two multicast group addresses are used by Auto-RP for RP announcements and RP discovery respectively?",
      options: [
        { id: "A", text: "224.0.1.39 (Cisco-RP-Announce) and 224.0.1.40 (Cisco-RP-Discovery)", is_correct: true },
        { id: "B", text: "224.0.0.2 (all routers) and 224.0.0.5 (all OSPF routers)", is_correct: false },
        { id: "C", text: "232.0.0.0/8 (SSM range) and 239.0.0.0/8 (admin-scoped)", is_correct: false },
        { id: "D", text: "224.0.0.13 (PIM routers) and 224.0.0.22 (IGMP reports)", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Cisco Auto-RP uses two reserved multicast addresses: 224.0.1.39 (Cisco-RP-Announce) — RP candidates send their announcements here; 224.0.1.40 (Cisco-RP-Discovery) — the RP Mapping Agent sends the elected RP information here; all PIM routers listen on 224.0.1.40 to learn the active RP. Sparse mode requires explicit RP configuration or Auto-RP/BSR to bootstrap RP knowledge.",
      cisco_reference: "ENCOR OCG Chapter 14 — Auto-RP",
      expert_trap_warning: null,
      tags: ["multicast", "auto-rp", "pim-sm", "rp-discovery"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // DOMAIN 3.0 — INFRASTRUCTURE (expanded)
    // =========================================================

    {
      id: "exam-exp-031",
      type: "single_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 3,
      prompt: "An OSPF router in a standard stub area receives a Type 7 LSA. What does this indicate about the area configuration?",
      options: [
        { id: "A", text: "Nothing unusual — Type 7 LSAs are used in all stub area types", is_correct: false },
        { id: "B", text: "This is impossible; a standard stub area would be misconfigured — Type 7 LSAs are only used in NSSAs", is_correct: true },
        { id: "C", text: "The router is an ASBR redistributing external routes into the stub area", is_correct: false },
        { id: "D", text: "The Type 7 LSA carries the default route injected by the ABR", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Type 7 LSAs are exclusively used in Not-So-Stubby Areas (NSSAs). They carry externally redistributed routes within the NSSA and are converted to Type 5 by the ABR at the NSSA boundary. Standard stub areas do NOT support redistribution and therefore will never have Type 7 LSAs. If a Type 7 appears in a standard stub area, the area is misconfigured as an NSSA.",
      cisco_reference: "ENCOR OCG Chapter 10 — OSPF NSSA",
      expert_trap_warning: null,
      tags: ["ospf", "type7-lsa", "nssa", "stub-area"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-032",
      type: "single_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 3,
      prompt: "What is the purpose of an OSPF virtual link, and where must it be configured?",
      options: [
        { id: "A", text: "A virtual link connects two non-contiguous Area 0 segments through a transit non-backbone area; configured on both ABRs of the transit area", is_correct: true },
        { id: "B", text: "A virtual link allows a stub area to connect to Area 0 without physical connectivity; configured on any router in the stub area", is_correct: false },
        { id: "C", text: "A virtual link provides a GRE tunnel backup path for OSPF if a physical link fails; configured globally on any OSPF router", is_correct: false },
        { id: "D", text: "A virtual link merges two separate OSPF processes into a single domain", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "An OSPF virtual link is used when a non-backbone area cannot directly connect to Area 0, or to repair a partitioned backbone. It creates a logical connection through a transit area (which must be a normal area — not stub/NSSA). Virtual links are configured on the ABRs at both ends of the transit area using 'area <transit-area> virtual-link <remote-RID>'.",
      cisco_reference: "ENCOR OCG Chapter 10 — OSPF Virtual Links",
      expert_trap_warning: null,
      tags: ["ospf", "virtual-link", "area0", "backbone"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-033",
      type: "single_choice",
      domain: "3.0",
      subdomain: "EIGRP",
      difficulty: 3,
      prompt: "An EIGRP router has lost its only successor route for 10.1.1.0/24 and has no feasible successor. What process does EIGRP initiate to find a new path?",
      options: [
        { id: "A", text: "The router immediately floods an EIGRP update to all neighbours", is_correct: false },
        { id: "B", text: "The route enters Active state and the router sends EIGRP Query messages to all neighbours", is_correct: true },
        { id: "C", text: "The route is withdrawn and a default route is used until convergence", is_correct: false },
        { id: "D", text: "The router drops the route and waits for a new unsolicited update from a neighbour", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "When a successor fails and no feasible successor exists, EIGRP places the route into Active state and sends DUAL Query messages to all neighbours asking if they have an alternative path. The router must receive EIGRP Reply messages from all queried neighbours before the route can transition back to Passive state (converged). Stuck-In-Active (SIA) occurs when a Reply is not received within the active timer (default 3 minutes).",
      cisco_reference: "ENCOR OCG Chapter 11 — EIGRP DUAL",
      expert_trap_warning: null,
      tags: ["eigrp", "dual", "active-state", "query", "sia"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-034",
      type: "single_choice",
      domain: "3.0",
      subdomain: "EIGRP",
      difficulty: 3,
      prompt: "What is the purpose of EIGRP Wide Metrics (introduced in EIGRP Named Mode) and which K-value was modified?",
      options: [
        { id: "A", text: "Wide metrics increase the metric calculation range to support high-bandwidth interfaces (>= 1 Gbps); K6 was added for jitter", is_correct: false },
        { id: "B", text: "Wide metrics scale the metric by a factor of 65536 and introduce K6 for jitter/energy; they resolve integer overflow on high-speed links (>1 Gbps)", is_correct: true },
        { id: "C", text: "Wide metrics replace bandwidth with throughput measured by IPSLA probes and add K5 for MTU", is_correct: false },
        { id: "D", text: "Wide metrics only apply to IPv6 EIGRP and have no effect on IPv4 metric calculation", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Classic EIGRP metrics overflow to 0 on links faster than ~10 Gbps because the bandwidth formula uses a fixed scale. EIGRP Wide Metrics (Named Mode) multiply the metric by 65536 to provide greater resolution on high-speed links. An additional K6 value was introduced for jitter/energy but K6 is 0 by default. Wide metrics require Named Mode and are not backwards-compatible with classic EIGRP.",
      cisco_reference: "ENCOR OCG Chapter 11 — EIGRP Named Mode Wide Metrics",
      expert_trap_warning: null,
      tags: ["eigrp", "wide-metrics", "named-mode", "k-values"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-035",
      type: "multi_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 3,
      prompt: "Which THREE BGP attributes are considered well-known mandatory and must be included in every BGP UPDATE message? (Choose three)",
      options: [
        { id: "A", text: "ORIGIN", is_correct: true },
        { id: "B", text: "AS_PATH", is_correct: true },
        { id: "C", text: "NEXT_HOP", is_correct: true },
        { id: "D", text: "LOCAL_PREF", is_correct: false },
        { id: "E", text: "MED", is_correct: false }
      ],
      correct_answer: ["A", "B", "C"],
      required_selections: 3,
      drag_items: null,
      drop_targets: null,
      explanation: "BGP attribute categories: Well-known mandatory (must be in every UPDATE): ORIGIN, AS_PATH, NEXT_HOP. Well-known discretionary (recognised by all, not always present): LOCAL_PREF, ATOMIC_AGGREGATE. Optional transitive (passed along even if not recognised): COMMUNITY, AGGREGATOR. Optional non-transitive (not passed if not recognised): MED (MULTI_EXIT_DISC), ORIGINATOR_ID, CLUSTER_LIST.",
      cisco_reference: "ENCOR OCG Chapter 12 — BGP Attributes",
      expert_trap_warning: "LOCAL_PREF is well-known discretionary, NOT mandatory. It is not included in eBGP updates (only iBGP). Many engineers assume all commonly-used attributes are mandatory.",
      tags: ["bgp", "attributes", "well-known-mandatory", "origin", "as-path", "next-hop"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-036",
      type: "single_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 3,
      prompt: "An iBGP router receives a prefix with NEXT_HOP set to an eBGP peer's IP address that is not reachable via the IGP. What happens to this route?",
      options: [
        { id: "A", text: "The route is accepted and installed with a recursive lookup failure — traffic blackholes", is_correct: false },
        { id: "B", text: "The route is marked invalid and not installed in the RIB because the NEXT_HOP is not resolvable", is_correct: true },
        { id: "C", text: "BGP automatically changes the NEXT_HOP to the iBGP peer's address", is_correct: false },
        { id: "D", text: "The route is installed but flagged as 'inaccessible' and generates a syslog warning", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "BGP requires the NEXT_HOP to be resolvable in the routing table before a route can be installed in the RIB. If the eBGP next-hop is not reachable (not advertised by IGP or no static route), the BGP route is marked as invalid (not best) and is not installed. The fix is either to advertise the eBGP interface subnet into the IGP, or to configure 'neighbor <ip> next-hop-self' on the iBGP-reflecting router.",
      cisco_reference: "ENCOR OCG Chapter 12 — BGP Next-Hop",
      expert_trap_warning: null,
      tags: ["bgp", "next-hop", "ibgp", "rib", "reachability"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-037",
      type: "single_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 3,
      prompt: "Which BGP confederation feature addresses the iBGP full-mesh scaling problem?",
      options: [
        { id: "A", text: "Confederations divide a large AS into smaller sub-ASes with eBGP between them, while appearing as a single AS to the outside", is_correct: true },
        { id: "B", text: "Confederations merge multiple eBGP ASes into a single large iBGP domain", is_correct: false },
        { id: "C", text: "Confederations allow iBGP routes to be reflected to non-client peers without a route reflector", is_correct: false },
        { id: "D", text: "Confederations reduce CPU usage by compressing BGP UPDATE messages with NLRI aggregation", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "BGP Confederations (RFC 5065) solve the iBGP full-mesh O(n²) scaling problem by splitting the AS into multiple sub-ASes (member-ASes with private AS numbers). eBGP-like peerings are used between sub-ASes but NEXT_HOP, MED, and LOCAL_PREF are preserved. The confederation appears as a single AS to external peers. Route Reflectors are the alternative scaling solution.",
      cisco_reference: "ENCOR OCG Chapter 12 — BGP Confederations",
      expert_trap_warning: null,
      tags: ["bgp", "confederation", "scaling", "ibgp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-038",
      type: "single_choice",
      domain: "3.0",
      subdomain: "STP",
      difficulty: 3,
      prompt: "In 802.1s MST (Multiple Spanning Tree), what is the concept of an 'IST' (Internal Spanning Tree)?",
      options: [
        { id: "A", text: "The IST is MST Instance 1, which carries all VLANs not mapped to other MST instances", is_correct: false },
        { id: "B", text: "The IST is MST Instance 0 — the common spanning tree for the MST region, handling interoperability with 802.1D/802.1w regions", is_correct: true },
        { id: "C", text: "The IST is an inter-switch trunk that carries BPDUs between MST regions", is_correct: false },
        { id: "D", text: "The IST is the spanning tree instance that runs per-VLAN, equivalent to PVST+", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In 802.1s MST, Instance 0 (IST — Internal Spanning Tree) is the backbone spanning tree. All VLANs not explicitly mapped to user-defined MST instances (MSTI 1–64) are automatically mapped to MSTI 0 (IST). The IST also handles interoperability between the MST region and legacy 802.1D/Rapid-PVST+ regions by presenting the entire MST region as a single virtual bridge.",
      cisco_reference: "ENCOR OCG Chapter 7 — MST",
      expert_trap_warning: null,
      tags: ["stp", "mst", "ist", "mstp", "802.1s"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-039",
      type: "single_choice",
      domain: "3.0",
      subdomain: "STP",
      difficulty: 3,
      prompt: "Loop Guard is configured on a port that is currently in the RSTP discarding (blocking) state. The port stops receiving BPDUs from the upstream switch. What does Loop Guard do?",
      options: [
        { id: "A", text: "Loop Guard places the port in a loop-inconsistent state instead of allowing it to transition to forwarding, preventing a loop", is_correct: true },
        { id: "B", text: "Loop Guard err-disables the port immediately when BPDUs stop arriving", is_correct: false },
        { id: "C", text: "Loop Guard is only effective on designated ports; it has no effect on non-designated (blocking) ports", is_correct: false },
        { id: "D", text: "Loop Guard sends a BPDU to the upstream switch to request resumption of BPDUs", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Loop Guard protects against unidirectional link failures that cause a non-designated (blocking) port to stop receiving BPDUs. Without Loop Guard, STP would age out the port and transition it to forwarding — potentially creating a loop. Loop Guard instead places the port into loop-inconsistent (blocking) state. When BPDUs resume, Loop Guard automatically recovers the port. It is recommended on root ports and alternate ports.",
      cisco_reference: "ENCOR OCG Chapter 7 — Loop Guard",
      expert_trap_warning: "Loop Guard and Root Guard are often confused. Root Guard prevents a port from BECOMING a root port (used toward switches that should never be root). Loop Guard prevents a non-root, non-designated port from incorrectly transitioning to forwarding on BPDU loss.",
      tags: ["stp", "loop-guard", "rstp", "unidirectional-link"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-040",
      type: "multi_choice",
      domain: "3.0",
      subdomain: "EtherChannel",
      difficulty: 2,
      prompt: "Which TWO statements about LACP (IEEE 802.3ad) EtherChannel are correct? (Choose two)",
      options: [
        { id: "A", text: "LACP is an open standard negotiation protocol supported by non-Cisco devices", is_correct: true },
        { id: "B", text: "LACP active mode will not form a bundle unless the peer is also in active mode", is_correct: false },
        { id: "C", text: "LACP passive mode will respond to LACP negotiations but will not initiate them", is_correct: true },
        { id: "D", text: "PAgP (Port Aggregation Protocol) is the IEEE standard equivalent to LACP", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "LACP (IEEE 802.3ad/802.1AX) is an open standard for EtherChannel negotiation, interoperable with non-Cisco switches. LACP modes: active (initiates + responds) and passive (only responds). At least one side must be active. PAgP is Cisco-proprietary, not an IEEE standard. LACP active + active = channel forms; active + passive = channel forms; passive + passive = no channel.",
      cisco_reference: "ENCOR OCG Chapter 7 — EtherChannel",
      expert_trap_warning: null,
      tags: ["etherchannel", "lacp", "pagp", "port-channel"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-041",
      type: "single_choice",
      domain: "3.0",
      subdomain: "FHRP",
      difficulty: 3,
      prompt: "VRRP (Virtual Router Redundancy Protocol) version 3 adds which capability that was not available in VRRPv2?",
      options: [
        { id: "A", text: "VRRPv3 supports preemption; VRRPv2 did not", is_correct: false },
        { id: "B", text: "VRRPv3 supports both IPv4 and IPv6; VRRPv2 only supported IPv4", is_correct: true },
        { id: "C", text: "VRRPv3 supports multiple virtual routers per interface; VRRPv2 was limited to one", is_correct: false },
        { id: "D", text: "VRRPv3 uses TCP for hello messages; VRRPv2 used IP multicast", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "VRRPv3 (RFC 5798) adds IPv6 support — VRRPv2 (RFC 3768) only supported IPv4. VRRPv3 uses 224.0.0.18 (IPv4) or FF02::12 (IPv6) for hellos. VRRPv2 already supported preemption and multiple groups. VRRPv3 also improves timer granularity (centiseconds vs whole seconds in v2). Both versions use IP multicast for hellos.",
      cisco_reference: "ENCOR OCG Chapter 6 — VRRP",
      expert_trap_warning: null,
      tags: ["vrrp", "vrrpv3", "fhrp", "ipv6"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-042",
      type: "single_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 2,
      prompt: "Which OSPF network type should be configured on a point-to-multipoint non-broadcast (PMNB) network to prevent DR/BDR election and allow OSPF neighbour statements for each peer?",
      options: [
        { id: "A", text: "broadcast", is_correct: false },
        { id: "B", text: "non-broadcast", is_correct: false },
        { id: "C", text: "point-to-multipoint non-broadcast", is_correct: true },
        { id: "D", text: "point-to-point", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "OSPF network types and their characteristics: broadcast (DR/BDR, multicast hellos), non-broadcast (DR/BDR, unicast hellos, manual neighbors), point-to-point (no DR/BDR, single neighbour), point-to-multipoint (no DR/BDR, multicast hellos, host routes for each peer), point-to-multipoint non-broadcast (no DR/BDR, unicast hellos, manual neighbor statements). PMNB is used on NBMA networks where multicast is not supported and no DR is desired.",
      cisco_reference: "ENCOR OCG Chapter 10 — OSPF Network Types",
      expert_trap_warning: null,
      tags: ["ospf", "network-type", "pmnb", "nbma", "dr-bdr"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-044",
      type: "single_choice",
      domain: "3.0",
      subdomain: "QoS",
      difficulty: 3,
      prompt: "WRED (Weighted Random Early Detection) is applied to a congested output queue. How does WRED avoid global synchronisation compared to tail-drop?",
      options: [
        { id: "A", text: "WRED drops all packets when the queue reaches 100% capacity, just like tail-drop, but notifies senders first", is_correct: false },
        { id: "B", text: "WRED randomly drops packets before the queue is full, causing individual TCP senders to reduce their window size at different times instead of all simultaneously", is_correct: true },
        { id: "C", text: "WRED uses ECN bits to signal congestion without dropping packets, eliminating all packet loss", is_correct: false },
        { id: "D", text: "WRED prioritises high-DSCP packets for dropping, ensuring low-priority traffic is removed first", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Global synchronisation occurs with tail-drop when all TCP flows simultaneously detect loss (full queue drop), reduce their window, then simultaneously ramp up again — causing oscillating bandwidth utilisation. WRED randomly drops packets from individual flows before the queue is full, staggering TCP congestion responses across different flows. DSCP-weighted WRED drops lower-DSCP packets more aggressively, while ECN can be used to signal without dropping.",
      cisco_reference: "ENCOR OCG Chapter 13 — WRED",
      expert_trap_warning: null,
      tags: ["qos", "wred", "tail-drop", "global-synchronisation", "tcp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-045",
      type: "single_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 2,
      prompt: "Which BGP community value signals to all receiving peers that the route should NOT be advertised outside the local AS?",
      options: [
        { id: "A", text: "no-export (0xFFFFFF01)", is_correct: true },
        { id: "B", text: "no-advertise (0xFFFFFF02)", is_correct: false },
        { id: "C", text: "internet (0x00000000)", is_correct: false },
        { id: "D", text: "local-AS (0xFFFFFF03)", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "BGP well-known communities: no-export (0xFFFFFF01) — route is NOT advertised to eBGP peers; it stays within the AS (or within the confederation sub-AS). no-advertise (0xFFFFFF02) — route is NOT advertised to ANY neighbour (iBGP or eBGP). local-AS (0xFFFFFF03) — route is NOT advertised outside the confederation sub-AS. internet — route can be advertised to all peers.",
      cisco_reference: "ENCOR OCG Chapter 12 — BGP Communities",
      expert_trap_warning: "no-export and no-advertise are commonly confused. no-export stays within the AS; no-advertise stays local to the receiving router only.",
      tags: ["bgp", "communities", "no-export", "no-advertise"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-046",
      type: "single_choice",
      domain: "3.0",
      subdomain: "EIGRP",
      difficulty: 2,
      prompt: "An EIGRP stub router is configured with the 'eigrp stub connected' keyword. What routes will this stub router advertise to its hub neighbours?",
      options: [
        { id: "A", text: "All routes in its routing table, including learned EIGRP routes", is_correct: false },
        { id: "B", text: "Only routes directly connected to its own interfaces", is_correct: true },
        { id: "C", text: "No routes at all — the stub keyword suppresses all advertisements", is_correct: false },
        { id: "D", text: "Only summary routes and the default route", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "EIGRP stub with 'connected' keyword advertises only the stub router's directly connected networks. EIGRP stub options: connected (directly connected routes), summary (summary routes), static (redistributed static), redistributed (all redistributed routes), receive-only (receives but advertises nothing). Importantly, hub routers do not send EIGRP queries to stub routers, significantly improving convergence.",
      cisco_reference: "ENCOR OCG Chapter 11 — EIGRP Stub",
      expert_trap_warning: null,
      tags: ["eigrp", "stub", "connected", "query"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-047",
      type: "drag_drop",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 3,
      prompt: "Match each OSPF area type to the correct statement about which LSA types are permitted inside that area.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "Normal (standard) area" },
        { id: "item2", text: "Stub area" },
        { id: "item3", text: "Totally Stubby Area (TSA)" },
        { id: "item4", text: "NSSA (Not-So-Stubby Area)" }
      ],
      drop_targets: [
        { id: "target1", label: "Permits Type 1, 2, 3, 4, 5 — all LSA types allowed; full OSPF functionality", accepts: "item1" },
        { id: "target2", label: "Permits Type 1, 2, 3 only; blocks Type 4 and 5; ABR injects default via Type 3", accepts: "item2" },
        { id: "target3", label: "Permits Type 1 and 2 only; blocks Type 3, 4, and 5; ABR injects single default Type 3", accepts: "item3" },
        { id: "target4", label: "Permits Type 1, 2, 3 (limited), and Type 7; blocks Type 4 and 5; supports redistribution via Type 7", accepts: "item4" }
      ],
      explanation: "OSPF area LSA filtering: Normal area accepts all types. Stub blocks Type 4/5 (allows Type 3). Totally Stubby blocks Type 3/4/5 except default. NSSA blocks Type 4/5 but allows Type 7 for local redistribution, and the ABR converts Type 7 to Type 5 at the boundary.",
      cisco_reference: "ENCOR OCG Chapter 10 — OSPF Area Types",
      expert_trap_warning: null,
      tags: ["ospf", "area-types", "lsa", "stub", "nssa", "totally-stubby"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-049",
      type: "single_choice",
      domain: "3.0",
      subdomain: "STP",
      difficulty: 2,
      prompt: "In Rapid PVST+, when does an edge port (PortFast) immediately transition to forwarding without going through discarding/learning states?",
      options: [
        { id: "A", text: "When the port is connected to another switch and receives a BPDU", is_correct: false },
        { id: "B", text: "When the port is connected to an end device (PC, server) that does not send BPDUs", is_correct: true },
        { id: "C", text: "When the port receives an inferior BPDU and the root bridge changes", is_correct: false },
        { id: "D", text: "When BPDU Guard detects the port has been err-disabled and re-enables it", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "PortFast/Edge port immediately transitions to forwarding on link-up because it is connected to an end device that cannot participate in STP (no BPDU generation). This eliminates the 30-second learning/listening delay (802.1D) or the discarding/learning delay (802.1w RSTP). If a BPDU is received on a PortFast port, it immediately loses its edge port status and participates in normal STP.",
      cisco_reference: "ENCOR OCG Chapter 7 — PortFast and RSTP Edge Ports",
      expert_trap_warning: null,
      tags: ["stp", "portfast", "rstp", "edge-port"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-050",
      type: "single_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 3,
      prompt: "Router R1 is redistributing EIGRP routes into OSPF. What LSA type does R1 generate, and what OSPF router role does R1 assume?",
      options: [
        { id: "A", text: "Type 3 Summary LSA; R1 becomes an ABR", is_correct: false },
        { id: "B", text: "Type 5 AS External LSA; R1 becomes an ASBR", is_correct: true },
        { id: "C", text: "Type 7 NSSA External LSA; R1 becomes an NSSA router", is_correct: false },
        { id: "D", text: "Type 4 ASBR Summary LSA; R1 becomes a DR", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "When a router redistributes external routing information into OSPF, it becomes an ASBR (Autonomous System Boundary Router). The ASBR generates Type 5 AS External LSAs for redistributed routes (unless in an NSSA, where it generates Type 7). Type 4 LSAs are generated by ABRs to advertise the ASBR's location to other areas. Type 3 is for inter-area summary routes.",
      cisco_reference: "ENCOR OCG Chapter 10 — OSPF ASBR",
      expert_trap_warning: null,
      tags: ["ospf", "asbr", "type5-lsa", "redistribution"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-051",
      type: "multi_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 3,
      prompt: "Which TWO BGP attributes can be used to influence inbound traffic FROM external autonomous systems? (Choose two)",
      options: [
        { id: "A", text: "MED (Multi-Exit Discriminator) — advertised to neighbouring AS to suggest preferred ingress", is_correct: true },
        { id: "B", text: "AS_PATH prepending — makes the route look longer/less preferred to external peers", is_correct: true },
        { id: "C", text: "LOCAL_PREF — advertised to eBGP peers to influence their path selection", is_correct: false },
        { id: "D", text: "WEIGHT — set on eBGP peer sessions to influence inbound routing decisions", is_correct: false }
      ],
      correct_answer: ["A", "B"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "To influence traffic coming INTO your AS from external ASes: MED (sent to neighbouring AS to suggest preferred entry point — lower MED wins) and AS_PATH prepending (artificially lengthening your AS_PATH makes your routes look less preferred to external peers). LOCAL_PREF and WEIGHT are not propagated to eBGP peers and only affect routing decisions within or from your own AS.",
      cisco_reference: "ENCOR OCG Chapter 12 — BGP Traffic Engineering",
      expert_trap_warning: null,
      tags: ["bgp", "inbound-traffic", "med", "as-path-prepend"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-053",
      type: "single_choice",
      domain: "3.0",
      subdomain: "FHRP",
      difficulty: 3,
      prompt: "In HSRP, what is the function of the 'standby track' command and how does it affect HSRP priority?",
      options: [
        { id: "A", text: "It monitors a track object; if the tracked object fails, the router's HSRP priority decreases by a configured decrement, potentially triggering preemption", is_correct: true },
        { id: "B", text: "It forces the standby router to become active by increasing its priority above the current active router", is_correct: false },
        { id: "C", text: "It tracks BFD sessions; if BFD fails, HSRP immediately preempts the active router", is_correct: false },
        { id: "D", text: "It monitors an interface; if it fails, HSRP shuts down the standby router's virtual IP", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The HSRP 'standby track' command monitors a tracked object (interface, IP SLA, etc.). If the tracked object transitions to down, the router's HSRP priority is reduced by the configured decrement (default 10). If the resulting priority falls below the standby router's priority AND preemption is enabled, the standby router takes over as active. This enables automatic failover when an uplink — not just the HSRP interface — fails.",
      cisco_reference: "ENCOR OCG Chapter 6 — HSRP Object Tracking",
      expert_trap_warning: null,
      tags: ["hsrp", "tracking", "object-tracking", "preemption"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-054",
      type: "single_choice",
      domain: "3.0",
      subdomain: "EIGRP",
      difficulty: 2,
      prompt: "By default, how does EIGRP behave when it detects unequal-cost paths to the same destination?",
      options: [
        { id: "A", text: "EIGRP automatically load-balances across all unequal paths without any configuration", is_correct: false },
        { id: "B", text: "EIGRP installs only equal-cost paths (successor routes) by default; unequal-cost load balancing requires 'variance' configuration", is_correct: true },
        { id: "C", text: "EIGRP uses unequal-cost load balancing by default but limits to 4 paths", is_correct: false },
        { id: "D", text: "EIGRP ignores unequal paths entirely — only the single best path is considered", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "By default, EIGRP performs equal-cost load balancing (up to 4 paths, max 16 with 'maximum-paths'). Unequal-cost load balancing requires the 'variance <multiplier>' command. A feasible successor with FD <= (variance × successor FD) and meeting the Feasibility Condition is eligible for unequal-cost load balancing. Traffic is distributed in proportion to the path metrics.",
      cisco_reference: "ENCOR OCG Chapter 11 — EIGRP Variance",
      expert_trap_warning: null,
      tags: ["eigrp", "variance", "unequal-cost", "load-balancing"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-055",
      type: "single_choice",
      domain: "3.0",
      subdomain: "IP Services",
      difficulty: 2,
      prompt: "Which IP SLA operation type requires a Cisco IP SLA Responder to be configured on the target device?",
      options: [
        { id: "A", text: "ICMP Echo", is_correct: false },
        { id: "B", text: "HTTP Get", is_correct: false },
        { id: "C", text: "UDP Jitter", is_correct: true },
        { id: "D", text: "TCP Connect", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "UDP Jitter operations measure one-way delay, jitter, and packet loss by sending a stream of UDP packets. Because the measurements are one-way, the target must run the IP SLA Responder (IOS command: 'ip sla responder') to timestamp packets accurately. ICMP Echo, HTTP Get, and TCP Connect do not require a Responder because the target simply replies using standard protocol mechanisms.",
      cisco_reference: "ENCOR OCG Chapter 18 — IP SLA Responder",
      expert_trap_warning: null,
      tags: ["ip-sla", "udp-jitter", "responder", "one-way-delay"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // DOMAIN 4.0 — NETWORK ASSURANCE (expanded)
    // =========================================================

    {
      id: "exam-exp-056",
      type: "single_choice",
      domain: "4.0",
      subdomain: "SNMP",
      difficulty: 3,
      prompt: "An SNMP v3 user is configured with authPriv security level. Which combination of security services does this provide?",
      options: [
        { id: "A", text: "No authentication, no encryption", is_correct: false },
        { id: "B", text: "Authentication only (HMAC-MD5 or HMAC-SHA)", is_correct: false },
        { id: "C", text: "Authentication AND encryption (AES/DES) for both authentication and privacy", is_correct: true },
        { id: "D", text: "Encryption only without authentication", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "SNMPv3 USM security levels: noAuthNoPriv (no authentication, no encryption — community-string equivalent), authNoPriv (HMAC-MD5 or HMAC-SHA authentication, no encryption), authPriv (authentication + DES/3DES/AES encryption). authPriv provides both message integrity verification and confidentiality — the most secure SNMPv3 option.",
      cisco_reference: "ENCOR OCG Chapter 18 — SNMPv3 Security Levels",
      expert_trap_warning: null,
      tags: ["snmp", "snmpv3", "authpriv", "usm", "encryption"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-057",
      type: "single_choice",
      domain: "4.0",
      subdomain: "NetFlow",
      difficulty: 3,
      prompt: "In Flexible NetFlow (FNF), what is the difference between a 'flow record', 'flow exporter', and 'flow monitor'?",
      options: [
        { id: "A", text: "Flow record defines what to collect; flow exporter defines where to send the data; flow monitor binds the record and exporter to an interface", is_correct: true },
        { id: "B", text: "Flow monitor defines what to collect; flow record binds the policy to an interface; flow exporter processes the data", is_correct: false },
        { id: "C", text: "Flow exporter defines what fields to match; flow record sends data to the collector; flow monitor enables the collection", is_correct: false },
        { id: "D", text: "All three terms describe the same function with different configuration hierarchy levels", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Flexible NetFlow architecture: Flow Record — defines the key fields (match = flow identity) and non-key fields (collect = what to measure). Flow Exporter — defines the NetFlow collector IP, UDP port (typically 2055 or 9995), and export version. Flow Monitor — ties a flow record to a flow exporter and is applied to interfaces with 'ip flow monitor <name> input/output'.",
      cisco_reference: "ENCOR OCG Chapter 18 — Flexible NetFlow",
      expert_trap_warning: null,
      tags: ["netflow", "flexible-netflow", "flow-record", "flow-monitor", "flow-exporter"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-058",
      type: "single_choice",
      domain: "4.0",
      subdomain: "Streaming Telemetry",
      difficulty: 3,
      prompt: "In model-driven telemetry (MDT), what is the difference between a 'dial-in' and 'dial-out' subscription?",
      options: [
        { id: "A", text: "Dial-in: the collector initiates a gRPC/gNMI connection TO the device and subscribes. Dial-out: the device initiates a connection TO the collector and pushes data", is_correct: true },
        { id: "B", text: "Dial-in: the device pushes data to the collector via SNMP traps. Dial-out: the collector polls the device via NETCONF", is_correct: false },
        { id: "C", text: "Dial-in uses gRPC; dial-out uses NETCONF — both can be configured with the same subscription", is_correct: false },
        { id: "D", text: "Dial-in and dial-out refer to inbound and outbound traffic measurements, not connection direction", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "MDT subscription types: Dial-in (dynamic) — the collector establishes a gRPC/gNMI session to the network device and sends a subscription request. Dial-out (configured/static) — the network device is configured with the collector address and proactively establishes the outbound connection to push telemetry data. Dial-out is more scalable for large deployments since the collector doesn't need to manage individual device connections.",
      cisco_reference: "ENCOR OCG Chapter 19 — Model-Driven Telemetry",
      expert_trap_warning: null,
      tags: ["telemetry", "mdt", "dial-in", "dial-out", "grpc", "gnmi"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-059",
      type: "multi_choice",
      domain: "4.0",
      subdomain: "SPAN / RSPAN",
      difficulty: 2,
      prompt: "Which TWO statements about ERSPAN (Encapsulated Remote SPAN) are correct compared to RSPAN? (Choose two)",
      options: [
        { id: "A", text: "ERSPAN encapsulates mirrored traffic in GRE, allowing it to traverse Layer 3 networks to reach the destination analyser", is_correct: true },
        { id: "B", text: "ERSPAN is limited to a single switch; RSPAN works across multiple switches", is_correct: false },
        { id: "C", text: "ERSPAN requires a dedicated VLAN to transport mirrored frames between switches", is_correct: false },
        { id: "D", text: "ERSPAN can mirror traffic to a destination that is multiple router hops away from the source switch", is_correct: true }
      ],
      correct_answer: ["A", "D"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "ERSPAN (Encapsulated Remote SPAN) uses GRE encapsulation to carry mirrored traffic across Layer 3 (routed) networks to a remote destination. This allows the analyser to be anywhere reachable via IP routing — unlike RSPAN which requires a dedicated VLAN in the same switched domain. ERSPAN is the most flexible SPAN variant for enterprise deployments with distributed monitoring infrastructure.",
      cisco_reference: "ENCOR OCG Chapter 18 — ERSPAN",
      expert_trap_warning: null,
      tags: ["erspan", "span", "rspan", "gre", "monitoring"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-060",
      type: "single_choice",
      domain: "4.0",
      subdomain: "Syslog",
      difficulty: 2,
      prompt: "An engineer configures 'logging trap 4' on a Cisco router. Which syslog severity levels will be sent to the remote syslog server?",
      options: [
        { id: "A", text: "Only level 4 (Warning) messages", is_correct: false },
        { id: "B", text: "Levels 0 through 4 (Emergency, Alert, Critical, Error, Warning)", is_correct: true },
        { id: "C", text: "Levels 4 through 7 (Warning, Notice, Informational, Debug)", is_correct: false },
        { id: "D", text: "All levels 0 through 7 regardless of the trap level setting", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "'logging trap <level>' sends all messages from level 0 (most severe) UP TO AND INCLUDING the specified level. 'logging trap 4' sends levels 0, 1, 2, 3, and 4 (Emergency through Warning). It does NOT send levels 5 (Notice), 6 (Informational), and 7 (Debug). To include informational: 'logging trap informational' or 'logging trap 6'.",
      cisco_reference: "ENCOR OCG Chapter 18 — Syslog Configuration",
      expert_trap_warning: "Candidates often interpret 'logging trap 4' as sending ONLY level 4 messages. It sends ALL messages at level 4 and LOWER (i.e., levels 0–4, which are the MORE severe messages).",
      tags: ["syslog", "logging", "severity-levels", "trap"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-061",
      type: "single_choice",
      domain: "4.0",
      subdomain: "NETCONF",
      difficulty: 3,
      prompt: "When using NETCONF to retrieve operational data (state data) from a Cisco IOS-XE device, which NETCONF operation and datastore should be used?",
      options: [
        { id: "A", text: "<get-config> operation targeting the 'running' datastore", is_correct: false },
        { id: "B", text: "<get> operation with a filter — retrieves both configuration and operational state data", is_correct: true },
        { id: "C", text: "<get-data> operation targeting the 'operational' datastore (RFC 8526)", is_tml: false, is_correct: false },
        { id: "D", text: "<get-config> operation targeting the 'candidate' datastore", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "NETCONF operations: <get-config> retrieves ONLY configuration data from a specified datastore (running, startup, candidate). <get> retrieves BOTH configuration data AND operational state data (interface statistics, routing tables, etc.). RFC 8526 introduces <get-data> for NMDA-compliant implementations targeting an 'operational' datastore, but the standard NETCONF operation for operational data remains <get>.",
      cisco_reference: "ENCOR OCG Chapter 19 — NETCONF Operations",
      expert_trap_warning: null,
      tags: ["netconf", "get", "get-config", "operational-data", "datastore"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-062",
      type: "single_choice",
      domain: "4.0",
      subdomain: "DNA Center",
      difficulty: 2,
      prompt: "DNA Center Assurance uses which data sources to build its 360-degree network health view? (Choose the MOST complete answer)",
      options: [
        { id: "A", text: "SNMP polling only", is_correct: false },
        { id: "B", text: "Syslog events only", is_correct: false },
        { id: "C", text: "Streaming telemetry, SNMP, syslog, NETCONF, and client probes (ICMP/DNS/DHCP synthetic tests)", is_correct: true },
        { id: "D", text: "NetFlow data only, processed by the DNA Center analytics engine", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DNA Center Assurance collects data from multiple sources: streaming telemetry (MDT via gRPC for real-time metrics), SNMP (for device health, MIBs), syslog (events and alerts), NETCONF/RESTCONF (structured configuration and state retrieval), and client experience probes (synthetic tests simulating DHCP, DNS, and connectivity). This multi-source approach enables comprehensive network health scoring and AI-driven root cause analysis.",
      cisco_reference: "ENCOR OCG Chapter 19 — DNA Center Assurance Architecture",
      expert_trap_warning: null,
      tags: ["dna-center", "assurance", "telemetry", "snmp", "syslog"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-063",
      type: "single_choice",
      domain: "4.0",
      subdomain: "IP SLA",
      difficulty: 2,
      prompt: "Which command on a Cisco router configures an IP SLA operation to run continuously starting immediately and schedule the first measurement?",
      options: [
        { id: "A", text: "ip sla schedule <n> start-time now life forever", is_correct: true },
        { id: "B", text: "ip sla start <n> continuous", is_correct: false },
        { id: "C", text: "ip sla <n> enable schedule life perpetual", is_correct: false },
        { id: "D", text: "ip sla schedule <n> recurring every 60", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "IP SLA operations must be scheduled to run. The command 'ip sla schedule <n> start-time now life forever' starts the operation immediately (now) and keeps it running indefinitely (life forever). Without scheduling, the IP SLA operation is configured but never executes. 'recurring' re-runs daily at the same time but stops between runs. 'life forever' with 'start-time now' is the standard method for always-on monitoring.",
      cisco_reference: "ENCOR OCG Chapter 18 — IP SLA Scheduling",
      expert_trap_warning: null,
      tags: ["ip-sla", "schedule", "configuration", "ios-xe"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-064",
      type: "single_choice",
      domain: "4.0",
      subdomain: "Streaming Telemetry",
      difficulty: 3,
      prompt: "Which gNMI (gRPC Network Management Interface) RPC is used to subscribe to a stream of periodic updates from a network device for a specific YANG path?",
      options: [
        { id: "A", text: "gNMI Get", is_correct: false },
        { id: "B", text: "gNMI Set", is_correct: false },
        { id: "C", text: "gNMI Subscribe", is_correct: true },
        { id: "D", text: "gNMI Capabilities", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "gNMI RPCs: Get (retrieves current state of a YANG path — one-time pull), Set (modifies configuration), Subscribe (establishes a streaming subscription for periodic or on-change updates — the core telemetry operation), Capabilities (discovers supported YANG models and gNMI version). Subscribe supports SAMPLE (periodic), ON_CHANGE, and TARGET_DEFINED modes.",
      cisco_reference: "ENCOR OCG Chapter 19 — gNMI",
      expert_trap_warning: null,
      tags: ["gnmi", "grpc", "subscribe", "telemetry", "yang"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-065",
      type: "multi_choice",
      domain: "4.0",
      subdomain: "SNMP",
      difficulty: 2,
      prompt: "Which TWO SNMP operations are initiated by the managed device (agent) rather than the NMS (manager)? (Choose two)",
      options: [
        { id: "A", text: "SNMP Trap (SNMPv1/v2c)", is_correct: true },
        { id: "B", text: "SNMP Inform (SNMPv2c/v3)", is_correct: true },
        { id: "C", text: "SNMP Get", is_correct: false },
        { id: "D", text: "SNMP GetBulk", is_correct: false }
      ],
      correct_answer: ["A", "B"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "SNMP Traps and Informs are unsolicited notifications sent FROM the agent (network device) TO the NMS when events occur. Traps are fire-and-forget (no acknowledgement). Informs require an acknowledgement from the NMS — if not acknowledged within a timeout, the agent retransmits. Get, GetNext, GetBulk, and Set are all NMS-initiated operations.",
      cisco_reference: "ENCOR OCG Chapter 18 — SNMP Operations",
      expert_trap_warning: null,
      tags: ["snmp", "trap", "inform", "agent", "nms"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-066",
      type: "single_choice",
      domain: "4.0",
      subdomain: "NetFlow",
      difficulty: 2,
      prompt: "What is the default NetFlow flow timeout for inactive flows on Cisco IOS-XE, and what triggers an active timeout?",
      options: [
        { id: "A", text: "Inactive timeout: 15 seconds (no new packets in 15s); Active timeout: 30 minutes (long-duration flows)", is_correct: false },
        { id: "B", text: "Inactive timeout: 15 seconds; Active timeout: 1 minute (forces export of ongoing long flows)", is_correct: true },
        { id: "C", text: "Inactive timeout: 30 seconds; Active timeout: 10 minutes", is_correct: false },
        { id: "D", text: "All flows are exported only when the flow table is full — there is no timer-based export", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "NetFlow default timeouts: Inactive timeout (15 seconds) — a flow record is exported when no packets are seen for 15 seconds; Active timeout (1 minute / 60 seconds) — a long-running flow is force-exported every minute even if still active, preventing stale flow records. Both are configurable under the flow monitor. These defaults can be tuned to balance flow granularity versus export volume.",
      cisco_reference: "ENCOR OCG Chapter 18 — NetFlow Timers",
      expert_trap_warning: null,
      tags: ["netflow", "flow-timeout", "inactive-timeout", "active-timeout"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-067",
      type: "single_choice",
      domain: "4.0",
      subdomain: "IP SLA",
      difficulty: 3,
      prompt: "Which IP SLA operation generates synthetic traffic that most accurately simulates the quality experienced by a VoIP codec (e.g., G.711)?",
      options: [
        { id: "A", text: "ICMP Path Jitter — traces each hop and measures jitter at every point", is_correct: false },
        { id: "B", text: "UDP Jitter with 'codec g711ulaw' option — mimics the packet size and send rate of the codec", is_correct: true },
        { id: "C", text: "RTP Monitor — monitors live RTP streams passively without generating synthetic packets", is_correct: false },
        { id: "D", text: "TCP Connect with payload matching G.711 frame size", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The IP SLA UDP Jitter operation supports 'codec' sub-types (g711ulaw, g711alaw, g729a, etc.) that configure the probe to generate packets at the exact packet rate and payload size of the specified voice codec. This provides a VoQ (Voice Quality) score (MOS-CQ, ICPIF) that directly reflects what the codec would experience. The IP SLA Responder must be configured on the far-end router.",
      cisco_reference: "ENCOR OCG Chapter 18 — IP SLA Voice Operations",
      expert_trap_warning: null,
      tags: ["ip-sla", "udp-jitter", "codec", "voip", "mos"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // DOMAIN 5.0 — SECURITY (expanded)
    // =========================================================

    {
      id: "exam-exp-068",
      type: "multi_choice",
      domain: "5.0",
      subdomain: "AAA",
      difficulty: 3,
      prompt: "Which THREE are key differences between RADIUS and TACACS+ that are relevant to the ENCOR exam? (Choose three)",
      options: [
        { id: "A", text: "TACACS+ encrypts the entire packet body; RADIUS encrypts only the password field", is_correct: true },
        { id: "B", text: "RADIUS uses UDP (1812 auth, 1813 accounting); TACACS+ uses TCP port 49", is_correct: true },
        { id: "C", text: "TACACS+ separates authentication, authorisation, and accounting; RADIUS combines auth and authz", is_correct: true },
        { id: "D", text: "RADIUS supports command-level authorisation for device management; TACACS+ does not", is_correct: false }
      ],
      correct_answer: ["A", "B", "C"],
      required_selections: 3,
      drag_items: null,
      drop_targets: null,
      explanation: "Key RADIUS vs TACACS+ differences: (1) TACACS+ encrypts entire payload; RADIUS only encrypts password. (2) RADIUS uses UDP 1812/1813; TACACS+ uses TCP 49. (3) TACACS+ separates AAA into independent transactions; RADIUS combines authentication and authorisation. TACACS+ is preferred for network device administration (command authorisation); RADIUS is preferred for network access (802.1X, VPN). TACACS+ — not RADIUS — supports command-level authorisation.",
      cisco_reference: "ENCOR OCG Chapter 20 — RADIUS vs TACACS+",
      expert_trap_warning: null,
      tags: ["aaa", "tacacs+", "radius", "comparison"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-069",
      type: "single_choice",
      domain: "5.0",
      subdomain: "802.1X",
      difficulty: 3,
      prompt: "A switch port is configured for 802.1X with MAB (MAC Authentication Bypass) as a fallback. A device without an 802.1X supplicant connects. What happens during the authentication process?",
      options: [
        { id: "A", text: "The port is immediately err-disabled because no EAP response is received", is_correct: false },
        { id: "B", text: "The switch sends 802.1X EAPOL identity requests; after no response, the switch sends the device MAC as RADIUS username/password for MAB", is_correct: true },
        { id: "C", text: "The switch bypasses 802.1X entirely and places the device in the guest VLAN immediately", is_correct: false },
        { id: "D", text: "MAB and 802.1X run simultaneously; whichever succeeds first grants access", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "With 802.1X + MAB: The switch first attempts 802.1X by sending EAPOL START and EAPOL Request Identity messages. If no EAP response is received (after 3 retransmissions by default), the switch falls back to MAB. In MAB, the switch sends a RADIUS Access-Request using the device's MAC address as both the username and password. RADIUS (ISE) then looks up the MAC to grant or deny access and optionally assign a VLAN.",
      cisco_reference: "ENCOR OCG Chapter 20 — 802.1X MAB",
      expert_trap_warning: null,
      tags: ["802.1x", "mab", "radius", "eap", "fallback"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-070",
      type: "single_choice",
      domain: "5.0",
      subdomain: "802.1X",
      difficulty: 2,
      prompt: "What is the purpose of the 802.1X Guest VLAN on a switch port?",
      options: [
        { id: "A", text: "It provides limited network access for devices that fail authentication (RADIUS rejects the credential)", is_correct: false },
        { id: "B", text: "It provides limited access for devices that do not respond to 802.1X EAPOL messages (no supplicant)", is_correct: true },
        { id: "C", text: "It is the VLAN assigned to phones identified via CDP before 802.1X completes", is_correct: false },
        { id: "D", text: "It is the default VLAN before any authentication occurs and is replaced by the authorised VLAN after success", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Guest VLAN is triggered when a device does NOT respond to 802.1X EAPOL messages (no 802.1X supplicant). The port moves to the Guest VLAN to provide limited/internet-only access. Auth-Fail VLAN is different — it is assigned when a device HAS a supplicant but RADIUS authentication fails (wrong credentials). Understanding this distinction is critical for the ENCOR exam.",
      cisco_reference: "ENCOR OCG Chapter 20 — 802.1X VLANs",
      expert_trap_warning: "Guest VLAN (no supplicant/no response) and Auth-Fail VLAN (authentication failure/RADIUS reject) are distinct features. Confusing them is a frequent exam error.",
      tags: ["802.1x", "guest-vlan", "auth-fail-vlan", "supplicant"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-071",
      type: "single_choice",
      domain: "5.0",
      subdomain: "DHCP Security",
      difficulty: 2,
      prompt: "Dynamic ARP Inspection (DAI) relies on which feature for its ARP validation database?",
      options: [
        { id: "A", text: "The MAC address table (CAM table) on the switch", is_correct: false },
        { id: "B", text: "The DHCP Snooping Binding Table", is_correct: true },
        { id: "C", text: "The ARP cache of the default gateway router", is_correct: false },
        { id: "D", text: "An externally configured static ARP table pushed via SNMP", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DAI (Dynamic ARP Inspection) validates ARP packets on untrusted ports by checking the IP-to-MAC binding against the DHCP Snooping Binding Table (populated by DHCP Snooping when clients receive IP addresses via DHCP). If an ARP packet's IP-MAC binding doesn't match the snooping table, it is dropped as a potential ARP spoofing attack. For static IP clients, static ARP ACLs must be configured manually.",
      cisco_reference: "ENCOR OCG Chapter 21 — DAI and DHCP Snooping",
      expert_trap_warning: null,
      tags: ["dai", "dhcp-snooping", "arp-inspection", "layer2-security"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-072",
      type: "single_choice",
      domain: "5.0",
      subdomain: "MACsec",
      difficulty: 3,
      prompt: "What does MACsec (IEEE 802.1AE) provide that IPsec does not, and at which layer does it operate?",
      options: [
        { id: "A", text: "MACsec operates at Layer 3 and encrypts IP payloads including routing protocol updates", is_correct: false },
        { id: "B", text: "MACsec operates at Layer 2 and encrypts Ethernet frames hop-by-hop, including all Layer 2 headers between switches", is_correct: true },
        { id: "C", text: "MACsec operates at Layer 2 and provides end-to-end encryption across multiple WAN hops", is_correct: false },
        { id: "D", text: "MACsec operates at Layer 4 and encrypts TCP/UDP payloads without protocol awareness", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "MACsec (IEEE 802.1AE) provides hop-by-hop Layer 2 encryption between directly connected devices (switch-to-switch or switch-to-endpoint). It encrypts Ethernet frames on a per-link basis using AES-GCM (128 or 256-bit). Because it operates at Layer 2, it can encrypt ALL traffic on the wire including non-IP protocols. IPsec operates at Layer 3 and provides end-to-end encryption between IP endpoints. MACsec requires MKA (MACsec Key Agreement) for key exchange.",
      cisco_reference: "ENCOR OCG Chapter 20 — MACsec",
      expert_trap_warning: null,
      tags: ["macsec", "802.1ae", "layer2", "encryption"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-073",
      type: "single_choice",
      domain: "5.0",
      subdomain: "TrustSec",
      difficulty: 3,
      prompt: "In Cisco TrustSec, which method is used to propagate SGT (Scalable Group Tag) values across network devices that do not natively understand SGT inline tagging?",
      options: [
        { id: "A", text: "SGT values are distributed exclusively via 802.1Q VLAN tagging in the Ethernet header", is_correct: false },
        { id: "B", text: "SXP (SGT Exchange Protocol) is used to map IP addresses to SGT values across non-TrustSec-capable devices", is_correct: true },
        { id: "C", text: "SGTs are embedded in DSCP bits of the IP header for L3 propagation", is_correct: false },
        { id: "D", text: "RADIUS assigns SGT values which are stored in the DHCP snooping table", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "SXP (SGT Exchange Protocol, TCP port 64999) is a Cisco protocol that distributes IP-to-SGT bindings between network devices over TCP. It is used when inline SGT tagging (Cisco Meta-Data / CMD) is not supported by a device. SXP speaker-listener relationships allow SGT information to traverse non-TrustSec-capable infrastructure. Inline tagging (CMD) carries the SGT in the Ethernet frame itself on TrustSec-capable links.",
      cisco_reference: "ENCOR OCG Chapter 20 — TrustSec SXP",
      expert_trap_warning: null,
      tags: ["trustsec", "sgt", "sxp", "sgacl"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-076",
      type: "single_choice",
      domain: "5.0",
      subdomain: "ACL",
      difficulty: 3,
      prompt: "An IPv6 access list is applied to an interface. Which statement about implicit entries at the end of an IPv6 ACL in Cisco IOS-XE is correct?",
      options: [
        { id: "A", text: "IPv6 ACLs have the same implicit deny any at the end as IPv4 ACLs", is_correct: false },
        { id: "B", text: "IPv6 ACLs have two implicit entries: 'permit icmp any any nd-na' and 'permit icmp any any nd-ns' followed by 'deny ipv6 any any'", is_correct: true },
        { id: "C", text: "IPv6 ACLs have no implicit deny — all unmatched traffic is permitted by default", is_correct: false },
        { id: "D", text: "IPv6 ACLs implicitly permit all ICMPv6 traffic and deny all other unmatched traffic", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Unlike IPv4 ACLs (single implicit deny), IPv6 ACLs on Cisco IOS-XE include implicit permits for Neighbor Discovery Protocol (NDP) messages — nd-ns (Neighbor Solicitation, ICMPv6 type 135) and nd-na (Neighbor Advertisement, ICMPv6 type 136) — before the final implicit deny. Without these implicit permits, NDP would be blocked, breaking IPv6 neighbour resolution. These implicit entries appear with sequence numbers 0.",
      cisco_reference: "ENCOR OCG Chapter 21 — IPv6 ACLs",
      expert_trap_warning: "Engineers who apply IPv4 ACL knowledge to IPv6 forget the implicit NDP permits. Blocking NDP breaks IPv6 address resolution — this is a common misconfiguration and a known exam trap.",
      tags: ["acl", "ipv6", "ndp", "implicit-deny", "icmpv6"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-077",
      type: "single_choice",
      domain: "5.0",
      subdomain: "CoPP",
      difficulty: 3,
      prompt: "CoPP is configured with a class matching OSPF traffic and a 'police rate 100000 bps' action with 'exceed-action drop'. What happens to OSPF hello packets that exceed the 100 Kbps rate?",
      options: [
        { id: "A", text: "Excess OSPF hellos are delayed (shaped) to fit the 100 Kbps rate", is_correct: false },
        { id: "B", text: "Excess OSPF hellos are dropped, which can cause OSPF neighbour adjacencies to fail", is_correct: true },
        { id: "C", text: "Excess OSPF hellos are marked down (DSCP CS0) and forwarded with best-effort priority", is_correct: false },
        { id: "D", text: "Excess OSPF hellos are redirected to the management plane for logging without being dropped", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "CoPP polices traffic destined to the router CPU. If OSPF hello packets exceed the policed rate, they are dropped. Dropped OSPF hellos can cause hello/dead timer expiry, leading to adjacency drops and routing instability. CoPP policies should be sized carefully with sufficient burst allowance for routing protocol traffic. The 'exceed-action transmit' option (with re-marking) or a higher rate limit is safer for routing protocol classes.",
      cisco_reference: "ENCOR OCG Chapter 20 — CoPP Design Considerations",
      expert_trap_warning: "A poorly tuned CoPP policy that drops routing protocol traffic is worse than no CoPP — it can cause network-wide routing failures. CoPP should protect the CPU but must allow legitimate control-plane traffic through at adequate rates.",
      tags: ["copp", "ospf", "policing", "control-plane"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-078",
      type: "multi_choice",
      domain: "5.0",
      subdomain: "DHCP Security",
      difficulty: 2,
      prompt: "Which TWO features work together to prevent IP address spoofing on untrusted switch ports? (Choose two)",
      options: [
        { id: "A", text: "DHCP Snooping — builds the IP-MAC-port binding table", is_correct: true },
        { id: "B", text: "IP Source Guard — enforces IP-MAC bindings on untrusted ports using the DHCP snooping table", is_correct: true },
        { id: "C", text: "Port Security — limits the number of MAC addresses but does not validate IP-MAC bindings", is_correct: false },
        { id: "D", text: "DAI (Dynamic ARP Inspection) — validates ARP but does not filter IP packet source addresses", is_correct: false }
      ],
      correct_answer: ["A", "B"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "DHCP Snooping builds the binding table (IP address, MAC address, port, VLAN) by monitoring DHCP exchanges. IP Source Guard uses this binding table to create per-port ACLs that permit only the IP-MAC pair assigned via DHCP. If a device sends a packet with a spoofed IP address, IP Source Guard drops it. Port Security and DAI protect against MAC flooding and ARP spoofing respectively — not IP source spoofing.",
      cisco_reference: "ENCOR OCG Chapter 21 — IP Source Guard",
      expert_trap_warning: null,
      tags: ["ip-source-guard", "dhcp-snooping", "layer2-security", "antispoofing"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-079",
      type: "single_choice",
      domain: "5.0",
      subdomain: "uRPF",
      difficulty: 2,
      prompt: "Which uRPF mode is appropriate for an upstream provider edge (PE) router with asymmetric routing where the return path may not match the arrival interface?",
      options: [
        { id: "A", text: "uRPF strict mode — validates the arrival interface matches the FIB return path", is_correct: false },
        { id: "B", text: "uRPF loose mode — only validates that the source IP exists anywhere in the routing table", is_correct: true },
        { id: "C", text: "uRPF is not recommended in asymmetric routing — disable it entirely", is_correct: false },
        { id: "D", text: "uRPF VRF mode — applies RPF checks per VRF instance only", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "uRPF loose mode performs a simple check: does the source IP address exist in the routing table (as ANY route)? If not, drop the packet. It does NOT check whether the return path uses the same interface. Loose mode is appropriate for asymmetric routing environments (multi-homed networks, ISP environments) where strict mode would incorrectly drop legitimate traffic. Strict mode is used on single-homed customer-facing interfaces.",
      cisco_reference: "ENCOR OCG Chapter 21 — uRPF Modes",
      expert_trap_warning: null,
      tags: ["urpf", "loose-mode", "asymmetric-routing", "antispoofing"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-080",
      type: "single_choice",
      domain: "5.0",
      subdomain: "ACL",
      difficulty: 3,
      prompt: "A named extended ACL contains the sequence: 10 permit tcp 10.0.0.0/8 any eq 443. An engineer wants to insert a new rule to deny traffic from 10.1.1.0/24 before this permit. What is the most efficient method in IOS-XE?",
      options: [
        { id: "A", text: "Delete the ACL and recreate it with the deny statement first", is_correct: false },
        { id: "B", text: "Add the deny statement with sequence number 5 — IOS-XE supports inserting entries by sequence number in named ACLs", is_correct: true },
        { id: "C", text: "Use 'ip access-list extended <name>' and type 'deny tcp 10.1.1.0...' — it automatically inserts before seq 10", is_correct: false },
        { id: "D", text: "Use 'ip access-list resequence' to renumber all entries, then add the deny at the top", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Named ACLs in IOS-XE support sequence numbers for individual entries. To insert an entry before sequence 10, enter the ACL configuration mode ('ip access-list extended <name>') and add the rule with a lower sequence number (e.g., 'seq 5 deny tcp 10.1.1.0 0.0.0.255 any eq 443'). The ACL is processed in sequence order, so seq 5 is evaluated before seq 10. No need to delete and recreate the entire ACL.",
      cisco_reference: "ENCOR OCG Chapter 21 — Named ACL Sequence Numbers",
      expert_trap_warning: null,
      tags: ["acl", "named-acl", "sequence-numbers", "ios-xe"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-082",
      type: "single_choice",
      domain: "5.0",
      subdomain: "AAA",
      difficulty: 3,
      prompt: "A Cisco router is configured with 'aaa authorization exec default group tacacs+ if-authenticated'. What does 'if-authenticated' mean in this context?",
      options: [
        { id: "A", text: "Authorisation is performed only if the user was authenticated by TACACS+, not local", is_correct: false },
        { id: "B", text: "If TACACS+ is unreachable for authorisation, the user is permitted if they were successfully authenticated by any method", is_correct: true },
        { id: "C", text: "The user must be authenticated AND authorised simultaneously before gaining access", is_correct: false },
        { id: "D", text: "Authorisation is skipped if the user has the enable password configured locally", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "'if-authenticated' is an authorisation method that succeeds if the user was already successfully authenticated (by any method in the authentication method list). It serves as a fallback: if TACACS+ authorisation is unreachable, the 'if-authenticated' method grants access. Without 'if-authenticated', TACACS+ authorisation failure (due to server unreachability) would deny the user even if authentication succeeded.",
      cisco_reference: "ENCOR OCG Chapter 20 — AAA Authorisation",
      expert_trap_warning: null,
      tags: ["aaa", "authorisation", "if-authenticated", "tacacs+"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-083",
      type: "drag_drop",
      domain: "5.0",
      subdomain: "Layer 2 Security",
      difficulty: 2,
      prompt: "Match each Layer 2 security feature to its primary attack it mitigates.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "DHCP Snooping" },
        { id: "item2", text: "Dynamic ARP Inspection (DAI)" },
        { id: "item3", text: "IP Source Guard" },
        { id: "item4", text: "Port Security" }
      ],
      drop_targets: [
        { id: "target1", label: "Prevents rogue DHCP servers and DHCP starvation attacks", accepts: "item1" },
        { id: "target2", label: "Prevents ARP spoofing and ARP cache poisoning attacks", accepts: "item2" },
        { id: "target3", label: "Prevents IP address spoofing on untrusted access ports", accepts: "item3" },
        { id: "target4", label: "Prevents MAC flooding attacks against the CAM table", accepts: "item4" }
      ],
      explanation: "DHCP Snooping: rogue DHCP servers and DHCP starvation. DAI: ARP spoofing/poisoning (uses DHCP snooping binding table). IP Source Guard: IP source spoofing (uses DHCP snooping binding table). Port Security: MAC flooding/CAM table overflow attacks by limiting MAC addresses per port.",
      cisco_reference: "ENCOR OCG Chapter 21 — Layer 2 Security",
      expert_trap_warning: null,
      tags: ["dhcp-snooping", "dai", "ip-source-guard", "port-security", "layer2-security"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-085",
      type: "single_choice",
      domain: "5.0",
      subdomain: "ACL",
      difficulty: 3,
      prompt: "Which ACL feature in IOS-XE allows different match criteria per traffic direction on the same interface without applying separate ACLs?",
      options: [
        { id: "A", text: "An extended ACL can be applied both inbound and outbound on the same interface simultaneously", is_correct: false },
        { id: "B", text: "A reflexive ACL uses the 'reflect' keyword to dynamically create return ACL entries based on outbound traffic", is_correct: true },
        { id: "C", text: "CBAC (Context-Based Access Control) is the only way to permit return traffic", is_correct: false },
        { id: "D", text: "ACL tethering ties an inbound and outbound ACL pair with shared state", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Reflexive ACLs use the 'reflect' keyword in an outbound ACL to dynamically create temporary permit entries in an inbound ACL for return traffic. When an outbound session is matched, a reflected (mirrored) ACL entry is created that permits the return traffic. The reflected entry is removed when the session ends (TCP FIN/RST or IP traffic timeout). This provides basic stateful-like behaviour without a full stateful firewall.",
      cisco_reference: "ENCOR OCG Chapter 21 — Reflexive ACLs",
      expert_trap_warning: null,
      tags: ["acl", "reflexive-acl", "reflect", "return-traffic"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // DOMAIN 6.0 — AUTOMATION (expanded)
    // =========================================================

    {
      id: "exam-exp-086",
      type: "single_choice",
      domain: "6.0",
      subdomain: "REST APIs",
      difficulty: 2,
      prompt: "Which HTTP method is idempotent AND creates or completely replaces a resource at a specific URI?",
      options: [
        { id: "A", text: "POST — creates a new resource; the server determines the URI", is_correct: false },
        { id: "B", text: "PUT — creates or replaces the entire resource at the specified URI; subsequent identical PUT requests have the same result", is_correct: true },
        { id: "C", text: "PATCH — partially updates a resource; idempotent only if designed that way", is_correct: false },
        { id: "D", text: "DELETE — removes a resource; not used to create resources", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "REST idempotency: GET (idempotent — read only), PUT (idempotent — full resource replacement), DELETE (idempotent — repeated deletes have same result after first), POST (NOT idempotent — creates new resource each time), PATCH (not guaranteed idempotent). PUT creates the resource at a client-specified URI or replaces an existing resource entirely. The key distinction: PUT replaces the WHOLE resource; PATCH modifies part of it.",
      cisco_reference: "ENCOR OCG Chapter 19 — REST API Methods",
      expert_trap_warning: "PATCH is often assumed to be idempotent but is not guaranteed to be — unlike PUT. For the ENCOR exam, PUT is the idempotent method for full resource creation/replacement.",
      tags: ["rest", "http-methods", "put", "idempotent", "api"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-087",
      type: "single_choice",
      domain: "6.0",
      subdomain: "YANG",
      difficulty: 3,
      prompt: "In a YANG data model, what is the distinction between a 'container' and a 'list' node?",
      options: [
        { id: "A", text: "A container holds a single instance of grouped nodes; a list holds multiple instances (entries) identified by one or more key leaf values", is_correct: true },
        { id: "B", text: "A container can hold only leaf nodes; a list can hold containers but not leaves", is_correct: false },
        { id: "C", text: "A list is a YANG 1.0 construct; container was introduced in YANG 1.1 (RFC 7950)", is_correct: false },
        { id: "D", text: "Container and list are identical — they are interchangeable terms in YANG modelling", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "YANG data nodes: leaf (single value of a base type), leaf-list (multiple values of the same type), container (a single instance grouping of related child nodes — like a struct), list (multiple instances/entries of the same structure, each identified by key leaves — like a table). Example: 'interfaces' container contains an 'interface' list where each entry is identified by the 'name' key.",
      cisco_reference: "ENCOR OCG Chapter 19 — YANG Data Modelling",
      expert_trap_warning: null,
      tags: ["yang", "container", "list", "leaf", "data-model"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-088",
      type: "single_choice",
      domain: "6.0",
      subdomain: "NETCONF",
      difficulty: 3,
      prompt: "A NETCONF <edit-config> operation uses 'operation=\"merge\"' on a leaf node that already exists. What is the result?",
      options: [
        { id: "A", text: "The operation fails with an error because the node already exists", is_correct: false },
        { id: "B", text: "The existing value is replaced with the new value specified in the RPC", is_correct: true },
        { id: "C", text: "The new value is appended to the existing value as a list entry", is_correct: false },
        { id: "D", text: "The existing node is deleted and a new node is created", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "NETCONF <edit-config> operations: 'merge' (default) — if node exists, update/replace it; if not, create it. 'create' — create if not exists; error if already exists. 'replace' — replace entire subtree. 'delete' — delete if exists; error if not exists. 'remove' — delete if exists; no error if not exists. 'merge' is the most common operation and the default when no 'operation' attribute is specified.",
      cisco_reference: "ENCOR OCG Chapter 19 — NETCONF edit-config",
      expert_trap_warning: null,
      tags: ["netconf", "edit-config", "merge", "operations"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-089",
      type: "multi_choice",
      domain: "6.0",
      subdomain: "NETCONF / RESTCONF",
      difficulty: 3,
      prompt: "Which TWO statements correctly distinguish RESTCONF from NETCONF? (Choose two)",
      options: [
        { id: "A", text: "RESTCONF uses HTTP/HTTPS with standard HTTP verbs; NETCONF uses XML-RPC over SSH", is_correct: true },
        { id: "B", text: "RESTCONF does not support the 'candidate' datastore; NETCONF supports running, startup, and candidate datastores", is_correct: true },
        { id: "C", text: "RESTCONF can only use JSON encoding; NETCONF can only use XML", is_correct: false },
        { id: "D", text: "RESTCONF requires a management VRF; NETCONF uses the global routing table only", is_correct: false }
      ],
      correct_answer: ["A", "B"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Key NETCONF vs RESTCONF differences: (1) Transport — NETCONF uses XML over SSH (port 830); RESTCONF uses HTTP verbs (GET/POST/PUT/PATCH/DELETE) over HTTPS. (2) Datastores — NETCONF supports running, startup, and candidate datastores; RESTCONF in RFC 8040 only supports the 'running' datastore (no candidate/commit workflow). RESTCONF supports both JSON and XML encoding (not JSON only). Both use YANG data models.",
      cisco_reference: "ENCOR OCG Chapter 19 — RESTCONF vs NETCONF",
      expert_trap_warning: null,
      tags: ["restconf", "netconf", "comparison", "datastore", "https"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-090",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Ansible",
      difficulty: 2,
      prompt: "In an Ansible playbook targeting Cisco IOS-XE devices, which connection plugin is used for SSH-based configuration management without requiring Python on the device?",
      options: [
        { id: "A", text: "ansible.builtin.ssh (standard SSH module)", is_correct: false },
        { id: "B", text: "network_cli — uses SSH to connect to network devices via CLI", is_correct: true },
        { id: "C", text: "httpapi — required for all Cisco IOS-XE devices", is_correct: false },
        { id: "D", text: "paramiko — the only supported connection type for network devices", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Ansible's 'network_cli' connection plugin is specifically designed for network devices accessed via SSH CLI. It handles the interactive CLI session without requiring a Python interpreter on the managed device. 'httpapi' is used for devices with HTTP/HTTPS-based APIs (e.g., DNA Center, Cisco IOS-XE RESTCONF). 'netconf' plugin is used for NETCONF-capable devices. Network_cli is the standard for traditional CLI-managed network devices.",
      cisco_reference: "ENCOR OCG Chapter 19 — Ansible for Network Automation",
      expert_trap_warning: null,
      tags: ["ansible", "network_cli", "ios-xe", "connection-plugin"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-091",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Python / Scripting",
      difficulty: 2,
      prompt: "In Python, which data type is used to represent an ordered, mutable collection of items that allows duplicate values?",
      options: [
        { id: "A", text: "tuple — ordered, immutable, allows duplicates", is_correct: false },
        { id: "B", text: "list — ordered, mutable, allows duplicates", is_correct: true },
        { id: "C", text: "set — unordered, mutable, no duplicates", is_correct: false },
        { id: "D", text: "dict — key-value pairs, keys must be unique", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Python data types: list (ordered, mutable, duplicates allowed — [1,2,3]), tuple (ordered, IMMUTABLE, duplicates allowed — (1,2,3)), set (unordered, mutable, NO duplicates — {1,2,3}), dict (key-value pairs, mutable, keys unique, values can duplicate — {'a':1, 'b':2}). For network automation, lists are used for ordered sequences of commands/interfaces; dicts map device parameters.",
      cisco_reference: "ENCOR OCG Chapter 19 — Python Fundamentals",
      expert_trap_warning: null,
      tags: ["python", "list", "data-types", "automation"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-092",
      type: "single_choice",
      domain: "6.0",
      subdomain: "EEM",
      difficulty: 3,
      prompt: "Which Cisco EEM (Embedded Event Manager) applet configuration would execute a SNMP trap AND run a CLI command when interface GigabitEthernet0/1 goes down?",
      options: [
        { id: "A", text: "event interface name GigabitEthernet0/1 parameter bandwidth ge 0\n action 1.0 syslog msg \"Interface down\"", is_correct: false },
        { id: "B", text: "event track 1 state down\n action 1.0 cli command \"show interface GigabitEthernet0/1\"", is_correct: false },
        { id: "C", text: "event syslog pattern \"GigabitEthernet0/1.*changed state to down\"\n action 1.0 snmp-trap intdata1 0 intdata2 0 strdata \"Intf Down\"\n action 2.0 cli command \"show interface GigabitEthernet0/1\"", is_correct: true },
        { id: "D", text: "event timer cron cron-entry \"*/1 * * * *\"\n action 1.0 cli command \"show interface GigabitEthernet0/1 counters\"", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "EEM applets match events and execute actions. The syslog event detector ('event syslog pattern') matches a syslog message regex when the interface goes down. The 'action snmp-trap' sends an SNMP notification; 'action cli command' executes a CLI command. EEM can also use 'event interface' with 'get-type sync' for more direct interface state events. Actions use decimal numbered sequences for ordering.",
      cisco_reference: "ENCOR OCG Chapter 19 — EEM",
      expert_trap_warning: null,
      tags: ["eem", "embedded-event-manager", "applet", "automation", "syslog"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-093",
      type: "single_choice",
      domain: "6.0",
      subdomain: "DNA Center",
      difficulty: 3,
      prompt: "Which DNA Center API endpoint returns the authentication token required for all subsequent API calls?",
      options: [
        { id: "A", text: "GET /dna/system/api/v1/auth/token", is_correct: false },
        { id: "B", text: "POST /dna/system/api/v1/auth/token", is_correct: true },
        { id: "C", text: "POST /api/v1/ticket", is_correct: false },
        { id: "D", text: "GET /api/system/v1/identitymgmt/login", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DNA Center API authentication: POST /dna/system/api/v1/auth/token with HTTP Basic Auth (base64 encoded username:password in Authorization header). The response body contains the 'Token' field. This token is then passed as 'X-Auth-Token: <token>' in the header of all subsequent API calls. Tokens expire and must be renewed with a new POST request. GET is incorrect — token generation requires POST.",
      cisco_reference: "ENCOR OCG Chapter 19; Cisco DNA Center API Reference v2.x",
      expert_trap_warning: "Using GET instead of POST for the token endpoint is a common mistake. The token endpoint requires POST with Basic Auth credentials in the Authorization header.",
      tags: ["dna-center", "api", "authentication", "token", "rest"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-094",
      type: "single_choice",
      domain: "6.0",
      subdomain: "REST APIs",
      difficulty: 2,
      prompt: "A REST API call returns HTTP status code 429. What does this indicate and what should the client do?",
      options: [
        { id: "A", text: "429 Bad Request — the request body is malformed; fix the JSON and retry", is_correct: false },
        { id: "B", text: "429 Too Many Requests — the client has exceeded the API rate limit; implement backoff and retry after the Retry-After header interval", is_correct: true },
        { id: "C", text: "429 Gateway Timeout — the upstream server didn't respond; retry after 60 seconds", is_correct: false },
        { id: "D", text: "429 Conflict — another operation is modifying the resource; wait for a lock release", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "HTTP 429 Too Many Requests indicates the client has sent too many requests in a given time window (rate limiting). The server may include a 'Retry-After' header indicating when the client can resume. Best practice: implement exponential backoff, respect Retry-After headers, and use batch operations where possible. DNA Center and other Cisco APIs enforce rate limits to protect the management plane.",
      cisco_reference: "ENCOR OCG Chapter 19 — REST API Error Codes",
      expert_trap_warning: null,
      tags: ["rest", "http-status", "rate-limiting", "429"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-095",
      type: "multi_choice",
      domain: "6.0",
      subdomain: "Ansible",
      difficulty: 3,
      prompt: "Which TWO concepts in Ansible support idempotent playbook execution? (Choose two)",
      options: [
        { id: "A", text: "Modules that check the current state before making changes — they only apply changes if needed", is_correct: true },
        { id: "B", text: "The 'changed_when: false' directive that marks a task as never having changed the device state", is_correct: false },
        { id: "C", text: "Using 'ios_config' with 'save_when: changed' — the module compares desired state to current config and only pushes differences", is_correct: true },
        { id: "D", text: "The '--check' mode in Ansible always skips all tasks, ensuring idempotency", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Ansible idempotency is achieved through: (1) State-checking modules that compare desired state to current state and only make changes when needed. (2) Network modules like ios_config that use 'diff' comparisons — ios_config compares the config block to the running config and only pushes lines that are missing/different. 'changed_when: false' suppresses the changed indicator but doesn't prevent execution. '--check' mode is a dry run, not an idempotency mechanism.",
      cisco_reference: "ENCOR OCG Chapter 19 — Ansible Idempotency",
      expert_trap_warning: null,
      tags: ["ansible", "idempotency", "ios_config", "network-automation"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-096",
      type: "single_choice",
      domain: "6.0",
      subdomain: "YANG",
      difficulty: 3,
      prompt: "Which YANG model is a Cisco-specific model and how does it differ from an OpenConfig YANG model?",
      options: [
        { id: "A", text: "Cisco Native models (e.g., Cisco-IOS-XE-native) represent Cisco-specific CLI constructs; OpenConfig models are vendor-neutral abstractions of network features", is_correct: true },
        { id: "B", text: "Cisco Native models use JSON encoding; OpenConfig models use XML only", is_correct: false },
        { id: "C", text: "OpenConfig models can only be used with gNMI; Cisco Native models require NETCONF", is_correct: false },
        { id: "D", text: "Cisco Native models are public domain; OpenConfig models are proprietary to Google", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "YANG model types: Cisco Native (Cisco-IOS-XE-native, Cisco-IOS-XE-bgp-oper, etc.) — vendor-specific models that expose ALL Cisco IOS-XE features including Cisco-proprietary ones. OpenConfig — vendor-neutral YANG models developed by an industry consortium (openconfig.net) representing common network features in a platform-agnostic way. IETF models (ietf-interfaces, ietf-routing) are IETF RFC-defined standards. Multiple model types can coexist on a device.",
      cisco_reference: "ENCOR OCG Chapter 19 — YANG Model Types",
      expert_trap_warning: null,
      tags: ["yang", "cisco-native", "openconfig", "ietf", "data-model"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-097",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Configuration Management",
      difficulty: 2,
      prompt: "In Terraform, what is the purpose of the 'terraform plan' command?",
      options: [
        { id: "A", text: "It applies all configuration changes and provisions the infrastructure", is_correct: false },
        { id: "B", text: "It generates an execution plan showing what changes Terraform will make without applying them — a dry run", is_correct: true },
        { id: "C", text: "It initialises the working directory and downloads required providers", is_correct: false },
        { id: "D", text: "It destroys all managed infrastructure defined in the configuration files", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Terraform workflow: 'terraform init' (initialise, download providers), 'terraform plan' (dry run — compare current state to desired state, show proposed changes), 'terraform apply' (execute the plan, provision/modify infrastructure), 'terraform destroy' (destroy managed resources). 'plan' is a critical step to review changes before applying in production environments.",
      cisco_reference: "ENCOR OCG Chapter 19 — Terraform",
      expert_trap_warning: null,
      tags: ["terraform", "iac", "plan", "automation"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-098",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Python / Scripting",
      difficulty: 2,
      prompt: "A Python script uses the `requests` library to make a REST API call with the following: `response = requests.get(url, headers=headers, verify=False)`. What does `verify=False` disable?",
      options: [
        { id: "A", text: "HTTP authentication verification — skips the username/password check", is_correct: false },
        { id: "B", text: "TLS/SSL certificate verification — the script will not validate the server's HTTPS certificate", is_correct: true },
        { id: "C", text: "Response body verification — the script skips JSON schema validation", is_correct: false },
        { id: "D", text: "Rate limit enforcement — the requests library will not throttle API calls", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In Python's `requests` library, `verify=False` disables SSL/TLS certificate verification. This is commonly used in lab environments with self-signed certificates (like Cisco DNA Center sandbox) but is a security risk in production as it makes the connection vulnerable to MITM attacks. The library will also raise an InsecureRequestWarning unless explicitly suppressed. In production, use `verify='/path/to/cert.pem'` to specify a trusted CA bundle.",
      cisco_reference: "ENCOR OCG Chapter 19 — Python Requests Library",
      expert_trap_warning: null,
      tags: ["python", "requests", "ssl", "tls", "api"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-099",
      type: "single_choice",
      domain: "6.0",
      subdomain: "EEM",
      difficulty: 2,
      prompt: "Which EEM event detector would trigger an applet when a specific syslog message matching a regular expression is generated by the device?",
      options: [
        { id: "A", text: "event timer", is_correct: false },
        { id: "B", text: "event snmp", is_correct: false },
        { id: "C", text: "event syslog", is_correct: true },
        { id: "D", text: "event interface", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "EEM event detectors: event syslog (triggers on syslog message matching a regex pattern), event timer (periodic, countdown, watchdog, absolute time triggers), event snmp (triggers on SNMP OID threshold crossing), event interface (triggers on interface counter thresholds), event cli (triggers on CLI command pattern matching), event track (triggers on object tracking state changes). The syslog detector is the most versatile for responding to operational events.",
      cisco_reference: "ENCOR OCG Chapter 19 — EEM Event Detectors",
      expert_trap_warning: null,
      tags: ["eem", "syslog", "event-detector", "automation"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-100",
      type: "drag_drop",
      domain: "6.0",
      subdomain: "REST APIs",
      difficulty: 2,
      prompt: "Match each HTTP status code to its correct meaning.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "200 OK" },
        { id: "item2", text: "201 Created" },
        { id: "item3", text: "400 Bad Request" },
        { id: "item4", text: "404 Not Found" }
      ],
      drop_targets: [
        { id: "target1", label: "Request succeeded; response body contains the requested resource or result", accepts: "item1" },
        { id: "target2", label: "Resource successfully created (typically response to POST or PUT)", accepts: "item2" },
        { id: "target3", label: "The request is malformed or contains invalid parameters — client error", accepts: "item3" },
        { id: "target4", label: "The requested resource does not exist at the specified URI", accepts: "item4" }
      ],
      explanation: "HTTP status code families: 2xx = success (200 OK, 201 Created, 204 No Content), 3xx = redirection, 4xx = client error (400 Bad Request, 401 Unauthorized, 403 Forbidden, 404 Not Found, 429 Too Many Requests), 5xx = server error (500 Internal Server Error, 503 Service Unavailable). Understanding these is essential for troubleshooting REST API integrations.",
      cisco_reference: "ENCOR OCG Chapter 19 — HTTP Status Codes",
      expert_trap_warning: null,
      tags: ["rest", "http-status", "api", "200", "404"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-101",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Configuration Management",
      difficulty: 2,
      prompt: "In Git version control, what does the 'git commit' command do, and what is required before committing?",
      options: [
        { id: "A", text: "Pushes changes directly to the remote repository; no staging is required", is_correct: false },
        { id: "B", text: "Saves a snapshot of staged changes to the local repository with a commit message; files must be staged first with 'git add'", is_correct: true },
        { id: "C", text: "Creates a new branch for the committed changes; branching happens automatically during commit", is_correct: false },
        { id: "D", text: "Merges the working directory changes with the remote branch automatically", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Git workflow: 'git add <file>' stages changes from the working directory to the staging area (index). 'git commit -m \"message\"' creates a permanent snapshot of staged changes in the LOCAL repository. 'git push' sends local commits to the remote repository. Committing without staging ('git add') results in no changes being committed (empty commit). Understanding this three-stage model is essential for automation workflows.",
      cisco_reference: "ENCOR OCG Chapter 19 — Git Fundamentals",
      expert_trap_warning: null,
      tags: ["git", "commit", "staging", "version-control"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-102",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Python / Scripting",
      difficulty: 3,
      prompt: "A Python script parses a JSON API response. The response contains: `{\"interfaces\": [{\"name\": \"Gi0/0\", \"status\": \"up\"}, {\"name\": \"Gi0/1\", \"status\": \"down\"}]}`. Which Python code correctly retrieves the status of 'Gi0/1'?",
      options: [
        { id: "A", text: "response['interfaces']['Gi0/1']['status']", is_correct: false },
        { id: "B", text: "response['interfaces'][1]['status']", is_correct: true },
        { id: "C", text: "response.interfaces[1].status", is_correct: false },
        { id: "D", text: "response['interfaces']['status'][1]", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The JSON structure has a top-level dict with key 'interfaces' containing a list (array) of dicts. To access 'Gi0/1' (index 1 in the list), use: response['interfaces'][1]['status']. response['interfaces'] returns the list; [1] accesses the second element (0-indexed); ['status'] gets the value. Dot notation (response.interfaces) is not valid for Python dicts. After json.loads() or response.json(), the data is a Python dict, not an object.",
      cisco_reference: "ENCOR OCG Chapter 19 — Python JSON Parsing",
      expert_trap_warning: null,
      tags: ["python", "json", "parsing", "api", "dict"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-103",
      type: "single_choice",
      domain: "6.0",
      subdomain: "DNA Center",
      difficulty: 2,
      prompt: "Which DNA Center API category provides access to network device inventory information such as device type, software version, and reachability status?",
      options: [
        { id: "A", text: "Path Trace API", is_correct: false },
        { id: "B", text: "Device Inventory API (Know Your Network)", is_correct: true },
        { id: "C", text: "Command Runner API", is_correct: false },
        { id: "D", text: "Site Hierarchy API", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DNA Center API categories: Know Your Network (KYN) — device inventory, topology, site design. Path Trace — flow analysis between endpoints. Command Runner — execute show commands on devices. Sites — site hierarchy, building/floor management. Swim — software image management. Policy — group policy and SGT. The Device Inventory endpoint (GET /dna/intent/api/v1/network-device) returns platform info, IP, software version, and management status.",
      cisco_reference: "ENCOR OCG Chapter 19 — DNA Center API Categories",
      expert_trap_warning: null,
      tags: ["dna-center", "api", "inventory", "network-device"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // ADDITIONAL SCENARIO QUESTIONS
    // =========================================================

    {
      id: "exam-exp-sc-001",
      type: "scenario",
      domain: "5.0",
      subdomain: "Security",
      difficulty: 4,
      prompt: "Refer to the network topology and security configuration described below.",
      diagram:
        "  [Internet] ──── e0/0 [R1-Edge] e0/1 ──── [Core SW] ──── [Servers]\n" +
        "                   │\n" +
        "                [ISE RADIUS]\n" +
        "  Access Switch: 802.1X on all user ports\n" +
        "  CoPP: applied on R1-Edge protecting routing protocols",
      scenario_context:
        "R1 is the internet edge router with CoPP configured to rate-limit BGP, OSPF, and ICMP to the CPU. " +
        "The access layer switches enforce 802.1X with RADIUS server ISE. " +
        "DHCP Snooping and DAI are enabled on all access switches. " +
        "A network security audit identifies that corporate users with expired 802.1X certificates are being " +
        "granted access. An attacker on the LAN has crafted gratuitous ARP replies to redirect traffic. " +
        "R1's OSPF adjacency with the core intermittently drops under DDoS attack.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      sub_questions: [
        {
          id: "exam-exp-sc-001-q1",
          type: "single_choice",
          prompt: "Corporate users with expired certificates are gaining network access despite failed EAP-TLS authentication. Which 802.1X feature is most likely causing this unintended access?",
          options: [
            { id: "A", text: "Auth-Fail VLAN — users with failed authentication are placed in a VLAN with full network access", is_correct: false },
            { id: "B", text: "Guest VLAN — triggered when there is no EAP response, not when authentication fails", is_correct: false },
            { id: "C", text: "Auth-Fail VLAN configured with the same VLAN as the production network, giving failed users full access", is_correct: true },
            { id: "D", text: "MAB fallback — MAC authentication is succeeding for devices with expired certificates", is_correct: false }
          ],
          correct_answer: "C",
          explanation: "Auth-Fail VLAN is assigned when 802.1X authentication fails (RADIUS explicitly rejects). If Auth-Fail VLAN is accidentally set to the same VLAN as the production network (or a VLAN with full access), failed users get unintended access. The fix is to configure Auth-Fail VLAN to a restricted VLAN with limited access or remediation resources only."
        },
        {
          id: "exam-exp-sc-001-q2",
          type: "single_choice",
          prompt: "DAI is enabled on the access switch but the ARP spoofing attack is still succeeding. What is the MOST likely reason?",
          options: [
            { id: "A", text: "DAI requires DHCP snooping to be disabled on the VLAN — they conflict with each other", is_correct: false },
            { id: "B", text: "The attacker's port is configured as a DAI trusted port, bypassing DAI validation", is_correct: true },
            { id: "C", text: "DAI only validates ARP replies, not gratuitous ARP frames", is_correct: false },
            { id: "D", text: "DAI cannot protect against gratuitous ARP — only ARP Request spoofing is mitigated", is_correct: false }
          ],
          correct_answer: "B",
          explanation: "DAI skips validation on trusted ports (typically uplinks). If the attacker's port is misconfigured as trusted, all ARP packets from that port bypass DAI inspection. The fix is to ensure only genuine uplink/trunk ports are marked as DAI trusted, and all user-facing access ports are untrusted. DAI does validate gratuitous ARP when ports are untrusted."
        },
        {
          id: "exam-exp-sc-001-q3",
          type: "single_choice",
          prompt: "R1's OSPF adjacency with the core drops during a DDoS attack. CoPP is configured but OSPF hellos are being dropped. What is the MOST appropriate fix?",
          options: [
            { id: "A", text: "Disable CoPP entirely during DDoS events using an EEM script", is_correct: false },
            { id: "B", text: "Increase the CoPP police rate for the OSPF class to ensure hellos are not dropped during high CPU load", is_correct: true },
            { id: "C", text: "Move OSPF to a separate VRF to isolate it from DDoS traffic", is_correct: false },
            { id: "D", text: "Replace OSPF with BGP on the core link — BGP is not affected by CoPP", is_correct: false }
          ],
          correct_answer: "B",
          explanation: "If OSPF hellos are being dropped by CoPP during a DDoS attack, the OSPF traffic class police rate is too restrictive. Increase the rate limit and burst values for the OSPF/routing protocol class to ensure control-plane stability. CoPP should be tuned to block attack traffic (ICMP floods, SYN floods) while preserving routing protocol traffic at sufficient rates. BGP is also subject to CoPP."
        }
      ],
      explanation: "This scenario tests integration of multiple security features: 802.1X VLAN assignment, DAI trusted vs untrusted ports, and CoPP rate tuning. The key insight is that each security feature has operational caveats — misconfiguration of trusted ports, VLAN assignments, or police rates can undermine the intended protection.",
      cisco_reference: "ENCOR OCG Chapters 20–21 — Security Features",
      expert_trap_warning: "The exam often combines multiple security features in scenarios to test whether candidates understand their interdependencies. CoPP protecting the CPU from DDoS while also potentially dropping routing protocol traffic is a classic tension.",
      tags: ["802.1x", "dai", "copp", "auth-fail-vlan", "scenario", "security"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-sc-002",
      type: "scenario",
      domain: "6.0",
      subdomain: "Automation",
      difficulty: 3,
      prompt: "Refer to the automation scenario described below.",
      diagram:
        "  [DNA Center] ←─ REST API ─→ [Python Script / Ansible Tower]\n" +
        "       │\n" +
        "  [NETCONF] ──→ [Cisco IOS-XE Routers]\n" +
        "       │\n" +
        "  [gRPC/gNMI] ──→ [Streaming Telemetry Collector (InfluxDB)]",
      scenario_context:
        "An enterprise uses DNA Center as the network controller. A Python automation script calls the DNA Center " +
        "Intent API to retrieve device inventory and push interface configurations. " +
        "Simultaneously, streaming telemetry is configured on IOS-XE routers to push interface counters " +
        "to an InfluxDB time-series database every 10 seconds. " +
        "An Ansible playbook also runs nightly to apply SNMP v3 configuration to all devices. " +
        "The team encounters issues: the Python script receives 401 errors intermittently, " +
        "telemetry gaps appear in the database, and NETCONF operations fail with 'in-use' lock errors.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      sub_questions: [
        {
          id: "exam-exp-sc-002-q1",
          type: "single_choice",
          prompt: "The Python script receives intermittent HTTP 401 errors from the DNA Center API after running successfully for 30 minutes. What is the MOST likely cause?",
          options: [
            { id: "A", text: "The DNA Center server is rejecting requests due to IP-based rate limiting", is_correct: false },
            { id: "B", text: "The DNA Center authentication token has expired — tokens have a limited lifespan and must be renewed", is_correct: true },
            { id: "C", text: "The Python requests library dropped the Authorization header due to a redirect", is_correct: false },
            { id: "D", text: "The 401 errors indicate the DNA Center certificate was revoked", is_correct: false }
          ],
          correct_answer: "B",
          explanation: "DNA Center API tokens expire (typically after a configurable period). After expiry, API calls receive 401 Unauthorized. The script must implement token renewal — either by re-authenticating with Basic Auth when a 401 is received (retry logic) or by proactively refreshing the token before expiry. This is a very common operational issue with long-running automation scripts."
        },
        {
          id: "exam-exp-sc-002-q2",
          type: "single_choice",
          prompt: "NETCONF <edit-config> operations from the Ansible playbook fail with a 'resource-denied' error referencing a lock. What is the MOST likely explanation?",
          options: [
            { id: "A", text: "NETCONF requires root access on IOS-XE that the Ansible user does not have", is_correct: false },
            { id: "B", text: "Another NETCONF session has a lock on the running datastore — concurrent NETCONF sessions conflict", is_correct: true },
            { id: "C", text: "The YANG model version used by Ansible is incompatible with the IOS-XE version on the device", is_correct: false },
            { id: "D", text: "Ansible's network_cli plugin cannot coexist with NETCONF sessions on the same device", is_correct: false }
          ],
          correct_answer: "B",
          explanation: "NETCONF supports optional datastore locking (<lock> operation). If DNA Center (or another management tool) has acquired a lock on the running datastore for a configuration transaction, Ansible's NETCONF edit-config will be denied until the lock is released. Best practice: use short-lived locks, implement retry logic, or avoid concurrent write operations from multiple management platforms to the same device."
        }
      ],
      explanation: "Multi-platform automation environments require careful management of API token lifetimes and NETCONF locking semantics. Understanding token expiry (HTTP 401), NETCONF lock conflicts (resource-denied), and telemetry gaps (dial-out subscription failures) are practical skills tested in ENCOR automation scenarios.",
      cisco_reference: "ENCOR OCG Chapter 19 — API Integration and NETCONF",
      expert_trap_warning: "In multi-tool environments (DNA Center + Ansible + custom scripts), NETCONF lock conflicts are a common real-world problem. The exam may present this as a troubleshooting scenario.",
      tags: ["dna-center", "api", "netconf", "lock", "token", "ansible", "scenario", "automation"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-sc-003",
      type: "scenario",
      domain: "3.0",
      subdomain: "BGP / EIGRP",
      difficulty: 4,
      prompt: "Refer to the redistribution scenario below.",
      diagram:
        "  [R1-EIGRP AS1]──e0/0──[R2 ASBR]──e0/1──[R3-OSPF Area 0]\n" +
        "  EIGRP routes: 10.1.0.0/24, 10.2.0.0/24\n" +
        "  OSPF routes: 192.168.1.0/24, 192.168.2.0/24\n" +
        "  R2 runs both EIGRP and OSPF, redistributing between them",
      scenario_context:
        "R2 is configured to redistribute OSPF into EIGRP and EIGRP into OSPF. " +
        "No route filters are applied. The metric for redistributing into EIGRP is set to seed metric " +
        "bandwidth 1000 delay 100 reliability 255 load 1 mtu 1500. " +
        "When testing, R1 cannot reach 192.168.1.0/24. " +
        "R3 shows 10.1.0.0/24 as an OSPF external route (O E2) with metric 20.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      sub_questions: [
        {
          id: "exam-exp-sc-003-q1",
          type: "single_choice",
          prompt: "R1 cannot reach 192.168.1.0/24 even though R2 is redistributing OSPF into EIGRP. Which is the MOST likely cause?",
          options: [
            { id: "A", text: "OSPF external routes (O E2) cannot be redistributed into EIGRP", is_correct: false },
            { id: "B", text: "R2 requires a seed metric when redistributing into EIGRP — without it, the redistributed EIGRP metric defaults to infinity and the routes are unreachable", is_correct: true },
            { id: "C", text: "The EIGRP K-values on R1 and R2 are mismatched, preventing acceptance of redistributed routes", is_correct: false },
            { id: "D", text: "OSPF routes must be redistributed via BGP first before they can be injected into EIGRP", is_correct: false }
          ],
          correct_answer: "B",
          explanation: "When redistributing into EIGRP, a seed metric MUST be configured. Without a seed metric ('default-metric' or inline metric in the redistribute command), EIGRP assigns an infinite metric to redistributed routes, making them unusable. The problem states a seed metric IS configured, but the scenario says R1 cannot reach the routes — re-reading confirms the seed metric is there, but the issue might be a misconfigured metric or the metric command is on a different router. Verify that 'redistribute ospf <pid> metric bandwidth delay reliability load mtu' or 'default-metric' is correctly applied on R2 in the EIGRP process."
        },
        {
          id: "exam-exp-sc-003-q2",
          type: "single_choice",
          prompt: "R3 sees 10.1.0.0/24 as 'O E2' with metric 20. The network team wants to change this to O E1 type so the metric increases as the route is propagated further into OSPF. Which OSPF redistribution command on R2 achieves this?",
          options: [
            { id: "A", text: "redistribute eigrp 1 metric 20 metric-type 1 subnets", is_correct: true },
            { id: "B", text: "redistribute eigrp 1 metric 20 external type1 subnets", is_correct: false },
            { id: "C", text: "redistribute eigrp 1 metric-type 1 cost 20 subnets", is_correct: false },
            { id: "D", text: "redistribute eigrp 1 type1 metric 20 subnets", is_correct: false }
          ],
          correct_answer: "A",
          explanation: "The correct IOS-XE syntax to redistribute EIGRP into OSPF as E1 routes is: 'redistribute eigrp <AS> metric <seed-cost> metric-type 1 subnets'. The 'subnets' keyword is required to redistribute classful and classless (subnetted) routes. Without 'subnets', only classful network addresses are redistributed. E1 routes accumulate OSPF cost as they traverse the domain; E2 routes use only the external metric (default type)."
        }
      ],
      explanation: "Route redistribution between EIGRP and OSPF requires: seed metrics for EIGRP redistribution, 'subnets' keyword for OSPF redistribution, and careful metric-type selection (E1 vs E2). Missing the seed metric or 'subnets' keyword are the most common redistribution failures on the ENCOR exam.",
      cisco_reference: "ENCOR OCG Chapter 15 — Route Redistribution",
      expert_trap_warning: "Forgetting 'subnets' in 'redistribute into OSPF' is one of the most tested and most commonly failed ENCOR redistribution questions. Without 'subnets', classlessly subnetted routes are silently dropped.",
      tags: ["redistribution", "eigrp", "ospf", "seed-metric", "subnets", "scenario"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // ADDITIONAL SINGLE-CHOICE FILL-IN QUESTIONS
    // (covering gaps in domain distribution)
    // =========================================================

    {
      id: "exam-exp-104",
      type: "single_choice",
      domain: "2.0",
      subdomain: "VRF",
      difficulty: 2,
      prompt: "When configuring MP-BGP for MPLS L3VPN, which BGP address family must be activated to carry VPNv4 prefixes between PE routers?",
      options: [
        { id: "A", text: "address-family ipv4 unicast", is_correct: false },
        { id: "B", text: "address-family vpnv4", is_correct: true },
        { id: "C", text: "address-family l2vpn evpn", is_correct: false },
        { id: "D", text: "address-family ipv4 vrf", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "MP-BGP for MPLS L3VPN requires the 'address-family vpnv4' to be activated between PE routers. VPNv4 routes are IPv4 prefixes prepended with an 8-byte Route Distinguisher. The 'address-family ipv4 unicast' carries global (non-VPN) IPv4 routes. 'address-family l2vpn evpn' is for VXLAN BGP EVPN. 'neighbor <ip> activate' must be issued under the vpnv4 address-family for each PE peer.",
      cisco_reference: "ENCOR OCG Chapter 8 — MP-BGP VPNv4",
      expert_trap_warning: null,
      tags: ["mp-bgp", "vpnv4", "mpls-l3vpn", "address-family"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-105",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Infrastructure",
      difficulty: 2,
      prompt: "Which command correctly verifies that Flexible NetFlow is capturing traffic on a Cisco IOS-XE interface?",
      options: [
        { id: "A", text: "show ip flow interface", is_correct: false },
        { id: "B", text: "show flow monitor <name> statistics", is_correct: false },
        { id: "C", text: "show flow monitor <name> cache", is_correct: true },
        { id: "D", text: "debug ip flow export", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "'show flow monitor <name> cache' displays the active flow cache entries for a Flexible NetFlow monitor, showing source/destination IPs, ports, protocol, and collected statistics. 'show flow monitor <name> statistics' shows export statistics (packets exported, flows). 'show ip flow interface' is for legacy NetFlow v5/v9 (show ip cache flow for v5). The cache command is the primary verification tool for FNF.",
      cisco_reference: "ENCOR OCG Chapter 18 — Flexible NetFlow Verification",
      expert_trap_warning: null,
      tags: ["netflow", "flexible-netflow", "verification", "show-commands"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-106",
      type: "single_choice",
      domain: "1.0",
      subdomain: "Architecture",
      difficulty: 3,
      prompt: "In Cisco SD-Access, what is the difference between a 'Default Border Node' and an 'External Border Node'?",
      options: [
        { id: "A", text: "Default Border handles known external prefixes; External Border handles unknown/default routes only", is_correct: false },
        { id: "B", text: "Default Border handles unknown EIDs (sends to external for default routing); External Border handles known/specific external prefixes (connected to specific external domains)", is_correct: true },
        { id: "C", text: "Default Border is the primary; External Border is the secondary failover", is_correct: false },
        { id: "D", text: "External Border connects to the internet only; Default Border connects to the data centre only", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "SD-Access Border Node types: Default Border — handles traffic for EIDs not in the fabric database (sends to external network via a default route, typically toward internet/WAN). External Border — handles specific known prefixes from external domains (data centre, traditional campus) with explicit route advertisements. A site may have both types — Default for internet and External for data centre prefixes.",
      cisco_reference: "ENCOR OCG Chapter 16 — SD-Access Border Node Types",
      expert_trap_warning: null,
      tags: ["sd-access", "border-node", "default-border", "external-border"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-107",
      type: "multi_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 3,
      prompt: "Which TWO options can be used to filter OSPF routes from being installed in the routing table WITHOUT affecting the OSPF LSDB or adjacencies? (Choose two)",
      options: [
        { id: "A", text: "distribute-list with an access-list applied under the OSPF process", is_correct: true },
        { id: "B", text: "area filter-list to filter Type 3 LSAs at the ABR", is_correct: false },
        { id: "C", text: "ip route <prefix> null0 with AD 254 to suppress OSPF route installation", is_correct: false },
        { id: "D", text: "distance ospf with a higher administrative distance for specific prefixes using an access-list", is_correct: true }
      ],
      correct_answer: ["A", "D"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Filtering OSPF routes from the RIB (without affecting LSDB): (1) distribute-list — filters which OSPF routes are installed into the routing table on the local router (LSDB unchanged, OSPF neighbours unaffected). (2) distance ospf — increases AD for matched routes so other sources (static/connected) win. area filter-list filters Type 3 LSAs at the ABR, affecting the LSDB. Route-map under OSPF affects redistribution, not local installation.",
      cisco_reference: "ENCOR OCG Chapter 10 — OSPF Route Filtering",
      expert_trap_warning: "distribute-list in OSPF affects only local route installation — it does NOT filter LSA propagation to OSPF neighbours. Engineers who expect it to stop LSA flooding are mistaken.",
      tags: ["ospf", "distribute-list", "route-filtering", "rib"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-108",
      type: "single_choice",
      domain: "4.0",
      subdomain: "Network Assurance",
      difficulty: 2,
      prompt: "Which Cisco IOS-XE feature creates an automated response to network events without external controller intervention, using predefined Tcl scripts or CLI actions?",
      options: [
        { id: "A", text: "NETCONF/YANG subscription with on-change notifications", is_correct: false },
        { id: "B", text: "EEM (Embedded Event Manager)", is_correct: true },
        { id: "C", text: "IP SLA with threshold-based object tracking", is_correct: false },
        { id: "D", text: "Flexible NetFlow with real-time export", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "EEM (Embedded Event Manager) is the Cisco IOS/IOS-XE framework for event-driven automation directly on the device. It detects events (syslog, SNMP, CLI, timers, interface state, track objects) and executes configured actions (CLI commands, SNMP traps, syslog messages, Tcl scripts, Python scripts on newer platforms). EEM operates without requiring an external controller or management system.",
      cisco_reference: "ENCOR OCG Chapter 19 — EEM",
      expert_trap_warning: null,
      tags: ["eem", "automation", "ios-xe", "event-driven"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-109",
      type: "single_choice",
      domain: "2.0",
      subdomain: "DMVPN",
      difficulty: 3,
      prompt: "In DMVPN, what is the purpose of the NHRP 'ip nhrp map multicast dynamic' command on the hub?",
      options: [
        { id: "A", text: "It creates static NHRP mappings for multicast group addresses", is_correct: false },
        { id: "B", text: "It allows the hub to dynamically build a list of spoke NBMA addresses for multicast replication, enabling routing protocols that use multicast (OSPF, EIGRP) over the mGRE interface", is_correct: true },
        { id: "C", text: "It configures the hub to use multicast NHRP registration instead of unicast", is_correct: false },
        { id: "D", text: "It enables dynamic spoke-to-spoke multicast tunnels without hub involvement", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "'ip nhrp map multicast dynamic' on the hub allows routing protocol multicast packets (OSPF hellos at 224.0.0.5, EIGRP hellos at 224.0.0.10) to be replicated and sent as unicast NHRP to all registered spokes. As spokes register their NBMA addresses with the hub via NHRP, they are automatically added to the hub's multicast replication list. This eliminates the need for static 'ip nhrp map multicast <nbma-address>' entries for each spoke.",
      cisco_reference: "ENCOR OCG Chapter 9 — DMVPN NHRP Multicast",
      expert_trap_warning: null,
      tags: ["dmvpn", "nhrp", "multicast", "mgre", "routing-protocols"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-110",
      type: "single_choice",
      domain: "3.0",
      subdomain: "EtherChannel",
      difficulty: 3,
      prompt: "A four-port EtherChannel uses the default IOS-XE load-balancing hash algorithm based on source-destination IP. Two hosts communicate exclusively with each other across the EtherChannel. What is the observed load balancing behaviour?",
      options: [
        { id: "A", text: "Traffic is distributed equally across all four links because IOS-XE uses round-robin per packet", is_correct: false },
        { id: "B", text: "All traffic between the two hosts uses only ONE link because the src-dst-IP hash produces the same result for the single IP pair", is_correct: true },
        { id: "C", text: "Traffic is hashed across two links — even-to-odd port assignment based on IP addresses", is_correct: false },
        { id: "D", text: "IOS-XE automatically switches to per-packet load balancing when flow-based hashing is unbalanced", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "EtherChannel load balancing uses a hash of specific fields (src-mac, dst-mac, src-dst-IP, etc.). The hash produces a consistent result for the same flow parameters. With a single src-dst IP pair, the hash always produces the same index, directing all traffic to one link. To improve distribution, use src-dst-port (layer 4) hashing to add entropy. EtherChannel never does per-packet (out-of-order) load balancing.",
      cisco_reference: "ENCOR OCG Chapter 7 — EtherChannel Load Balancing",
      expert_trap_warning: "Network engineers often assume EtherChannel provides perfect load distribution. It only distributes based on flow hashes — a few large flows between the same IPs will always use the same link regardless of EtherChannel size.",
      tags: ["etherchannel", "load-balancing", "hash", "port-channel"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-111",
      type: "single_choice",
      domain: "5.0",
      subdomain: "Security",
      difficulty: 3,
      prompt: "An engineer configures 'ip verify unicast source reachable-via rx' on an interface. What does this mean and which uRPF mode does it implement?",
      options: [
        { id: "A", text: "Loose mode — source only needs to exist in the routing table via any interface", is_correct: false },
        { id: "B", text: "Strict mode — the FIB must have a return path for the source IP via the same 'rx' (receive) interface", is_correct: true },
        { id: "C", text: "VRF mode — performs RPF check within the specific VRF the interface is assigned to", is_correct: false },
        { id: "D", text: "Any mode — allows traffic if any route to the source exists, equivalent to loose mode", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "'ip verify unicast source reachable-via rx' = uRPF strict mode. 'rx' means the best return path to the source must be through the same interface where the packet was RECEIVED. If the FIB indicates the return path is via a different interface, the packet is dropped as potentially spoofed. 'reachable-via any' = loose mode (source must exist in FIB via any interface).",
      cisco_reference: "ENCOR OCG Chapter 21 — uRPF Configuration Syntax",
      expert_trap_warning: null,
      tags: ["urpf", "strict-mode", "rx", "antispoofing"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-112",
      type: "single_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 2,
      prompt: "Which OSPF feature allows a router to authenticate OSPF packets with neighbouring routers to prevent unauthorised routers from forming adjacencies?",
      options: [
        { id: "A", text: "OSPF MD5 authentication configured under the interface or area", is_correct: true },
        { id: "B", text: "OSPF HMAC-SHA authentication using the 'ip ospf authentication message-digest' command", is_correct: false },
        { id: "C", text: "OSPF TTL security to validate the hop count of OSPF packets", is_correct: false },
        { id: "D", text: "OSPF virtual link authentication uses a separate 802.1X process", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "OSPF supports authentication at the interface level ('ip ospf authentication message-digest' + 'ip ospf message-digest-key <n> md5 <key>') or at the area level ('area <n> authentication message-digest'). Interface-level overrides area-level. MD5 hashes are appended to OSPF packets. SHA authentication is available in OSPFv3 for IPv6. TTL security (GTSM) is a separate feature that validates the TTL value of routing protocol packets.",
      cisco_reference: "ENCOR OCG Chapter 10 — OSPF Authentication",
      expert_trap_warning: null,
      tags: ["ospf", "authentication", "md5", "security"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-114",
      type: "single_choice",
      domain: "5.0",
      subdomain: "Security",
      difficulty: 2,
      prompt: "Which of the following correctly describes the 'permit ip any any log' entry at the end of an ACL and its operational impact?",
      options: [
        { id: "A", text: "It permits all traffic and logs nothing — the 'log' keyword requires a syslog server to be configured separately", is_correct: false },
        { id: "B", text: "It permits all traffic and generates a syslog message for each matched packet, which can cause high CPU utilisation on busy interfaces", is_correct: true },
        { id: "C", text: "It creates a security risk only if placed before deny statements; at the end of an ACL it is safe", is_correct: false },
        { id: "D", text: "The 'log' keyword rate-limits syslog messages to one per second automatically", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The 'log' keyword in an ACL generates a syslog message for EVERY packet that matches that ACE. On a busy interface with 'permit ip any any log', this creates a syslog message for every forwarded packet, which can overwhelm both the logging buffer and the router CPU (process switching for log generation). Use 'log-input' to include input interface/MAC info. Rate-limiting with 'ip access-list log-update threshold' can reduce this impact but does not eliminate it.",
      cisco_reference: "ENCOR OCG Chapter 21 — ACL Logging",
      expert_trap_warning: "Adding 'log' to a high-traffic ACE can cause severe performance degradation. This is a common operational mistake — use 'log' only on specific deny rules or low-traffic ACEs.",
      tags: ["acl", "log", "syslog", "cpu", "performance"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-115",
      type: "single_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 3,
      prompt: "A BGP router has 'bgp deterministic-med' configured. What problem does this solve with MED comparison?",
      options: [
        { id: "A", text: "Without this command, MED is compared across all paths including those from different ASes, leading to non-deterministic results", is_correct: false },
        { id: "B", text: "By default, IOS only compares MED between routes from the same neighbouring AS. deterministic-med sorts paths by AS before comparing MED, ensuring consistent selection regardless of route arrival order", is_correct: true },
        { id: "C", text: "Without this command, MED values are ignored entirely in BGP best-path selection", is_correct: false },
        { id: "D", text: "deterministic-med forces MED comparison across all paths including from different ASes (equivalent to always-compare-med)", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "By default, Cisco IOS only compares MED between routes from the SAME neighbouring AS. However, the order in which paths are evaluated can cause non-deterministic results (different results depending on which path was received first). 'bgp deterministic-med' groups paths by advertising AS and compares MED within each group before selecting the best from each group, ensuring consistent selection regardless of update arrival order. 'bgp always-compare-med' compares MED across ALL peers regardless of AS.",
      cisco_reference: "ENCOR OCG Chapter 12 — BGP MED",
      expert_trap_warning: null,
      tags: ["bgp", "med", "deterministic-med", "best-path"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-116",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Automation",
      difficulty: 2,
      prompt: "Which Python library is specifically designed for interacting with network devices via SSH CLI and provides a 'send_command' method for executing show commands?",
      options: [
        { id: "A", text: "requests", is_correct: false },
        { id: "B", text: "ncclient", is_correct: false },
        { id: "C", text: "netmiko", is_correct: true },
        { id: "D", text: "paramiko", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Netmiko is a Python library built on top of Paramiko (SSH) specifically designed for network device automation. It provides device-type-aware SSH connections, 'send_command()' for executing show commands and receiving output, 'send_config_set()' for pushing configuration lines, and handles device-specific prompts and timing. Requests is for HTTP/REST APIs. ncclient is for NETCONF. Paramiko is the low-level SSH library that Netmiko uses internally.",
      cisco_reference: "ENCOR OCG Chapter 19 — Python Network Libraries",
      expert_trap_warning: null,
      tags: ["python", "netmiko", "ssh", "automation", "library"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-117",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Multicast",
      difficulty: 3,
      prompt: "In PIM-SM, when does a last-hop router (LHR) switch from the Shared Tree (RPT) to the Shortest Path Tree (SPT)?",
      options: [
        { id: "A", text: "Immediately after the first multicast packet is received from the source via the RP", is_correct: false },
        { id: "B", text: "After the multicast traffic rate exceeds the 'ip pim spt-threshold' — default is 0 kbps (switch to SPT immediately after first packet)", is_correct: true },
        { id: "C", text: "Only when manually triggered by the 'ip pim spt-switch' command", is_correct: false },
        { id: "D", text: "After the RP sends an explicit SPT-switch notification to the LHR", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "PIM-SM SPT switchover: when a LHR receives the first multicast packet via the shared tree (from the RP), it can switch to the SPT (source-based tree) for lower latency/more optimal path. The default threshold is 0 kbps (switch immediately after first packet). The command 'ip pim spt-threshold <kbps>' delays the switch until traffic exceeds the threshold. Setting 'infinity' prevents SPT switchover entirely (use shared tree always — like BIDIR-PIM).",
      cisco_reference: "ENCOR OCG Chapter 14 — PIM-SM SPT Switchover",
      expert_trap_warning: null,
      tags: ["multicast", "pim-sm", "spt", "rpt", "spt-threshold"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-118",
      type: "multi_choice",
      domain: "1.0",
      subdomain: "Architecture",
      difficulty: 3,
      prompt: "Which TWO characteristics of DNA Center distinguish it from a traditional NMS (Network Management System)? (Choose two)",
      options: [
        { id: "A", text: "DNA Center uses intent-based networking (IBN) — operators define WHAT they want, not HOW to configure each device", is_correct: true },
        { id: "B", text: "DNA Center communicates with devices exclusively via SNMP like traditional NMS platforms", is_correct: false },
        { id: "C", text: "DNA Center provides AI/ML-driven analytics and closed-loop automation for proactive issue resolution", is_correct: true },
        { id: "D", text: "DNA Center requires manual CLI configuration for each device as it does not support APIs", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "DNA Center represents IBN (Intent-Based Networking): operators express desired business outcomes and DNA Center translates them into device configurations via programmable southbound APIs (NETCONF, RESTCONF, CLI, gRPC). Traditional NMS platforms use SNMP polling and lack closed-loop automation. DNA Center's Assurance component uses AI/ML for anomaly detection, root cause analysis, and can trigger automated remediation actions.",
      cisco_reference: "ENCOR OCG Chapter 16 — DNA Center and IBN",
      expert_trap_warning: null,
      tags: ["dna-center", "ibn", "ai-ml", "assurance", "automation"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-119",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Infrastructure",
      difficulty: 2,
      prompt: "An engineer notices OSPF routes are missing from the routing table on R3 after configuring redistribution from EIGRP on R2. The 'show ip ospf database' on R3 shows the external LSAs are present. What is the MOST likely cause?",
      options: [
        { id: "A", text: "OSPF requires 'nsf' to be enabled before installing redistributed routes", is_correct: false },
        { id: "B", text: "A distribute-list is filtering the routes from being installed into R3's routing table even though the LSDB contains them", is_correct: true },
        { id: "C", text: "OSPF external routes require 'ip ospf external-lsa accept' to be configured", is_correct: false },
        { id: "D", text: "R3 is in a stub area that blocks Type 5 LSAs from being installed even if they are in the database", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "If LSAs are present in the OSPF database but routes are NOT in the routing table, the most likely cause is a distribute-list filtering route installation. The distribute-list filters the RIB insertion without affecting LSDB flooding. A stub area would prevent Type 5 LSAs from entering the LSDB at all — but the question states LSAs ARE in the database, ruling out stub area filtering. Check for 'distribute-list <acl> in' under the OSPF process.",
      cisco_reference: "ENCOR OCG Chapter 10 — OSPF Troubleshooting",
      expert_trap_warning: null,
      tags: ["ospf", "distribute-list", "lsdb", "rib", "troubleshooting"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-120",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Automation",
      difficulty: 3,
      prompt: "Which RESTCONF HTTP method is used to create a NEW resource when the client does NOT know or specify the URI of the new resource (the server assigns the URI)?",
      options: [
        { id: "A", text: "GET — retrieves existing resources", is_correct: false },
        { id: "B", text: "PUT — creates or replaces at a client-specified URI", is_correct: false },
        { id: "C", text: "POST — creates a new resource; the server determines and returns the new resource URI", is_correct: true },
        { id: "D", text: "PATCH — partially updates an existing resource", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In RESTCONF (and REST), POST is used to create a new resource when the server assigns the URI. The server's response includes a 'Location' header with the new resource's URI. PUT creates or replaces a resource at a CLIENT-specified URI (client knows the exact URI). For YANG list entries in RESTCONF, POST to the parent list resource creates a new entry; the server determines the resource key/URI.",
      cisco_reference: "ENCOR OCG Chapter 19 — RESTCONF Operations",
      expert_trap_warning: null,
      tags: ["restconf", "post", "put", "http-methods", "yang"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-121",
      type: "single_choice",
      domain: "4.0",
      subdomain: "Network Assurance",
      difficulty: 2,
      prompt: "When configuring SNMPv3 on Cisco IOS-XE, which three components must be configured in the correct order to enable a functional SNMPv3 user?",
      options: [
        { id: "A", text: "snmp-server group → snmp-server user → snmp-server view", is_correct: false },
        { id: "B", text: "snmp-server view → snmp-server group → snmp-server user (user must reference an existing group)", is_correct: true },
        { id: "C", text: "snmp-server user → snmp-server group → snmp-server host", is_correct: false },
        { id: "D", text: "snmp-server community → snmp-server group → snmp-server user", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "SNMPv3 configuration order: (1) snmp-server view <view-name> <oid-tree> included — defines what MIB objects are accessible. (2) snmp-server group <group-name> v3 auth|priv [read <view>] [write <view>] — defines access policy referencing the view. (3) snmp-server user <username> <group-name> v3 auth sha <auth-key> priv aes 128 <priv-key> — creates user referencing the group. The 'snmp-server host' command configures trap destinations.",
      cisco_reference: "ENCOR OCG Chapter 18 — SNMPv3 Configuration",
      expert_trap_warning: null,
      tags: ["snmpv3", "configuration", "view", "group", "user", "ios-xe"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-122",
      type: "single_choice",
      domain: "5.0",
      subdomain: "Security",
      difficulty: 3,
      prompt: "An 802.1X multi-auth port mode is configured on an access switch port. A Cisco IP phone connects, followed by a PC connected behind the phone. Which statement correctly describes multi-auth behaviour?",
      options: [
        { id: "A", text: "Only the first device to authenticate is allowed; the second is denied regardless of credentials", is_correct: false },
        { id: "B", text: "Each device authenticates independently; the phone may be placed in a voice VLAN and the PC in a data VLAN based on RADIUS policy per device", is_correct: true },
        { id: "C", text: "The port allows only two devices — phone + PC — both sharing the same VLAN", is_correct: false },
        { id: "D", text: "Multi-auth mode disables 802.1X entirely and relies only on MAB for both devices", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "802.1X host modes: single-host (only one authenticated device), multi-host (one device authenticates, rest allowed on same VLAN), multi-auth (each device authenticates independently — each gets its own RADIUS policy, VLAN, ACL assignment; IP phones in voice VLAN and PCs in data VLAN can coexist on the same port with independent authentication states). Multi-auth is the most secure and flexible mode for phone+PC scenarios.",
      cisco_reference: "ENCOR OCG Chapter 20 — 802.1X Host Modes",
      expert_trap_warning: null,
      tags: ["802.1x", "multi-auth", "host-modes", "voice-vlan"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "exam-exp-123",
      type: "single_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 2,
      prompt: "Which IOS-XE command displays the BGP path selection reasoning for a specific prefix, showing which attributes were compared and why a path was chosen?",
      options: [
        { id: "A", text: "show ip bgp <prefix>", is_correct: false },
        { id: "B", text: "show ip bgp <prefix> bestpath-compare", is_correct: false },
        { id: "C", text: "show ip bgp <prefix> (review the '>' symbol and attribute values in output)", is_correct: false },
        { id: "D", text: "debug ip bgp <peer> updates and show ip bgp <prefix> — the '>' marks the best path with detailed attribute comparison in the detail output", is_correct: true }
      ],
      correct_answer: "D",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "'show ip bgp <prefix>' displays all known paths with the '>' marking the best path. The detail output shows WEIGHT, LOCAL_PREF, MED, ORIGIN, AS_PATH, and other attributes for each path. 'debug ip bgp <peer> updates' shows real-time path processing but is disruptive in production. For non-debug verification, 'show ip bgp <prefix>' with the full attribute listing is the standard operational verification command.",
      cisco_reference: "ENCOR OCG Chapter 12 — BGP Verification",
      expert_trap_warning: null,
      tags: ["bgp", "show-commands", "best-path", "verification"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // VLAN / TRUNKING (5 questions) — Domain 3.0
    // =========================================================

    {
      id: "vlan-001",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Switching",
      difficulty: 2,
      prompt: "Two switches are connected via a trunk link. Switch A has the native VLAN set to VLAN 1, while Switch B has the native VLAN set to VLAN 99. What is the result of this misconfiguration?",
      options: [
        { id: "A", text: "The trunk link is automatically disabled by STP", is_correct: false },
        { id: "B", text: "CDP reports a native VLAN mismatch; untagged frames sent by one switch are received on the wrong VLAN on the other, causing forwarding issues", is_correct: true },
        { id: "C", text: "Both switches negotiate to use VLAN 1 as the native VLAN", is_correct: false },
        { id: "D", text: "The trunk still forms but all VLANs are pruned except the native VLAN", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "When native VLANs are mismatched on a trunk, CDP and STP detect and log the mismatch. Frames sent untagged by one switch (on its native VLAN) arrive on the other switch and are placed into that switch's native VLAN — a different VLAN than intended. This causes traffic leaking between VLANs and unpredictable forwarding behavior. The trunk does not auto-disable, nor do the switches negotiate a common native VLAN.",
      cisco_reference: "ENCOR OCG Chapter 3 — VLANs and Trunking",
      expert_trap_warning: null,
      tags: ["vlan", "trunking", "native-vlan", "cdp", "mismatch"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "vlan-002",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Switching",
      difficulty: 2,
      prompt: "A switch port is configured as 'dynamic desirable' and is connected to another switch port configured as 'dynamic auto'. What trunking mode will the link negotiate?",
      options: [
        { id: "A", text: "Access mode — neither side actively initiates trunking", is_correct: false },
        { id: "B", text: "Trunk — dynamic desirable actively initiates and dynamic auto responds", is_correct: true },
        { id: "C", text: "The link remains in an inconsistent state and goes err-disabled", is_correct: false },
        { id: "D", text: "Trunk only if both switches are the same model", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DTP (Dynamic Trunking Protocol) negotiation follows this logic: desirable actively sends DTP frames to form a trunk, while auto waits for the other side to initiate. Desirable + auto = trunk. Auto + auto = access (neither initiates). Desirable + desirable = trunk. The only combination that stays access is auto + auto.",
      cisco_reference: "ENCOR OCG Chapter 3 — DTP Negotiation",
      expert_trap_warning: null,
      tags: ["vlan", "trunking", "dtp", "negotiation"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "vlan-003",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Switching",
      difficulty: 2,
      prompt: "On an 802.1Q trunk link, which frames are transmitted WITHOUT a VLAN tag?",
      options: [
        { id: "A", text: "All frames are tagged — 802.1Q tags every frame on the trunk", is_correct: false },
        { id: "B", text: "Management VLAN frames only", is_correct: false },
        { id: "C", text: "Native VLAN frames — they are sent untagged across the trunk", is_correct: true },
        { id: "D", text: "Frames from VLAN 1 only, regardless of the native VLAN configuration", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "802.1Q defines a native VLAN whose frames are sent untagged on the trunk. The receiving switch places untagged frames into its configured native VLAN. This is a key differentiator from ISL, which tags all frames. A common exam trap is believing all frames are tagged on 802.1Q — the native VLAN is the exception.",
      cisco_reference: "ENCOR OCG Chapter 3 — 802.1Q Trunking",
      expert_trap_warning: "A common trap is selecting 'all frames are tagged.' 802.1Q specifically exempts native VLAN frames from tagging — this is a core protocol behavior, not an edge case.",
      tags: ["vlan", "trunking", "802.1q", "native-vlan", "tagging"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "vlan-004",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Switching",
      difficulty: 2,
      prompt: "A switch operating in VTP transparent mode receives a VTP advertisement from a neighboring switch with a higher configuration revision number. What does the transparent-mode switch do?",
      options: [
        { id: "A", text: "Updates its local VLAN database to match the advertisement", is_correct: false },
        { id: "B", text: "Drops the advertisement silently", is_correct: false },
        { id: "C", text: "Forwards the advertisement to other trunk neighbors but does NOT update its own VLAN database", is_correct: true },
        { id: "D", text: "Resets its configuration revision number to 0", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "A VTP transparent-mode switch does not participate in VTP synchronization — it maintains its own independent VLAN database. However, it does forward VTP advertisements received on trunk ports to other trunk neighbors, acting as a relay. It never updates its own VLAN database based on received VTP messages, regardless of the revision number.",
      cisco_reference: "ENCOR OCG Chapter 3 — VTP Modes",
      expert_trap_warning: null,
      tags: ["vlan", "vtp", "transparent-mode"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "vlan-005",
      type: "multi_choice",
      domain: "3.0",
      subdomain: "Switching",
      difficulty: 2,
      prompt: "Which TWO components are required to implement router-on-a-stick inter-VLAN routing? (Choose two)",
      options: [
        { id: "A", text: "Subinterfaces on the router configured with encapsulation dot1q for each VLAN", is_correct: true },
        { id: "B", text: "A trunk port on the switch connecting to the router", is_correct: true },
        { id: "C", text: "Access ports on the switch — one per VLAN — each connected to a separate router interface", is_correct: false },
        { id: "D", text: "A Layer 3 switch with SVIs configured for each VLAN", is_correct: false }
      ],
      correct_answer: ["A", "B"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Router-on-a-stick uses a single physical router interface divided into subinterfaces, each assigned to a VLAN using 'encapsulation dot1q <vlan-id>'. The switch port connecting to the router must be configured as a trunk so it can carry multiple VLAN-tagged frames. Option C describes the legacy multi-interface approach (not router-on-a-stick). Option D describes SVI-based routing on a Layer 3 switch, which is a different inter-VLAN routing method.",
      cisco_reference: "ENCOR OCG Chapter 3 — Inter-VLAN Routing",
      expert_trap_warning: null,
      tags: ["vlan", "inter-vlan-routing", "router-on-a-stick", "trunking", "subinterfaces"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // NAT / PAT (5 questions) — Domain 3.0
    // =========================================================

    {
      id: "nat-001",
      type: "single_choice",
      domain: "3.0",
      subdomain: "IP Services",
      difficulty: 2,
      prompt: "An internal host with IP address 10.1.1.5 is being translated to public IP 203.0.113.1 via NAT. In the NAT translation table, what is the address 10.1.1.5 referred to as?",
      options: [
        { id: "A", text: "Inside global", is_correct: false },
        { id: "B", text: "Outside local", is_correct: false },
        { id: "C", text: "Inside local", is_correct: true },
        { id: "D", text: "Outside global", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Inside local is the private IP address assigned to a host on the inside network — it is how the inside host is known before translation. Inside global is the public IP address representing the inside host to the outside world (203.0.113.1 in this case). The most common exam mistake is confusing inside local with inside global. Remember: 'local' = the address as seen on its own side, 'global' = the address as seen from the other side.",
      cisco_reference: "ENCOR OCG Chapter 8 — NAT Terminology",
      expert_trap_warning: "Confusing inside local with inside global is one of the most common NAT mistakes. Inside local = private address on the inside network. Inside global = public address representing the inside host to the outside world.",
      tags: ["nat", "inside-local", "inside-global", "terminology"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "nat-002",
      type: "single_choice",
      domain: "3.0",
      subdomain: "IP Services",
      difficulty: 2,
      prompt: "An organization has 300 internal hosts sharing a single public IP address using PAT (Port Address Translation). What mechanism allows all 300 hosts to communicate simultaneously through the same public IP?",
      options: [
        { id: "A", text: "Each host is assigned a different TTL value to differentiate sessions", is_correct: false },
        { id: "B", text: "The router assigns unique source port numbers to create distinct translation entries (unique sockets)", is_correct: true },
        { id: "C", text: "The router uses different destination IPs for each internal host", is_correct: false },
        { id: "D", text: "PAT cannot support more than 255 simultaneous translations per public IP", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "PAT (also called NAT overload) multiplexes multiple private addresses through a single public IP by assigning unique source port numbers to each translation. Each session is identified by the combination of the public IP + unique port number, creating a distinct socket. Since port numbers are 16-bit (65,535 possible), PAT can theoretically support tens of thousands of simultaneous translations per public IP.",
      cisco_reference: "ENCOR OCG Chapter 8 — PAT (NAT Overload)",
      expert_trap_warning: null,
      tags: ["nat", "pat", "overload", "port-translation"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "nat-003",
      type: "single_choice",
      domain: "3.0",
      subdomain: "IP Services",
      difficulty: 3,
      prompt: "For traffic flowing from an INSIDE interface to an OUTSIDE interface on a Cisco router performing NAT, which operation happens first — the routing decision or the NAT translation?",
      options: [
        { id: "A", text: "NAT translates the source address first, then the router makes the routing decision using the translated (global) address", is_correct: false },
        { id: "B", text: "The routing decision is made first using the original (inside local) address, then NAT translates the source address before the packet exits", is_correct: true },
        { id: "C", text: "NAT and routing happen simultaneously in the CEF pipeline", is_correct: false },
        { id: "D", text: "The order depends on whether static or dynamic NAT is configured", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "For inside-to-outside traffic, the NAT order of operations is: routing first, then translation. The router looks up the destination in the routing table using the original (pre-NAT) addresses, determines the exit interface, and THEN applies NAT to translate the source address before forwarding. For outside-to-inside traffic, the order reverses: NAT translates the destination address first, then routing uses the translated (inside local) address to determine the exit interface.",
      cisco_reference: "ENCOR OCG Chapter 8 — NAT Order of Operations",
      expert_trap_warning: "The order of operations reverses depending on direction: inside-to-outside = route then translate; outside-to-inside = translate then route. This is a classic exam trap.",
      tags: ["nat", "order-of-operations", "routing", "inside-to-outside"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "nat-004",
      type: "single_choice",
      domain: "3.0",
      subdomain: "IP Services",
      difficulty: 2,
      prompt: "An administrator configures 'ip nat inside source static 192.168.1.10 203.0.113.10' on a router but forgets to apply 'ip nat inside' on the LAN-facing interface. What happens when host 192.168.1.10 sends traffic to the internet?",
      options: [
        { id: "A", text: "NAT still works because the static mapping is globally configured", is_correct: false },
        { id: "B", text: "The router translates the address but only for TCP traffic", is_correct: false },
        { id: "C", text: "No translation occurs — NAT requires the interface-level 'ip nat inside' and 'ip nat outside' commands to identify which interfaces participate in translation", is_correct: true },
        { id: "D", text: "The router generates a syslog error and rejects the static NAT configuration", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "NAT on Cisco IOS-XE requires three components: (1) a NAT rule (static, dynamic, or PAT), (2) 'ip nat inside' on the inside-facing interface, and (3) 'ip nat outside' on the outside-facing interface. Without the interface-level designations, the router has no way to determine which traffic should be translated. The static mapping alone is not sufficient — the interface commands are mandatory for NAT to function.",
      cisco_reference: "ENCOR OCG Chapter 8 — NAT Configuration",
      expert_trap_warning: null,
      tags: ["nat", "static-nat", "configuration", "troubleshooting"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "nat-005",
      type: "single_choice",
      domain: "3.0",
      subdomain: "IP Services",
      difficulty: 2,
      prompt: "A router is configured with dynamic NAT using an ACL to match inside hosts and a NAT pool of 5 public addresses. All 5 pool addresses are currently in use. What happens when a sixth inside host attempts to reach the internet?",
      options: [
        { id: "A", text: "The router queues the packet until a pool address becomes available", is_correct: false },
        { id: "B", text: "The router uses the last available address and shares it with the new host via PAT", is_correct: false },
        { id: "C", text: "The translation fails and the packet is dropped — the NAT pool is exhausted", is_correct: true },
        { id: "D", text: "The router evicts the oldest translation and reassigns that address", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "With dynamic NAT (not PAT/overload), each inside host requires a dedicated public IP from the pool. When the pool is exhausted, no more translations can be created — new packets matching the NAT ACL are dropped, not queued. The router does not automatically fall back to PAT or evict existing translations. To avoid this, administrators typically use PAT (overload) or ensure the pool is sized appropriately for the number of concurrent users.",
      cisco_reference: "ENCOR OCG Chapter 8 — Dynamic NAT Pool Exhaustion",
      expert_trap_warning: null,
      tags: ["nat", "dynamic-nat", "pool-exhaustion", "acl"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // NTP (3 questions) — Domain 4.0
    // =========================================================

    {
      id: "ntp-001",
      type: "single_choice",
      domain: "4.0",
      subdomain: "Network Assurance",
      difficulty: 2,
      prompt: "A router synchronizes its clock to an NTP server that advertises itself as stratum 2. What stratum level does the router advertise to its own NTP clients?",
      options: [
        { id: "A", text: "Stratum 2 — it inherits the same stratum as its source", is_correct: false },
        { id: "B", text: "Stratum 1 — it becomes more accurate after synchronization", is_correct: false },
        { id: "C", text: "Stratum 3 — each hop from the reference clock increments the stratum by one", is_correct: true },
        { id: "D", text: "Stratum 0 — the router becomes a primary reference source", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "NTP stratum indicates the distance from the authoritative reference clock (stratum 0, e.g., GPS or atomic clock). Each hop increments the stratum by 1. A server at stratum 2 received its time from a stratum 1 server, which synced to a stratum 0 source. When a router syncs to a stratum 2 server, it becomes stratum 3. Lower stratum = closer to the reference = more accurate. Stratum 16 means unsynchronized.",
      cisco_reference: "ENCOR OCG Chapter 12 — NTP Stratum",
      expert_trap_warning: "Lower stratum means MORE accurate (closer to the reference clock), not less. Stratum 1 is more accurate than stratum 3. This is counterintuitive and frequently tested.",
      tags: ["ntp", "stratum", "time-synchronization", "network-assurance"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ntp-002",
      type: "single_choice",
      domain: "4.0",
      subdomain: "Network Assurance",
      difficulty: 2,
      prompt: "What is the primary purpose of configuring NTP authentication on a Cisco router?",
      options: [
        { id: "A", text: "To encrypt the time data in transit using AES-256", is_correct: false },
        { id: "B", text: "To ensure the router only synchronizes time from trusted and authorized NTP sources, preventing rogue time source attacks", is_correct: true },
        { id: "C", text: "To prevent unauthorized clients from querying the router's NTP service", is_correct: false },
        { id: "D", text: "To enable NTP version 4 features over version 3", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "NTP authentication uses MD5 or SHA keys to verify that time updates come from trusted sources. Without authentication, an attacker could introduce a rogue NTP server with a lower stratum, causing devices to sync to an incorrect time source. This could disrupt certificate validation, log correlation, scheduled tasks, and security event timelines. NTP authentication does not encrypt the time data — it only validates the source.",
      cisco_reference: "ENCOR OCG Chapter 12 — NTP Authentication",
      expert_trap_warning: null,
      tags: ["ntp", "authentication", "security", "network-assurance"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ntp-003",
      type: "single_choice",
      domain: "4.0",
      subdomain: "Network Assurance",
      difficulty: 2,
      prompt: "Why is NTP synchronization considered critical for network assurance in a multi-device enterprise environment?",
      options: [
        { id: "A", text: "NTP is required for OSPF adjacencies to form between routers", is_correct: false },
        { id: "B", text: "Without NTP, switches cannot forward frames because STP depends on synchronized clocks", is_correct: false },
        { id: "C", text: "Consistent timestamps across all devices are essential for correlating syslog messages, SNMP traps, and NetFlow records during troubleshooting", is_correct: true },
        { id: "D", text: "NTP synchronization is required for SNMP polling to function", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In multi-device environments, troubleshooting and security incident response depend on correlating events across routers, switches, firewalls, and servers. If device clocks are out of sync, syslog timestamps, SNMP trap times, and NetFlow flow records cannot be accurately correlated, making root-cause analysis and forensic investigation extremely difficult. NTP ensures all devices share a common time reference. OSPF and STP do not require NTP to function.",
      cisco_reference: "ENCOR OCG Chapter 12 — NTP and Network Assurance",
      expert_trap_warning: null,
      tags: ["ntp", "syslog", "snmp", "netflow", "troubleshooting", "network-assurance"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // ETHERCHANNEL EXPANSION (4 questions) — Domain 3.0
    // =========================================================

    {
      id: "ether-001",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Switching",
      difficulty: 2,
      prompt: "Switch A has its ports configured for LACP active mode, and Switch B has its ports configured for LACP passive mode. Will an EtherChannel form between them?",
      options: [
        { id: "A", text: "No — both sides must be set to active for LACP to negotiate", is_correct: false },
        { id: "B", text: "Yes — active mode initiates LACP negotiation and passive mode responds; at least one side must be active", is_correct: true },
        { id: "C", text: "No — LACP passive mode disables EtherChannel negotiation entirely", is_correct: false },
        { id: "D", text: "Yes, but only if PAgP is also configured as a fallback", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "LACP has two modes: active (initiates negotiation by sending LACP PDUs) and passive (waits for LACP PDUs from the other side before responding). Active + passive = EtherChannel forms. Active + active = EtherChannel forms. Passive + passive = no EtherChannel (neither side initiates). At least one side must be active for the channel to negotiate.",
      cisco_reference: "ENCOR OCG Chapter 4 — LACP Modes",
      expert_trap_warning: null,
      tags: ["etherchannel", "lacp", "active", "passive", "negotiation"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ether-002",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Switching",
      difficulty: 2,
      prompt: "An administrator attempts to bundle four ports into an EtherChannel, but two ports are running at 1 Gbps and two are running at 100 Mbps. What is the result?",
      options: [
        { id: "A", text: "The EtherChannel forms using only the two 1 Gbps ports", is_correct: false },
        { id: "B", text: "The EtherChannel forms at the lowest common speed (100 Mbps per port)", is_correct: false },
        { id: "C", text: "The EtherChannel does not form — all member ports must have identical speed, duplex, and VLAN settings", is_correct: true },
        { id: "D", text: "The EtherChannel forms but load-balancing is disabled", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "EtherChannel requires all member ports to have identical configurations: same speed, duplex, switchport mode, VLAN assignment (for access ports), or allowed VLANs and native VLAN (for trunks). Mismatched speed settings prevent the EtherChannel from forming. The switch will log an error indicating incompatible port settings. This is one of the most common EtherChannel troubleshooting issues.",
      cisco_reference: "ENCOR OCG Chapter 4 — EtherChannel Requirements",
      expert_trap_warning: null,
      tags: ["etherchannel", "misconfiguration", "speed", "troubleshooting"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ether-003",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Switching",
      difficulty: 2,
      prompt: "Which EtherChannel negotiation protocol is an IEEE standard?",
      options: [
        { id: "A", text: "PAgP (Port Aggregation Protocol)", is_correct: false },
        { id: "B", text: "LACP (Link Aggregation Control Protocol, IEEE 802.3ad)", is_correct: true },
        { id: "C", text: "DTP (Dynamic Trunking Protocol)", is_correct: false },
        { id: "D", text: "VTP (VLAN Trunking Protocol)", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "LACP (IEEE 802.3ad, now part of IEEE 802.1AX) is the industry-standard EtherChannel negotiation protocol, supported across multi-vendor environments. PAgP is Cisco proprietary and only works between Cisco devices. DTP negotiates trunking mode (not EtherChannel), and VTP manages VLAN databases — neither are EtherChannel protocols. In multi-vendor environments, LACP is the only negotiation option.",
      cisco_reference: "ENCOR OCG Chapter 4 — LACP vs PAgP",
      expert_trap_warning: null,
      tags: ["etherchannel", "lacp", "pagp", "ieee", "802.3ad"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ether-004",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Switching",
      difficulty: 2,
      prompt: "An administrator wants EtherChannel to distribute traffic across member links based on both source and destination IP addresses. Which load-balancing method should be configured?",
      options: [
        { id: "A", text: "src-mac", is_correct: false },
        { id: "B", text: "dst-ip", is_correct: false },
        { id: "C", text: "src-dst-ip", is_correct: true },
        { id: "D", text: "round-robin", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "EtherChannel load-balancing is configured globally with 'port-channel load-balance <method>'. The src-dst-ip method hashes both source and destination IP addresses to determine which member link carries each flow. This provides the best distribution for IP-based traffic with many unique source-destination pairs. Other options include src-mac, dst-mac, src-ip, dst-ip, and src-dst-mac. Round-robin is not an available EtherChannel load-balancing method — EtherChannel uses hash-based distribution to maintain frame ordering within flows.",
      cisco_reference: "ENCOR OCG Chapter 4 — EtherChannel Load Balancing",
      expert_trap_warning: null,
      tags: ["etherchannel", "load-balancing", "src-dst-ip", "hashing"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // PORT SECURITY (3 questions) — Domain 5.0
    // =========================================================

    {
      id: "portsec-001",
      type: "single_choice",
      domain: "5.0",
      subdomain: "Security",
      difficulty: 2,
      prompt: "A switch port has port security enabled with the violation mode set to 'restrict'. An unauthorized MAC address is detected on the port. What happens?",
      options: [
        { id: "A", text: "The port is placed in err-disabled state and all traffic stops", is_correct: false },
        { id: "B", text: "Frames from the unauthorized MAC are dropped, a syslog message is generated, and the violation counter increments — but the port remains up for authorized MACs", is_correct: true },
        { id: "C", text: "The unauthorized MAC is added to the secure MAC table and traffic is allowed", is_correct: false },
        { id: "D", text: "All traffic on the port is dropped until the unauthorized device is removed", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Port security has three violation modes: shutdown (default — err-disables the port), restrict (drops violating frames, logs a syslog message, increments the violation counter, but the port stays up for legitimate traffic), and protect (silently drops violating frames with no syslog and no counter increment). Restrict is the middle ground — it blocks unauthorized MACs while keeping the port operational and providing visibility through logging.",
      cisco_reference: "ENCOR OCG Chapter 14 — Port Security Violation Modes",
      expert_trap_warning: null,
      tags: ["port-security", "violation-modes", "restrict", "syslog"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "portsec-002",
      type: "single_choice",
      domain: "5.0",
      subdomain: "Security",
      difficulty: 2,
      prompt: "What does the 'switchport port-security mac-address sticky' command accomplish on a Cisco switch port?",
      options: [
        { id: "A", text: "Configures a static MAC address that persists across reboots automatically", is_correct: false },
        { id: "B", text: "Dynamically learns the connected device's MAC address and adds it to the running configuration as a static secure MAC entry", is_correct: true },
        { id: "C", text: "Enables 802.1X authentication using the device MAC as credentials", is_correct: false },
        { id: "D", text: "Pins the MAC address to a specific VLAN permanently", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Sticky MAC learning dynamically learns the MAC address of the connected device and converts it into a static secure MAC entry in the running configuration (visible as 'switchport port-security mac-address sticky <mac>'). This combines the convenience of dynamic learning with the persistence of static entries. However, the sticky MAC is only in the running-config — it must be explicitly saved to startup-config with 'copy run start' or it will be lost on reload.",
      cisco_reference: "ENCOR OCG Chapter 14 — Sticky MAC Addresses",
      expert_trap_warning: "Sticky MACs are added to the running-config, NOT startup-config. If the switch reloads before 'copy run start', all sticky MACs are lost and must be re-learned. This is a common operational mistake.",
      tags: ["port-security", "sticky-mac", "mac-address", "running-config"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "portsec-003",
      type: "single_choice",
      domain: "5.0",
      subdomain: "Security",
      difficulty: 2,
      prompt: "A switch port enters err-disabled state due to a port security violation (shutdown mode is configured). What must an administrator do to restore the port to operational status?",
      options: [
        { id: "A", text: "Remove the offending device and wait — the port recovers automatically after 300 seconds by default", is_correct: false },
        { id: "B", text: "Clear the MAC address table with 'clear mac address-table dynamic'", is_correct: false },
        { id: "C", text: "Manually shut down the interface and then bring it back up with 'no shutdown', or configure 'errdisable recovery cause psecure-violation' for automatic recovery", is_correct: true },
        { id: "D", text: "Reboot the switch — err-disabled state can only be cleared by a reload", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "When a port enters err-disabled state, it does NOT automatically recover by default. The administrator must manually 'shutdown' and then 'no shutdown' the interface after addressing the violation cause. Alternatively, 'errdisable recovery cause psecure-violation' can be configured globally to enable automatic recovery after a configurable timer (default 300 seconds). Simply removing the offending device is not enough — the port remains err-disabled until manually or automatically recovered.",
      cisco_reference: "ENCOR OCG Chapter 14 — Err-Disabled Recovery",
      expert_trap_warning: null,
      tags: ["port-security", "err-disabled", "recovery", "troubleshooting"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // CoPP EXPANSION (2 questions) — Domain 5.0
    // =========================================================

    {
      id: "copp-001",
      type: "single_choice",
      domain: "5.0",
      subdomain: "Security",
      difficulty: 2,
      prompt: "What is the PRIMARY purpose of Control Plane Policing (CoPP) on a Cisco router?",
      options: [
        { id: "A", text: "To filter transit data-plane traffic based on source and destination IP", is_correct: false },
        { id: "B", text: "To encrypt control-plane communications between routing protocol peers", is_correct: false },
        { id: "C", text: "To protect the router's CPU from being overwhelmed by excessive control-plane and management-plane traffic such as routing protocol floods, SNMP storms, or SSH brute-force attempts", is_correct: true },
        { id: "D", text: "To prioritize CEF-switched traffic over process-switched traffic in the data plane", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "CoPP protects the route processor (CPU) by applying QoS policies to traffic destined to or originating from the control plane. Without CoPP, a flood of control-plane traffic (e.g., thousands of SNMP requests, BGP session floods, or ICMP floods targeting the router itself) could overwhelm the CPU, impacting routing protocol processing, CLI access, and overall device stability. CoPP uses class-maps and policy-maps applied to the 'control-plane' to rate-limit or drop excessive traffic before it reaches the CPU.",
      cisco_reference: "ENCOR OCG Chapter 15 — Control Plane Policing",
      expert_trap_warning: null,
      tags: ["copp", "control-plane", "cpu-protection", "qos", "security"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "copp-002",
      type: "single_choice",
      domain: "5.0",
      subdomain: "Security",
      difficulty: 3,
      prompt: "How does Control Plane Policing (CoPP) fundamentally differ from a standard ACL applied to a router interface?",
      options: [
        { id: "A", text: "CoPP uses wildcard masks while ACLs use subnet masks", is_correct: false },
        { id: "B", text: "CoPP applies to the control plane (punt path to the CPU) and uses QoS policy-maps to rate-limit traffic, while ACLs filter data-plane transit traffic on interfaces using permit/deny logic", is_correct: true },
        { id: "C", text: "CoPP can only match Layer 4 traffic while ACLs match Layer 3", is_correct: false },
        { id: "D", text: "CoPP replaces ACLs entirely — they cannot be used together", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The key distinction is where and how they operate. Standard/extended ACLs are applied to interfaces and filter transit (data-plane) traffic using permit/deny rules — they do not protect the router's own CPU. CoPP is applied to the 'control-plane' and uses MQC (Modular QoS CLI) with class-maps and policy-maps to classify and rate-limit traffic destined TO the router itself (management traffic like SSH, SNMP, and control protocols like OSPF, BGP). CoPP and ACLs complement each other and are often used together.",
      cisco_reference: "ENCOR OCG Chapter 15 — CoPP vs Interface ACLs",
      expert_trap_warning: null,
      tags: ["copp", "acl", "control-plane", "data-plane", "qos", "security"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // EXPANSION WAVE — DOMAIN 1.0 + 2.0 (Architecture + Virtualization)
    // =========================================================
    // =========================================================
    // DOMAIN 1.0 — ARCHITECTURE (~35 questions)
    // =========================================================

    // --- CEF ---

    {
      id: "cef-001",
      type: "single_choice",
      domain: "1.0",
      subdomain: "CEF",
      difficulty: 1,
      prompt: "Which two data structures does CEF use for hardware-rate packet forwarding?",
      options: [
        { id: "A", text: "Routing table and ARP cache", is_correct: false },
        { id: "B", text: "FIB and adjacency table", is_correct: true },
        { id: "C", text: "TCAM and CAM table", is_correct: false },
        { id: "D", text: "MAC address table and routing table", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "CEF uses the FIB (Forwarding Information Base), which mirrors the routing table in a format optimized for fast lookup, and the adjacency table, which stores Layer 2 rewrite information (next-hop MAC addresses). Together they enable line-rate forwarding without CPU involvement for each packet. The routing table and ARP cache are control-plane structures that populate the FIB and adjacency table respectively.",
      cisco_reference: "ENCOR OCG Chapter 4 — CEF",
      expert_trap_warning: null,
      tags: ["cef", "fib", "adjacency-table"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "cef-002",
      type: "single_choice",
      domain: "1.0",
      subdomain: "CEF",
      difficulty: 2,
      prompt: "A router is forwarding packets to a directly connected subnet. The destination host has not yet been resolved via ARP. What type of CEF adjacency is used?",
      options: [
        { id: "A", text: "Null adjacency", is_correct: false },
        { id: "B", text: "Drop adjacency", is_correct: false },
        { id: "C", text: "Punt adjacency", is_correct: false },
        { id: "D", text: "Glean adjacency", is_correct: true }
      ],
      correct_answer: "D",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "A glean adjacency is used when the destination is on a directly connected subnet but the specific host's Layer 2 address is not yet known. CEF punts the packet to the CPU to trigger ARP resolution. Once ARP resolves, a complete adjacency is built. A punt adjacency is for packets requiring CPU processing (routing protocols, options). A null adjacency silently discards. A drop adjacency drops with potential ICMP unreachable.",
      cisco_reference: "ENCOR OCG Chapter 4 — CEF Adjacency Types",
      expert_trap_warning: "Don't confuse glean with punt. Glean specifically means the subnet is connected but the host MAC is unknown. Punt means the packet needs CPU processing for other reasons.",
      tags: ["cef", "adjacency-table", "glean", "arp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "cef-003",
      type: "single_choice",
      domain: "1.0",
      subdomain: "CEF",
      difficulty: 2,
      prompt: "What is CEF polarization?",
      options: [
        { id: "A", text: "A condition where CEF is disabled on one interface due to a software bug", is_correct: false },
        { id: "B", text: "All switches in a multi-tier topology hash traffic to the same path because they use the same hash algorithm", is_correct: true },
        { id: "C", text: "The FIB becomes out of sync with the routing table after a route withdrawal", is_correct: false },
        { id: "D", text: "Packets are forwarded to the CPU instead of being hardware-switched", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "CEF polarization happens when multiple switches in a path use the same hash algorithm and inputs for ECMP load balancing, causing all of them to select the same link. This results in uneven utilization across equal-cost paths. It is mitigated by using unique hash seeds on each switch via 'ip cef load-sharing algorithm universal <unique-id>' or alternating hash inputs at each tier.",
      cisco_reference: "ENCOR OCG Chapter 4 — CEF Load Balancing",
      expert_trap_warning: null,
      tags: ["cef", "load-balancing", "polarization", "ecmp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "cef-004",
      type: "single_choice",
      domain: "1.0",
      subdomain: "CEF",
      difficulty: 1,
      prompt: "Which switching method does IOS-XE use by default on hardware-forwarding platforms?",
      options: [
        { id: "A", text: "Process switching", is_correct: false },
        { id: "B", text: "Fast switching (route cache)", is_correct: false },
        { id: "C", text: "CEF (Cisco Express Forwarding)", is_correct: true },
        { id: "D", text: "Optimized switching", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "CEF is the default and preferred switching method on all modern IOS-XE platforms. It pre-builds the FIB and adjacency table from the routing table and ARP cache, enabling hardware-rate forwarding. Process switching (every packet hits CPU) and fast switching (route cache) are legacy methods. On hardware platforms like Catalyst 9000, CEF typically cannot be disabled.",
      cisco_reference: "ENCOR OCG Chapter 4 — Switching Mechanisms",
      expert_trap_warning: null,
      tags: ["cef", "ios-xe", "switching"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "cef-005",
      type: "multi_choice",
      domain: "1.0",
      subdomain: "CEF",
      difficulty: 3,
      prompt: "Which TWO methods can mitigate CEF polarization in a multi-tier network? (Choose 2)",
      options: [
        { id: "A", text: "Configure unique universal IDs on each switch using 'ip cef load-sharing algorithm universal'", is_correct: true },
        { id: "B", text: "Disable CEF and use fast switching instead", is_correct: false },
        { id: "C", text: "Alternate between source-based and destination-based hashing at each tier", is_correct: true },
        { id: "D", text: "Enable per-packet load balancing on all switches", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "CEF polarization is mitigated by ensuring different switches produce different hash results. Method 1: unique universal IDs add a per-switch salt to the hash algorithm. Method 2: alternating hash input types at each tier produces different path selections. Disabling CEF would cripple performance. Per-packet load balancing causes out-of-order delivery and is not recommended.",
      cisco_reference: "ENCOR OCG Chapter 4 — CEF Load Sharing",
      expert_trap_warning: "Per-packet load balancing does distribute across links but causes out-of-order packets that break many applications — it is not the recommended mitigation for polarization.",
      tags: ["cef", "polarization", "load-balancing", "ecmp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- TCAM ---

    {
      id: "tcam-001",
      type: "single_choice",
      domain: "1.0",
      subdomain: "TCAM",
      difficulty: 1,
      prompt: "What is the primary advantage of TCAM over standard RAM for ACL processing?",
      options: [
        { id: "A", text: "TCAM is cheaper to manufacture than standard RAM", is_correct: false },
        { id: "B", text: "TCAM can perform lookups in a single clock cycle regardless of table size", is_correct: true },
        { id: "C", text: "TCAM supports larger table sizes than standard RAM", is_correct: false },
        { id: "D", text: "TCAM consumes less power than standard RAM", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "TCAM (Ternary Content-Addressable Memory) performs parallel lookups across all entries simultaneously, delivering results in a single clock cycle — O(1) lookup time. Standard RAM requires sequential or indexed searching. This makes TCAM ideal for ACL, QoS, and routing lookups at line rate. The trade-off: TCAM is expensive and power-hungry compared to standard RAM.",
      cisco_reference: "ENCOR OCG Chapter 3 — TCAM and CAM",
      expert_trap_warning: null,
      tags: ["tcam", "hardware-forwarding", "acl"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "tcam-002",
      type: "single_choice",
      domain: "1.0",
      subdomain: "TCAM",
      difficulty: 2,
      prompt: "What distinguishes TCAM from binary CAM (Content-Addressable Memory)?",
      options: [
        { id: "A", text: "TCAM is faster than CAM", is_correct: false },
        { id: "B", text: "TCAM supports a third 'don't care' (X) state in addition to 0 and 1", is_correct: true },
        { id: "C", text: "TCAM can store more entries than CAM", is_correct: false },
        { id: "D", text: "TCAM is used for MAC lookups while CAM is used for IP lookups", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Binary CAM matches only on 0 and 1 (exact match), making it suitable for MAC address lookups. TCAM adds a third state — 'don't care' (X) — which enables wildcard/mask matching. This is essential for ACLs (where you match on partial fields) and longest-prefix-match routing lookups. Both perform single-cycle lookups, but only TCAM supports partial matching.",
      cisco_reference: "ENCOR OCG Chapter 3 — CAM and TCAM",
      expert_trap_warning: "The answer is NOT that TCAM is 'faster' — both CAM and TCAM perform single-cycle lookups. The key difference is the third state enabling mask/wildcard matching.",
      tags: ["tcam", "cam", "ternary"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "tcam-003",
      type: "single_choice",
      domain: "1.0",
      subdomain: "TCAM",
      difficulty: 2,
      prompt: "A network administrator needs to allocate more TCAM space for routing entries on a Catalyst switch. What must be configured?",
      options: [
        { id: "A", text: "Increase the TCAM hardware module", is_correct: false },
        { id: "B", text: "Change the SDM template and reload the switch", is_correct: true },
        { id: "C", text: "Add additional supervisor modules", is_correct: false },
        { id: "D", text: "Enable TCAM expansion mode in global config", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "SDM (Switching Database Manager) templates control how TCAM resources are partitioned among routing, ACLs, QoS, and other features. Changing the template (e.g., 'sdm prefer routing') reallocates TCAM space. A reload is required for the change to take effect. There is no 'TCAM expansion mode' or way to add TCAM hardware post-purchase.",
      cisco_reference: "ENCOR OCG Chapter 3 — SDM Templates",
      expert_trap_warning: null,
      tags: ["tcam", "sdm-template", "catalyst"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "tcam-004",
      type: "multi_choice",
      domain: "1.0",
      subdomain: "TCAM",
      difficulty: 2,
      prompt: "Which THREE functions use TCAM for hardware-rate processing on a Catalyst switch? (Choose 3)",
      options: [
        { id: "A", text: "ACL evaluation", is_correct: true },
        { id: "B", text: "QoS classification and marking", is_correct: true },
        { id: "C", text: "Spanning Tree BPDU generation", is_correct: false },
        { id: "D", text: "IP routing (longest prefix match)", is_correct: true }
      ],
      correct_answer: ["A", "B", "D"],
      required_selections: 3,
      drag_items: null,
      drop_targets: null,
      explanation: "TCAM is used for any function that requires pattern matching at line rate: ACL evaluation (match fields with wildcards), QoS classification (match and mark packets), and IP routing (longest prefix match). STP BPDU generation is a control-plane function handled by the CPU, not a data-plane forwarding lookup.",
      cisco_reference: "ENCOR OCG Chapter 3 — TCAM Usage",
      expert_trap_warning: null,
      tags: ["tcam", "acl", "qos", "routing"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- Enterprise Design ---

    {
      id: "arch-026",
      type: "single_choice",
      domain: "1.0",
      subdomain: "EnterpriseDesign",
      difficulty: 1,
      prompt: "In a traditional three-tier campus design, which layer provides high-speed backbone transport between distribution blocks?",
      options: [
        { id: "A", text: "Access layer", is_correct: false },
        { id: "B", text: "Distribution layer", is_correct: false },
        { id: "C", text: "Core layer", is_correct: true },
        { id: "D", text: "Aggregation layer", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The core layer is the high-speed backbone that interconnects distribution blocks. Its primary design goals are speed and reliability — it should not perform complex policy processing, filtering, or manipulation. The distribution layer handles policy and inter-VLAN routing. The access layer connects endpoints.",
      cisco_reference: "ENCOR OCG Chapter 1 — Enterprise Campus Design",
      expert_trap_warning: null,
      tags: ["enterprise-design", "three-tier", "core"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "arch-027",
      type: "single_choice",
      domain: "1.0",
      subdomain: "EnterpriseDesign",
      difficulty: 2,
      prompt: "When is a collapsed core (two-tier) design recommended over a three-tier design?",
      options: [
        { id: "A", text: "When the campus has more than 10,000 endpoints", is_correct: false },
        { id: "B", text: "When the campus has fewer than 3-4 distribution blocks", is_correct: true },
        { id: "C", text: "When the campus requires sub-millisecond convergence", is_correct: false },
        { id: "D", text: "When the campus spans multiple geographic locations", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "A collapsed core merges the core and distribution into a single tier. It is appropriate for small-to-medium campuses with fewer than 3-4 distribution blocks. A dedicated core layer becomes necessary as the number of distribution blocks grows, because full-mesh interconnections between distribution switches become unmanageable. Large or multi-site campuses need a proper three-tier design.",
      cisco_reference: "ENCOR OCG Chapter 1 — Collapsed Core",
      expert_trap_warning: null,
      tags: ["enterprise-design", "collapsed-core", "two-tier"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "arch-028",
      type: "single_choice",
      domain: "1.0",
      subdomain: "EnterpriseDesign",
      difficulty: 2,
      prompt: "In a spine-leaf data center topology, how many hops separate any two servers?",
      options: [
        { id: "A", text: "1 hop", is_correct: false },
        { id: "B", text: "2 hops (leaf-spine-leaf)", is_correct: true },
        { id: "C", text: "3 hops (leaf-spine-core-spine-leaf)", is_correct: false },
        { id: "D", text: "Variable, depending on placement", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In a spine-leaf (Clos) topology, every leaf connects to every spine and there are no leaf-to-leaf or spine-to-spine links. Any server-to-server path crosses exactly one leaf, one spine, and one leaf — 2 network hops. This provides predictable, uniform latency regardless of which servers are communicating, unlike traditional three-tier designs where hop count varies.",
      cisco_reference: "ENCOR OCG Chapter 1 — Data Center Topologies",
      expert_trap_warning: null,
      tags: ["spine-leaf", "data-center", "latency"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "arch-029",
      type: "drag_drop",
      domain: "1.0",
      subdomain: "EnterpriseDesign",
      difficulty: 2,
      prompt: "Match each campus design layer to its primary function.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "Access Layer" },
        { id: "item2", text: "Distribution Layer" },
        { id: "item3", text: "Core Layer" },
        { id: "item4", text: "Spine-Leaf Fabric" }
      ],
      drop_targets: [
        { id: "target1", label: "Endpoint connectivity, VLANs, PoE, port security", accepts: "item1" },
        { id: "target2", label: "Policy enforcement, inter-VLAN routing, route summarization", accepts: "item2" },
        { id: "target3", label: "High-speed backbone transport with minimal processing", accepts: "item3" },
        { id: "target4", label: "Predictable 2-hop latency with full ECMP in data center", accepts: "item4" }
      ],
      explanation: "The access layer connects endpoints and provides Layer 2 services. The distribution layer enforces policies and routes between VLANs. The core layer provides fast transport between distribution blocks without complex processing. Spine-leaf provides uniform 2-hop paths with ECMP across all spines, designed for east-west data center traffic.",
      cisco_reference: "ENCOR OCG Chapter 1 — Enterprise and Data Center Design",
      expert_trap_warning: null,
      tags: ["enterprise-design", "three-tier", "spine-leaf"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- StackWise ---

    {
      id: "arch-030",
      type: "single_choice",
      domain: "1.0",
      subdomain: "StackWise",
      difficulty: 2,
      prompt: "What mechanism prevents a split-brain condition when the StackWise Virtual Link (SVL) fails between two chassis?",
      options: [
        { id: "A", text: "HSRP preemption", is_correct: false },
        { id: "B", text: "Dual-Active Detection (DAD)", is_correct: true },
        { id: "C", text: "VRRP failover", is_correct: false },
        { id: "D", text: "BFD session monitoring", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Dual-Active Detection (DAD) is a dedicated link between the two StackWise Virtual chassis that detects split-brain scenarios. If the SVL fails, both chassis could become active simultaneously. The DAD link detects this and forces the standby chassis to error-disable its interfaces, preventing duplicate IP addresses and MAC addresses on the network. DAD can use a direct Ethernet link or Enhanced PAgP over downstream switches.",
      cisco_reference: "ENCOR OCG Chapter 5 — StackWise Virtual",
      expert_trap_warning: null,
      tags: ["stackwise-virtual", "dual-active-detection", "split-brain"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "arch-031",
      type: "single_choice",
      domain: "1.0",
      subdomain: "StackWise",
      difficulty: 1,
      prompt: "What is the key benefit of StackWise Virtual compared to running two independent distribution switches with HSRP?",
      options: [
        { id: "A", text: "StackWise Virtual supports more VLANs", is_correct: false },
        { id: "B", text: "StackWise Virtual eliminates the need for physical connections between switches", is_correct: false },
        { id: "C", text: "StackWise Virtual creates a single logical switch, enabling multi-chassis EtherChannel and eliminating STP between the pair", is_correct: true },
        { id: "D", text: "StackWise Virtual provides faster routing convergence through EIGRP optimization", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "StackWise Virtual combines two physical switches into a single logical switch with one management plane and control plane. This enables Multi-chassis EtherChannel (MEC) from downstream switches, which uses all uplinks actively (no STP blocking). With HSRP, one link is typically STP-blocked, wasting bandwidth. SVL also simplifies management since there is only one config to maintain.",
      cisco_reference: "ENCOR OCG Chapter 5 — StackWise Virtual Benefits",
      expert_trap_warning: null,
      tags: ["stackwise-virtual", "mec", "etherchannel"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- SD-Access (deeper) ---

    {
      id: "sda-006",
      type: "single_choice",
      domain: "1.0",
      subdomain: "SD-Access",
      difficulty: 2,
      prompt: "In SD-Access, which segmentation method uses SGTs (Scalable Group Tags) to control traffic within a Virtual Network?",
      options: [
        { id: "A", text: "Macro-segmentation", is_correct: false },
        { id: "B", text: "Micro-segmentation", is_correct: true },
        { id: "C", text: "VLAN-based segmentation", is_correct: false },
        { id: "D", text: "VRF-based segmentation", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Micro-segmentation uses SGTs (Scalable Group Tags) and SGACLs to provide fine-grained access control WITHIN a Virtual Network. Macro-segmentation (answer A/D) isolates traffic at the VN level using VRFs — traffic between VNs is blocked by default. SGTs allow policies like 'developers can reach dev servers but not HR servers' within the same VN.",
      cisco_reference: "ENCOR OCG Chapter 16 — SD-Access Segmentation",
      expert_trap_warning: "VRF-based segmentation IS macro-segmentation, just described differently. The exam may present both terms.",
      tags: ["sd-access", "micro-segmentation", "sgt", "sgacl"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sda-007",
      type: "single_choice",
      domain: "1.0",
      subdomain: "SD-Access",
      difficulty: 2,
      prompt: "Which device in the SD-Access architecture acts as the policy engine for authentication and SGT assignment?",
      options: [
        { id: "A", text: "DNA Center", is_correct: false },
        { id: "B", text: "vManage", is_correct: false },
        { id: "C", text: "ISE (Identity Services Engine)", is_correct: true },
        { id: "D", text: "Fabric Border Node", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "ISE is the policy engine in SD-Access. It authenticates endpoints (802.1X, MAB, Web Auth), assigns them to a Virtual Network and SGT based on identity and posture, and defines SGACL policies. DNA Center is the orchestration/automation platform that designs and deploys the fabric, but ISE handles the runtime identity and policy decisions.",
      cisco_reference: "ENCOR OCG Chapter 16 — ISE Integration",
      expert_trap_warning: "DNA Center designs and deploys the fabric; ISE makes runtime policy decisions. The exam tests this distinction.",
      tags: ["sd-access", "ise", "authentication", "sgt"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sda-008",
      type: "multi_choice",
      domain: "1.0",
      subdomain: "SD-Access",
      difficulty: 3,
      prompt: "Which TWO protocols are used in the SD-Access overlay for control plane and data plane functions? (Choose 2)",
      options: [
        { id: "A", text: "LISP for the control plane (EID-to-RLOC mapping)", is_correct: true },
        { id: "B", text: "OSPF for the control plane (overlay routing)", is_correct: false },
        { id: "C", text: "VXLAN for the data plane (traffic encapsulation)", is_correct: true },
        { id: "D", text: "MPLS for the data plane (label switching)", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "SD-Access uses LISP for the control plane (mapping endpoint EIDs to infrastructure RLOCs via Map-Server/Map-Resolver) and VXLAN for the data plane (MAC-in-UDP encapsulation of overlay traffic across the IP underlay). The underlay uses IS-IS for routing, not OSPF. MPLS is not used in SD-Access fabric.",
      cisco_reference: "ENCOR OCG Chapter 16 — SD-Access Planes",
      expert_trap_warning: null,
      tags: ["sd-access", "lisp", "vxlan", "overlay"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sda-009",
      type: "single_choice",
      domain: "1.0",
      subdomain: "SD-Access",
      difficulty: 2,
      prompt: "Which routing protocol does DNA Center configure for the SD-Access underlay by default?",
      options: [
        { id: "A", text: "OSPF", is_correct: false },
        { id: "B", text: "EIGRP", is_correct: false },
        { id: "C", text: "IS-IS", is_correct: true },
        { id: "D", text: "BGP", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DNA Center automates the SD-Access underlay using IS-IS as the IGP. IS-IS was chosen because it runs directly over Layer 2 (not dependent on IP), is protocol-agnostic, scales well, and has simple configuration. The underlay provides IP reachability between all fabric node loopback addresses, which serve as RLOC endpoints for the LISP/VXLAN overlay.",
      cisco_reference: "ENCOR OCG Chapter 16 — SD-Access Underlay",
      expert_trap_warning: null,
      tags: ["sd-access", "underlay", "is-is", "dna-center"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sda-010",
      type: "single_choice",
      domain: "1.0",
      subdomain: "SD-Access",
      difficulty: 3,
      prompt: "An SD-Access network uses two Virtual Networks: Corporate and Guest. A user on the Corporate VN needs to access a shared DNS server. How should this be implemented?",
      options: [
        { id: "A", text: "Place the DNS server in both VNs simultaneously", is_correct: false },
        { id: "B", text: "Use a fusion router at the border to leak DNS routes between VNs", is_correct: true },
        { id: "C", text: "Disable macro-segmentation for DNS traffic", is_correct: false },
        { id: "D", text: "Configure a static LISP mapping for the DNS server in both VNs", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Virtual Networks are isolated by design (separate VRFs). A fusion router sits outside the fabric at the border node and provides controlled route leaking between VNs. It imports routes from both VRFs and selectively leaks the DNS server routes, typically with a firewall in the path for policy enforcement. Placing a server in multiple VNs is not standard practice. You cannot selectively 'disable' macro-segmentation for one protocol.",
      cisco_reference: "ENCOR OCG Chapter 16 — Shared Services and Fusion Router",
      expert_trap_warning: "The fusion router is external to the fabric — it connects at the border node. It is not a fabric role itself.",
      tags: ["sd-access", "fusion-router", "shared-services", "route-leaking"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- SD-WAN (deeper) ---

    {
      id: "sdwan-006",
      type: "single_choice",
      domain: "1.0",
      subdomain: "SD-WAN",
      difficulty: 2,
      prompt: "In Cisco SD-WAN, what does OMP advertise to WAN Edge routers?",
      options: [
        { id: "A", text: "Only IP prefixes and next-hops", is_correct: false },
        { id: "B", text: "vRoutes, TLOCs, and service routes", is_correct: true },
        { id: "C", text: "OSPF LSAs converted to OMP format", is_correct: false },
        { id: "D", text: "BGP NLRI with extended communities", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "OMP (Overlay Management Protocol) advertises three types of information: vRoutes (prefixes with TLOC bindings), TLOCs (Transport Locators identifying WAN interfaces by system-ip + color + encap), and service routes (for service chaining through firewalls or IDS). OMP is conceptually similar to BGP but is specific to the SD-WAN overlay.",
      cisco_reference: "ENCOR OCG Chapter 15 — OMP Route Types",
      expert_trap_warning: null,
      tags: ["sd-wan", "omp", "vroute", "tloc", "service-route"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sdwan-007",
      type: "single_choice",
      domain: "1.0",
      subdomain: "SD-WAN",
      difficulty: 2,
      prompt: "Which SD-WAN component applies control policies that manipulate OMP route advertisements?",
      options: [
        { id: "A", text: "vManage", is_correct: false },
        { id: "B", text: "vBond", is_correct: false },
        { id: "C", text: "vSmart", is_correct: true },
        { id: "D", text: "WAN Edge router", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "vSmart controllers apply control policies that manipulate OMP route advertisements — they can filter, modify, or prefer specific routes/TLOCs before distributing them to WAN Edge routers. Data policies (which match on packet fields and affect forwarding) are pushed to WAN Edge routers. vManage is where policies are configured but vSmart enforces control policies. vBond handles orchestration only.",
      cisco_reference: "ENCOR OCG Chapter 15 — SD-WAN Policy Framework",
      expert_trap_warning: "Control policies live on vSmart; data policies are pushed to WAN Edge routers. Both are configured via vManage, but they execute in different places.",
      tags: ["sd-wan", "vsmart", "control-policy", "omp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sdwan-008",
      type: "single_choice",
      domain: "1.0",
      subdomain: "SD-WAN",
      difficulty: 2,
      prompt: "How does SD-WAN app-aware routing detect transport quality issues?",
      options: [
        { id: "A", text: "SNMP polling of interface counters", is_correct: false },
        { id: "B", text: "IP SLA probes configured manually on each router", is_correct: false },
        { id: "C", text: "BFD probes that measure latency, jitter, and loss on each tunnel", is_correct: true },
        { id: "D", text: "NetFlow export analysis on vManage", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "App-aware routing uses BFD (Bidirectional Forwarding Detection) probes between WAN Edge routers on each IPsec tunnel. These probes continuously measure latency, jitter, and packet loss. When a tunnel violates the SLA thresholds defined in an app-aware routing policy, traffic is automatically moved to a tunnel that meets the SLA. This is automatic — no manual IP SLA configuration needed.",
      cisco_reference: "ENCOR OCG Chapter 15 — App-Aware Routing",
      expert_trap_warning: null,
      tags: ["sd-wan", "app-aware-routing", "bfd", "sla"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sdwan-009",
      type: "drag_drop",
      domain: "1.0",
      subdomain: "SD-WAN",
      difficulty: 2,
      prompt: "Match each SD-WAN component to its primary function.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "vManage" },
        { id: "item2", text: "vSmart" },
        { id: "item3", text: "vBond" },
        { id: "item4", text: "WAN Edge" }
      ],
      drop_targets: [
        { id: "target1", label: "Centralized GUI/API management, template provisioning, monitoring", accepts: "item1" },
        { id: "target2", label: "Centralized routing policy, OMP route distribution to edges", accepts: "item2" },
        { id: "target3", label: "Initial authentication, NAT traversal, controller discovery", accepts: "item3" },
        { id: "target4", label: "Data-plane forwarding, IPsec tunnels, local policy enforcement", accepts: "item4" }
      ],
      explanation: "vManage is the management plane (GUI, API, templates). vSmart is the control plane (OMP, policies). vBond is the orchestration plane (authentication, NAT traversal, initial bootstrap). WAN Edge routers are the data plane (IPsec tunnels, packet forwarding, local/data policy enforcement). Each component has a distinct role in the SD-WAN architecture.",
      cisco_reference: "ENCOR OCG Chapter 15 — SD-WAN Architecture",
      expert_trap_warning: null,
      tags: ["sd-wan", "vmanage", "vsmart", "vbond", "wan-edge"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sdwan-010",
      type: "single_choice",
      domain: "1.0",
      subdomain: "SD-WAN",
      difficulty: 3,
      prompt: "A TLOC in Cisco SD-WAN is uniquely identified by which combination of attributes?",
      options: [
        { id: "A", text: "Hostname + interface name + IP address", is_correct: false },
        { id: "B", text: "System IP + color + encapsulation type", is_correct: true },
        { id: "C", text: "Site ID + VPN ID + interface name", is_correct: false },
        { id: "D", text: "Organization name + serial number + WAN IP", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "A TLOC (Transport Locator) uniquely identifies a WAN Edge transport interface using three attributes: system-ip (the router's loopback identifier), color (the transport type — e.g., mpls, biz-internet, lte), and encapsulation type (IPsec or GRE). vSmart uses TLOCs to resolve where OMP vRoutes can be reached, enabling the data plane tunnel mesh between WAN Edge routers.",
      cisco_reference: "ENCOR OCG Chapter 15 — TLOCs",
      expert_trap_warning: null,
      tags: ["sd-wan", "tloc", "system-ip", "color"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- QoS (deeper) ---

    {
      id: "qos-006",
      type: "single_choice",
      domain: "1.0",
      subdomain: "QoS",
      difficulty: 1,
      prompt: "What DSCP value is assigned to EF (Expedited Forwarding), and what type of traffic typically uses it?",
      options: [
        { id: "A", text: "DSCP 34, video conferencing", is_correct: false },
        { id: "B", text: "DSCP 46, voice bearer (RTP)", is_correct: true },
        { id: "C", text: "DSCP 48, network control", is_correct: false },
        { id: "D", text: "DSCP 26, transactional data", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "EF (Expedited Forwarding) is DSCP 46 (binary 101110). It is designated for voice bearer traffic (RTP media streams) because voice requires consistently low latency, low jitter, and low loss. EF traffic is placed in the strict priority queue (LLQ). DSCP 34 is AF41 (video), DSCP 48 is CS6 (network control), DSCP 26 is AF31.",
      cisco_reference: "ENCOR OCG Chapter 10 — DSCP Values",
      expert_trap_warning: null,
      tags: ["qos", "dscp", "ef", "voice"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "qos-007",
      type: "single_choice",
      domain: "1.0",
      subdomain: "QoS",
      difficulty: 2,
      prompt: "What is the key difference between QoS policing and shaping?",
      options: [
        { id: "A", text: "Policing buffers excess traffic; shaping drops it", is_correct: false },
        { id: "B", text: "Policing drops or re-marks excess traffic immediately; shaping buffers it for later transmission", is_correct: true },
        { id: "C", text: "Policing works only inbound; shaping works only inbound", is_correct: false },
        { id: "D", text: "There is no difference; they are interchangeable terms", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Policing drops or re-marks packets that exceed the configured rate — there is no buffering, so the output is bursty. It can be applied inbound or outbound. Shaping buffers excess traffic and releases it at a controlled rate — this smooths the output but introduces delay. Shaping can only be applied outbound. Shaping is preferred when connecting to a provider's committed information rate (CIR).",
      cisco_reference: "ENCOR OCG Chapter 10 — Policing vs Shaping",
      expert_trap_warning: "A common trap: policing can be applied inbound OR outbound, but shaping is OUTBOUND only. Also, policing does not always drop — it can re-mark excess traffic to a lower DSCP.",
      tags: ["qos", "policing", "shaping"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "qos-008",
      type: "single_choice",
      domain: "1.0",
      subdomain: "QoS",
      difficulty: 2,
      prompt: "What problem does WRED solve that tail drop does not?",
      options: [
        { id: "A", text: "WRED provides faster queue processing", is_correct: false },
        { id: "B", text: "WRED prevents TCP global synchronization by randomly dropping before the queue is full", is_correct: true },
        { id: "C", text: "WRED eliminates all packet drops during congestion", is_correct: false },
        { id: "D", text: "WRED increases the maximum queue depth", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Tail drop discards all packets once a queue is full, causing all TCP flows to back off simultaneously (global synchronization), then ramp up together, creating oscillating congestion. WRED randomly drops packets BEFORE the queue is full, based on average queue depth and DSCP/precedence. Different flows experience drops at different times, preventing synchronization. WRED does not eliminate drops — it makes them more intelligent.",
      cisco_reference: "ENCOR OCG Chapter 10 — WRED and Congestion Avoidance",
      expert_trap_warning: null,
      tags: ["qos", "wred", "tail-drop", "tcp-synchronization"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "qos-009",
      type: "multi_choice",
      domain: "1.0",
      subdomain: "QoS",
      difficulty: 2,
      prompt: "Which TWO statements about CoS (Class of Service) and DSCP are correct? (Choose 2)",
      options: [
        { id: "A", text: "CoS is a 3-bit field in the 802.1Q tag and only exists on trunk links", is_correct: true },
        { id: "B", text: "DSCP is a 3-bit field in the IP header", is_correct: false },
        { id: "C", text: "DSCP persists across Layer 3 hops, while CoS is lost when a frame is routed", is_correct: true },
        { id: "D", text: "CoS provides more granularity than DSCP because it has 8 classes", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "CoS is a 3-bit field (0-7) in the 802.1Q VLAN tag — it only exists on tagged (trunk) frames and is stripped when routed. DSCP is a 6-bit field (0-63) in the IP header ToS byte — it persists end-to-end through L3 hops. DSCP has MORE granularity (64 values vs 8) than CoS, not less. Best practice: map CoS to DSCP at the access switch.",
      cisco_reference: "ENCOR OCG Chapter 10 — CoS and DSCP",
      expert_trap_warning: null,
      tags: ["qos", "cos", "dscp", "802.1q"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "qos-010",
      type: "single_choice",
      domain: "1.0",
      subdomain: "QoS",
      difficulty: 3,
      prompt: "In LLQ (Low Latency Queuing), what prevents the priority queue from starving other traffic classes?",
      options: [
        { id: "A", text: "A round-robin scheduler shares time between priority and non-priority queues", is_correct: false },
        { id: "B", text: "A policer limits the priority queue to its configured bandwidth; excess is dropped", is_correct: true },
        { id: "C", text: "The priority queue is rate-limited by the interface clock speed", is_correct: false },
        { id: "D", text: "Non-priority queues have a minimum guaranteed service interval", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "LLQ provides a strict priority queue that is always serviced first — without a limit, it could consume all bandwidth. To prevent starvation, a built-in policer caps the priority queue at its configured bandwidth (e.g., 'priority 256' = 256 Kbps). Traffic exceeding this rate is DROPPED (not queued or re-classified). This ensures other CBWFQ classes receive their guaranteed bandwidth.",
      cisco_reference: "ENCOR OCG Chapter 10 — LLQ Priority Queuing",
      expert_trap_warning: "The key trap: excess priority traffic is DROPPED, not shaped or re-queued. This is why voice bandwidth must be carefully planned — oversubscribing the priority queue causes voice packet drops.",
      tags: ["qos", "llq", "priority-queue", "policer", "starvation"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "qos-011",
      type: "drag_drop",
      domain: "1.0",
      subdomain: "QoS",
      difficulty: 3,
      prompt: "Match each QoS mechanism to its function.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "Classification" },
        { id: "item2", text: "Marking" },
        { id: "item3", text: "Queuing (LLQ/CBWFQ)" },
        { id: "item4", text: "WRED" },
        { id: "item5", text: "Shaping" }
      ],
      drop_targets: [
        { id: "target1", label: "Identify and categorize traffic using ACLs, NBAR, or DSCP values", accepts: "item1" },
        { id: "target2", label: "Write a QoS value (DSCP/CoS) into the packet/frame header", accepts: "item2" },
        { id: "target3", label: "Assign traffic to queues with guaranteed bandwidth and strict priority", accepts: "item3" },
        { id: "target4", label: "Randomly drop packets before queue is full to prevent TCP global sync", accepts: "item4" },
        { id: "target5", label: "Buffer excess traffic and send at a smoothed rate to match downstream CIR", accepts: "item5" }
      ],
      explanation: "Classification identifies traffic (read-only). Marking writes QoS values for downstream use. Queuing (LLQ/CBWFQ) assigns bandwidth guarantees and priority treatment. WRED drops packets early to avoid tail drop and TCP synchronization. Shaping buffers and meters output to a configured rate. These mechanisms work together in the QoS policy chain.",
      cisco_reference: "ENCOR OCG Chapter 10 — QoS Mechanisms",
      expert_trap_warning: null,
      tags: ["qos", "classification", "marking", "queuing", "wred", "shaping"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- High Availability ---

    {
      id: "ha-006",
      type: "single_choice",
      domain: "1.0",
      subdomain: "HighAvailability",
      difficulty: 2,
      prompt: "What is the relationship between NSF and SSO?",
      options: [
        { id: "A", text: "NSF and SSO are the same feature with different names", is_correct: false },
        { id: "B", text: "NSF depends on SSO — SSO syncs state to the standby while NSF keeps the data plane forwarding during switchover", is_correct: true },
        { id: "C", text: "SSO depends on NSF — NSF must be enabled before SSO can function", is_correct: false },
        { id: "D", text: "NSF handles Layer 2 failover and SSO handles Layer 3 failover", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "SSO (Stateful Switchover) continuously synchronizes control-plane state (interface states, L2 protocols, CEF tables) to the standby supervisor. NSF (Non-Stop Forwarding) relies on SSO to maintain the FIB on the standby and continues forwarding packets during the switchover using the pre-synced CEF state. NSF requires SSO as a prerequisite — without SSO, the standby has no state to forward with.",
      cisco_reference: "ENCOR OCG Chapter 5 — NSF/SSO",
      expert_trap_warning: null,
      tags: ["high-availability", "nsf", "sso"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ha-007",
      type: "single_choice",
      domain: "1.0",
      subdomain: "HighAvailability",
      difficulty: 1,
      prompt: "What is the primary advantage of BFD over relying on routing protocol hello/dead timers for failure detection?",
      options: [
        { id: "A", text: "BFD supports more routing protocols", is_correct: false },
        { id: "B", text: "BFD detects failures in milliseconds, much faster than routing protocol timers", is_correct: true },
        { id: "C", text: "BFD does not require any configuration", is_correct: false },
        { id: "D", text: "BFD eliminates the need for routing protocols entirely", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "BFD provides sub-second failure detection (typically 150ms with 50ms intervals x 3 multiplier) compared to routing protocol dead timers (OSPF default 40s, EIGRP 15s). BFD runs in the data plane (often hardware-assisted) and notifies the routing protocol when a neighbor is down, triggering immediate reconvergence. It works alongside routing protocols, not as a replacement.",
      cisco_reference: "ENCOR OCG Chapter 5 — BFD",
      expert_trap_warning: null,
      tags: ["bfd", "high-availability", "convergence"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ha-008",
      type: "multi_choice",
      domain: "1.0",
      subdomain: "HighAvailability",
      difficulty: 3,
      prompt: "Which THREE are prerequisites for ISSU (In-Service Software Upgrade) on a Catalyst chassis? (Choose 3)",
      options: [
        { id: "A", text: "Dual supervisor modules", is_correct: true },
        { id: "B", text: "SSO mode enabled", is_correct: true },
        { id: "C", text: "StackWise cables between chassis", is_correct: false },
        { id: "D", text: "NSF enabled", is_correct: true }
      ],
      correct_answer: ["A", "B", "D"],
      required_selections: 3,
      drag_items: null,
      drop_targets: null,
      explanation: "ISSU requires: (1) Dual supervisors so one can be upgraded while the other runs, (2) SSO to synchronize state to the standby, and (3) NSF to maintain forwarding during the switchover. The process upgrades the standby sup, performs SSO switchover, then upgrades the former active. StackWise cables are for access-layer stacking and are not an ISSU prerequisite for chassis platforms.",
      cisco_reference: "ENCOR OCG Chapter 5 — ISSU Prerequisites",
      expert_trap_warning: "ISSU also requires compatible IOS-XE version pairs — not all versions support ISSU between them. This is often overlooked in planning.",
      tags: ["high-availability", "issu", "sso", "nsf"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ha-009",
      type: "single_choice",
      domain: "1.0",
      subdomain: "HighAvailability",
      difficulty: 2,
      prompt: "During an NSF restart, what does the restarting router's OSPF neighbor (the 'helper') do?",
      options: [
        { id: "A", text: "Immediately tears down the adjacency and re-establishes it", is_correct: false },
        { id: "B", text: "Floods a new LSA indicating the restarting router is down", is_correct: false },
        { id: "C", text: "Continues to use existing routes through the restarting router during the grace period", is_correct: true },
        { id: "D", text: "Switches to a backup path and ignores the restarting router", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "When an NSF-capable router restarts, it sends a grace LSA to its OSPF neighbors. The NSF-aware helper neighbor continues advertising the restarting router's LSAs and forwarding traffic through it during the grace period, rather than tearing down the adjacency. The restarting router's data plane (CEF) continues forwarding using the pre-existing FIB. Once the control plane recovers, full adjacency is re-synchronized.",
      cisco_reference: "ENCOR OCG Chapter 5 — OSPF Graceful Restart",
      expert_trap_warning: null,
      tags: ["high-availability", "nsf", "ospf", "graceful-restart"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ha-010",
      type: "single_choice",
      domain: "1.0",
      subdomain: "HighAvailability",
      difficulty: 1,
      prompt: "Which HA feature synchronizes interface states, Layer 2 protocols, and forwarding tables to a standby supervisor?",
      options: [
        { id: "A", text: "NSF (Non-Stop Forwarding)", is_correct: false },
        { id: "B", text: "SSO (Stateful Switchover)", is_correct: true },
        { id: "C", text: "ISSU (In-Service Software Upgrade)", is_correct: false },
        { id: "D", text: "GR (Graceful Restart)", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "SSO (Stateful Switchover) keeps the standby supervisor in hot-standby mode by continuously synchronizing interface states, L2 protocols (STP, LACP), line card states, and CEF tables. This enables sub-second failover when the active supervisor fails. NSF uses the state that SSO syncs to keep forwarding. ISSU uses SSO/NSF for hitless upgrades. GR is the protocol-level extension.",
      cisco_reference: "ENCOR OCG Chapter 5 — SSO",
      expert_trap_warning: null,
      tags: ["high-availability", "sso", "supervisor"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // DOMAIN 2.0 — VIRTUALIZATION (~18 questions)
    // =========================================================

    // --- VRF ---

    {
      id: "vrf-006",
      type: "single_choice",
      domain: "2.0",
      subdomain: "VRF",
      difficulty: 1,
      prompt: "What happens to the IP address on an interface when you apply 'vrf forwarding CUSTOMER-A' to it?",
      options: [
        { id: "A", text: "The IP address is retained and moved to the VRF routing table", is_correct: false },
        { id: "B", text: "The IP address is removed and must be re-configured", is_correct: true },
        { id: "C", text: "The IP address is duplicated in both global and VRF tables", is_correct: false },
        { id: "D", text: "The interface is shut down automatically", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "When you assign a VRF to an interface using 'vrf forwarding <name>', IOS-XE removes the existing IP address configuration from the interface. You must re-apply the IP address after the VRF assignment. This is a well-known exam trap and a common operational mistake. The interface remains up/up but has no IP address until re-configured.",
      cisco_reference: "ENCOR OCG Chapter 8 — VRF Configuration",
      expert_trap_warning: "This is one of the most commonly tested VRF gotchas. The IP address is silently removed — there is no confirmation prompt.",
      tags: ["vrf", "configuration", "exam-trap"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "vrf-007",
      type: "single_choice",
      domain: "2.0",
      subdomain: "VRF",
      difficulty: 2,
      prompt: "In MPLS VPN, what is the purpose of the Route Distinguisher (RD)?",
      options: [
        { id: "A", text: "To determine which VRF a route should be imported into", is_correct: false },
        { id: "B", text: "To make IPv4 prefixes unique in the BGP table by creating VPNv4 addresses", is_correct: true },
        { id: "C", text: "To encrypt routes as they traverse the MPLS backbone", is_correct: false },
        { id: "D", text: "To assign MPLS labels to routes for forwarding", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The RD (Route Distinguisher) is a 64-bit value prepended to an IPv4 prefix to create a globally unique VPNv4 address (e.g., 65000:1:10.1.1.0/24). This prevents collisions when multiple customers use the same private IP space. The RD does NOT control import/export — that is the job of Route Targets (RT). The RD is locally significant to the PE router.",
      cisco_reference: "ENCOR OCG Chapter 8 — Route Distinguisher",
      expert_trap_warning: "RD makes routes unique; RT controls import/export policy. This is the most frequently confused pair on the exam. RD is NOT used for route import decisions.",
      tags: ["vrf", "route-distinguisher", "mpls", "vpnv4"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "vrf-008",
      type: "single_choice",
      domain: "2.0",
      subdomain: "VRF",
      difficulty: 3,
      prompt: "A network uses VRF-Lite with three VRFs across four routers. How many sub-interfaces are needed between each pair of routers?",
      options: [
        { id: "A", text: "One — VRF-Lite multiplexes all VRFs on a single sub-interface", is_correct: false },
        { id: "B", text: "Two — one for data and one for control plane", is_correct: false },
        { id: "C", text: "Three — one sub-interface per VRF", is_correct: true },
        { id: "D", text: "Four — one per VRF plus one for management", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "VRF-Lite does not use MPLS labels, so each VRF requires its own sub-interface (or physical interface) between each router pair to maintain isolation. With 3 VRFs, you need 3 sub-interfaces per link. This is the primary scalability limitation of VRF-Lite compared to full MPLS VPN, where a single link between PE routers carries all VRFs using labels.",
      cisco_reference: "ENCOR OCG Chapter 8 — VRF-Lite Scalability",
      expert_trap_warning: "This scalability issue is exactly why full MPLS VPN with MP-BGP exists. VRF-Lite works for small deployments but becomes unmanageable with many VRFs.",
      tags: ["vrf", "vrf-lite", "sub-interfaces", "scalability"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "vrf-009",
      type: "single_choice",
      domain: "2.0",
      subdomain: "VRF",
      difficulty: 2,
      prompt: "How can a shared DNS server be made accessible from multiple VRFs without full inter-VRF routing?",
      options: [
        { id: "A", text: "Place the DNS server in every VRF simultaneously", is_correct: false },
        { id: "B", text: "Use route leaking to selectively share only the DNS server route between VRFs", is_correct: true },
        { id: "C", text: "Disable VRF isolation for DNS traffic using an ACL", is_correct: false },
        { id: "D", text: "Configure NAT to translate DNS traffic between VRFs", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Route leaking allows selective sharing of specific routes between VRFs. You can leak only the DNS server's /32 route into each VRF using static routes pointing to the other VRF's next-hop, or via MP-BGP with export/import Route Targets and a route-map filter. This provides shared services access without opening full connectivity between VRFs.",
      cisco_reference: "ENCOR OCG Chapter 8 — VRF Route Leaking",
      expert_trap_warning: null,
      tags: ["vrf", "route-leaking", "shared-services", "dns"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- GRE ---

    {
      id: "gre-004",
      type: "single_choice",
      domain: "2.0",
      subdomain: "GRE",
      difficulty: 2,
      prompt: "What is GRE recursive routing and what causes it?",
      options: [
        { id: "A", text: "GRE packets being re-encapsulated multiple times due to a routing loop", is_correct: false },
        { id: "B", text: "The tunnel destination being reachable only through the tunnel itself, creating a routing loop", is_correct: true },
        { id: "C", text: "GRE headers being duplicated when traversing multiple tunnel interfaces", is_correct: false },
        { id: "D", text: "A mismatch between the tunnel source and destination IP addresses", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Recursive routing occurs when the route to the GRE tunnel destination is learned through the tunnel itself. The router tries to encapsulate the packet, but the best route to the tunnel endpoint goes through the tunnel, creating an infinite loop. This causes the tunnel interface to flap. Prevention: use a static route for the tunnel destination via a physical next-hop, or place tunnel endpoint routes in a separate VRF.",
      cisco_reference: "ENCOR OCG Chapter 8 — GRE Tunnel Issues",
      expert_trap_warning: null,
      tags: ["gre", "recursive-routing", "troubleshooting"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "gre-005",
      type: "single_choice",
      domain: "2.0",
      subdomain: "GRE",
      difficulty: 2,
      prompt: "What is the primary advantage of GRE over IPsec for tunnel encapsulation?",
      options: [
        { id: "A", text: "GRE provides encryption while IPsec does not", is_correct: false },
        { id: "B", text: "GRE can encapsulate multicast, broadcast, and non-IP protocols; native IPsec cannot", is_correct: true },
        { id: "C", text: "GRE has less overhead than IPsec", is_correct: false },
        { id: "D", text: "GRE provides authentication while IPsec does not", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "GRE is protocol-agnostic — it can carry multicast, broadcast, and non-IP protocols (like OSPF hellos, EIGRP, IS-IS). Native IPsec (tunnel mode) only transports unicast IP. This is why 'GRE over IPsec' is commonly used: GRE provides the multi-protocol transport and IPsec provides encryption. GRE itself provides no encryption or authentication.",
      cisco_reference: "ENCOR OCG Chapter 8 — GRE vs IPsec",
      expert_trap_warning: null,
      tags: ["gre", "ipsec", "multicast", "encapsulation"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "gre-006",
      type: "single_choice",
      domain: "2.0",
      subdomain: "GRE",
      difficulty: 3,
      prompt: "A GRE tunnel has been configured over an IPsec VPN. Users report that large file transfers fail but small pings work. What is the most likely cause?",
      options: [
        { id: "A", text: "The GRE tunnel source address is incorrect", is_correct: false },
        { id: "B", text: "The DF bit is set and the MTU has not been adjusted for GRE+IPsec overhead, causing fragmentation issues", is_correct: true },
        { id: "C", text: "The IPsec SA has expired and needs rekeying", is_correct: false },
        { id: "D", text: "GRE over IPsec does not support TCP traffic", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "GRE adds 24 bytes and IPsec adds ~52-73 bytes of overhead, reducing the effective MTU to ~1400 bytes. If the DF (Don't Fragment) bit is set and packets exceed the tunnel MTU, they are dropped with ICMP 'fragmentation needed' messages — which are often blocked by firewalls. Small pings work because they fit within the reduced MTU. Fix: set 'ip mtu 1400' on the tunnel interface and 'ip tcp adjust-mss 1360' to clamp TCP MSS.",
      cisco_reference: "ENCOR OCG Chapter 8 — GRE MTU and Fragmentation",
      expert_trap_warning: "This is a classic troubleshooting scenario. The symptoms (small packets work, large fail) always point to MTU/fragmentation issues.",
      tags: ["gre", "ipsec", "mtu", "fragmentation", "troubleshooting"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- DMVPN ---

    {
      id: "dmvpn-011",
      type: "single_choice",
      domain: "2.0",
      subdomain: "DMVPN",
      difficulty: 2,
      prompt: "In DMVPN, what protocol resolves the NBMA (public IP) address of a remote spoke for spoke-to-spoke communication?",
      options: [
        { id: "A", text: "ARP", is_correct: false },
        { id: "B", text: "DNS", is_correct: false },
        { id: "C", text: "NHRP (Next Hop Resolution Protocol)", is_correct: true },
        { id: "D", text: "OSPF", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "NHRP (Next Hop Resolution Protocol) maps tunnel IP addresses to NBMA (public/underlay) IP addresses. Spokes register their mapping with the hub (NHS). When a spoke needs to communicate with another spoke, it sends an NHRP resolution request to learn the remote spoke's public IP, enabling a direct mGRE tunnel. ARP resolves L2 addresses, not NBMA-to-tunnel mappings.",
      cisco_reference: "ENCOR OCG Chapter 8 — NHRP in DMVPN",
      expert_trap_warning: null,
      tags: ["dmvpn", "nhrp", "spoke-to-spoke"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "dmvpn-012",
      type: "single_choice",
      domain: "2.0",
      subdomain: "DMVPN",
      difficulty: 3,
      prompt: "Why does route summarization at the hub break spoke-to-spoke traffic in DMVPN Phase 2 but not Phase 3?",
      options: [
        { id: "A", text: "Phase 2 does not support NHRP at all", is_correct: false },
        { id: "B", text: "In Phase 2, the routing table next-hop must be the remote spoke's tunnel IP; summarization changes it to the hub", is_correct: true },
        { id: "C", text: "Phase 3 uses a different encapsulation that supports summarization", is_correct: false },
        { id: "D", text: "Phase 2 uses static routing that cannot be summarized", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In Phase 2, spoke-to-spoke traffic relies on the routing table showing the remote spoke's tunnel IP as the next-hop. This triggers NHRP resolution for the spoke's public IP. When the hub summarizes routes, the next-hop becomes the hub's tunnel IP, forcing all traffic through the hub. In Phase 3, NHRP redirects and shortcuts override the routing table — the hub sends an NHRP redirect message, and the spoke builds a shortcut directly to the remote spoke regardless of the routing next-hop.",
      cisco_reference: "ENCOR OCG Chapter 8 — DMVPN Phase Comparison",
      expert_trap_warning: "This is a high-frequency ENCOR question. Understand that Phase 3's NHRP redirect/shortcut mechanism decouples routing from tunnel resolution.",
      tags: ["dmvpn", "phase2", "phase3", "summarization", "nhrp-redirect"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "dmvpn-013",
      type: "multi_choice",
      domain: "2.0",
      subdomain: "DMVPN",
      difficulty: 2,
      prompt: "Which TWO statements correctly describe DMVPN Phase 1? (Choose 2)",
      options: [
        { id: "A", text: "All traffic flows hub-and-spoke; no direct spoke-to-spoke tunnels are built", is_correct: true },
        { id: "B", text: "Spokes use point-to-point GRE (not mGRE) tunnel interfaces", is_correct: false },
        { id: "C", text: "The hub uses mGRE to accept connections from multiple spokes", is_correct: true },
        { id: "D", text: "NHRP is not used in Phase 1", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "In Phase 1, all traffic transits the hub — there are no direct spoke-to-spoke tunnels. The hub uses mGRE to accept connections from multiple spokes dynamically. Spokes CAN use either mGRE or p2p GRE in Phase 1 (both work, though mGRE is more common for future Phase 2/3 migration). NHRP IS used in Phase 1 for spoke registration with the hub; it just is not used for spoke-to-spoke resolution.",
      cisco_reference: "ENCOR OCG Chapter 8 — DMVPN Phase 1",
      expert_trap_warning: "Spokes CAN use mGRE in Phase 1 — the answer says the hub uses mGRE (true), not that spokes must use p2p GRE.",
      tags: ["dmvpn", "phase1", "hub-and-spoke", "mgre"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "dmvpn-014",
      type: "drag_drop",
      domain: "2.0",
      subdomain: "DMVPN",
      difficulty: 3,
      prompt: "Match each DMVPN phase to its spoke-to-spoke traffic behavior.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "Phase 1" },
        { id: "item2", text: "Phase 2" },
        { id: "item3", text: "Phase 3" }
      ],
      drop_targets: [
        { id: "target1", label: "All traffic through hub; no spoke-to-spoke tunnels possible", accepts: "item1" },
        { id: "target2", label: "Spoke-to-spoke via NHRP resolution; hub must preserve next-hop (no summarization)", accepts: "item2" },
        { id: "target3", label: "Spoke-to-spoke via NHRP redirect/shortcut; hub can summarize routes", accepts: "item3" }
      ],
      explanation: "Phase 1: hub-and-spoke only. Phase 2: spoke-to-spoke through NHRP resolution, but the routing table must show the remote spoke as next-hop (no summarization at hub). Phase 3: spoke-to-spoke via NHRP redirect messages from the hub and NHRP shortcut routes that override the routing table, allowing full summarization at the hub.",
      cisco_reference: "ENCOR OCG Chapter 8 — DMVPN Phases",
      expert_trap_warning: null,
      tags: ["dmvpn", "phase1", "phase2", "phase3"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- LISP ---

    {
      id: "lisp-003",
      type: "single_choice",
      domain: "2.0",
      subdomain: "LISP",
      difficulty: 2,
      prompt: "In LISP, what is the role of the Map-Server?",
      options: [
        { id: "A", text: "Encapsulates packets from non-LISP sources to LISP destinations", is_correct: false },
        { id: "B", text: "Accepts and stores EID-to-RLOC registrations from ETRs", is_correct: true },
        { id: "C", text: "Performs data-plane encapsulation for all LISP traffic", is_correct: false },
        { id: "D", text: "Resolves DNS names to LISP EID addresses", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The Map-Server accepts EID-to-RLOC Map-Register messages from ETRs and stores them in the mapping database. When an ITR needs to resolve an EID, it sends a Map-Request to the Map-Resolver, which forwards it to the Map-Server (or directly to the authoritative ETR). The Map-Server/Map-Resolver often runs on the same device. Data-plane encapsulation is performed by ITRs and ETRs, not the Map-Server.",
      cisco_reference: "ENCOR OCG Chapter 8 — LISP Components",
      expert_trap_warning: null,
      tags: ["lisp", "map-server", "eid", "rloc"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "lisp-004",
      type: "single_choice",
      domain: "2.0",
      subdomain: "LISP",
      difficulty: 2,
      prompt: "What is the purpose of a PxTR (Proxy Tunnel Router) in LISP?",
      options: [
        { id: "A", text: "To provide redundancy for the Map-Server", is_correct: false },
        { id: "B", text: "To enable communication between LISP sites and non-LISP sites", is_correct: true },
        { id: "C", text: "To perform route aggregation for LISP EID prefixes", is_correct: false },
        { id: "D", text: "To encrypt LISP data-plane traffic", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "A PxTR combines PITR and PETR functions. The PITR advertises LISP EID prefixes into the non-LISP internet (e.g., via BGP) to attract traffic destined for LISP sites, then encapsulates it toward the ETR. The PETR accepts LISP-encapsulated traffic from ITRs that need to reach non-LISP destinations and forwards it de-encapsulated into the internet. This provides interworking between LISP and non-LISP domains.",
      cisco_reference: "ENCOR OCG Chapter 8 — LISP Interworking",
      expert_trap_warning: null,
      tags: ["lisp", "pxtr", "pitr", "petr", "interworking"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "lisp-005",
      type: "drag_drop",
      domain: "2.0",
      subdomain: "LISP",
      difficulty: 2,
      prompt: "Match each LISP component to its function.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "ITR (Ingress Tunnel Router)" },
        { id: "item2", text: "ETR (Egress Tunnel Router)" },
        { id: "item3", text: "Map-Server" },
        { id: "item4", text: "Map-Resolver" }
      ],
      drop_targets: [
        { id: "target1", label: "Encapsulates non-LISP packets in LISP header and forwards to destination RLOC", accepts: "item1" },
        { id: "target2", label: "Decapsulates LISP packets and delivers to local EID endpoints", accepts: "item2" },
        { id: "target3", label: "Stores EID-to-RLOC registrations received from ETRs", accepts: "item3" },
        { id: "target4", label: "Receives Map-Requests from ITRs and forwards to appropriate Map-Server/ETR", accepts: "item4" }
      ],
      explanation: "ITR receives packets from sources, looks up the EID in the mapping system, and encapsulates toward the RLOC. ETR decapsulates and delivers to the endpoint. Map-Server stores registrations from ETRs. Map-Resolver handles resolution requests from ITRs. Often MS and MR run on the same device, and a single router can be both an ITR and ETR (called an xTR).",
      cisco_reference: "ENCOR OCG Chapter 8 — LISP Architecture",
      expert_trap_warning: null,
      tags: ["lisp", "itr", "etr", "map-server", "map-resolver"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- VXLAN ---

    {
      id: "vxlan-004",
      type: "single_choice",
      domain: "2.0",
      subdomain: "VXLAN",
      difficulty: 1,
      prompt: "How many logical network segments can VXLAN support compared to traditional VLANs?",
      options: [
        { id: "A", text: "4,094 (same as VLANs)", is_correct: false },
        { id: "B", text: "65,535 (16-bit identifier)", is_correct: false },
        { id: "C", text: "Approximately 16 million (24-bit VNI)", is_correct: true },
        { id: "D", text: "Unlimited (no identifier limit)", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "VXLAN uses a 24-bit VNI (VXLAN Network Identifier), supporting 2^24 = 16,777,216 logical segments. Traditional 802.1Q VLANs use a 12-bit VLAN ID, limited to 4,094 usable VLANs. This massive increase in segmentation capacity is one of the primary reasons VXLAN was developed for multi-tenant data centers.",
      cisco_reference: "ENCOR OCG Chapter 8 — VXLAN Overview",
      expert_trap_warning: null,
      tags: ["vxlan", "vni", "scalability", "vlan"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "vxlan-005",
      type: "single_choice",
      domain: "2.0",
      subdomain: "VXLAN",
      difficulty: 2,
      prompt: "What is the primary advantage of using BGP EVPN as a control plane for VXLAN instead of flood-and-learn?",
      options: [
        { id: "A", text: "BGP EVPN requires less CPU on VTEPs", is_correct: false },
        { id: "B", text: "BGP EVPN eliminates unknown unicast flooding by distributing MAC/IP reachability via control plane", is_correct: true },
        { id: "C", text: "BGP EVPN supports larger VNI values than flood-and-learn", is_correct: false },
        { id: "D", text: "BGP EVPN encrypts VXLAN traffic while flood-and-learn does not", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "BGP EVPN distributes MAC and IP address reachability information via MP-BGP (Route Type 2 for MAC/IP, Type 3 for VTEP discovery). This eliminates the need for unknown unicast flooding because VTEPs already know where each MAC lives. It also enables ARP suppression (the VTEP answers ARP locally from its EVPN table) and supports integrated routing and bridging. Flood-and-learn requires multicast in the underlay for BUM traffic.",
      cisco_reference: "ENCOR OCG Chapter 8 — VXLAN with BGP EVPN",
      expert_trap_warning: null,
      tags: ["vxlan", "bgp-evpn", "flood-and-learn", "arp-suppression"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "vxlan-006",
      type: "single_choice",
      domain: "2.0",
      subdomain: "VXLAN",
      difficulty: 2,
      prompt: "What is a VTEP and where does it typically run in a spine-leaf data center?",
      options: [
        { id: "A", text: "Virtual Tunnel Endpoint; runs on spine switches", is_correct: false },
        { id: "B", text: "VXLAN Tunnel Endpoint; runs on leaf switches", is_correct: true },
        { id: "C", text: "Virtual Transport Encryption Point; runs on border routers", is_correct: false },
        { id: "D", text: "VXLAN Termination Endpoint; runs on hypervisors only", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "VTEP (VXLAN Tunnel Endpoint) performs VXLAN encapsulation and decapsulation. In a spine-leaf data center, VTEPs run on leaf switches (hardware VTEPs). Each leaf has a VTEP IP address (typically the loopback) used as the tunnel source/destination. Spine switches are pure L3 transit — they route VXLAN-encapsulated packets but do not perform encap/decap. Software VTEPs can also run on hypervisor vSwitches.",
      cisco_reference: "ENCOR OCG Chapter 8 — VTEP Placement",
      expert_trap_warning: "VTEPs are on LEAF switches, not spines. Spines are the underlay transport — they see VXLAN packets as regular UDP/IP.",
      tags: ["vxlan", "vtep", "spine-leaf", "leaf"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "vxlan-007",
      type: "single_choice",
      domain: "2.0",
      subdomain: "VXLAN",
      difficulty: 3,
      prompt: "What is the total byte overhead added by VXLAN encapsulation, and what is the recommended underlay MTU setting?",
      options: [
        { id: "A", text: "24 bytes; underlay MTU of 1524", is_correct: false },
        { id: "B", text: "50 bytes; underlay MTU of 9216 (jumbo frames)", is_correct: true },
        { id: "C", text: "50 bytes; underlay MTU of 1550", is_correct: false },
        { id: "D", text: "72 bytes; underlay MTU of 9216 (jumbo frames)", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "VXLAN adds 50 bytes: 14-byte outer Ethernet + 20-byte outer IP + 8-byte UDP + 8-byte VXLAN header = 50 bytes. With a standard 1500-byte inner frame, the outer frame is 1550 bytes. While setting underlay MTU to 1550 would technically work, Cisco best practice recommends jumbo frames (9216) on the underlay to handle any inner frame size without fragmentation and to accommodate additional overhead from features like encryption.",
      cisco_reference: "ENCOR OCG Chapter 8 — VXLAN MTU Considerations",
      expert_trap_warning: "The overhead is exactly 50 bytes, not 54. Answer C (MTU 1550) would technically prevent fragmentation but is not the Cisco recommendation — jumbo frames are best practice.",
      tags: ["vxlan", "mtu", "overhead", "jumbo-frames"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // EXPANSION WAVE — DOMAIN 3.0 + 4.0 (Infrastructure + Network Assurance)
    // =========================================================
    // =========================================================
    // DOMAIN 3.0 — INFRASTRUCTURE (~50 questions)
    // =========================================================

    // --- OSPF Advanced ---

    {
      id: "ospf-015",
      type: "single_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 1,
      prompt: "Which OSPF LSA type is generated by the Designated Router (DR) on a multi-access network to list all attached routers?",
      options: [
        { id: "A", text: "Type 1 — Router LSA", is_correct: false },
        { id: "B", text: "Type 2 — Network LSA", is_correct: true },
        { id: "C", text: "Type 3 — Summary LSA", is_correct: false },
        { id: "D", text: "Type 4 — ASBR Summary LSA", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Type 2 (Network LSA) is generated only by the DR on multi-access (broadcast/NBMA) segments. It describes all routers attached to that network segment, enabling the SPF algorithm to build the correct topology for multi-access links.",
      cisco_reference: "ENCOR OCG Chapter 8 — OSPF LSA Types",
      expert_trap_warning: null,
      tags: ["ospf", "lsa", "dr"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ospf-016",
      type: "single_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 2,
      prompt: "An OSPF area is configured as NSSA. An ASBR inside this area redistributes external routes. What LSA type carries these external routes within the NSSA?",
      options: [
        { id: "A", text: "Type 3 — Summary LSA", is_correct: false },
        { id: "B", text: "Type 5 — External LSA", is_correct: false },
        { id: "C", text: "Type 7 — NSSA External LSA", is_correct: true },
        { id: "D", text: "Type 4 — ASBR Summary LSA", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In an NSSA, external routes are carried as Type 7 LSAs instead of Type 5. Type 5 LSAs are blocked by the NSSA. The ABR at the NSSA boundary converts Type 7 LSAs to Type 5 LSAs before flooding them into the backbone area.",
      cisco_reference: "ENCOR OCG Chapter 8 — NSSA Areas",
      expert_trap_warning: "Type 5 LSAs are blocked in all stub area variants including NSSA. The key difference with NSSA is that Type 7 allows local redistribution.",
      tags: ["ospf", "nssa", "type-7-lsa"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ospf-017",
      type: "single_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 2,
      prompt: "A router has three OSPF routes to the same destination: an O IA route, an O E1 route, and an O E2 route. Which route will be installed in the routing table?",
      options: [
        { id: "A", text: "O E1, because it includes internal cost", is_correct: false },
        { id: "B", text: "O E2, because external routes are preferred", is_correct: false },
        { id: "C", text: "O IA, because inter-area routes are preferred over external routes", is_correct: true },
        { id: "D", text: "The route with the lowest total metric, regardless of type", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "OSPF path preference order is: Intra-area (O) > Inter-area (O IA) > External Type 1 (O E1) > External Type 2 (O E2). The O IA route is always preferred over any external route, regardless of metric values. This is a fundamental OSPF design principle.",
      cisco_reference: "ENCOR OCG Chapter 8 — OSPF Path Selection",
      expert_trap_warning: "Many engineers incorrectly assume the lowest-metric route always wins. In OSPF, route TYPE preference takes precedence over metric comparison.",
      tags: ["ospf", "path-selection", "route-preference"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ospf-018",
      type: "single_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 2,
      prompt: "What command configures a totally stubby area on an ABR in OSPF?",
      options: [
        { id: "A", text: "area 10 stub", is_correct: false },
        { id: "B", text: "area 10 stub no-summary", is_correct: true },
        { id: "C", text: "area 10 nssa", is_correct: false },
        { id: "D", text: "area 10 totally-stub", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The 'area X stub no-summary' command on the ABR creates a totally stubby area, blocking both Type 3 (inter-area) and Type 5 (external) LSAs. Only a single default route (Type 3) is injected. The 'no-summary' keyword is only configured on the ABR; other routers use 'area X stub'.",
      cisco_reference: "ENCOR OCG Chapter 8 — OSPF Stub Areas",
      expert_trap_warning: null,
      tags: ["ospf", "stub-areas", "totally-stubby"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ospf-019",
      type: "multi_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 2,
      prompt: "Which TWO statements about OSPF virtual links are correct?",
      options: [
        { id: "A", text: "A virtual link can traverse a stub area", is_correct: false },
        { id: "B", text: "A virtual link connects a disconnected area to Area 0 through a transit area", is_correct: true },
        { id: "C", text: "A virtual link is configured on both ABRs using the remote router's Router ID", is_correct: true },
        { id: "D", text: "A virtual link uses the OSPF process ID for identification", is_correct: false }
      ],
      correct_answer: ["B", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Virtual links connect a disconnected area to Area 0 by tunneling through a transit area. They are configured on both ABRs using 'area <transit-area> virtual-link <remote-router-id>'. A virtual link CANNOT traverse a stub area (any variant), and it uses the Router ID (not process ID) for identification.",
      cisco_reference: "ENCOR OCG Chapter 8 — OSPF Virtual Links",
      expert_trap_warning: null,
      tags: ["ospf", "virtual-link", "area-0"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ospf-020",
      type: "single_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 3,
      prompt: "In OSPFv3, how is authentication handled differently from OSPFv2?",
      options: [
        { id: "A", text: "OSPFv3 uses the same MD5 authentication fields in the OSPF header", is_correct: false },
        { id: "B", text: "OSPFv3 uses IPsec (AH or ESP) for authentication instead of OSPF-native authentication", is_correct: true },
        { id: "C", text: "OSPFv3 does not support any form of authentication", is_correct: false },
        { id: "D", text: "OSPFv3 uses HMAC-SHA-256 in the OSPF header, replacing MD5", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "OSPFv3 removed the authentication fields from the OSPF header entirely. Instead, it relies on IPv6's built-in IPsec framework (AH for authentication, ESP for authentication + encryption). This provides stronger security and a cleaner protocol design.",
      cisco_reference: "ENCOR OCG Chapter 8 — OSPFv3",
      expert_trap_warning: null,
      tags: ["ospf", "ospfv3", "ipsec", "authentication"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ospf-021",
      type: "drag_drop",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 2,
      prompt: "Match each OSPF area type to the LSA types it blocks.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "Stub Area" },
        { id: "item2", text: "Totally Stubby Area" },
        { id: "item3", text: "NSSA" },
        { id: "item4", text: "Totally NSSA" }
      ],
      drop_targets: [
        { id: "target1", label: "Blocks Type 4 and Type 5; allows Type 3", accepts: "item1" },
        { id: "target2", label: "Blocks Type 3 (except default), Type 4, and Type 5", accepts: "item2" },
        { id: "target3", label: "Blocks Type 4 and Type 5; allows Type 3 and Type 7", accepts: "item3" },
        { id: "target4", label: "Blocks Type 3 (except default), Type 4, Type 5; allows Type 7", accepts: "item4" }
      ],
      explanation: "All stub variants block Type 5 (and Type 4 which describes ASBR reachability). 'Totally' variants additionally block Type 3 (except a default route). NSSA variants allow Type 7 LSAs for local external redistribution. Types 1 and 2 are always present in all area types.",
      cisco_reference: "ENCOR OCG Chapter 8 — OSPF Area Types",
      expert_trap_warning: null,
      tags: ["ospf", "area-types", "lsa-types"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ospf-022",
      type: "single_choice",
      domain: "3.0",
      subdomain: "OSPF",
      difficulty: 1,
      prompt: "What is the OSPF default cost formula for an interface?",
      options: [
        { id: "A", text: "Reference bandwidth / Interface bandwidth (10^8 / BW)", is_correct: true },
        { id: "B", text: "Interface bandwidth / Reference bandwidth", is_correct: false },
        { id: "C", text: "256 × (Bandwidth + Delay)", is_correct: false },
        { id: "D", text: "Hop count to destination", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "OSPF cost = Reference Bandwidth / Interface Bandwidth. The default reference bandwidth is 100 Mbps (10^8 bps). For a 100 Mbps interface, cost = 1. For 10 Mbps, cost = 10. For interfaces faster than 100 Mbps (GigE, 10G), the cost is also 1 unless you increase the reference with 'auto-cost reference-bandwidth'.",
      cisco_reference: "ENCOR OCG Chapter 8 — OSPF Cost Calculation",
      expert_trap_warning: null,
      tags: ["ospf", "cost", "metric"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- EIGRP Advanced ---

    {
      id: "eigrp-008",
      type: "single_choice",
      domain: "3.0",
      subdomain: "EIGRP",
      difficulty: 2,
      prompt: "In EIGRP, what must be true for a neighbor to qualify as a feasible successor?",
      options: [
        { id: "A", text: "The neighbor's feasible distance must be less than the successor's feasible distance", is_correct: false },
        { id: "B", text: "The neighbor's reported distance must be less than the current feasible distance", is_correct: true },
        { id: "C", text: "The neighbor must have the same AS number and K-values", is_correct: false },
        { id: "D", text: "The neighbor must have a lower metric than the current successor", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The feasibility condition states: a neighbor qualifies as a feasible successor if its Reported Distance (RD) — the neighbor's total metric to the destination — is strictly less than the current Feasible Distance (FD) — the successor's total metric. This guarantees the neighbor is downstream and prevents routing loops.",
      cisco_reference: "ENCOR OCG Chapter 7 — EIGRP DUAL",
      expert_trap_warning: "The comparison is RD < FD, not RD < successor's RD. The feasible distance is the metric through the successor, not just the successor's reported distance.",
      tags: ["eigrp", "dual", "feasibility-condition"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "eigrp-009",
      type: "single_choice",
      domain: "3.0",
      subdomain: "EIGRP",
      difficulty: 2,
      prompt: "What happens when an EIGRP route transitions to Active state?",
      options: [
        { id: "A", text: "The route is immediately removed from the routing table", is_correct: false },
        { id: "B", text: "The router sends Query packets to all EIGRP neighbors for that destination", is_correct: true },
        { id: "C", text: "The router installs the feasible successor as the new best path", is_correct: false },
        { id: "D", text: "The router resets all neighbor adjacencies to re-learn topology", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "When the successor is lost and no feasible successor meets the feasibility condition, the route goes Active. The router sends DUAL Query packets to all neighbors (except the failed path). Once all Replies are received, the best path is selected and the route returns to Passive state. If replies aren't received within 3 minutes, the route goes SIA.",
      cisco_reference: "ENCOR OCG Chapter 7 — EIGRP Active/Passive States",
      expert_trap_warning: null,
      tags: ["eigrp", "dual", "active-state", "query"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "eigrp-010",
      type: "single_choice",
      domain: "3.0",
      subdomain: "EIGRP",
      difficulty: 3,
      prompt: "Why are EIGRP wide metrics needed in named mode?",
      options: [
        { id: "A", text: "Classic EIGRP metrics cannot differentiate interfaces faster than 10 Gbps", is_correct: true },
        { id: "B", text: "Wide metrics support IPv6 address families only", is_correct: false },
        { id: "C", text: "Wide metrics use 128-bit values for better precision", is_correct: false },
        { id: "D", text: "Classic metrics do not support unequal-cost load balancing", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Classic EIGRP uses 32-bit metrics with bandwidth scaled as 10^7/BW_kbps. All interfaces at 10 Gbps or above calculate to the same bandwidth value (1), making them indistinguishable. Wide metrics use 64-bit calculations (scaled by 65,536) with picosecond delay, allowing differentiation of 10G, 40G, and 100G+ links. A rib-scale divisor (default 128) fits the result into the 32-bit RIB.",
      cisco_reference: "ENCOR OCG Chapter 7 — EIGRP Named Mode",
      expert_trap_warning: null,
      tags: ["eigrp", "wide-metrics", "named-mode"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "eigrp-011",
      type: "multi_choice",
      domain: "3.0",
      subdomain: "EIGRP",
      difficulty: 2,
      prompt: "Which TWO methods help prevent EIGRP stuck-in-active (SIA) conditions?",
      options: [
        { id: "A", text: "Configuring EIGRP stub routing on spoke routers", is_correct: true },
        { id: "B", text: "Disabling split-horizon on all interfaces", is_correct: false },
        { id: "C", text: "Using route summarization at distribution boundaries", is_correct: true },
        { id: "D", text: "Increasing the hold timer to 60 seconds", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Stub routing prevents queries from being sent to stub routers, dramatically reducing query scope. Route summarization stops query propagation at summary boundaries since the queried specific prefix doesn't exist in the summary router's topology table. Both techniques limit the blast radius of DUAL queries, reducing SIA risk.",
      cisco_reference: "ENCOR OCG Chapter 7 — EIGRP Convergence",
      expert_trap_warning: null,
      tags: ["eigrp", "stuck-in-active", "stub", "summarization"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "eigrp-012",
      type: "single_choice",
      domain: "3.0",
      subdomain: "EIGRP",
      difficulty: 1,
      prompt: "What is the default EIGRP active timer (stuck-in-active timeout)?",
      options: [
        { id: "A", text: "1 minute", is_correct: false },
        { id: "B", text: "3 minutes", is_correct: true },
        { id: "C", text: "5 minutes", is_correct: false },
        { id: "D", text: "10 minutes", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The default active timer is 3 minutes. If a router sends a DUAL query and does not receive a reply from a neighbor within 3 minutes, the neighbor is declared stuck-in-active and the adjacency is reset. SIA-Query messages are sent at the halfway point (90 seconds) to give the neighbor a chance to respond.",
      cisco_reference: "ENCOR OCG Chapter 7 — EIGRP Timers",
      expert_trap_warning: null,
      tags: ["eigrp", "stuck-in-active", "timers"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- BGP Advanced ---

    {
      id: "bgp-014",
      type: "single_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 2,
      prompt: "In the BGP best path selection algorithm, which attribute is evaluated first?",
      options: [
        { id: "A", text: "Local Preference", is_correct: false },
        { id: "B", text: "AS_PATH length", is_correct: false },
        { id: "C", text: "Weight (Cisco proprietary)", is_correct: true },
        { id: "D", text: "MED (Multi-Exit Discriminator)", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The BGP best path selection evaluates Weight first (highest wins). Weight is Cisco-proprietary and local to the router (never advertised). Default weight is 0 for learned routes and 32768 for locally originated routes. After Weight, the order continues: Local Preference → Locally originated → AS_PATH length → Origin type → MED → eBGP over iBGP → IGP metric to next-hop.",
      cisco_reference: "ENCOR OCG Chapter 10 — BGP Best Path Selection",
      expert_trap_warning: "Weight and Local Preference are often confused. Weight is LOCAL only (never advertised); Local Preference is shared within the AS via iBGP.",
      tags: ["bgp", "path-selection", "weight"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "bgp-015",
      type: "single_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 2,
      prompt: "What is the primary purpose of a BGP route reflector?",
      options: [
        { id: "A", text: "To aggregate routes before sending them to eBGP peers", is_correct: false },
        { id: "B", text: "To eliminate the iBGP full-mesh requirement by reflecting routes to clients", is_correct: true },
        { id: "C", text: "To filter routes between iBGP and eBGP peers", is_correct: false },
        { id: "D", text: "To provide faster convergence by caching BGP routes locally", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "A route reflector (RR) relaxes the iBGP full-mesh requirement. Without RRs, every iBGP speaker must peer with every other (n×(n-1)/2 sessions). The RR reflects routes from clients to other clients and non-clients, reducing the number of required peerings. Loop prevention uses Originator ID and Cluster List attributes.",
      cisco_reference: "ENCOR OCG Chapter 10 — BGP Route Reflectors",
      expert_trap_warning: null,
      tags: ["bgp", "route-reflector", "ibgp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "bgp-016",
      type: "single_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 2,
      prompt: "Which BGP community prevents a route from being advertised to any eBGP peer?",
      options: [
        { id: "A", text: "no-advertise", is_correct: false },
        { id: "B", text: "no-export", is_correct: true },
        { id: "C", text: "local-AS", is_correct: false },
        { id: "D", text: "internet", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The 'no-export' community tells receiving routers not to advertise the route to any eBGP peer — it stays within the AS (or confederation sub-AS). 'no-advertise' is even more restrictive — the route is not advertised to ANY peer (iBGP or eBGP). 'local-AS' keeps the route within the local confederation sub-AS.",
      cisco_reference: "ENCOR OCG Chapter 10 — BGP Communities",
      expert_trap_warning: "no-export and no-advertise are frequently confused. no-export = no eBGP advertisement. no-advertise = no advertisement at all (iBGP or eBGP).",
      tags: ["bgp", "communities", "no-export"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "bgp-017",
      type: "single_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 3,
      prompt: "A router receives BGP routes for the same prefix from two eBGP peers. Both routes have the same Weight, Local Preference, and AS_PATH length. Peer A advertises MED 100, Peer B advertises MED 200. Both peers are in different autonomous systems. By default, which route is selected?",
      options: [
        { id: "A", text: "The route from Peer A (MED 100) because lower MED is preferred", is_correct: false },
        { id: "B", text: "The route from Peer B (MED 200) because higher MED is preferred", is_correct: false },
        { id: "C", text: "MED is not compared because the routes are from different ASes by default", is_correct: true },
        { id: "D", text: "Both routes are installed for ECMP load balancing", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "By default, BGP only compares MED values between routes received from the SAME neighboring AS. Since these routes come from different ASes, MED comparison is skipped and the next tiebreaker (eBGP over iBGP, then IGP metric to next-hop, etc.) is evaluated. To force MED comparison across different ASes, configure 'bgp always-compare-med'.",
      cisco_reference: "ENCOR OCG Chapter 10 — BGP MED",
      expert_trap_warning: "This is a classic exam trap. MED is non-transitive and by default only compared within the same AS. The 'bgp always-compare-med' command changes this behavior.",
      tags: ["bgp", "med", "path-selection"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "bgp-018",
      type: "multi_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 3,
      prompt: "Which TWO attributes does a BGP route reflector use to prevent routing loops?",
      options: [
        { id: "A", text: "Originator ID", is_correct: true },
        { id: "B", text: "AS_PATH", is_correct: false },
        { id: "C", text: "Cluster List", is_correct: true },
        { id: "D", text: "Local Preference", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Originator ID is set to the Router ID of the route originator — if a router receives a route with its own RID as Originator ID, it discards it. Cluster List contains the Cluster IDs of all RRs the route has passed through — if an RR sees its own Cluster ID in the list, it discards the route. AS_PATH prevents eBGP loops but not iBGP RR loops.",
      cisco_reference: "ENCOR OCG Chapter 10 — Route Reflector Loop Prevention",
      expert_trap_warning: null,
      tags: ["bgp", "route-reflector", "loop-prevention"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "bgp-019",
      type: "single_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 2,
      prompt: "What is the purpose of BGP confederations?",
      options: [
        { id: "A", text: "To aggregate prefixes across multiple autonomous systems", is_correct: false },
        { id: "B", text: "To subdivide an AS into smaller sub-ASes to reduce the iBGP full-mesh requirement", is_correct: true },
        { id: "C", text: "To provide encrypted tunnels between eBGP peers", is_correct: false },
        { id: "D", text: "To enable BGP multipath load balancing across different ASes", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Confederations divide a single AS into multiple sub-ASes using private AS numbers (64512-65534). Within each sub-AS, a full iBGP mesh (or route reflectors) is needed. Between sub-ASes, eBGP-like sessions are used. External peers see only the confederation's public AS number. AS_CONFED_SEQUENCE/SET in the path are not counted in AS_PATH length by external peers.",
      cisco_reference: "ENCOR OCG Chapter 10 — BGP Confederations",
      expert_trap_warning: null,
      tags: ["bgp", "confederations", "ibgp-scaling"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "bgp-020",
      type: "single_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 1,
      prompt: "What is the default BGP Local Preference value?",
      options: [
        { id: "A", text: "0", is_correct: false },
        { id: "B", text: "32768", is_correct: false },
        { id: "C", text: "100", is_correct: true },
        { id: "D", text: "200", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The default BGP Local Preference is 100. Higher values are preferred. Local Preference is shared among all iBGP peers within the AS and is used to influence outbound path selection for the entire AS. Do not confuse with Weight (default 0 for learned routes, 32768 for locally originated), which is local to the router only.",
      cisco_reference: "ENCOR OCG Chapter 10 — BGP Local Preference",
      expert_trap_warning: null,
      tags: ["bgp", "local-preference"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "bgp-021",
      type: "single_choice",
      domain: "3.0",
      subdomain: "BGP",
      difficulty: 2,
      prompt: "Which command enables a BGP router to re-apply inbound routing policies without resetting the BGP session, using the route-refresh capability?",
      options: [
        { id: "A", text: "clear ip bgp * hard", is_correct: false },
        { id: "B", text: "clear bgp ipv4 unicast <neighbor> soft in", is_correct: true },
        { id: "C", text: "neighbor <IP> reset", is_correct: false },
        { id: "D", text: "neighbor <IP> route-map POLICY in", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The 'clear bgp ipv4 unicast <neighbor> soft in' command triggers a soft reconfiguration using the route-refresh capability (RFC 2918). The router asks the peer to re-send its full Adj-RIB-Out, and then applies the updated inbound policy without tearing down the BGP session. This is preferred over hard reset which destroys the session.",
      cisco_reference: "ENCOR OCG Chapter 10 — BGP Soft Reconfiguration",
      expert_trap_warning: null,
      tags: ["bgp", "soft-reconfiguration", "route-refresh"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- Route Redistribution ---

    {
      id: "redistrib-001",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Redistribution",
      difficulty: 2,
      prompt: "When redistributing OSPF routes into EIGRP, what is the default seed metric if no metric is specified?",
      options: [
        { id: "A", text: "20", is_correct: false },
        { id: "B", text: "1", is_correct: false },
        { id: "C", text: "Infinity (routes are unreachable)", is_correct: true },
        { id: "D", text: "The OSPF cost is automatically converted to EIGRP metric", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "EIGRP assigns a seed metric of infinity to redistributed routes by default, making them unreachable. You MUST explicitly set a metric using 'redistribute ospf 1 metric <BW> <DLY> <REL> <LOAD> <MTU>' or configure 'default-metric' under the EIGRP process. This is one of the most common redistribution mistakes on the exam and in production.",
      cisco_reference: "ENCOR OCG Chapter 11 — Route Redistribution",
      expert_trap_warning: "Forgetting the seed metric for EIGRP is a classic trap. OSPF defaults to metric 20, but EIGRP defaults to infinity. Routes will appear in the topology table but not be usable.",
      tags: ["redistribution", "eigrp", "seed-metric"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "redistrib-002",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Redistribution",
      difficulty: 2,
      prompt: "How do route tags prevent routing loops in mutual redistribution?",
      options: [
        { id: "A", text: "Tags encrypt routes to prevent spoofing between protocols", is_correct: false },
        { id: "B", text: "Tags mark redistributed routes so they can be denied when re-entering the original protocol", is_correct: true },
        { id: "C", text: "Tags set the administrative distance higher for redistributed routes", is_correct: false },
        { id: "D", text: "Tags limit the hop count of redistributed routes", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In mutual redistribution, routes can be re-redistributed back into their source protocol with different metrics, causing loops. By tagging routes during redistribution (e.g., 'set tag 100') and denying tagged routes on the reverse redistribution ('match tag 100' → deny), you prevent routes from being fed back into their original protocol.",
      cisco_reference: "ENCOR OCG Chapter 11 — Redistribution Loop Prevention",
      expert_trap_warning: null,
      tags: ["redistribution", "route-tags", "loop-prevention"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "redistrib-003",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Redistribution",
      difficulty: 2,
      prompt: "When redistributing routes into OSPF, what is the default external route type?",
      options: [
        { id: "A", text: "E1 (Type 1 External)", is_correct: false },
        { id: "B", text: "E2 (Type 2 External)", is_correct: true },
        { id: "C", text: "N1 (NSSA Type 1)", is_correct: false },
        { id: "D", text: "Inter-area (O IA)", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "By default, OSPF redistributes external routes as E2 (Type 2 External). E2 routes have a fixed metric (external cost only) that doesn't increase as the route propagates through OSPF. E1 routes add internal OSPF cost to the external metric, allowing optimal path selection with multiple redistribution points. Change with 'metric-type 1'.",
      cisco_reference: "ENCOR OCG Chapter 11 — OSPF External Routes",
      expert_trap_warning: null,
      tags: ["redistribution", "ospf", "e1-e2"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "redistrib-004",
      type: "multi_choice",
      domain: "3.0",
      subdomain: "Redistribution",
      difficulty: 3,
      prompt: "Which TWO are common pitfalls when performing mutual redistribution between OSPF and EIGRP?",
      options: [
        { id: "A", text: "EIGRP external routes (AD 170) may be less preferred than OSPF routes (AD 110), causing suboptimal routing", is_correct: true },
        { id: "B", text: "OSPF cannot redistribute EIGRP routes because they use different metric types", is_correct: false },
        { id: "C", text: "Routes can be re-redistributed back into their source protocol with different metrics, causing loops", is_correct: true },
        { id: "D", text: "EIGRP automatically tags redistributed routes, causing conflicts", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Administrative distance mismatches cause suboptimal routing: OSPF internal/external (AD 110) is preferred over EIGRP external (AD 170), potentially pulling traffic toward OSPF even when EIGRP internal is better. Re-redistribution loops occur when routes cycle back into their source protocol with modified metrics. Tags, route-maps, and AD tuning mitigate these issues.",
      cisco_reference: "ENCOR OCG Chapter 11 — Mutual Redistribution",
      expert_trap_warning: "AD comparison is one of the subtlest redistribution issues. EIGRP internal (90) < OSPF (110) < EIGRP external (170). This asymmetry causes unexpected path selection.",
      tags: ["redistribution", "mutual-redistribution", "pitfalls"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- Route Filtering ---

    {
      id: "preflist-001",
      type: "single_choice",
      domain: "3.0",
      subdomain: "RouteFiltering",
      difficulty: 1,
      prompt: "Which command matches all subnets of 10.0.0.0/8 with a prefix length between /16 and /24?",
      options: [
        { id: "A", text: "ip prefix-list PL permit 10.0.0.0/8", is_correct: false },
        { id: "B", text: "ip prefix-list PL permit 10.0.0.0/8 ge 16 le 24", is_correct: true },
        { id: "C", text: "ip prefix-list PL permit 10.0.0.0/8 le 24", is_correct: false },
        { id: "D", text: "ip prefix-list PL permit 0.0.0.0/0 ge 16 le 24", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The prefix-list entry '10.0.0.0/8 ge 16 le 24' matches any prefix within the 10.0.0.0/8 range that has a prefix length of /16 to /24. Without 'ge 16', option C would match 10.0.0.0/8 through /24 including /8 itself. Option D would match ANY prefix with /16-/24 length, not just 10.x.x.x.",
      cisco_reference: "ENCOR OCG Chapter 11 — Prefix Lists",
      expert_trap_warning: null,
      tags: ["route-filtering", "prefix-list"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "preflist-002",
      type: "single_choice",
      domain: "3.0",
      subdomain: "RouteFiltering",
      difficulty: 2,
      prompt: "In OSPF, what is the effect of applying a distribute-list to filter inbound routes?",
      options: [
        { id: "A", text: "It prevents filtered LSAs from being flooded to other routers", is_correct: false },
        { id: "B", text: "It only affects what is installed in the local routing table; the LSDB remains complete", is_correct: true },
        { id: "C", text: "It removes the filtered routes from the LSDB on the local router", is_correct: false },
        { id: "D", text: "It causes the router to send LSA withdrawals for the filtered routes", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In OSPF, distribute-lists filter inbound only and only affect what enters the local routing table. The LSDB remains complete because OSPF is a link-state protocol that requires all routers in an area to have identical LSDBs. To truly filter inter-area routes, use 'area X filter-list prefix' on an ABR.",
      cisco_reference: "ENCOR OCG Chapter 11 — OSPF Route Filtering",
      expert_trap_warning: "This is a fundamental difference from distance-vector protocols where distribute-lists affect what is sent/received from neighbors. OSPF distribute-lists cannot prevent LSA flooding.",
      tags: ["route-filtering", "ospf", "distribute-list"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "preflist-003",
      type: "single_choice",
      domain: "3.0",
      subdomain: "RouteFiltering",
      difficulty: 2,
      prompt: "A route-map applied to a BGP neighbor has three clauses: seq 10 (permit, match prefix-list A), seq 20 (permit, match prefix-list B, set local-preference 200), and no explicit final clause. What happens to routes not matching prefix-list A or B?",
      options: [
        { id: "A", text: "They are permitted with default attributes", is_correct: false },
        { id: "B", text: "They are denied by the implicit deny at the end of the route-map", is_correct: true },
        { id: "C", text: "They are sent to the next BGP neighbor for evaluation", is_correct: false },
        { id: "D", text: "They are permitted but with local-preference set to 0", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Route-maps have an implicit deny at the end, just like ACLs. Any routes not matching any permit clause are denied (filtered). If you want unmatched routes to pass through, you must add a final 'route-map <name> permit <seq>' clause with no match statement, which permits everything else.",
      cisco_reference: "ENCOR OCG Chapter 11 — Route Maps",
      expert_trap_warning: "The implicit deny catches many engineers off guard. Always add a final permit clause if you want non-matching routes to pass through.",
      tags: ["route-filtering", "route-maps", "bgp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- STP Advanced ---

    {
      id: "stp-007",
      type: "single_choice",
      domain: "3.0",
      subdomain: "STP",
      difficulty: 1,
      prompt: "How many port states does RSTP (802.1w) define, compared to 802.1D's five states?",
      options: [
        { id: "A", text: "Two: forwarding and blocking", is_correct: false },
        { id: "B", text: "Three: discarding, learning, and forwarding", is_correct: true },
        { id: "C", text: "Four: blocking, listening, learning, and forwarding", is_correct: false },
        { id: "D", text: "Five: same as 802.1D but with faster timers", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "RSTP reduces the port states from 5 (disabled, blocking, listening, learning, forwarding) to 3: Discarding (combines disabled, blocking, and listening), Learning, and Forwarding. RSTP achieves faster convergence through the proposal/agreement mechanism rather than timer-based transitions.",
      cisco_reference: "ENCOR OCG Chapter 4 — RSTP",
      expert_trap_warning: null,
      tags: ["stp", "rstp", "port-states"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "stp-008",
      type: "single_choice",
      domain: "3.0",
      subdomain: "STP",
      difficulty: 2,
      prompt: "What happens when a port with BPDU Guard enabled receives a BPDU?",
      options: [
        { id: "A", text: "The port ignores the BPDU and continues forwarding", is_correct: false },
        { id: "B", text: "The port transitions to root-inconsistent state", is_correct: false },
        { id: "C", text: "The port is placed in err-disabled state", is_correct: true },
        { id: "D", text: "The port transitions to blocking state", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "BPDU Guard shuts down the port (err-disable) immediately upon receiving any BPDU. This is a hard protection designed for PortFast-enabled access ports where no switch should ever be connected. Recovery requires manual intervention ('shutdown' then 'no shutdown') or automatic recovery via 'errdisable recovery cause bpduguard'.",
      cisco_reference: "ENCOR OCG Chapter 4 — STP Protection Mechanisms",
      expert_trap_warning: null,
      tags: ["stp", "bpdu-guard", "err-disable"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "stp-009",
      type: "multi_choice",
      domain: "3.0",
      subdomain: "STP",
      difficulty: 2,
      prompt: "Which TWO statements about MST (Multiple Spanning Tree) regions are correct?",
      options: [
        { id: "A", text: "Switches in the same MST region must share the same name, revision number, and VLAN-to-instance mapping", is_correct: true },
        { id: "B", text: "MST supports a maximum of 64 spanning tree instances", is_correct: false },
        { id: "C", text: "IST (Instance 0) handles BPDUs between MST regions", is_correct: true },
        { id: "D", text: "MST requires all switches to run the same IOS version", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "MST region membership is determined by three attributes: region name, revision number, and VLAN-to-instance mapping table — all three must match exactly. The IST (Internal Spanning Tree, Instance 0) is always present and handles BPDU exchange between different MST regions. MST supports up to 16 instances (0-15), not 64.",
      cisco_reference: "ENCOR OCG Chapter 4 — MST",
      expert_trap_warning: null,
      tags: ["stp", "mst", "ist"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "stp-010",
      type: "single_choice",
      domain: "3.0",
      subdomain: "STP",
      difficulty: 2,
      prompt: "Which STP protection feature prevents a port from transitioning to forwarding state if BPDUs stop arriving on a non-designated port?",
      options: [
        { id: "A", text: "BPDU Guard", is_correct: false },
        { id: "B", text: "Root Guard", is_correct: false },
        { id: "C", text: "Loop Guard", is_correct: true },
        { id: "D", text: "BPDU Filter", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Loop Guard prevents a root or alternate port from transitioning to designated forwarding state when BPDUs stop arriving (e.g., due to a unidirectional link failure). Without Loop Guard, a blocking port that stops receiving BPDUs would eventually transition to forwarding, potentially causing a Layer 2 loop. Loop Guard places the port in loop-inconsistent state instead.",
      cisco_reference: "ENCOR OCG Chapter 4 — STP Loop Guard",
      expert_trap_warning: "Loop Guard and Root Guard cannot be enabled on the same port. Loop Guard works on non-designated ports; Root Guard works on designated ports.",
      tags: ["stp", "loop-guard", "udld"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "stp-011",
      type: "drag_drop",
      domain: "3.0",
      subdomain: "STP",
      difficulty: 2,
      prompt: "Match each STP protection feature to its function.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "BPDU Guard" },
        { id: "item2", text: "Root Guard" },
        { id: "item3", text: "Loop Guard" },
        { id: "item4", text: "UDLD Aggressive" }
      ],
      drop_targets: [
        { id: "target1", label: "Err-disables a PortFast port that receives any BPDU", accepts: "item1" },
        { id: "target2", label: "Puts a designated port into root-inconsistent state if a superior BPDU is received", accepts: "item2" },
        { id: "target3", label: "Puts a non-designated port into loop-inconsistent state when BPDUs stop arriving", accepts: "item3" },
        { id: "target4", label: "Err-disables a port after detecting a unidirectional link at Layer 1", accepts: "item4" }
      ],
      explanation: "BPDU Guard protects access ports from rogue switches (err-disable). Root Guard protects root bridge placement on designated ports (root-inconsistent). Loop Guard prevents forwarding on non-designated ports when BPDUs stop (loop-inconsistent). UDLD Aggressive detects physical unidirectional links and err-disables the port.",
      cisco_reference: "ENCOR OCG Chapter 4 — STP Protection Features",
      expert_trap_warning: null,
      tags: ["stp", "bpdu-guard", "root-guard", "loop-guard", "udld"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- EtherChannel ---

    {
      id: "ether-005",
      type: "single_choice",
      domain: "3.0",
      subdomain: "EtherChannel",
      difficulty: 1,
      prompt: "What is the maximum number of active links supported by LACP in an EtherChannel?",
      options: [
        { id: "A", text: "4 active links", is_correct: false },
        { id: "B", text: "8 active links", is_correct: true },
        { id: "C", text: "16 active links", is_correct: false },
        { id: "D", text: "32 active links", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "LACP supports up to 16 member links in a bundle, with a maximum of 8 active and 8 in hot-standby. The standby links automatically activate if an active link fails. PAgP supports a maximum of 8 links total with no hot-standby capability.",
      cisco_reference: "ENCOR OCG Chapter 3 — EtherChannel",
      expert_trap_warning: null,
      tags: ["etherchannel", "lacp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ether-006",
      type: "single_choice",
      domain: "3.0",
      subdomain: "EtherChannel",
      difficulty: 2,
      prompt: "Which EtherChannel load-balancing method provides the best traffic distribution when most traffic traverses a single router?",
      options: [
        { id: "A", text: "src-mac", is_correct: false },
        { id: "B", text: "dst-mac", is_correct: false },
        { id: "C", text: "src-dst-ip", is_correct: true },
        { id: "D", text: "dst-ip", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "When traffic passes through a single router, the source or destination MAC address is the router's MAC for all flows, resulting in all traffic hashing to the same link. Using src-dst-ip hashes based on the original source and destination IP addresses, which vary across flows, providing much better distribution across EtherChannel member links.",
      cisco_reference: "ENCOR OCG Chapter 3 — EtherChannel Load Balancing",
      expert_trap_warning: "MAC-based hashing is a classic exam trap. In routed environments, the router's MAC dominates, causing uneven distribution. Always consider the traffic pattern when selecting the hash method.",
      tags: ["etherchannel", "load-balancing", "hashing"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ether-007",
      type: "single_choice",
      domain: "3.0",
      subdomain: "EtherChannel",
      difficulty: 1,
      prompt: "What happens when one side of an EtherChannel is configured as LACP active and the other side as LACP passive?",
      options: [
        { id: "A", text: "The EtherChannel fails to form", is_correct: false },
        { id: "B", text: "The EtherChannel forms successfully", is_correct: true },
        { id: "C", text: "Only half the links become active", is_correct: false },
        { id: "D", text: "The channel forms but in PAgP mode", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "LACP active/passive forms a successful EtherChannel. Active mode initiates LACP negotiation, and passive mode responds to it. The only combination that fails is passive/passive, where neither side initiates. Similarly, PAgP desirable/auto works, but auto/auto does not.",
      cisco_reference: "ENCOR OCG Chapter 3 — LACP Modes",
      expert_trap_warning: null,
      tags: ["etherchannel", "lacp", "negotiation"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- VLANs / VTP / Private VLANs ---

    {
      id: "vlan-006",
      type: "single_choice",
      domain: "3.0",
      subdomain: "VLANs",
      difficulty: 2,
      prompt: "Which VTP version supports extended VLANs (1006-4094) and has a primary server concept to prevent accidental VLAN database overwrites?",
      options: [
        { id: "A", text: "VTPv1", is_correct: false },
        { id: "B", text: "VTPv2", is_correct: false },
        { id: "C", text: "VTPv3", is_correct: true },
        { id: "D", text: "VTP transparent mode in any version", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "VTPv3 introduces several improvements: extended VLAN support (1006-4094), primary server concept (only one switch can push changes, preventing accidental overwrites from high revision numbers), private VLAN propagation, MST configuration propagation, improved password security (not sent in clear text), and an 'off' mode that stops VTP forwarding entirely.",
      cisco_reference: "ENCOR OCG Chapter 3 — VTP Versions",
      expert_trap_warning: null,
      tags: ["vtp", "vtpv3", "extended-vlans"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "pvlan-001",
      type: "single_choice",
      domain: "3.0",
      subdomain: "PrivateVLANs",
      difficulty: 2,
      prompt: "In a Private VLAN, which port type can communicate with ALL other port types?",
      options: [
        { id: "A", text: "Isolated port", is_correct: false },
        { id: "B", text: "Community port", is_correct: false },
        { id: "C", text: "Promiscuous port", is_correct: true },
        { id: "D", text: "Trunk port", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The promiscuous port (typically connected to the default gateway or router) can communicate with all port types: isolated, community, and other promiscuous ports. Isolated ports can ONLY communicate with promiscuous ports. Community ports can communicate with other ports in the same community and with promiscuous ports.",
      cisco_reference: "ENCOR OCG Chapter 3 — Private VLANs",
      expert_trap_warning: null,
      tags: ["pvlan", "private-vlan", "promiscuous"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "pvlan-002",
      type: "multi_choice",
      domain: "3.0",
      subdomain: "PrivateVLANs",
      difficulty: 2,
      prompt: "Which TWO statements about Private VLAN isolated ports are correct?",
      options: [
        { id: "A", text: "Isolated ports cannot communicate with each other, even within the same isolated VLAN", is_correct: true },
        { id: "B", text: "Isolated ports can communicate with community ports in the same primary VLAN", is_correct: false },
        { id: "C", text: "Isolated ports can only communicate with promiscuous ports", is_correct: true },
        { id: "D", text: "There can be multiple isolated VLANs per primary VLAN", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Isolated ports provide complete Layer 2 isolation — they cannot communicate with each other or with community ports. Their only communication path is to/from promiscuous ports (typically the router/gateway). There can only be ONE isolated VLAN per primary VLAN, but multiple community VLANs are allowed.",
      cisco_reference: "ENCOR OCG Chapter 3 — Private VLANs",
      expert_trap_warning: "Only one isolated VLAN is allowed per primary VLAN. Multiple community VLANs are supported.",
      tags: ["pvlan", "isolated", "private-vlan"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "vlan-007",
      type: "single_choice",
      domain: "3.0",
      subdomain: "VLANs",
      difficulty: 2,
      prompt: "Which attack exploits the native VLAN on trunk ports to send traffic to a different VLAN?",
      options: [
        { id: "A", text: "MAC flooding attack", is_correct: false },
        { id: "B", text: "DHCP starvation attack", is_correct: false },
        { id: "C", text: "Double-tagging (VLAN hopping) attack", is_correct: true },
        { id: "D", text: "ARP spoofing attack", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Double-tagging VLAN hopping sends frames with two 802.1Q tags. The switch strips the outer tag (matching the native VLAN) and forwards the inner-tagged frame toward the target VLAN. This only works when the attacker is on the native VLAN and is unidirectional. Mitigate by using an unused VLAN as native, enabling 'vlan dot1q tag native', and disabling DTP.",
      cisco_reference: "ENCOR OCG Chapter 3 — VLAN Security",
      expert_trap_warning: null,
      tags: ["vlans", "vlan-hopping", "security", "native-vlan"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- FHRP ---

    {
      id: "fhrp-006",
      type: "single_choice",
      domain: "3.0",
      subdomain: "FHRP",
      difficulty: 1,
      prompt: "Which FHRP enables active-active load balancing by using multiple virtual MAC addresses for a single virtual IP?",
      options: [
        { id: "A", text: "HSRPv1", is_correct: false },
        { id: "B", text: "HSRPv2", is_correct: false },
        { id: "C", text: "VRRP", is_correct: false },
        { id: "D", text: "GLBP", is_correct: true }
      ],
      correct_answer: "D",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "GLBP (Gateway Load Balancing Protocol) is the only FHRP that provides active-active load balancing. It uses one Active Virtual Gateway (AVG) that responds to ARP requests with different virtual MAC addresses, distributing clients across up to four Active Virtual Forwarders (AVFs). HSRP and VRRP use active/standby models.",
      cisco_reference: "ENCOR OCG Chapter 6 — GLBP",
      expert_trap_warning: null,
      tags: ["fhrp", "glbp", "load-balancing"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "fhrp-007",
      type: "single_choice",
      domain: "3.0",
      subdomain: "FHRP",
      difficulty: 2,
      prompt: "By default, which FHRP has preemption enabled?",
      options: [
        { id: "A", text: "HSRPv1", is_correct: false },
        { id: "B", text: "HSRPv2", is_correct: false },
        { id: "C", text: "VRRP", is_correct: true },
        { id: "D", text: "GLBP", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "VRRP enables preemption by default — if a higher-priority router comes back online, it automatically takes over as Master. HSRP (v1 and v2) and GLBP have preemption DISABLED by default — you must explicitly configure 'standby <group> preempt' (HSRP) or 'glbp <group> preempt' (GLBP) to enable it.",
      cisco_reference: "ENCOR OCG Chapter 6 — VRRP vs HSRP",
      expert_trap_warning: "This is a common exam question. VRRP preempts by default; HSRP does not. Many engineers assume all FHRPs behave the same way regarding preemption.",
      tags: ["fhrp", "vrrp", "preemption"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "fhrp-008",
      type: "single_choice",
      domain: "3.0",
      subdomain: "FHRP",
      difficulty: 2,
      prompt: "An HSRP active router has priority 110 with 'standby 10 track 1 decrement 20' configured. If the tracked object fails, what is the resulting priority?",
      options: [
        { id: "A", text: "100", is_correct: false },
        { id: "B", text: "90", is_correct: true },
        { id: "C", text: "110", is_correct: false },
        { id: "D", text: "0", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "When the tracked object (track 1) fails, the HSRP priority is decremented by the configured value (20). So 110 - 20 = 90. If the standby router has the default priority of 100 and preemption is enabled, it will take over as active because 100 > 90. When the tracked object recovers, the priority returns to 110.",
      cisco_reference: "ENCOR OCG Chapter 6 — HSRP Tracking",
      expert_trap_warning: null,
      tags: ["fhrp", "hsrp", "tracking"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "fhrp-009",
      type: "drag_drop",
      domain: "3.0",
      subdomain: "FHRP",
      difficulty: 2,
      prompt: "Match each FHRP to its multicast address.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "HSRPv1" },
        { id: "item2", text: "HSRPv2" },
        { id: "item3", text: "VRRP" },
        { id: "item4", text: "GLBP" }
      ],
      drop_targets: [
        { id: "target1", label: "224.0.0.2", accepts: "item1" },
        { id: "target2", label: "224.0.0.102", accepts: "item2" },
        { id: "target3", label: "224.0.0.18", accepts: "item3" },
        { id: "target4", label: "224.0.0.102 (same as HSRPv2)", accepts: "item4" }
      ],
      explanation: "HSRPv1 uses 224.0.0.2 (all routers multicast). HSRPv2 and GLBP both use 224.0.0.102 but use different UDP ports (HSRPv2: 1985, GLBP: 3222). VRRP uses 224.0.0.18. These addresses are in the link-local multicast range (224.0.0.x) so they are not forwarded by routers.",
      cisco_reference: "ENCOR OCG Chapter 6 — FHRP Comparison",
      expert_trap_warning: null,
      tags: ["fhrp", "multicast", "hsrp", "vrrp", "glbp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- Multicast ---

    {
      id: "mcast-006",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Multicast",
      difficulty: 2,
      prompt: "What does the RPF (Reverse Path Forwarding) check verify when a multicast packet arrives on an interface?",
      options: [
        { id: "A", text: "That the destination group address is registered with the RP", is_correct: false },
        { id: "B", text: "That the incoming interface is the one used to reach the source address in the unicast routing table", is_correct: true },
        { id: "C", text: "That the TTL has not expired", is_correct: false },
        { id: "D", text: "That the receiver has sent an IGMP join for the group", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The RPF check verifies that the multicast packet arrived on the interface that the unicast routing table would use to reach the SOURCE address. If the check passes, the packet is forwarded out the outgoing interface list (OIL). If it fails, the packet is dropped. This prevents multicast loops by ensuring packets are only accepted from the topologically correct direction.",
      cisco_reference: "ENCOR OCG Chapter 14 — Multicast RPF",
      expert_trap_warning: null,
      tags: ["multicast", "rpf", "loop-prevention"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "mcast-007",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Multicast",
      difficulty: 2,
      prompt: "Which PIM mode uses a flood-and-prune approach where all routers receive multicast traffic by default?",
      options: [
        { id: "A", text: "PIM Sparse Mode", is_correct: false },
        { id: "B", text: "PIM Dense Mode", is_correct: true },
        { id: "C", text: "PIM Source-Specific Mode", is_correct: false },
        { id: "D", text: "PIM Bidirectional Mode", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "PIM Dense Mode assumes all routers want multicast traffic and floods it everywhere. Routers with no interested receivers send Prune messages upstream. Prune state times out (default 3 minutes), causing re-flooding. This is wasteful for most networks. PIM Sparse Mode uses explicit joins toward a Rendezvous Point and is the standard for enterprise networks.",
      cisco_reference: "ENCOR OCG Chapter 14 — PIM Modes",
      expert_trap_warning: null,
      tags: ["multicast", "pim-dense", "flood-prune"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "mcast-008",
      type: "multi_choice",
      domain: "3.0",
      subdomain: "Multicast",
      difficulty: 2,
      prompt: "Which TWO are valid methods for Rendezvous Point (RP) discovery in PIM Sparse Mode?",
      options: [
        { id: "A", text: "Auto-RP using 224.0.1.39 and 224.0.1.40", is_correct: true },
        { id: "B", text: "IGMP Querier election", is_correct: false },
        { id: "C", text: "Bootstrap Router (BSR) using hop-by-hop BSR messages", is_correct: true },
        { id: "D", text: "OSPF LSA Type 6 advertisements", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Auto-RP (Cisco proprietary) uses 224.0.1.39 for candidate RP announcements and 224.0.1.40 for mapping agent distributions. BSR (RFC 5059, standards-based) distributes RP information hop-by-hop. Static RP configuration is the third method. IGMP handles group membership on local segments, not RP discovery.",
      cisco_reference: "ENCOR OCG Chapter 14 — RP Discovery",
      expert_trap_warning: null,
      tags: ["multicast", "rp", "auto-rp", "bsr"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "mcast-009",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Multicast",
      difficulty: 2,
      prompt: "What capability does IGMPv3 add that IGMPv2 does not support?",
      options: [
        { id: "A", text: "Group-specific queries", is_correct: false },
        { id: "B", text: "Leave group messages", is_correct: false },
        { id: "C", text: "Source-specific multicast (SSM) with INCLUDE/EXCLUDE source lists", is_correct: true },
        { id: "D", text: "Querier election on multi-access networks", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "IGMPv3 adds source-filtering capability, allowing hosts to specify INCLUDE or EXCLUDE source lists. This enables Source-Specific Multicast (SSM) where hosts join (S,G) directly, bypassing the RP for groups in the SSM range (232.0.0.0/8). IGMPv2 already supports group-specific queries, leave messages, and querier election.",
      cisco_reference: "ENCOR OCG Chapter 14 — IGMPv3",
      expert_trap_warning: null,
      tags: ["multicast", "igmp", "ssm", "igmpv3"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "mcast-010",
      type: "single_choice",
      domain: "3.0",
      subdomain: "Multicast",
      difficulty: 3,
      prompt: "In PIM Sparse Mode, what triggers the SPT (Shortest Path Tree) switchover from the shared tree?",
      options: [
        { id: "A", text: "The RP explicitly instructs the last-hop router to switch", is_correct: false },
        { id: "B", text: "The last-hop router receives the first packet on the shared tree and sends an (S,G) join toward the source", is_correct: true },
        { id: "C", text: "The source router sends a PIM Assert message", is_correct: false },
        { id: "D", text: "IGMP forces a switchover after a timeout", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "By default in IOS-XE, the last-hop router switches to the SPT after receiving the first packet on the shared tree (*,G). It sends an (S,G) join directly toward the source, building the shortest-path tree. This behavior can be modified with 'ip pim spt-threshold <kbps>' to delay switchover until a traffic rate threshold is met, or 'ip pim spt-threshold infinity' to prevent SPT switchover entirely.",
      cisco_reference: "ENCOR OCG Chapter 14 — PIM SPT Switchover",
      expert_trap_warning: "The default SPT threshold is 0 kbps (immediate switchover). The RP does NOT control SPT switchover — it's a last-hop router decision.",
      tags: ["multicast", "pim-sm", "spt", "shared-tree"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- IP Services ---

    {
      id: "ipsvc-007",
      type: "single_choice",
      domain: "3.0",
      subdomain: "IPServices",
      difficulty: 1,
      prompt: "What is the NTP stratum level of a server that is directly connected to an authoritative time source (atomic clock, GPS)?",
      options: [
        { id: "A", text: "Stratum 0", is_correct: false },
        { id: "B", text: "Stratum 1", is_correct: true },
        { id: "C", text: "Stratum 2", is_correct: false },
        { id: "D", text: "Stratum 16", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Stratum 0 devices are the authoritative time sources themselves (atomic clocks, GPS receivers) but are not directly on the network. Stratum 1 servers are directly connected to a Stratum 0 source and are the highest-stratum NTP servers on the network. Each subsequent hop adds one stratum level. Stratum 16 means unsynchronized.",
      cisco_reference: "ENCOR OCG Chapter 13 — NTP",
      expert_trap_warning: "Stratum 0 is the clock itself (not a network device). Stratum 1 is the first server reachable via NTP.",
      tags: ["ntp", "stratum", "time-sync"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "nat-006",
      type: "single_choice",
      domain: "3.0",
      subdomain: "NAT",
      difficulty: 1,
      prompt: "Which NAT type maps multiple inside local addresses to a SINGLE inside global address using port numbers to distinguish sessions?",
      options: [
        { id: "A", text: "Static NAT", is_correct: false },
        { id: "B", text: "Dynamic NAT", is_correct: false },
        { id: "C", text: "PAT (NAT Overload)", is_correct: true },
        { id: "D", text: "Policy NAT", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "PAT (Port Address Translation), also called NAT Overload, maps many internal addresses to a single public IP using unique source port numbers to track sessions. This is the most common NAT type for internet access. Configured with the 'overload' keyword: 'ip nat inside source list 1 interface Gi0/1 overload'.",
      cisco_reference: "ENCOR OCG Chapter 13 — NAT/PAT",
      expert_trap_warning: null,
      tags: ["nat", "pat", "overload"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ipsvc-008",
      type: "single_choice",
      domain: "3.0",
      subdomain: "IPServices",
      difficulty: 2,
      prompt: "What field does a DHCP relay agent insert into the DHCPDISCOVER message before forwarding it to the DHCP server?",
      options: [
        { id: "A", text: "The client's MAC address", is_correct: false },
        { id: "B", text: "The GIADDR (Gateway IP Address) field with the relay agent's interface IP", is_correct: true },
        { id: "C", text: "The server's IP address", is_correct: false },
        { id: "D", text: "The VLAN ID of the client segment", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The DHCP relay agent (configured with 'ip helper-address') inserts its own interface IP address into the GIADDR field. The DHCP server uses this value to determine which scope/pool to assign addresses from, since the GIADDR tells the server which subnet the client is on. The client's MAC address is already in the DHCP message.",
      cisco_reference: "ENCOR OCG Chapter 13 — DHCP Relay",
      expert_trap_warning: null,
      tags: ["dhcp", "relay", "giaddr"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- IPv6 ---

    {
      id: "ipv6-001",
      type: "single_choice",
      domain: "3.0",
      subdomain: "IPv6",
      difficulty: 2,
      prompt: "What type of IPv6 address does OSPFv3 use for neighbor adjacencies?",
      options: [
        { id: "A", text: "Global unicast addresses", is_correct: false },
        { id: "B", text: "Link-local addresses (FE80::/10)", is_correct: true },
        { id: "C", text: "Unique local addresses (FC00::/7)", is_correct: false },
        { id: "D", text: "Multicast addresses (FF00::/8)", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "OSPFv3 uses IPv6 link-local addresses (FE80::/10) for neighbor adjacencies and as next-hop addresses. This means neighbors are identified by Router ID (not IP address, since link-local addresses are not unique across the network). Hello packets are sent to the OSPFv3 multicast addresses FF02::5 (all OSPF routers) and FF02::6 (all DRs).",
      cisco_reference: "ENCOR OCG Chapter 8 — OSPFv3",
      expert_trap_warning: null,
      tags: ["ipv6", "ospfv3", "link-local"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ipv6-002",
      type: "single_choice",
      domain: "3.0",
      subdomain: "IPv6",
      difficulty: 2,
      prompt: "What is the primary purpose of IPv6 RA Guard on a switch?",
      options: [
        { id: "A", text: "To encrypt IPv6 Router Advertisement messages", is_correct: false },
        { id: "B", text: "To prevent rogue devices from sending Router Advertisement messages", is_correct: true },
        { id: "C", text: "To filter IPv6 multicast traffic on access ports", is_correct: false },
        { id: "D", text: "To authenticate legitimate IPv6 routers using certificates", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "IPv6 RA Guard prevents unauthorized Router Advertisement messages from being sent on switch ports configured with 'device-role host'. Since any IPv6 device can send RAs and potentially become the default gateway (man-in-the-middle), RA Guard drops these messages on ports that should not have routers connected. Only ports with 'device-role router' are allowed to send RAs.",
      cisco_reference: "ENCOR OCG Chapter 22 — IPv6 First-Hop Security",
      expert_trap_warning: null,
      tags: ["ipv6", "ra-guard", "security"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ipv6-003",
      type: "single_choice",
      domain: "3.0",
      subdomain: "IPv6",
      difficulty: 3,
      prompt: "How does OSPFv3 support both IPv4 and IPv6 routing on the same router?",
      options: [
        { id: "A", text: "By running separate OSPFv2 and OSPFv3 processes simultaneously", is_correct: false },
        { id: "B", text: "By using address families (AF) within a single OSPFv3 process", is_correct: true },
        { id: "C", text: "By encapsulating IPv4 routes inside IPv6 LSAs", is_correct: false },
        { id: "D", text: "OSPFv3 cannot support IPv4; only OSPFv2 handles IPv4", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "OSPFv3 (RFC 5838) supports multiple address families within a single process. Under 'router ospfv3 <process>', you configure 'address-family ipv4 unicast' and 'address-family ipv6 unicast' separately. Each AF maintains its own LSDB and SPF calculation, with different instance IDs to separate AF traffic on the same link. This eliminates the need for running both OSPFv2 and OSPFv3.",
      cisco_reference: "ENCOR OCG Chapter 8 — OSPFv3 Address Families",
      expert_trap_warning: null,
      tags: ["ipv6", "ospfv3", "address-family", "dual-stack"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // DOMAIN 4.0 — NETWORK ASSURANCE (~16 questions)
    // =========================================================

    // --- ERSPAN ---

    {
      id: "erspan-001",
      type: "single_choice",
      domain: "4.0",
      subdomain: "ERSPAN",
      difficulty: 2,
      prompt: "What encapsulation does ERSPAN use to transport mirrored traffic across a routed network?",
      options: [
        { id: "A", text: "VXLAN", is_correct: false },
        { id: "B", text: "GRE", is_correct: true },
        { id: "C", text: "IPsec", is_correct: false },
        { id: "D", text: "MPLS", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "ERSPAN (Encapsulated Remote SPAN) encapsulates mirrored traffic in GRE (Generic Routing Encapsulation) with protocol type 0x88BE. This allows the mirrored traffic to be transported across Layer 3 networks to a remote analyzer, unlike RSPAN which requires a dedicated VLAN and is limited to the Layer 2 domain.",
      cisco_reference: "ENCOR OCG Chapter 13 — ERSPAN",
      expert_trap_warning: null,
      tags: ["erspan", "gre", "monitoring"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "erspan-002",
      type: "drag_drop",
      domain: "4.0",
      subdomain: "ERSPAN",
      difficulty: 2,
      prompt: "Match each traffic mirroring technology to its capability.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "SPAN" },
        { id: "item2", text: "RSPAN" },
        { id: "item3", text: "ERSPAN" }
      ],
      drop_targets: [
        { id: "target1", label: "Mirrors to a local port on the same switch only", accepts: "item1" },
        { id: "target2", label: "Mirrors via a dedicated VLAN across L2 trunks", accepts: "item2" },
        { id: "target3", label: "Mirrors via GRE encapsulation across L3 networks", accepts: "item3" }
      ],
      explanation: "SPAN mirrors traffic to a destination port on the same switch. RSPAN extends this by using a special RSPAN VLAN that carries mirrored traffic across trunk links to a destination port on a different switch, but stays within the L2 domain. ERSPAN encapsulates mirrored traffic in GRE, allowing it to cross routed L3 boundaries to reach a remote collector.",
      cisco_reference: "ENCOR OCG Chapter 13 — Traffic Mirroring",
      expert_trap_warning: null,
      tags: ["span", "rspan", "erspan", "monitoring"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- Conditional Debug ---

    {
      id: "conddebug-001",
      type: "single_choice",
      domain: "4.0",
      subdomain: "ConditionalDebug",
      difficulty: 2,
      prompt: "What is the correct order of operations when using conditional debugging in a production environment?",
      options: [
        { id: "A", text: "Enable the debug first, then set conditions to filter output", is_correct: false },
        { id: "B", text: "Set the debug condition first, then enable the specific debug command", is_correct: true },
        { id: "C", text: "Enable the debug and condition simultaneously using a single command", is_correct: false },
        { id: "D", text: "Conditions can only be set from ROMMON mode", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Always set debug conditions BEFORE enabling the debug command. If you enable the debug first, it runs unfiltered until you add the condition, potentially overwhelming the router's CPU and console. Set the condition ('debug condition ip 10.1.1.1'), then enable the debug ('debug ip ospf adj'). For cleanup, disable debugs first, then remove conditions.",
      cisco_reference: "ENCOR OCG Chapter 13 — Conditional Debugging",
      expert_trap_warning: "If you remove conditions while debugs are still active, the debugs become UNFILTERED. Always 'undebug all' before 'no debug condition all'.",
      tags: ["debug", "conditional-debug", "troubleshooting"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "conddebug-002",
      type: "single_choice",
      domain: "4.0",
      subdomain: "ConditionalDebug",
      difficulty: 1,
      prompt: "Which command restricts debug output to only traffic involving IP address 10.1.1.1?",
      options: [
        { id: "A", text: "debug ip packet 10.1.1.1", is_correct: false },
        { id: "B", text: "debug condition ip 10.1.1.1", is_correct: true },
        { id: "C", text: "debug filter ip 10.1.1.1", is_correct: false },
        { id: "D", text: "access-list 1 permit 10.1.1.1", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The 'debug condition ip <address>' command sets a debug condition that filters all subsequent debug output to only show information related to the specified IP address. Other condition types include 'debug condition interface', 'debug condition vlan', and 'debug condition username'. Verify with 'show debug condition'.",
      cisco_reference: "ENCOR OCG Chapter 13 — Debug Commands",
      expert_trap_warning: null,
      tags: ["debug", "conditional-debug"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- DNA Center Assurance ---

    {
      id: "dna-006",
      type: "single_choice",
      domain: "4.0",
      subdomain: "DNACAssurance",
      difficulty: 1,
      prompt: "Which DNA Center Assurance feature visualizes the complete Layer 2/Layer 3 path between two network endpoints?",
      options: [
        { id: "A", text: "Network Health dashboard", is_correct: false },
        { id: "B", text: "Path Trace", is_correct: true },
        { id: "C", text: "Client Health dashboard", is_correct: false },
        { id: "D", text: "Application Health dashboard", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Path Trace in DNA Center Assurance visualizes the complete network path between a source and destination, showing every device and interface in the path, VLAN assignments, ACLs applied, QoS policies, and interface statistics. It is used for troubleshooting connectivity issues and validating traffic paths.",
      cisco_reference: "ENCOR OCG Chapter 17 — DNA Center Assurance",
      expert_trap_warning: null,
      tags: ["dna-center", "path-trace", "assurance"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "dna-007",
      type: "multi_choice",
      domain: "4.0",
      subdomain: "DNACAssurance",
      difficulty: 2,
      prompt: "Which TWO are capabilities of DNA Center Assurance's AI-driven analytics?",
      options: [
        { id: "A", text: "ML-based anomaly detection that identifies deviations from baseline behavior", is_correct: true },
        { id: "B", text: "Automatic firmware upgrades when vulnerabilities are detected", is_correct: false },
        { id: "C", text: "Proactive recommendations based on network health trends", is_correct: true },
        { id: "D", text: "Real-time packet capture on all switch ports simultaneously", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "DNA Center Assurance uses AI/ML to establish network behavior baselines and detect anomalies (deviations from normal patterns). It provides proactive insights and recommendations based on health trends, helping administrators address issues before they impact users. It does not perform automatic firmware upgrades or capture all ports simultaneously.",
      cisco_reference: "ENCOR OCG Chapter 17 — DNA Center AI-Driven Insights",
      expert_trap_warning: null,
      tags: ["dna-center", "ai-ml", "assurance"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- Streaming Telemetry ---

    {
      id: "telemetry-004",
      type: "single_choice",
      domain: "4.0",
      subdomain: "Telemetry",
      difficulty: 2,
      prompt: "In model-driven telemetry, what is the difference between dial-in and dial-out subscription modes?",
      options: [
        { id: "A", text: "Dial-in uses UDP; dial-out uses TCP", is_correct: false },
        { id: "B", text: "Dial-in is collector-initiated; dial-out is device-initiated", is_correct: true },
        { id: "C", text: "Dial-in is push-based; dial-out is pull-based", is_correct: false },
        { id: "D", text: "Dial-in supports YANG models; dial-out only supports SNMP MIBs", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Dial-in: the COLLECTOR initiates a session to the device and creates dynamic subscriptions (ephemeral — lost if the session drops). Good for on-demand troubleshooting. Dial-out: the DEVICE initiates a connection to the collector using pre-configured subscriptions (persistent). Most common in production because it works through firewalls and doesn't require collector-to-device reachability.",
      cisco_reference: "ENCOR OCG Chapter 17 — Model-Driven Telemetry",
      expert_trap_warning: "The naming is from the DEVICE's perspective: 'dial-out' = device dials out to collector. 'dial-in' = collector dials in to device.",
      tags: ["telemetry", "dial-in", "dial-out"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "telemetry-005",
      type: "single_choice",
      domain: "4.0",
      subdomain: "Telemetry",
      difficulty: 2,
      prompt: "Which transport protocol is most commonly used for model-driven streaming telemetry on IOS-XE?",
      options: [
        { id: "A", text: "SNMP over UDP", is_correct: false },
        { id: "B", text: "NETCONF over SSH", is_correct: false },
        { id: "C", text: "gRPC over HTTP/2", is_correct: true },
        { id: "D", text: "RESTCONF over HTTPS", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "gRPC (Google Remote Procedure Call) over HTTP/2 is the most common transport for model-driven telemetry. It provides efficient binary serialization (Protocol Buffers), bidirectional streaming, flow control, and TLS encryption. Other options (TCP, UDP) are also supported but gRPC is the standard for Cisco IOS-XE telemetry implementations.",
      cisco_reference: "ENCOR OCG Chapter 17 — Telemetry Transport",
      expert_trap_warning: null,
      tags: ["telemetry", "grpc", "transport"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "telemetry-006",
      type: "multi_choice",
      domain: "4.0",
      subdomain: "Telemetry",
      difficulty: 2,
      prompt: "Which TWO are advantages of streaming telemetry compared to SNMP polling?",
      options: [
        { id: "A", text: "Near real-time data with sub-second update intervals", is_correct: true },
        { id: "B", text: "No network bandwidth consumption", is_correct: false },
        { id: "C", text: "Push-based model eliminates polling overhead at scale", is_correct: true },
        { id: "D", text: "Works on all network devices without any configuration", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Streaming telemetry provides near real-time visibility with configurable intervals (even sub-second), compared to SNMP's typical 5-minute polling cycles. The push-based model is far more scalable — devices stream only subscribed data, eliminating the polling storms that occur when SNMP collectors query thousands of OIDs across hundreds of devices. Telemetry does consume bandwidth and requires device configuration.",
      cisco_reference: "ENCOR OCG Chapter 17 — Telemetry vs SNMP",
      expert_trap_warning: null,
      tags: ["telemetry", "snmp", "monitoring"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- SNMP ---

    {
      id: "snmp-004",
      type: "single_choice",
      domain: "4.0",
      subdomain: "SNMP",
      difficulty: 1,
      prompt: "Which SNMPv3 security level provides both authentication and encryption?",
      options: [
        { id: "A", text: "noAuthNoPriv", is_correct: false },
        { id: "B", text: "authNoPriv", is_correct: false },
        { id: "C", text: "authPriv", is_correct: true },
        { id: "D", text: "privAuth", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "SNMPv3 defines three security levels: noAuthNoPriv (username only), authNoPriv (authentication with MD5/SHA but no encryption), and authPriv (both authentication and encryption with DES/3DES/AES). authPriv is the recommended security level, providing both message integrity and confidentiality.",
      cisco_reference: "ENCOR OCG Chapter 13 — SNMPv3",
      expert_trap_warning: null,
      tags: ["snmp", "snmpv3", "security"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "snmp-005",
      type: "single_choice",
      domain: "4.0",
      subdomain: "SNMP",
      difficulty: 2,
      prompt: "What is the key difference between SNMP traps and SNMP informs?",
      options: [
        { id: "A", text: "Traps are sent from the manager to the agent; informs are agent-to-manager", is_correct: false },
        { id: "B", text: "Traps are unreliable (no acknowledgment); informs are acknowledged and retransmitted if needed", is_correct: true },
        { id: "C", text: "Traps use TCP; informs use UDP", is_correct: false },
        { id: "D", text: "Traps carry more data than informs", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Both traps and informs are asynchronous notifications sent from the SNMP agent to the manager. Traps are fire-and-forget (UDP, no acknowledgment) — if lost, the agent never knows. Informs include an acknowledgment mechanism — the agent retransmits if no acknowledgment is received, providing reliable delivery at the cost of slightly higher agent overhead.",
      cisco_reference: "ENCOR OCG Chapter 13 — SNMP Traps vs Informs",
      expert_trap_warning: null,
      tags: ["snmp", "traps", "informs"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "snmp-006",
      type: "single_choice",
      domain: "4.0",
      subdomain: "SNMP",
      difficulty: 2,
      prompt: "Which SNMPv3 component defines per-user credentials including authentication and privacy passwords?",
      options: [
        { id: "A", text: "View-based Access Control Model (VACM)", is_correct: false },
        { id: "B", text: "User-based Security Model (USM)", is_correct: true },
        { id: "C", text: "Community String", is_correct: false },
        { id: "D", text: "Engine ID", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The User-based Security Model (USM) handles per-user authentication and privacy credentials in SNMPv3. Each user has a username, authentication protocol/password (MD5/SHA), and optionally a privacy protocol/password (DES/AES). VACM controls which MIB objects each user can access. The Engine ID uniquely identifies each SNMP entity.",
      cisco_reference: "ENCOR OCG Chapter 13 — SNMPv3 Architecture",
      expert_trap_warning: null,
      tags: ["snmp", "snmpv3", "usm"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- IP SLA ---

    {
      id: "sla-005",
      type: "single_choice",
      domain: "4.0",
      subdomain: "IPSLA",
      difficulty: 2,
      prompt: "Which IP SLA operation type is best suited for measuring VoIP quality metrics including jitter, latency, and MOS score?",
      options: [
        { id: "A", text: "ICMP Echo", is_correct: false },
        { id: "B", text: "HTTP GET", is_correct: false },
        { id: "C", text: "UDP Jitter", is_correct: true },
        { id: "D", text: "TCP Connect", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The UDP Jitter operation sends a series of UDP packets and measures round-trip time, one-way latency (requires NTP sync), jitter (arrival time variation), packet loss, and can calculate a MOS (Mean Opinion Score) for voice quality. It simulates VoIP-like traffic patterns and is the standard tool for measuring voice/video quality on the network.",
      cisco_reference: "ENCOR OCG Chapter 13 — IP SLA Operations",
      expert_trap_warning: null,
      tags: ["ip-sla", "udp-jitter", "voip"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sla-006",
      type: "single_choice",
      domain: "4.0",
      subdomain: "IPSLA",
      difficulty: 2,
      prompt: "How does IP SLA integrate with static route failover?",
      options: [
        { id: "A", text: "IP SLA directly modifies the routing table when a probe fails", is_correct: false },
        { id: "B", text: "IP SLA results are linked to a tracking object, which is referenced by a static route", is_correct: true },
        { id: "C", text: "IP SLA automatically creates floating static routes", is_correct: false },
        { id: "D", text: "IP SLA sends ICMP redirects to change the default gateway", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "IP SLA doesn't directly modify routes. Instead: (1) an IP SLA operation probes reachability/performance, (2) a tracking object monitors the SLA result (reachability or threshold), (3) a static route references the tracking object with 'ip route ... track <id>'. When the SLA probe fails, the tracking object goes DOWN, the static route is removed, and the backup route (higher AD) takes over.",
      cisco_reference: "ENCOR OCG Chapter 13 — IP SLA with Tracking",
      expert_trap_warning: null,
      tags: ["ip-sla", "tracking", "static-route", "failover"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sla-007",
      type: "single_choice",
      domain: "4.0",
      subdomain: "IPSLA",
      difficulty: 1,
      prompt: "What must be configured on the target device for an IP SLA UDP jitter operation?",
      options: [
        { id: "A", text: "An IP SLA responder", is_correct: true },
        { id: "B", text: "An SNMP community string", is_correct: false },
        { id: "C", text: "A static route back to the source", is_correct: false },
        { id: "D", text: "No configuration needed — UDP jitter is unidirectional", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "UDP Jitter operations require an 'ip sla responder' configured on the target device. The responder listens for IP SLA control messages and timestamps packets for accurate one-way measurements. ICMP Echo operations do not require a responder (they use standard ICMP), but UDP Jitter and TCP Connect operations do.",
      cisco_reference: "ENCOR OCG Chapter 13 — IP SLA Responder",
      expert_trap_warning: "ICMP Echo does NOT need a responder. UDP Jitter and TCP Connect DO. This is a common exam distinction.",
      tags: ["ip-sla", "responder", "udp-jitter"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sla-008",
      type: "drag_drop",
      domain: "4.0",
      subdomain: "IPSLA",
      difficulty: 2,
      prompt: "Match each IP SLA operation type to what it measures.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "ICMP Echo" },
        { id: "item2", text: "UDP Jitter" },
        { id: "item3", text: "HTTP GET" },
        { id: "item4", text: "TCP Connect" }
      ],
      drop_targets: [
        { id: "target1", label: "Round-trip time and basic reachability (like ping)", accepts: "item1" },
        { id: "target2", label: "Jitter, latency, packet loss, and MOS score", accepts: "item2" },
        { id: "target3", label: "DNS lookup time, TCP connect time, and HTTP transaction time", accepts: "item3" },
        { id: "target4", label: "Time to establish a TCP three-way handshake", accepts: "item4" }
      ],
      explanation: "ICMP Echo is the simplest — measures basic RTT and reachability like a ping. UDP Jitter is the most comprehensive — measures jitter, latency, packet loss, and can calculate VoIP MOS scores. HTTP GET measures the full web transaction including DNS, TCP connect, and HTTP response. TCP Connect measures only the TCP handshake time to a specific port.",
      cisco_reference: "ENCOR OCG Chapter 13 — IP SLA Operation Types",
      expert_trap_warning: null,
      tags: ["ip-sla", "operations", "monitoring"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // EXPANSION WAVE — DOMAIN 5.0 + 6.0 (Security + Automation)
    // =========================================================
    // =========================================================
    // DOMAIN 5.0 — SECURITY (~46 questions)
    // =========================================================

    // --- ACLs ---
    {
      id: "acl-007",
      type: "single_choice",
      domain: "5.0",
      subdomain: "ACLs",
      difficulty: 1,
      prompt: "A standard ACL filters traffic based on which criteria?",
      options: [
        { id: "A", text: "Source and destination IP addresses", is_correct: false },
        { id: "B", text: "Source IP address only", is_correct: true },
        { id: "C", text: "Source IP address and protocol number", is_correct: false },
        { id: "D", text: "Destination IP address only", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Standard ACLs (numbered 1-99, 1300-1999) can only match on the source IP address. Extended ACLs (100-199, 2000-2699) can match on source IP, destination IP, protocol, and port numbers.",
      cisco_reference: "ENCOR OCG Chapter 17 — Access Control Lists",
      expert_trap_warning: null,
      tags: ["acl", "standard"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "acl-008",
      type: "single_choice",
      domain: "5.0",
      subdomain: "ACLs",
      difficulty: 2,
      prompt: "What happens when a packet does not match any ACE in an applied ACL?",
      options: [
        { id: "A", text: "The packet is forwarded to the default gateway", is_correct: false },
        { id: "B", text: "The packet is logged and forwarded", is_correct: false },
        { id: "C", text: "The packet is denied by the implicit deny all", is_correct: true },
        { id: "D", text: "The packet is permitted by default", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Every IOS ACL has an invisible implicit 'deny any' (standard) or 'deny ip any any' (extended) at the end. If no explicit ACE matches, the packet is dropped. This is why you must include at least one permit statement for desired traffic.",
      cisco_reference: "ENCOR OCG Chapter 17 — ACL Processing",
      expert_trap_warning: null,
      tags: ["acl", "implicit-deny"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "acl-009",
      type: "single_choice",
      domain: "5.0",
      subdomain: "ACLs",
      difficulty: 2,
      prompt: "Which advantage do named ACLs have over numbered ACLs?",
      options: [
        { id: "A", text: "Named ACLs support IPv6 only", is_correct: false },
        { id: "B", text: "Named ACLs can filter on Layer 4 ports", is_correct: false },
        { id: "C", text: "Individual ACEs can be deleted or inserted by sequence number", is_correct: true },
        { id: "D", text: "Named ACLs do not have an implicit deny", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Named ACLs allow you to edit individual entries using sequence numbers without removing and re-creating the entire ACL. Both named and numbered extended ACLs can filter on Layer 4 ports, and all ACLs have an implicit deny.",
      cisco_reference: "ENCOR OCG Chapter 17 — Named ACLs",
      expert_trap_warning: "Both numbered and named extended ACLs support L4 port filtering — that is not unique to named ACLs.",
      tags: ["acl", "named-acl"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "acl-010",
      type: "single_choice",
      domain: "5.0",
      subdomain: "ACLs",
      difficulty: 2,
      prompt: "A reflexive ACL is best described as providing which type of filtering?",
      options: [
        { id: "A", text: "Static packet filtering based on source IP", is_correct: false },
        { id: "B", text: "Time-based access restrictions", is_correct: false },
        { id: "C", text: "Session-based stateful filtering for return traffic", is_correct: true },
        { id: "D", text: "Rate-limiting on the control plane", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Reflexive ACLs create temporary ACEs that permit return traffic for outbound sessions. They use the 'reflect' keyword on the outbound ACL and 'evaluate' on the inbound ACL, providing basic stateful filtering without a full firewall.",
      cisco_reference: "ENCOR OCG Chapter 17 — Reflexive ACLs",
      expert_trap_warning: null,
      tags: ["acl", "reflexive", "stateful"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "acl-011",
      type: "single_choice",
      domain: "5.0",
      subdomain: "ACLs",
      difficulty: 2,
      prompt: "Which IOS-XE feature allows an ACL to be active only during specific hours of the day?",
      options: [
        { id: "A", text: "Conditional ACL", is_correct: false },
        { id: "B", text: "Time-range with periodic statement applied to ACE", is_correct: true },
        { id: "C", text: "Scheduled ACL via EEM applet", is_correct: false },
        { id: "D", text: "Dynamic ACL with autocommand", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Time-based ACLs reference a time-range object using the 'time-range' keyword in the ACE. The time-range can use 'periodic' (recurring schedule) or 'absolute' (one-time window) statements. NTP synchronization is critical for accuracy.",
      cisco_reference: "ENCOR OCG Chapter 17 — Time-Based ACLs",
      expert_trap_warning: null,
      tags: ["acl", "time-based", "time-range"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- CoPP ---
    {
      id: "copp-003",
      type: "single_choice",
      domain: "5.0",
      subdomain: "CoPP",
      difficulty: 2,
      prompt: "Under which IOS-XE configuration mode is a CoPP service-policy applied?",
      options: [
        { id: "A", text: "Interface configuration mode on each interface", is_correct: false },
        { id: "B", text: "Global configuration mode", is_correct: false },
        { id: "C", text: "Control-plane configuration mode", is_correct: true },
        { id: "D", text: "Line VTY configuration mode", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "CoPP uses MQC (class-map, policy-map) but the service-policy is applied under 'control-plane' configuration mode with 'service-policy input <name>'. This polices all traffic destined to the route processor CPU, regardless of ingress interface.",
      cisco_reference: "ENCOR OCG Chapter 17 — Control Plane Policing",
      expert_trap_warning: null,
      tags: ["copp", "control-plane", "mqc"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "copp-004",
      type: "multi_choice",
      domain: "5.0",
      subdomain: "CoPP",
      difficulty: 2,
      prompt: "Which TWO traffic types should typically be rate-limited (not dropped) in a CoPP policy? (Choose 2)",
      options: [
        { id: "A", text: "ICMP (ping) traffic", is_correct: true },
        { id: "B", text: "BGP (TCP 179) from configured peers", is_correct: false },
        { id: "C", text: "SNMP polling traffic", is_correct: true },
        { id: "D", text: "OSPF hello packets from neighbors", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "ICMP and SNMP are useful but can be abused for DoS — they should be rate-limited. Routing protocols like BGP and OSPF are critical for network operation and should be permitted (policed at a generous rate or permitted in a trusted class) rather than aggressively rate-limited.",
      cisco_reference: "ENCOR OCG Chapter 17 — CoPP Design",
      expert_trap_warning: "Dropping routing protocol traffic in CoPP can cause neighbor adjacency failures.",
      tags: ["copp", "rate-limit", "icmp", "snmp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- AAA ---
    {
      id: "aaa-005",
      type: "single_choice",
      domain: "5.0",
      subdomain: "AAA",
      difficulty: 1,
      prompt: "Which transport protocol does TACACS+ use?",
      options: [
        { id: "A", text: "UDP port 1812", is_correct: false },
        { id: "B", text: "TCP port 49", is_correct: true },
        { id: "C", text: "UDP port 49", is_correct: false },
        { id: "D", text: "TCP port 1812", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "TACACS+ uses TCP port 49 for reliable delivery. RADIUS uses UDP ports 1812/1813 (or legacy 1645/1646). TCP provides connection-oriented delivery and acknowledgment, which makes TACACS+ more reliable for device administration.",
      cisco_reference: "ENCOR OCG Chapter 17 — TACACS+ vs RADIUS",
      expert_trap_warning: null,
      tags: ["aaa", "tacacs", "transport"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "aaa-006",
      type: "single_choice",
      domain: "5.0",
      subdomain: "AAA",
      difficulty: 2,
      prompt: "In the command 'aaa authentication login default group tacacs+ local', when does the router use the local database?",
      options: [
        { id: "A", text: "When the TACACS+ server rejects the credentials", is_correct: false },
        { id: "B", text: "When the TACACS+ server is unreachable", is_correct: true },
        { id: "C", text: "Always, as a second factor of authentication", is_correct: false },
        { id: "D", text: "When the user requests local authentication", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "AAA method list fallback occurs only when the server is UNREACHABLE (timeout, no response). If TACACS+ explicitly rejects the credentials (authentication failure), the local database is NOT tried. This is a critical distinction for the exam.",
      cisco_reference: "ENCOR OCG Chapter 17 — AAA Method Lists",
      expert_trap_warning: "A common trap: fallback does NOT occur on explicit authentication rejection — only on server unreachable.",
      tags: ["aaa", "method-list", "fallback"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "aaa-007",
      type: "single_choice",
      domain: "5.0",
      subdomain: "AAA",
      difficulty: 2,
      prompt: "Which AAA protocol encrypts the entire packet body?",
      options: [
        { id: "A", text: "RADIUS", is_correct: false },
        { id: "B", text: "TACACS+", is_correct: true },
        { id: "C", text: "Kerberos", is_correct: false },
        { id: "D", text: "LDAP", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "TACACS+ encrypts the entire packet body (everything after the header). RADIUS only encrypts the password field in the Access-Request packet — all other attributes (username, accounting data, authorization attributes) are sent in cleartext. This makes TACACS+ more secure for device administration.",
      cisco_reference: "ENCOR OCG Chapter 17 — RADIUS vs TACACS+ Encryption",
      expert_trap_warning: null,
      tags: ["aaa", "tacacs", "radius", "encryption"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "aaa-008",
      type: "multi_choice",
      domain: "5.0",
      subdomain: "AAA",
      difficulty: 2,
      prompt: "Which TWO features are advantages of TACACS+ over RADIUS for device administration? (Choose 2)",
      options: [
        { id: "A", text: "Per-command authorization", is_correct: true },
        { id: "B", text: "Built-in support for 802.1X", is_correct: false },
        { id: "C", text: "Separate authentication, authorization, and accounting functions", is_correct: true },
        { id: "D", text: "Uses UDP for faster performance", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "TACACS+ provides per-command authorization (checking each CLI command against the server) and separates AAA into three independent functions. RADIUS combines authentication and authorization in a single exchange and does not support per-command authorization. 802.1X commonly uses RADIUS, not TACACS+.",
      cisco_reference: "ENCOR OCG Chapter 17 — TACACS+ Features",
      expert_trap_warning: null,
      tags: ["aaa", "tacacs", "command-authorization"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- 802.1X ---
    {
      id: "dot1x-004",
      type: "single_choice",
      domain: "5.0",
      subdomain: "802.1X",
      difficulty: 2,
      prompt: "Which EAP type requires certificates on BOTH the client and the server?",
      options: [
        { id: "A", text: "PEAP", is_correct: false },
        { id: "B", text: "EAP-FAST", is_correct: false },
        { id: "C", text: "EAP-TLS", is_correct: true },
        { id: "D", text: "EAP-MD5", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "EAP-TLS requires mutual certificate authentication — both the RADIUS server and the client must have X.509 certificates. PEAP only requires a server certificate (client uses username/password inside the TLS tunnel). EAP-FAST uses a PAC (Protected Access Credential) instead of certificates.",
      cisco_reference: "ENCOR OCG Chapter 18 — EAP Types",
      expert_trap_warning: null,
      tags: ["802.1x", "eap-tls", "certificates"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "dot1x-005",
      type: "single_choice",
      domain: "5.0",
      subdomain: "802.1X",
      difficulty: 2,
      prompt: "Which 802.1X host mode allows one device on the voice VLAN and one device on the data VLAN?",
      options: [
        { id: "A", text: "Single-host mode", is_correct: false },
        { id: "B", text: "Multi-host mode", is_correct: false },
        { id: "C", text: "Multi-domain authentication mode", is_correct: true },
        { id: "D", text: "Multi-auth mode", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Multi-domain authentication (MDA) mode allows exactly one device per domain — one on the data VLAN and one on the voice VLAN. This is designed for the common scenario of an IP phone with a PC connected behind it. Multi-auth mode allows multiple independently authenticated devices on the data VLAN.",
      cisco_reference: "ENCOR OCG Chapter 18 — 802.1X Host Modes",
      expert_trap_warning: null,
      tags: ["802.1x", "multi-domain", "host-mode"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "dot1x-007",
      type: "single_choice",
      domain: "5.0",
      subdomain: "802.1X",
      difficulty: 3,
      prompt: "Which EAP type was developed by Cisco as a replacement for LEAP and uses a Protected Access Credential (PAC)?",
      options: [
        { id: "A", text: "PEAP", is_correct: false },
        { id: "B", text: "EAP-TLS", is_correct: false },
        { id: "C", text: "EAP-FAST", is_correct: true },
        { id: "D", text: "EAP-TTLS", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "EAP-FAST (Flexible Authentication via Secure Tunneling) is Cisco proprietary. It uses a PAC (Protected Access Credential) for authentication, which can be provisioned automatically or manually. It was designed to replace the vulnerable LEAP protocol while avoiding the PKI overhead of EAP-TLS.",
      cisco_reference: "ENCOR OCG Chapter 18 — EAP-FAST",
      expert_trap_warning: null,
      tags: ["802.1x", "eap-fast", "pac"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- MAB ---
    {
      id: "mab-001",
      type: "single_choice",
      domain: "5.0",
      subdomain: "MAB",
      difficulty: 2,
      prompt: "When is MAC Authentication Bypass (MAB) typically used?",
      options: [
        { id: "A", text: "When the RADIUS server is unavailable", is_correct: false },
        { id: "B", text: "When the endpoint does not support an 802.1X supplicant", is_correct: true },
        { id: "C", text: "When the switch does not support 802.1X", is_correct: false },
        { id: "D", text: "When EAP-TLS certificates expire", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "MAB is used for devices that cannot run an 802.1X supplicant — such as printers, IP cameras, badge readers, and IoT sensors. The switch sends the device's MAC address as both username and password to the RADIUS server. MAB is less secure than 802.1X because MAC addresses can be spoofed.",
      cisco_reference: "ENCOR OCG Chapter 18 — MAB",
      expert_trap_warning: null,
      tags: ["mab", "802.1x", "authentication"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "mab-002",
      type: "single_choice",
      domain: "5.0",
      subdomain: "MAB",
      difficulty: 2,
      prompt: "In a typical 802.1X deployment, what is the correct authentication order when dot1x and MAB are both configured?",
      options: [
        { id: "A", text: "MAB first, then 802.1X if MAB fails", is_correct: false },
        { id: "B", text: "802.1X and MAB simultaneously", is_correct: false },
        { id: "C", text: "802.1X first, then MAB if no EAP response", is_correct: true },
        { id: "D", text: "The order is random", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The standard order is 802.1X first. The switch sends EAPoL frames and waits for a supplicant response. If no EAP response is received (timeout), the switch falls back to MAB, sending the device's MAC address to the RADIUS server. The 'authentication order' command controls this sequence.",
      cisco_reference: "ENCOR OCG Chapter 18 — Authentication Order",
      expert_trap_warning: "Fallback to MAB happens on 802.1X TIMEOUT (no supplicant), not on 802.1X authentication failure.",
      tags: ["mab", "802.1x", "authentication-order"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- WebAuth ---
    {
      id: "webauth-001",
      type: "single_choice",
      domain: "5.0",
      subdomain: "WebAuth",
      difficulty: 2,
      prompt: "In Centralized Web Authentication (CWA), where is the login portal hosted?",
      options: [
        { id: "A", text: "On the switch", is_correct: false },
        { id: "B", text: "On the client device", is_correct: false },
        { id: "C", text: "On ISE (or RADIUS server)", is_correct: true },
        { id: "D", text: "On the DNS server", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In CWA, the web portal is hosted on the ISE server. The switch redirects the client's HTTP traffic to the ISE portal URL using a RADIUS url-redirect attribute. After the user authenticates via the portal, ISE sends a CoA (Change of Authorization) to the switch to update the client's access policy.",
      cisco_reference: "ENCOR OCG Chapter 18 — Web Authentication",
      expert_trap_warning: null,
      tags: ["webauth", "cwa", "ise"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "webauth-002",
      type: "single_choice",
      domain: "5.0",
      subdomain: "WebAuth",
      difficulty: 2,
      prompt: "Which is a key advantage of Centralized Web Authentication (CWA) over Local Web Authentication (LWA)?",
      options: [
        { id: "A", text: "CWA does not require a RADIUS server", is_correct: false },
        { id: "B", text: "CWA supports centralized policy management and guest workflows", is_correct: true },
        { id: "C", text: "CWA encrypts all user traffic", is_correct: false },
        { id: "D", text: "CWA does not use HTTP redirect", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "CWA provides centralized policy management through ISE, supports guest portals with sponsor approval, self-registration, and customizable branding. LWA is simpler (hosted on the switch) but lacks these advanced guest access features and centralized management.",
      cisco_reference: "ENCOR OCG Chapter 18 — CWA vs LWA",
      expert_trap_warning: null,
      tags: ["webauth", "cwa", "lwa", "guest"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- TrustSec / SGT ---
    {
      id: "trustsec-002",
      type: "single_choice",
      domain: "5.0",
      subdomain: "TrustSec",
      difficulty: 2,
      prompt: "In Cisco TrustSec, what is the purpose of SXP (SGT Exchange Protocol)?",
      options: [
        { id: "A", text: "To encrypt data traffic between switches", is_correct: false },
        { id: "B", text: "To propagate IP-to-SGT mappings to devices that do not support inline tagging", is_correct: true },
        { id: "C", text: "To assign SGTs to users during 802.1X authentication", is_correct: false },
        { id: "D", text: "To enforce SGACL policies at the ingress switch", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "SXP is a TCP-based control protocol (port 64999) that propagates IP-to-SGT binding information between devices. It is used when a device in the path does not support inline tagging (embedding SGT in the Ethernet frame). SXP uses speaker and listener roles.",
      cisco_reference: "ENCOR OCG Chapter 18 — TrustSec SXP",
      expert_trap_warning: null,
      tags: ["trustsec", "sxp", "sgt"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "trustsec-003",
      type: "single_choice",
      domain: "5.0",
      subdomain: "TrustSec",
      difficulty: 3,
      prompt: "Where are SGACLs (Scalable Group ACLs) enforced in a TrustSec domain?",
      options: [
        { id: "A", text: "At the ingress switch closest to the source", is_correct: false },
        { id: "B", text: "At every switch in the path", is_correct: false },
        { id: "C", text: "At the egress switch closest to the destination", is_correct: true },
        { id: "D", text: "At the ISE policy server", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "SGACLs are enforced at the egress device (closest to the destination). The egress switch checks the source SGT in the frame against the destination SGT and applies the SGACL policy matrix. Policies are defined on ISE and downloaded to the enforcement points.",
      cisco_reference: "ENCOR OCG Chapter 18 — SGACL Enforcement",
      expert_trap_warning: "Unlike traditional ACLs, SGACL enforcement happens at egress, not ingress.",
      tags: ["trustsec", "sgacl", "enforcement"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "trustsec-004",
      type: "multi_choice",
      domain: "5.0",
      subdomain: "TrustSec",
      difficulty: 2,
      prompt: "Which TWO methods can assign an SGT to a device in TrustSec? (Choose 2)",
      options: [
        { id: "A", text: "Dynamic assignment via ISE during 802.1X authentication", is_correct: true },
        { id: "B", text: "Static IP-to-SGT mapping configured on the switch", is_correct: true },
        { id: "C", text: "Automatic assignment based on MAC OUI", is_correct: false },
        { id: "D", text: "DNS-based SGT lookup", is_correct: false }
      ],
      correct_answer: ["A", "B"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "SGTs can be assigned dynamically by ISE via RADIUS attributes during 802.1X/MAB authentication, or statically configured on the switch using 'cts role-based sgt-map' commands mapping IP addresses/subnets to SGT values. SXP can also propagate these bindings.",
      cisco_reference: "ENCOR OCG Chapter 18 — SGT Assignment",
      expert_trap_warning: null,
      tags: ["trustsec", "sgt", "assignment"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- DHCP Snooping ---
    {
      id: "dhcpsnoop-004",
      type: "single_choice",
      domain: "5.0",
      subdomain: "DHCPSnooping",
      difficulty: 1,
      prompt: "Which DHCP messages are dropped on an untrusted port when DHCP snooping is enabled?",
      options: [
        { id: "A", text: "DHCP Discover and Request", is_correct: false },
        { id: "B", text: "DHCP Offer and Acknowledge", is_correct: true },
        { id: "C", text: "All DHCP messages", is_correct: false },
        { id: "D", text: "Only DHCP Release", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DHCP snooping drops server-originated messages (OFFER, ACK, NAK) on untrusted ports to prevent rogue DHCP servers. Client messages (DISCOVER, REQUEST, DECLINE, RELEASE, INFORM) are permitted on untrusted ports because clients legitimately send these.",
      cisco_reference: "ENCOR OCG Chapter 18 — DHCP Snooping",
      expert_trap_warning: null,
      tags: ["dhcp-snooping", "untrusted"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "dhcpsnoop-005",
      type: "single_choice",
      domain: "5.0",
      subdomain: "DHCPSnooping",
      difficulty: 2,
      prompt: "What happens when the DHCP snooping rate limit is exceeded on an untrusted port?",
      options: [
        { id: "A", text: "DHCP packets are silently dropped but the port stays up", is_correct: false },
        { id: "B", text: "The port enters err-disabled state", is_correct: true },
        { id: "C", text: "The switch logs a warning but continues forwarding", is_correct: false },
        { id: "D", text: "The DHCP binding table is cleared", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "When the DHCP snooping rate limit is exceeded, the port transitions to err-disabled state, shutting down the port entirely. To recover, use manual shutdown/no shutdown or configure errdisable recovery: 'errdisable recovery cause dhcp-rate-limit'.",
      cisco_reference: "ENCOR OCG Chapter 18 — DHCP Snooping Rate Limiting",
      expert_trap_warning: null,
      tags: ["dhcp-snooping", "rate-limit", "err-disabled"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- DAI ---
    {
      id: "dai-001",
      type: "single_choice",
      domain: "5.0",
      subdomain: "DAI",
      difficulty: 2,
      prompt: "Dynamic ARP Inspection (DAI) validates ARP packets against which table?",
      options: [
        { id: "A", text: "MAC address table", is_correct: false },
        { id: "B", text: "DHCP snooping binding table", is_correct: true },
        { id: "C", text: "Routing table", is_correct: false },
        { id: "D", text: "CDP neighbor table", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DAI intercepts ARP packets on untrusted ports and validates the source IP-to-MAC mapping against the DHCP snooping binding table. If no valid binding exists, the ARP packet is dropped. For static IP hosts, ARP ACLs can be configured manually. DHCP snooping must be enabled for DAI to function with dynamic bindings.",
      cisco_reference: "ENCOR OCG Chapter 18 — Dynamic ARP Inspection",
      expert_trap_warning: "DAI requires DHCP snooping to be enabled first (for the binding table). Without it, all ARP on untrusted ports is dropped.",
      tags: ["dai", "dhcp-snooping", "arp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "dai-002",
      type: "single_choice",
      domain: "5.0",
      subdomain: "DAI",
      difficulty: 2,
      prompt: "What is the default ARP rate limit on untrusted ports when DAI is enabled?",
      options: [
        { id: "A", text: "5 packets per second", is_correct: false },
        { id: "B", text: "15 packets per second", is_correct: true },
        { id: "C", text: "100 packets per second", is_correct: false },
        { id: "D", text: "No rate limit by default", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The default DAI rate limit on untrusted ports is 15 ARP packets per second. If this rate is exceeded, the port enters err-disabled state. Trusted ports have no rate limit by default. The rate can be adjusted with 'ip arp inspection limit rate <pps>'.",
      cisco_reference: "ENCOR OCG Chapter 18 — DAI Rate Limiting",
      expert_trap_warning: null,
      tags: ["dai", "rate-limit"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "dai-003",
      type: "single_choice",
      domain: "5.0",
      subdomain: "DAI",
      difficulty: 2,
      prompt: "Which attack does Dynamic ARP Inspection (DAI) prevent?",
      options: [
        { id: "A", text: "DHCP starvation", is_correct: false },
        { id: "B", text: "MAC flooding", is_correct: false },
        { id: "C", text: "ARP spoofing/poisoning (man-in-the-middle)", is_correct: true },
        { id: "D", text: "VLAN hopping", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DAI prevents ARP spoofing/poisoning attacks where an attacker sends gratuitous ARP replies with a forged source IP to redirect traffic through their device (man-in-the-middle). DAI validates that the IP-to-MAC mapping in ARP packets matches the DHCP snooping binding table.",
      cisco_reference: "ENCOR OCG Chapter 18 — ARP Spoofing Prevention",
      expert_trap_warning: null,
      tags: ["dai", "arp-poisoning", "mitm"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- Port Security ---
    {
      id: "portsec-005",
      type: "single_choice",
      domain: "5.0",
      subdomain: "PortSecurity",
      difficulty: 2,
      prompt: "Which port security violation mode drops frames silently without generating a syslog message or incrementing the violation counter?",
      options: [
        { id: "A", text: "Shutdown", is_correct: false },
        { id: "B", text: "Restrict", is_correct: false },
        { id: "C", text: "Protect", is_correct: true },
        { id: "D", text: "Drop", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Protect mode silently drops frames from unauthorized MAC addresses. It does NOT generate syslog messages, SNMP traps, or increment the violation counter. This makes troubleshooting difficult. Restrict mode also drops frames but generates notifications and increments the counter.",
      cisco_reference: "ENCOR OCG Chapter 18 — Port Security",
      expert_trap_warning: "Protect vs Restrict is a common exam trap — both drop frames, but only Restrict sends notifications.",
      tags: ["port-security", "protect", "restrict"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "portsec-006",
      type: "single_choice",
      domain: "5.0",
      subdomain: "PortSecurity",
      difficulty: 2,
      prompt: "What does the 'switchport port-security mac-address sticky' command do?",
      options: [
        { id: "A", text: "Configures static MAC addresses that cannot be changed", is_correct: false },
        { id: "B", text: "Dynamically learns MACs and adds them to the running configuration", is_correct: true },
        { id: "C", text: "Enables MAC filtering based on OUI", is_correct: false },
        { id: "D", text: "Enables port security with a maximum of 1 MAC", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Sticky MAC learning dynamically learns MAC addresses and converts them to 'switchport port-security mac-address sticky <MAC>' entries in the running configuration. If you save the config (write memory), these persist across reboots. This avoids manual configuration while maintaining persistence.",
      cisco_reference: "ENCOR OCG Chapter 18 — Sticky MAC",
      expert_trap_warning: "Sticky MACs are in the RUNNING config only until saved. A reboot without 'write memory' loses them.",
      tags: ["port-security", "sticky-mac"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- Zone-Based Firewall ---
    {
      id: "zbf-002",
      type: "single_choice",
      domain: "5.0",
      subdomain: "ZBF",
      difficulty: 2,
      prompt: "In a Zone-Based Firewall, what is the default behavior for traffic between two different security zones?",
      options: [
        { id: "A", text: "Permitted and inspected", is_correct: false },
        { id: "B", text: "Permitted without inspection", is_correct: false },
        { id: "C", text: "Denied (dropped)", is_correct: true },
        { id: "D", text: "Rate-limited to 1 Mbps", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "By default, all traffic between different zones is denied. You must create a zone-pair and attach a policy-map with explicit inspect/pass actions to allow inter-zone traffic. Traffic within the SAME zone is permitted by default. This is a deny-by-default security model.",
      cisco_reference: "ENCOR OCG Chapter 17 — Zone-Based Firewall",
      expert_trap_warning: null,
      tags: ["zbf", "default-deny", "zone-pair"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "zbf-003",
      type: "single_choice",
      domain: "5.0",
      subdomain: "ZBF",
      difficulty: 2,
      prompt: "Which ZBF policy-map action provides stateful packet inspection and automatically allows return traffic?",
      options: [
        { id: "A", text: "Pass", is_correct: false },
        { id: "B", text: "Drop", is_correct: false },
        { id: "C", text: "Inspect", is_correct: true },
        { id: "D", text: "Police", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The 'inspect' action provides stateful inspection — it tracks session state and automatically creates dynamic entries to permit return traffic. 'Pass' is stateless (forwards without tracking, return traffic needs a separate policy). 'Drop' discards the traffic. 'Police' is used in QoS, not ZBF.",
      cisco_reference: "ENCOR OCG Chapter 17 — ZBF Actions",
      expert_trap_warning: "'Pass' does NOT track sessions — if you use 'pass', you need a separate zone-pair for the return direction.",
      tags: ["zbf", "inspect", "stateful"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "zbf-004",
      type: "single_choice",
      domain: "5.0",
      subdomain: "ZBF",
      difficulty: 3,
      prompt: "What is the default behavior for traffic to and from the 'self' zone in a Zone-Based Firewall?",
      options: [
        { id: "A", text: "All traffic is denied by default", is_correct: false },
        { id: "B", text: "All traffic is permitted by default", is_correct: true },
        { id: "C", text: "Only routing protocols are permitted", is_correct: false },
        { id: "D", text: "Only SSH and HTTPS are permitted", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The self zone represents the router itself. By default, traffic TO and FROM the self zone is PERMITTED. This ensures management access and routing protocols continue to work. To restrict self-zone traffic, you must create explicit zone-pairs involving the self zone and apply restrictive policies.",
      cisco_reference: "ENCOR OCG Chapter 17 — Self Zone",
      expert_trap_warning: "This is opposite of inter-zone behavior (which is deny by default). Self zone defaults to PERMIT.",
      tags: ["zbf", "self-zone"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- IPsec / VPN ---
    {
      id: "ipsec-001",
      type: "single_choice",
      domain: "5.0",
      subdomain: "IPsec",
      difficulty: 2,
      prompt: "How many messages does IKEv2 require to establish a security association, compared to IKEv1 Main Mode?",
      options: [
        { id: "A", text: "2 messages (vs 6 for IKEv1)", is_correct: false },
        { id: "B", text: "4 messages (vs 6 for IKEv1)", is_correct: true },
        { id: "C", text: "6 messages (same as IKEv1)", is_correct: false },
        { id: "D", text: "8 messages (vs 6 for IKEv1)", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "IKEv2 requires only 4 messages (IKE_SA_INIT + IKE_AUTH, 2 messages each) to establish both the IKE SA and the first Child SA (IPsec SA). IKEv1 Main Mode uses 6 messages for Phase 1 alone, plus 3 more for Quick Mode (Phase 2) — totaling 9 messages.",
      cisco_reference: "ENCOR OCG Chapter 19 — IKEv2",
      expert_trap_warning: null,
      tags: ["ipsec", "ikev2", "ikev1"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ipsec-002",
      type: "single_choice",
      domain: "5.0",
      subdomain: "IPsec",
      difficulty: 2,
      prompt: "Which IPsec mode encapsulates the entire original IP packet and adds a new IP header?",
      options: [
        { id: "A", text: "Transport mode", is_correct: false },
        { id: "B", text: "Tunnel mode", is_correct: true },
        { id: "C", text: "Aggressive mode", is_correct: false },
        { id: "D", text: "Quick mode", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Tunnel mode encapsulates the entire original IP packet (including the original IP header) and prepends a new outer IP header. This is used for site-to-site VPNs. Transport mode only encrypts the payload (keeping the original IP header), used for host-to-host communication.",
      cisco_reference: "ENCOR OCG Chapter 19 — IPsec Modes",
      expert_trap_warning: null,
      tags: ["ipsec", "tunnel-mode", "transport-mode"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ipsec-003",
      type: "single_choice",
      domain: "5.0",
      subdomain: "IPsec",
      difficulty: 2,
      prompt: "What is the main advantage of using VTI (Virtual Tunnel Interface) over crypto maps for IPsec?",
      options: [
        { id: "A", text: "VTI supports stronger encryption algorithms", is_correct: false },
        { id: "B", text: "VTI supports dynamic routing protocols over the IPsec tunnel", is_correct: true },
        { id: "C", text: "VTI does not require IKE negotiation", is_correct: false },
        { id: "D", text: "VTI uses less CPU", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "VTI creates a routable tunnel interface, allowing dynamic routing protocols (OSPF, EIGRP, BGP) to run directly over the IPsec tunnel. Crypto maps are policy-based and require ACLs to define interesting traffic — they do not support dynamic routing over the tunnel. VTI is the modern preferred approach.",
      cisco_reference: "ENCOR OCG Chapter 19 — VTI vs Crypto Maps",
      expert_trap_warning: null,
      tags: ["ipsec", "vti", "crypto-map", "routing"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ipsec-004",
      type: "single_choice",
      domain: "5.0",
      subdomain: "IPsec",
      difficulty: 3,
      prompt: "In DMVPN Phase 3, which NHRP feature enables spoke-to-spoke tunnels without the hub being in the data path?",
      options: [
        { id: "A", text: "NHRP registration", is_correct: false },
        { id: "B", text: "NHRP resolution request", is_correct: false },
        { id: "C", text: "NHRP redirect and shortcut", is_correct: true },
        { id: "D", text: "NHRP authentication", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In DMVPN Phase 3, when a spoke sends traffic through the hub, the hub sends an NHRP Redirect to the source spoke. The source spoke then sends an NHRP Resolution Request directly to the destination spoke and creates an NHRP Shortcut (direct tunnel). This removes the hub from the data path.",
      cisco_reference: "ENCOR OCG Chapter 19 — DMVPN Phase 3",
      expert_trap_warning: "Phase 2 also supports spoke-to-spoke but requires the routing protocol to preserve next-hop — Phase 3 uses NHRP shortcuts which work regardless of routing next-hop.",
      tags: ["dmvpn", "nhrp", "phase3"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- MACsec ---
    {
      id: "macsec-002",
      type: "single_choice",
      domain: "5.0",
      subdomain: "MACsec",
      difficulty: 2,
      prompt: "What is the purpose of the MKA (MACsec Key Agreement) protocol?",
      options: [
        { id: "A", text: "To encrypt Layer 3 traffic between routers", is_correct: false },
        { id: "B", text: "To negotiate and distribute encryption keys for MACsec", is_correct: true },
        { id: "C", text: "To authenticate users via 802.1X", is_correct: false },
        { id: "D", text: "To establish IPsec tunnels", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "MKA (IEEE 802.1X-2010) handles key management for MACsec. It discovers MACsec-capable peers, elects a Key Server (lowest priority), generates and distributes the SAK (Secure Association Key) used for encryption, and handles key rotation. MKA uses EAPoL-MKA frames.",
      cisco_reference: "ENCOR OCG Chapter 18 — MACsec and MKA",
      expert_trap_warning: null,
      tags: ["macsec", "mka", "key-management"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "macsec-003",
      type: "single_choice",
      domain: "5.0",
      subdomain: "MACsec",
      difficulty: 2,
      prompt: "What happens when a MACsec 'must-secure' policy is configured and the peer does not support MACsec?",
      options: [
        { id: "A", text: "Traffic flows unencrypted", is_correct: false },
        { id: "B", text: "Traffic is rate-limited", is_correct: false },
        { id: "C", text: "The link is blocked (no traffic flows)", is_correct: true },
        { id: "D", text: "MACsec is automatically downgraded to IPsec", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "With must-secure policy, MACsec encryption is mandatory. If the MKA session cannot be established (peer doesn't support MACsec), the link blocks all traffic. The should-secure policy allows cleartext fallback when MACsec is unavailable.",
      cisco_reference: "ENCOR OCG Chapter 18 — MACsec Policies",
      expert_trap_warning: null,
      tags: ["macsec", "must-secure", "should-secure"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- uRPF ---
    {
      id: "urpf-003",
      type: "single_choice",
      domain: "5.0",
      subdomain: "uRPF",
      difficulty: 2,
      prompt: "Which uRPF mode should be used on a multi-homed router where asymmetric routing is expected?",
      options: [
        { id: "A", text: "Strict mode", is_correct: false },
        { id: "B", text: "Loose mode", is_correct: true },
        { id: "C", text: "Feasible-path mode", is_correct: false },
        { id: "D", text: "Passive mode", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Loose mode only checks that the source IP EXISTS in the FIB (any route, any interface). It doesn't check which interface the packet arrived on. This is ideal for multi-homed environments where traffic may arrive on a different interface than the FIB route indicates (asymmetric routing). Strict mode requires the source to be reachable via the SAME interface.",
      cisco_reference: "ENCOR OCG Chapter 17 — uRPF",
      expert_trap_warning: null,
      tags: ["urpf", "loose-mode", "asymmetric"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "urpf-004",
      type: "single_choice",
      domain: "5.0",
      subdomain: "uRPF",
      difficulty: 2,
      prompt: "What does the IOS-XE command 'ip verify unicast source reachable-via rx' enable?",
      options: [
        { id: "A", text: "uRPF in loose mode", is_correct: false },
        { id: "B", text: "uRPF in strict mode", is_correct: true },
        { id: "C", text: "IP Source Guard", is_correct: false },
        { id: "D", text: "Reverse ARP checking", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The 'rx' keyword means the source must be reachable via the RECEIVING interface (strict mode). The 'any' keyword means the source just needs to exist in the FIB regardless of interface (loose mode). Remember: rx = strict, any = loose.",
      cisco_reference: "ENCOR OCG Chapter 17 — uRPF Configuration",
      expert_trap_warning: "Remember the mapping: rx = strict mode, any = loose mode. This is frequently tested.",
      tags: ["urpf", "strict-mode", "configuration"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- IPv6 Security ---
    {
      id: "ipv6sec-001",
      type: "single_choice",
      domain: "5.0",
      subdomain: "IPv6Security",
      difficulty: 2,
      prompt: "What does IPv6 RA Guard protect against?",
      options: [
        { id: "A", text: "Rogue DHCPv6 servers", is_correct: false },
        { id: "B", text: "Rogue Router Advertisements from unauthorized devices", is_correct: true },
        { id: "C", text: "IPv6 address spoofing", is_correct: false },
        { id: "D", text: "ICMPv6 flood attacks", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "RA Guard filters Router Advertisement messages on switch ports. Host-facing ports are configured with 'device-role host' to block RAs. Router-facing ports use 'device-role router' to permit RAs. This prevents attackers from sending fake RAs to become the default gateway or advertise malicious prefixes.",
      cisco_reference: "ENCOR OCG Chapter 18 — IPv6 First Hop Security",
      expert_trap_warning: null,
      tags: ["ipv6", "ra-guard", "ndp"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ipv6sec-002",
      type: "single_choice",
      domain: "5.0",
      subdomain: "IPv6Security",
      difficulty: 2,
      prompt: "Which IPv6 First Hop Security feature is the equivalent of DHCP snooping for IPv6?",
      options: [
        { id: "A", text: "RA Guard", is_correct: false },
        { id: "B", text: "DHCPv6 Guard", is_correct: true },
        { id: "C", text: "ND Inspection", is_correct: false },
        { id: "D", text: "Source Guard", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DHCPv6 Guard filters DHCPv6 server messages on untrusted ports, preventing rogue DHCPv6 servers — exactly analogous to DHCP snooping for IPv4. RA Guard protects against rogue router advertisements. ND Inspection validates Neighbor Discovery messages (similar to DAI for IPv4).",
      cisco_reference: "ENCOR OCG Chapter 18 — DHCPv6 Guard",
      expert_trap_warning: null,
      tags: ["ipv6", "dhcpv6-guard", "first-hop-security"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- Drag and Drop (Security) ---
    {
      id: "sec-010",
      type: "drag_drop",
      domain: "5.0",
      subdomain: "Layer2Security",
      difficulty: 2,
      prompt: "Match each Layer 2 security feature with the attack it prevents.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "DHCP Snooping" },
        { id: "item2", text: "Dynamic ARP Inspection" },
        { id: "item3", text: "Port Security" },
        { id: "item4", text: "RA Guard" }
      ],
      drop_targets: [
        { id: "target1", label: "Rogue DHCP server", accepts: "item1" },
        { id: "target2", label: "ARP spoofing / man-in-the-middle", accepts: "item2" },
        { id: "target3", label: "MAC flooding / CAM table overflow", accepts: "item3" },
        { id: "target4", label: "Rogue IPv6 Router Advertisements", accepts: "item4" }
      ],
      explanation: "DHCP Snooping prevents rogue DHCP servers by filtering server messages on untrusted ports. DAI prevents ARP spoofing by validating ARP against the binding table. Port Security prevents MAC flooding by limiting MACs per port. RA Guard blocks unauthorized IPv6 Router Advertisements.",
      cisco_reference: "ENCOR OCG Chapter 18 — Layer 2 Security Features",
      expert_trap_warning: null,
      tags: ["layer2-security", "dhcp-snooping", "dai", "port-security", "ra-guard"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sec-021",
      type: "drag_drop",
      domain: "5.0",
      subdomain: "AAA",
      difficulty: 2,
      prompt: "Match each characteristic to RADIUS or TACACS+.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "TCP port 49" },
        { id: "item2", text: "UDP ports 1812/1813" },
        { id: "item3", text: "Encrypts entire packet body" },
        { id: "item4", text: "Encrypts only the password" }
      ],
      drop_targets: [
        { id: "target1", label: "TACACS+", accepts: "item1" },
        { id: "target2", label: "RADIUS", accepts: "item2" },
        { id: "target3", label: "TACACS+", accepts: "item3" },
        { id: "target4", label: "RADIUS", accepts: "item4" }
      ],
      explanation: "TACACS+ uses TCP port 49 and encrypts the entire packet body. RADIUS uses UDP ports 1812/1813 (or legacy 1645/1646) and only encrypts the password field. These differences make TACACS+ more suitable for device administration and RADIUS more common for network access.",
      cisco_reference: "ENCOR OCG Chapter 17 — RADIUS vs TACACS+",
      expert_trap_warning: null,
      tags: ["aaa", "radius", "tacacs", "comparison"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sec-022",
      type: "drag_drop",
      domain: "5.0",
      subdomain: "PortSecurity",
      difficulty: 2,
      prompt: "Match each port security violation mode with its behavior.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "Shutdown" },
        { id: "item2", text: "Restrict" },
        { id: "item3", text: "Protect" }
      ],
      drop_targets: [
        { id: "target1", label: "Port enters err-disabled, sends syslog and SNMP trap", accepts: "item1" },
        { id: "target2", label: "Drops frames, increments counter, sends syslog and SNMP trap", accepts: "item2" },
        { id: "target3", label: "Drops frames silently — no syslog, no counter increment", accepts: "item3" }
      ],
      explanation: "Shutdown (default) puts the port in err-disabled state with notifications. Restrict drops violating frames and generates notifications. Protect drops frames silently with no notifications or counter increments, making it the hardest to troubleshoot.",
      cisco_reference: "ENCOR OCG Chapter 18 — Port Security Violation Modes",
      expert_trap_warning: null,
      tags: ["port-security", "violation-modes"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sec-023",
      type: "multi_choice",
      domain: "5.0",
      subdomain: "IPsec",
      difficulty: 3,
      prompt: "Which THREE are improvements of IKEv2 over IKEv1? (Choose 3)",
      options: [
        { id: "A", text: "Built-in NAT traversal (NAT-T)", is_correct: true },
        { id: "B", text: "Fewer messages to establish SA (4 vs 9)", is_correct: true },
        { id: "C", text: "Uses UDP port 4500 exclusively", is_correct: false },
        { id: "D", text: "Built-in EAP authentication support", is_correct: true }
      ],
      correct_answer: ["A", "B", "D"],
      required_selections: 3,
      drag_items: null,
      drop_targets: null,
      explanation: "IKEv2 has built-in NAT-T (no separate RFC needed), requires only 4 messages for IKE+IPsec SA establishment (vs 9 for IKEv1 Main+Quick mode), and includes native EAP support. IKEv2 starts on UDP 500 and switches to 4500 only when NAT is detected — it doesn't use 4500 exclusively.",
      cisco_reference: "ENCOR OCG Chapter 19 — IKEv1 vs IKEv2",
      expert_trap_warning: "IKEv2 uses UDP 500 initially and 4500 only when NAT is detected — not exclusively 4500.",
      tags: ["ipsec", "ikev2", "improvements"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "sec-014",
      type: "single_choice",
      domain: "5.0",
      subdomain: "IPsec",
      difficulty: 2,
      prompt: "Which IPsec protocol provides encryption AND integrity, and is the most commonly used?",
      options: [
        { id: "A", text: "AH (Authentication Header)", is_correct: false },
        { id: "B", text: "ESP (Encapsulating Security Payload)", is_correct: true },
        { id: "C", text: "IKE (Internet Key Exchange)", is_correct: false },
        { id: "D", text: "ISAKMP", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "ESP (IP protocol 50) provides both encryption and integrity/authentication. AH (IP protocol 51) provides only integrity/authentication without encryption and breaks with NAT (because it includes the IP header in its hash). ESP is used in virtually all modern IPsec deployments.",
      cisco_reference: "ENCOR OCG Chapter 19 — ESP vs AH",
      expert_trap_warning: null,
      tags: ["ipsec", "esp", "ah"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // =========================================================
    // DOMAIN 6.0 — AUTOMATION (~29 questions)
    // =========================================================

    // --- JSON/XML/YAML ---
    {
      id: "json-003",
      type: "single_choice",
      domain: "6.0",
      subdomain: "DataFormats",
      difficulty: 2,
      prompt: "Which data format does NETCONF use exclusively for encoding?",
      options: [
        { id: "A", text: "JSON", is_correct: false },
        { id: "B", text: "XML", is_correct: true },
        { id: "C", text: "YAML", is_correct: false },
        { id: "D", text: "Protocol Buffers", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "NETCONF uses XML exclusively for data encoding. All RPC requests and responses are formatted in XML, structured according to YANG data models. RESTCONF supports both JSON and XML. gNMI uses Protocol Buffers (binary encoding).",
      cisco_reference: "ENCOR OCG Chapter 20 — NETCONF",
      expert_trap_warning: "NETCONF = XML only. RESTCONF = JSON or XML. Don't confuse them.",
      tags: ["netconf", "xml", "data-formats"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "json-004",
      type: "drag_drop",
      domain: "6.0",
      subdomain: "DataFormats",
      difficulty: 1,
      prompt: "Match each data format to the protocol or tool that primarily uses it.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "XML" },
        { id: "item2", text: "JSON" },
        { id: "item3", text: "YAML" },
        { id: "item4", text: "Protocol Buffers" }
      ],
      drop_targets: [
        { id: "target1", label: "NETCONF", accepts: "item1" },
        { id: "target2", label: "REST APIs (DNA Center)", accepts: "item2" },
        { id: "target3", label: "Ansible playbooks", accepts: "item3" },
        { id: "target4", label: "gNMI / gRPC", accepts: "item4" }
      ],
      explanation: "NETCONF uses XML exclusively. REST APIs (including DNA Center) primarily use JSON. Ansible playbooks are written in YAML. gNMI/gRPC uses Protocol Buffers (protobuf) for efficient binary encoding.",
      cisco_reference: "ENCOR OCG Chapter 20 — Data Encoding Formats",
      expert_trap_warning: null,
      tags: ["data-formats", "json", "xml", "yaml", "protobuf"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- gRPC / gNMI ---
    {
      id: "grpc-001",
      type: "single_choice",
      domain: "6.0",
      subdomain: "gNMI",
      difficulty: 2,
      prompt: "Which transport protocol does gNMI use?",
      options: [
        { id: "A", text: "SSH", is_correct: false },
        { id: "B", text: "HTTPS (HTTP/1.1)", is_correct: false },
        { id: "C", text: "gRPC over HTTP/2", is_correct: true },
        { id: "D", text: "NETCONF over TCP", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "gNMI runs over gRPC, which uses HTTP/2 as its transport protocol. HTTP/2 provides multiplexing, bidirectional streaming, and header compression. Compare: NETCONF uses SSH (TCP 830), RESTCONF uses HTTPS (HTTP/1.1, TCP 443).",
      cisco_reference: "ENCOR OCG Chapter 20 — gNMI and gRPC",
      expert_trap_warning: null,
      tags: ["gnmi", "grpc", "http2"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "grpc-002",
      type: "single_choice",
      domain: "6.0",
      subdomain: "gNMI",
      difficulty: 2,
      prompt: "In model-driven telemetry, what is a 'dial-out' subscription?",
      options: [
        { id: "A", text: "The collector connects to the device and requests data", is_correct: false },
        { id: "B", text: "The device initiates a connection to the collector and pushes data", is_correct: true },
        { id: "C", text: "The device polls the collector for configuration updates", is_correct: false },
        { id: "D", text: "A one-time data export triggered by SNMP", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In dial-out mode, the network device initiates the connection to the telemetry collector and pushes data based on a configured subscription. This is preferred when devices are behind NAT/firewalls. In dial-in mode, the collector initiates the connection to the device.",
      cisco_reference: "ENCOR OCG Chapter 20 — Model-Driven Telemetry",
      expert_trap_warning: null,
      tags: ["telemetry", "dial-out", "streaming"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "grpc-003",
      type: "multi_choice",
      domain: "6.0",
      subdomain: "gNMI",
      difficulty: 2,
      prompt: "Which TWO are advantages of gNMI over NETCONF? (Choose 2)",
      options: [
        { id: "A", text: "Native support for streaming telemetry subscriptions", is_correct: true },
        { id: "B", text: "Uses human-readable XML encoding", is_correct: false },
        { id: "C", text: "Binary encoding (Protocol Buffers) for efficiency", is_correct: true },
        { id: "D", text: "Built-in candidate datastore with commit/rollback", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "gNMI excels at streaming telemetry (Subscribe RPC with STREAM/POLL/ONCE modes) and uses efficient binary Protocol Buffers encoding. NETCONF uses verbose XML and while it supports notifications, it lacks the rich subscription model of gNMI. NETCONF has the advantage of candidate datastores with commit/rollback.",
      cisco_reference: "ENCOR OCG Chapter 20 — gNMI vs NETCONF",
      expert_trap_warning: null,
      tags: ["gnmi", "netconf", "comparison", "telemetry"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- DNA Center APIs ---
    {
      id: "dnacapi-001",
      type: "single_choice",
      domain: "6.0",
      subdomain: "DNACenterAPI",
      difficulty: 2,
      prompt: "How does a client authenticate to the Cisco DNA Center REST API?",
      options: [
        { id: "A", text: "API key in the URL query string", is_correct: false },
        { id: "B", text: "OAuth 2.0 with client credentials", is_correct: false },
        { id: "C", text: "POST to /auth/token with Basic Auth, then use the returned token in X-Auth-Token header", is_correct: true },
        { id: "D", text: "Certificate-based mutual TLS", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "DNA Center API authentication: POST to /dna/system/api/v1/auth/token with HTTP Basic Auth (username:password Base64 encoded). The response contains a JWT token. Use this token in all subsequent requests via the 'X-Auth-Token' header. Tokens expire after 60 minutes by default.",
      cisco_reference: "ENCOR OCG Chapter 21 — DNA Center API Authentication",
      expert_trap_warning: null,
      tags: ["dna-center", "api", "authentication"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "dnacapi-002",
      type: "single_choice",
      domain: "6.0",
      subdomain: "DNACenterAPI",
      difficulty: 2,
      prompt: "Which DNA Center API category provides simplified, abstracted APIs for common network tasks like provisioning and health monitoring?",
      options: [
        { id: "A", text: "Integration API", is_correct: false },
        { id: "B", text: "Intent API", is_correct: true },
        { id: "C", text: "Command Runner API", is_correct: false },
        { id: "D", text: "Events API", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The Intent API is the northbound REST API that abstracts complex network operations into simple API calls. It covers tasks like device provisioning, network health, client tracking, and path trace. The Integration API is for ITSM/webhook integrations. Command Runner executes CLI commands.",
      cisco_reference: "ENCOR OCG Chapter 21 — DNA Center Intent API",
      expert_trap_warning: null,
      tags: ["dna-center", "intent-api"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "dnacapi-003",
      type: "single_choice",
      domain: "6.0",
      subdomain: "DNACenter",
      difficulty: 2,
      prompt: "What is the purpose of SWIM (Software Image Management) in DNA Center?",
      options: [
        { id: "A", text: "Manage VLAN configurations across the network", is_correct: false },
        { id: "B", text: "Manage and deploy software images to ensure compliance with golden images", is_correct: true },
        { id: "C", text: "Monitor swimming (spanning tree) protocol convergence", is_correct: false },
        { id: "D", text: "Manage SSL/TLS certificates for network devices", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "SWIM (Software Image Management) in DNA Center manages IOS/IOS-XE images across the network. Key features: designate golden images, check device compliance against golden images, stage images to devices, and schedule upgrades. This ensures all devices run the approved software version.",
      cisco_reference: "ENCOR OCG Chapter 21 — DNA Center SWIM",
      expert_trap_warning: null,
      tags: ["dna-center", "swim", "image-management"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "dnacapi-004",
      type: "single_choice",
      domain: "6.0",
      subdomain: "DNACenter",
      difficulty: 2,
      prompt: "Which DNA Center feature allows creation of reusable configuration templates with variables using Jinja2 or Velocity syntax?",
      options: [
        { id: "A", text: "SWIM", is_correct: false },
        { id: "B", text: "Network Profiles", is_correct: false },
        { id: "C", text: "Template Editor", is_correct: true },
        { id: "D", text: "Command Runner", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The Template Editor creates reusable configuration templates with variables and logic using Jinja2 or Velocity template syntax. Templates can be versioned, assigned to sites/devices, and deployed during provisioning (day-0 onboarding or day-N changes).",
      cisco_reference: "ENCOR OCG Chapter 21 — DNA Center Template Editor",
      expert_trap_warning: null,
      tags: ["dna-center", "template-editor", "jinja2"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- Python ---
    {
      id: "python-005",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Python",
      difficulty: 2,
      prompt: "Which Python library simplifies SSH connections to network devices by handling prompts, enable mode, and vendor-specific behavior?",
      options: [
        { id: "A", text: "Paramiko", is_correct: false },
        { id: "B", text: "Netmiko", is_correct: true },
        { id: "C", text: "Requests", is_correct: false },
        { id: "D", text: "Flask", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Netmiko is built on top of Paramiko and adds network-device-specific handling: automatic detection of prompts, enable mode entry, send_command() for show commands, send_config_set() for configuration, and support for 50+ network vendors. Paramiko is a low-level SSH library requiring manual I/O handling.",
      cisco_reference: "ENCOR OCG Chapter 21 — Python for Network Automation",
      expert_trap_warning: null,
      tags: ["python", "netmiko", "ssh"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "python-006",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Python",
      difficulty: 2,
      prompt: "Which Python library provides a vendor-neutral abstraction layer with unified getters like get_interfaces() and get_bgp_neighbors()?",
      options: [
        { id: "A", text: "Netmiko", is_correct: false },
        { id: "B", text: "Paramiko", is_correct: false },
        { id: "C", text: "NAPALM", is_correct: true },
        { id: "D", text: "Nornir", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "NAPALM (Network Automation and Programmability Abstraction Layer with Multivendor support) provides unified getter methods that return structured data regardless of vendor. It also supports config replace/merge with diff and rollback. NAPALM uses Netmiko or vendor APIs underneath.",
      cisco_reference: "ENCOR OCG Chapter 21 — NAPALM",
      expert_trap_warning: null,
      tags: ["python", "napalm", "multivendor"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "python-007",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Python",
      difficulty: 2,
      prompt: "Which Python method in the 'requests' library is used to send data to a REST API endpoint to create a new resource?",
      options: [
        { id: "A", text: "requests.get()", is_correct: false },
        { id: "B", text: "requests.post()", is_correct: true },
        { id: "C", text: "requests.delete()", is_correct: false },
        { id: "D", text: "requests.head()", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "requests.post() sends an HTTP POST request, which is the standard method for creating new resources in REST APIs. GET retrieves, PUT creates/replaces, PATCH partially updates, and DELETE removes. The requests library's json= parameter auto-serializes Python dicts to JSON.",
      cisco_reference: "ENCOR OCG Chapter 21 — Python REST API Interactions",
      expert_trap_warning: null,
      tags: ["python", "requests", "rest-api", "post"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "python-008",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Python",
      difficulty: 3,
      prompt: "What is Nornir in the context of network automation?",
      options: [
        { id: "A", text: "A YANG model compiler", is_correct: false },
        { id: "B", text: "A Python automation framework with inventory, threading, and plugin-based task execution", is_correct: true },
        { id: "C", text: "A Cisco-proprietary REST API client", is_correct: false },
        { id: "D", text: "An SSH key management tool", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Nornir is a pure-Python automation framework (similar to Ansible but Python-native). It provides inventory management, concurrent task execution with threading, and a plugin system. It uses Netmiko and NAPALM as plugins for device interaction. Being pure Python gives full programmatic control vs Ansible's YAML-based approach.",
      cisco_reference: "ENCOR OCG Chapter 21 — Network Automation Frameworks",
      expert_trap_warning: null,
      tags: ["python", "nornir", "framework"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- Ansible ---
    {
      id: "ansible-005",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Ansible",
      difficulty: 2,
      prompt: "Which Ansible module is used to push configuration lines to a Cisco IOS-XE device?",
      options: [
        { id: "A", text: "cisco.ios.ios_command", is_correct: false },
        { id: "B", text: "cisco.ios.ios_config", is_correct: true },
        { id: "C", text: "cisco.ios.ios_facts", is_correct: false },
        { id: "D", text: "cisco.ios.ios_interface", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "ios_config is used to push configuration lines (enters config mode, sends lines, exits). ios_command is for sending show/exec commands (read-only). ios_facts gathers device information (hostname, interfaces, neighbors). The 'lines' parameter in ios_config specifies the configuration commands.",
      cisco_reference: "ENCOR OCG Chapter 21 — Ansible Network Modules",
      expert_trap_warning: "ios_command is for show commands only. Use ios_config for configuration changes.",
      tags: ["ansible", "ios_config", "modules"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ansible-006",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Ansible",
      difficulty: 2,
      prompt: "In an Ansible inventory file, what is the purpose of 'group_vars'?",
      options: [
        { id: "A", text: "To define variables that apply to all hosts in a group", is_correct: true },
        { id: "B", text: "To group hosts by geographic location only", is_correct: false },
        { id: "C", text: "To set the execution order of tasks", is_correct: false },
        { id: "D", text: "To encrypt sensitive variables", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "group_vars/ is a directory containing YAML files that define variables applied to all hosts in a specific group. For example, group_vars/routers.yml would apply to all hosts in the [routers] group. This avoids repeating variables for each host. host_vars/ contains per-host variables.",
      cisco_reference: "ENCOR OCG Chapter 21 — Ansible Inventory",
      expert_trap_warning: null,
      tags: ["ansible", "inventory", "group_vars"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ansible-007",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Ansible",
      difficulty: 2,
      prompt: "What connection type should be used in Ansible when managing Cisco IOS-XE devices via CLI over SSH?",
      options: [
        { id: "A", text: "ansible.builtin.ssh", is_correct: false },
        { id: "B", text: "ansible.netcommon.network_cli", is_correct: true },
        { id: "C", text: "ansible.builtin.local", is_correct: false },
        { id: "D", text: "ansible.netcommon.httpapi", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "network_cli is the connection plugin for SSH-based CLI management of network devices. Unlike the standard 'ssh' connection (which expects Python on the target), network_cli runs modules on the control node and sends CLI commands over SSH. Set ansible_network_os: cisco.ios.ios to identify the platform.",
      cisco_reference: "ENCOR OCG Chapter 21 — Ansible Network Connections",
      expert_trap_warning: null,
      tags: ["ansible", "network_cli", "connection"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "ansible-008",
      type: "multi_choice",
      domain: "6.0",
      subdomain: "Ansible",
      difficulty: 2,
      prompt: "Which TWO are components of an Ansible role? (Choose 2)",
      options: [
        { id: "A", text: "tasks/ directory containing main.yml", is_correct: true },
        { id: "B", text: "A compiled binary executable", is_correct: false },
        { id: "C", text: "templates/ directory for Jinja2 templates", is_correct: true },
        { id: "D", text: "A mandatory Dockerfile", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Ansible roles have a standard directory structure including tasks/, templates/, handlers/, vars/, defaults/, files/, and meta/. The tasks/main.yml contains the role's task list. The templates/ directory holds Jinja2 templates for generating configuration files. Roles are reusable and shareable via Ansible Galaxy.",
      cisco_reference: "ENCOR OCG Chapter 21 — Ansible Roles",
      expert_trap_warning: null,
      tags: ["ansible", "roles", "structure"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- RESTCONF ---
    {
      id: "restconf-003",
      type: "single_choice",
      domain: "6.0",
      subdomain: "RESTCONF",
      difficulty: 2,
      prompt: "Which transport protocol does RESTCONF use?",
      options: [
        { id: "A", text: "SSH on TCP port 830", is_correct: false },
        { id: "B", text: "HTTPS on TCP port 443", is_correct: true },
        { id: "C", text: "gRPC on HTTP/2", is_correct: false },
        { id: "D", text: "SNMP on UDP port 161", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "RESTCONF uses HTTPS (TCP 443) as its transport. NETCONF uses SSH (TCP 830). gNMI uses gRPC over HTTP/2. RESTCONF maps YANG data models to RESTful HTTP operations (GET, POST, PUT, PATCH, DELETE), making it accessible to web developers.",
      cisco_reference: "ENCOR OCG Chapter 20 — RESTCONF Transport",
      expert_trap_warning: null,
      tags: ["restconf", "https", "transport"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "restconf-004",
      type: "single_choice",
      domain: "6.0",
      subdomain: "RESTCONF",
      difficulty: 2,
      prompt: "Which HTTP method in RESTCONF is used to merge/update a resource without replacing the entire resource?",
      options: [
        { id: "A", text: "GET", is_correct: false },
        { id: "B", text: "PUT", is_correct: false },
        { id: "C", text: "PATCH", is_correct: true },
        { id: "D", text: "POST", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "PATCH merges/updates specific attributes of an existing resource without replacing the entire resource. PUT creates or replaces the entire resource. POST creates a new resource. GET retrieves data. DELETE removes a resource. PATCH is the safest for partial updates.",
      cisco_reference: "ENCOR OCG Chapter 20 — RESTCONF Operations",
      expert_trap_warning: "PUT replaces the ENTIRE resource — if you only send partial data with PUT, missing fields may be deleted. Use PATCH for partial updates.",
      tags: ["restconf", "http-methods", "patch"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "restconf-005",
      type: "single_choice",
      domain: "6.0",
      subdomain: "RESTCONF",
      difficulty: 3,
      prompt: "What is the correct RESTCONF URL format to access the configuration of GigabitEthernet1 on a device?",
      options: [
        { id: "A", text: "/restconf/data/ietf-interfaces:interfaces/interface=GigabitEthernet1", is_correct: true },
        { id: "B", text: "/restconf/ietf-interfaces/GigabitEthernet1", is_correct: false },
        { id: "C", text: "/restconf/config/interfaces/GigabitEthernet1", is_correct: false },
        { id: "D", text: "/api/v1/interfaces/GigabitEthernet1", is_correct: false }
      ],
      correct_answer: "A",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "RESTCONF URLs follow the pattern: /restconf/data/<module>:<container>/<list>=<key>. The 'data' resource represents both configuration and operational state. The YANG module name (ietf-interfaces) prefixes the container, and list keys are specified with '=' notation.",
      cisco_reference: "ENCOR OCG Chapter 20 — RESTCONF URL Structure",
      expert_trap_warning: null,
      tags: ["restconf", "url", "yang"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "restconf-006",
      type: "drag_drop",
      domain: "6.0",
      subdomain: "RESTCONF",
      difficulty: 2,
      prompt: "Match each management protocol with its transport.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "NETCONF" },
        { id: "item2", text: "RESTCONF" },
        { id: "item3", text: "gNMI" },
        { id: "item4", text: "SNMP" }
      ],
      drop_targets: [
        { id: "target1", label: "SSH (TCP 830)", accepts: "item1" },
        { id: "target2", label: "HTTPS (TCP 443)", accepts: "item2" },
        { id: "target3", label: "gRPC over HTTP/2", accepts: "item3" },
        { id: "target4", label: "UDP 161/162", accepts: "item4" }
      ],
      explanation: "NETCONF uses SSH on TCP port 830 with XML encoding. RESTCONF uses HTTPS on TCP port 443 with JSON or XML. gNMI uses gRPC over HTTP/2 with Protocol Buffers. SNMP uses UDP ports 161 (queries) and 162 (traps).",
      cisco_reference: "ENCOR OCG Chapter 20 — Network Management Protocols",
      expert_trap_warning: null,
      tags: ["netconf", "restconf", "gnmi", "snmp", "transport"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- Git / CI/CD ---
    {
      id: "git-001",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Git",
      difficulty: 1,
      prompt: "What is the primary purpose of Git in a network automation context?",
      options: [
        { id: "A", text: "To compile Python scripts into executables", is_correct: false },
        { id: "B", text: "To provide version control for network configurations and automation scripts", is_correct: true },
        { id: "C", text: "To replace TFTP for file transfers to network devices", is_correct: false },
        { id: "D", text: "To encrypt configuration files at rest", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "Git provides version control for network configurations and automation code, enabling change tracking, collaboration via branches and pull requests, and the ability to revert to previous versions. This is the foundation of NetDevOps and Infrastructure as Code (IaC) practices.",
      cisco_reference: "ENCOR OCG Chapter 21 — Version Control",
      expert_trap_warning: null,
      tags: ["git", "version-control", "netdevops"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "git-002",
      type: "single_choice",
      domain: "6.0",
      subdomain: "Git",
      difficulty: 2,
      prompt: "In a CI/CD pipeline for network automation, what typically happens after a Git push to the main branch?",
      options: [
        { id: "A", text: "The network devices automatically pull the config via TFTP", is_correct: false },
        { id: "B", text: "Automated validation and deployment tools push changes to devices", is_correct: true },
        { id: "C", text: "Git compiles the configuration into binary format", is_correct: false },
        { id: "D", text: "The RADIUS server updates its policies", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "In a CI/CD pipeline for networking: a Git push triggers automated steps including syntax validation, compliance checks, and then deployment tools (Ansible, Terraform, NSO) push the validated configurations to network devices. This is the 'Infrastructure as Code' (IaC) approach to network management.",
      cisco_reference: "ENCOR OCG Chapter 21 — CI/CD for Networking",
      expert_trap_warning: null,
      tags: ["git", "cicd", "iac", "netdevops"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "git-003",
      type: "multi_choice",
      domain: "6.0",
      subdomain: "Git",
      difficulty: 2,
      prompt: "Which TWO Git features are most valuable for network configuration management? (Choose 2)",
      options: [
        { id: "A", text: "Commit history provides a full audit trail of changes", is_correct: true },
        { id: "B", text: "Git automatically pushes configs to routers", is_correct: false },
        { id: "C", text: "Branching allows testing changes before merging to production", is_correct: true },
        { id: "D", text: "Git encrypts all stored configurations", is_correct: false }
      ],
      correct_answer: ["A", "C"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "Git's commit history provides a complete audit trail of who changed what and when. Branching enables testing configuration changes in isolation before merging to the main branch (production). Git does not natively push to routers (that requires Ansible/tools) and does not encrypt stored files (that requires separate tools like git-crypt or Ansible Vault).",
      cisco_reference: "ENCOR OCG Chapter 21 — Version Control Benefits",
      expert_trap_warning: null,
      tags: ["git", "audit-trail", "branching"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    // --- Additional Automation Questions ---
    {
      id: "auto-014",
      type: "drag_drop",
      domain: "6.0",
      subdomain: "Python",
      difficulty: 2,
      prompt: "Match each Python library with its description.",
      options: null,
      correct_answer: null,
      required_selections: null,
      drag_items: [
        { id: "item1", text: "Paramiko" },
        { id: "item2", text: "Netmiko" },
        { id: "item3", text: "NAPALM" },
        { id: "item4", text: "Requests" }
      ],
      drop_targets: [
        { id: "target1", label: "Low-level SSH library", accepts: "item1" },
        { id: "target2", label: "Network device SSH with prompt handling", accepts: "item2" },
        { id: "target3", label: "Vendor-neutral getters with config diff/rollback", accepts: "item3" },
        { id: "target4", label: "HTTP/REST API client library", accepts: "item4" }
      ],
      explanation: "Paramiko provides raw SSH functionality. Netmiko builds on Paramiko with network-device-specific handling. NAPALM provides vendor-neutral abstractions with unified getters and config management. Requests is the standard Python HTTP library for REST API interactions.",
      cisco_reference: "ENCOR OCG Chapter 21 — Python Libraries for Networking",
      expert_trap_warning: null,
      tags: ["python", "paramiko", "netmiko", "napalm", "requests"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "auto-015",
      type: "single_choice",
      domain: "6.0",
      subdomain: "RESTCONF",
      difficulty: 2,
      prompt: "Which media type header should a RESTCONF client use to request data in JSON format?",
      options: [
        { id: "A", text: "application/json", is_correct: false },
        { id: "B", text: "application/yang-data+json", is_correct: true },
        { id: "C", text: "text/json", is_correct: false },
        { id: "D", text: "application/xml", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "RESTCONF uses specific YANG-aware media types: 'application/yang-data+json' for JSON encoding and 'application/yang-data+xml' for XML encoding. These are specified in the Accept header (for response format) and Content-Type header (for request body format). Plain 'application/json' is not the correct RESTCONF media type.",
      cisco_reference: "ENCOR OCG Chapter 20 — RESTCONF Media Types",
      expert_trap_warning: "RESTCONF uses 'application/yang-data+json', NOT plain 'application/json'.",
      tags: ["restconf", "media-types", "json"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "auto-016",
      type: "single_choice",
      domain: "6.0",
      subdomain: "gNMI",
      difficulty: 3,
      prompt: "Which gNMI Subscribe mode provides continuous streaming of data at configured intervals?",
      options: [
        { id: "A", text: "ONCE", is_correct: false },
        { id: "B", text: "POLL", is_correct: false },
        { id: "C", text: "STREAM", is_correct: true },
        { id: "D", text: "BATCH", is_correct: false }
      ],
      correct_answer: "C",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "gNMI Subscribe has three modes: ONCE (retrieve data one time, like a poll), POLL (retrieve data on-demand when the collector requests), and STREAM (continuous delivery at configured sample intervals or on-change). STREAM mode is used for real-time telemetry monitoring.",
      cisco_reference: "ENCOR OCG Chapter 20 — gNMI Subscribe Modes",
      expert_trap_warning: null,
      tags: ["gnmi", "subscribe", "streaming", "telemetry"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "auto-017",
      type: "multi_choice",
      domain: "6.0",
      subdomain: "RESTCONF",
      difficulty: 2,
      prompt: "Which TWO encoding formats does RESTCONF support? (Choose 2)",
      options: [
        { id: "A", text: "JSON", is_correct: true },
        { id: "B", text: "XML", is_correct: true },
        { id: "C", text: "YAML", is_correct: false },
        { id: "D", text: "Protocol Buffers", is_correct: false }
      ],
      correct_answer: ["A", "B"],
      required_selections: 2,
      drag_items: null,
      drop_targets: null,
      explanation: "RESTCONF supports both JSON (application/yang-data+json) and XML (application/yang-data+xml) encoding. The client specifies the desired format via Accept and Content-Type headers. YAML is used by Ansible, and Protocol Buffers are used by gNMI — neither is supported by RESTCONF.",
      cisco_reference: "ENCOR OCG Chapter 20 — RESTCONF Encoding",
      expert_trap_warning: null,
      tags: ["restconf", "json", "xml", "encoding"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

    {
      id: "auto-018",
      type: "single_choice",
      domain: "6.0",
      subdomain: "DNACenterAPI",
      difficulty: 2,
      prompt: "The DNA Center Command Runner API supports which type of commands?",
      options: [
        { id: "A", text: "Both show commands and configuration commands", is_correct: false },
        { id: "B", text: "Read-only show commands only", is_correct: true },
        { id: "C", text: "Configuration commands only", is_correct: false },
        { id: "D", text: "Only 'ping' and 'traceroute' commands", is_correct: false }
      ],
      correct_answer: "B",
      required_selections: null,
      drag_items: null,
      drop_targets: null,
      explanation: "The Command Runner API is restricted to read-only (show) commands for safety. It cannot execute configuration commands that change the device state. This is a deliberate design choice to prevent accidental or unauthorized configuration changes via the API. Use templates or other provisioning APIs for config changes.",
      cisco_reference: "ENCOR OCG Chapter 21 — DNA Center Command Runner",
      expert_trap_warning: null,
      tags: ["dna-center", "command-runner", "read-only"],
      stats: { times_seen: 0, times_correct: 0, avg_time_seconds: 0 }
    },

  ];
}
